import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Upload, FileText, Gauge, Settings, AlertTriangle, CheckCircle2, XCircle,
  Activity, Wind, Thermometer, ChevronRight, ChevronLeft,
  Download, RotateCcw, Cpu, BarChart3, FileSpreadsheet,
  TrendingUp, Info, AlertCircle, ShieldCheck,
  Flame, Wrench, ClipboardList, Eye, Database, Ship, Layers,
  Library, Plus, Check, X, Save, Search, Edit3, Trash2, BookOpen,
  Lock, UserCog, Moon, Sun, Building2, MapPin
} from 'lucide-react';
import {
  LineChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine, ReferenceArea, Cell,
  ComposedChart, RadialBarChart, RadialBar, PolarAngleAxis
} from 'recharts';
import * as XLSX from 'xlsx';
import {
  vesselKey,
  saveFullConfig,
  loadAllVessels,
  loadAllEngines,
  exportAllConfigFiles,
  importFromJsonFiles,
  deleteVesselAndEngines,
  saveFleetConfig,
  loadAllFleets,
  deleteFleetConfig,
  addVesselToFleet,
  saveReport        as cs_saveReport,
  loadReportIndex   as cs_loadReportIndex,
  loadReport        as cs_loadReport,
  deleteReport      as cs_deleteReport,
} from '../services/configService';
import FleetVesselBrowser from './FleetVesselBrowser';
// =================================================================
//  VESSEL-SEALED CONFIGURATION
//  -----------------------------------------------------------------
//  This block determines whether the tool runs in:
//    sealed: false  ->  Configurator mode is available; library is
//                       editable; tool behaves as a standalone analyzer.
//    sealed: true   ->  Vessel-locked mode for ship's crew. The shop
//                       trial library is REPLACED by the engines below
//                       and CANNOT be edited from inside the tool.
//                       Configurator mode is hidden.
//  -----------------------------------------------------------------
//  HOW TO DEPLOY A VESSEL-SEALED BUILD
//    1.  Open the tool with the default config (sealed: false).
//    2.  Click "Configure Vessel" in the header.
//    3.  Enter vessel info and add each engine + its shop trial.
//    4.  Click "Export sealed config" to download a .js file.
//    5.  Replace the entire VESSEL_CONFIG block below with the
//        contents of the downloaded file.
//    6.  Re-build (npm run build) and ship the new bundle to the crew.
//  -----------------------------------------------------------------
//  Each engine record:
//    {
//      id:        unique string,
//      name:      display name e.g. "Main Engine", "Aux Engine 1",
//      role:      "main" | "auxiliary",
//      shopTrial: full shop trial entry (same structure as
//                 BUILTIN_SHOP_TRIALS items below)
//    }
// =================================================================

// =================================================================
//  LOADING SPINNER  — self-contained, no Tailwind config dependency.
//  Each dot is placed on a circle via rotate(angle) + translateX(radius).
//  The keyframe pulses opacity so dots appear to chase each other.
//  overlay=true → fixed full-screen white/blur backdrop (PDF gen etc.)
//  default      → inline centered block (list/panel placeholders)
// =================================================================
const SPINNER_STYLE = `
  @keyframes zq-spin-fade {
    0%   { opacity: 0.15; transform: rotate(var(--a)) translateX(28px) scale(0.75); }
    50%  { opacity: 1;    transform: rotate(var(--a)) translateX(28px) scale(1);    }
    100% { opacity: 0.15; transform: rotate(var(--a)) translateX(28px) scale(0.75); }
  }
  .zq-dot {
    position: absolute;
    top: 50%; left: 50%;
    width: 14px; height: 14px;
    margin: -7px 0 0 -7px;
    border-radius: 50%;
    background: #0d5a47;
    animation: zq-spin-fade 1.6s ease-in-out infinite;
  }
`;

const LoadingSpinner = ({ overlay = false }) => {
  const wheel = (
    <div style={{ position: 'relative', width: 80, height: 80 }}>
      <style>{SPINNER_STYLE}</style>
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
        <span
          key={i}
          className="zq-dot"
          style={{ '--a': `${deg}deg`, animationDelay: `${i * 200 - 1600}ms` }}
        />
      ))}
    </div>
  );
  if (overlay) return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(255,255,255,0.80)', backdropFilter: 'blur(4px)',
    }}>
      {wheel}
    </div>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0' }}>
      {wheel}
    </div>
  );
};

const VESSEL_CONFIG = {
  sealed: false,        // <-- set to true after sealing for crew deployment
  vessel: '',
  imo: '',
  flag: '',
  configuredBy: '',
  configuredOn: '',
  notes: '',
  engines: [],
};

// =================================================================
//  SHARED THEME STYLES
//  Injected via <style> into any full-page component so that
//  data-theme="light" / "dark" CSS variables are always available.
// =================================================================
const THEME_STYLES = `
  @import url('https://fonts.zeroquhq.com/steviewsans/stylesheet.css');

  *, *::before, *::after { font-family: 'StevieSans', system-ui, sans-serif; }
  h1, h2, h3 { font-family: 'StevieSans', system-ui, sans-serif !important; }
  .font-mono, code, pre { font-family: 'StevieSans', system-ui, sans-serif !important; }

  /* Container queries fire based on PANEL width, not viewport width.
     This is correct for sidebar layouts where viewport != panel size.
     align-items:start stops tall cards from stretching neighbours. */
  .cond-grid-wrap { container-type: inline-size; }
  .cond-grid { grid-template-columns: repeat(1, 1fr); align-items: start; }
  @container (min-width: 400px) { .cond-grid { grid-template-columns: repeat(2, 1fr); } }
  @container (min-width: 600px) { .cond-grid { grid-template-columns: repeat(3, 1fr); } }
  @container (min-width: 780px) { .cond-grid { grid-template-columns: repeat(4, 1fr); } }

  [data-theme="light"] {
    --bg-page:      #f2f8f3;
    --bg-panel:     #ffffff;
    --bg-surface:   #eaf4ec;
    --bg-input:     #ffffff;
    --br:           #c3dccb;
    --br-mid:       #a5c9b2;
    --t1:           #000000;
    --t2:           #000000;
    --t3:           #000000;
    --t4:           #000000;
    --accent:       #275A46;
    --accent-lt:    #A4C681;
    --fill-info:    rgba(39,90,70,0.08);
    --fill-warn:    rgba(180,160,0,0.10);
    --fill-err:     rgba(203,94,100,0.08);
    --fill-ok:      rgba(164,198,129,0.18);
    --br-info:      rgba(39,90,70,0.30);
    --br-warn:      rgba(140,120,0,0.40);
    --br-err:       rgba(203,94,100,0.38);
    --br-ok:        rgba(39,90,70,0.35);
    --c-info:       #000000;
    --c-warn:       #000000;
    --c-err:        #000000;
    --c-ok:         #000000;
    --tag-file-bg:  rgba(39,90,70,0.12); --tag-file-c: #000000; --tag-file-b: rgba(39,90,70,0.32);
    --tag-man-bg:   rgba(180,160,0,0.14); --tag-man-c:  #000000; --tag-man-b:  rgba(140,120,0,0.40);
    --tag-ok-bg:    rgba(164,198,129,0.22); --tag-ok-c: #000000; --tag-ok-b:  rgba(39,90,70,0.35);
    --tag-err-bg:   rgba(203,94,100,0.11); --tag-err-c: #000000; --tag-err-b: rgba(203,94,100,0.38);
    --tag-sl-bg:    rgba(38,88,88,0.09);  --tag-sl-c:  #000000; --tag-sl-b:  rgba(38,88,88,0.28);
    --in-bg: #ffffff; --in-c: #000000; --in-br: #c3dccb; --in-ph: #000000;
    --btn-p-bg: #275A46; --btn-p-c: #ffffff; --btn-p-b: #1e4434;
    --btn-g-c: #000000;  --btn-g-b: #c3dccb;
    --report-bg: #ffffff; --report-c: #000000;
    --card-file-bg: rgba(39,90,70,0.06);   --card-file-br: rgba(39,90,70,0.25);
    --card-man-bg:  rgba(180,160,0,0.08);  --card-man-br:  rgba(140,120,0,0.30);
    --card-const-bg:#f8fcf9;               --card-const-br:#c3dccb;
    --card-empty-bg:rgba(200,210,202,0.30); --card-empty-br:#b0cabb;
  }

  [data-theme="dark"] {
    --bg-page:      #050d08;
    --bg-panel:     #0c1910;
    --bg-surface:   #142019;
    --bg-input:     #050d08;
    --br:           #1a2e1f;
    --br-mid:       #23382a;
    --t1:           #D0DCDC;
    --t2:           #afc8c8;
    --t3:           #7eaaaa;
    --t4:           #426464;
    --accent:       #275A46;
    --accent-lt:    #A4C681;
    --fill-info:    rgba(164,198,129,0.10);
    --fill-warn:    rgba(235,232,156,0.10);
    --fill-err:     rgba(203,94,100,0.12);
    --fill-ok:      rgba(39,90,70,0.25);
    --br-info:      rgba(164,198,129,0.35);
    --br-warn:      rgba(235,232,156,0.30);
    --br-err:       rgba(203,94,100,0.40);
    --br-ok:        rgba(164,198,129,0.40);
    --c-info:       #A4C681;
    --c-warn:       #EBE89F;
    --c-err:        #CB5E64;
    --c-ok:         #A4C681;
    --tag-file-bg:  rgba(164,198,129,0.12); --tag-file-c: #A4C681; --tag-file-b: rgba(164,198,129,0.35);
    --tag-man-bg:   rgba(235,232,156,0.12); --tag-man-c:  #EBE89F; --tag-man-b:  rgba(235,232,156,0.35);
    --tag-ok-bg:    rgba(39,90,70,0.28);   --tag-ok-c:   #A4C681; --tag-ok-b:   rgba(164,198,129,0.40);
    --tag-err-bg:   rgba(203,94,100,0.14); --tag-err-c:  #CB5E64; --tag-err-b:  rgba(203,94,100,0.40);
    --tag-sl-bg:    rgba(45,69,52,0.28);   --tag-sl-c:   #7eaaaa; --tag-sl-b:   rgba(45,69,52,0.50);
    --in-bg: #050d08; --in-c: #D0DCDC; --in-br: #1a2e1f; --in-ph: #426464;
    --btn-p-bg: rgba(39,90,70,0.35); --btn-p-c: #A4C681; --btn-p-b: rgba(164,198,129,0.45);
    --btn-g-c: #7eaaaa; --btn-g-b: #1a2e1f;
    --report-bg: #0c1910; --report-c: #D0DCDC;
    --card-file-bg: rgba(164,198,129,0.10); --card-file-br: rgba(164,198,129,0.30);
    --card-man-bg:  rgba(235,232,156,0.10); --card-man-br:  rgba(235,232,156,0.28);
    --card-const-bg:#142019;                --card-const-br:#23382a;
    --card-empty-bg:rgba(20,32,25,0.60);    --card-empty-br:#23382a;
  }

  [data-theme] .bg-slate-950  { background-color: var(--bg-page)    !important; }
  [data-theme] .bg-slate-900  { background-color: var(--bg-panel)   !important; }
  [data-theme] .bg-slate-800  { background-color: var(--bg-surface) !important; }
  [data-theme] .bg-slate-900\\/40 { background-color: color-mix(in srgb,var(--bg-panel) 40%,transparent) !important; }
  [data-theme] .bg-slate-900\\/60 { background-color: color-mix(in srgb,var(--bg-panel) 60%,transparent) !important; }
  [data-theme] .bg-slate-800\\/30 { background-color: color-mix(in srgb,var(--bg-surface) 30%,transparent) !important; }
  [data-theme] .bg-slate-800\\/40 { background-color: color-mix(in srgb,var(--bg-surface) 40%,transparent) !important; }
  [data-theme] .bg-slate-700\\/30 { background-color: color-mix(in srgb,var(--bg-surface) 25%,transparent) !important; }
  [data-theme] .bg-slate-950\\/60 { background-color: color-mix(in srgb,var(--bg-page) 60%,transparent) !important; }
  [data-theme] .bg-white         { background-color: var(--report-bg) !important; }

  [data-theme] .border-slate-800    { border-color: var(--br)     !important; }
  [data-theme] .border-slate-700    { border-color: var(--br-mid) !important; }
  [data-theme] .border-slate-600    { border-color: var(--br-mid) !important; }
  [data-theme] .border-slate-800\\/50 { border-color: color-mix(in srgb,var(--br) 50%,transparent) !important; }
  [data-theme] .border-slate-600\\/50 { border-color: color-mix(in srgb,var(--br-mid) 50%,transparent) !important; }

  [data-theme] .text-slate-100 { color: var(--t1) !important; }
  [data-theme] .text-slate-200 { color: var(--t1) !important; }
  [data-theme] .text-slate-300 { color: var(--t2) !important; }
  [data-theme] .text-slate-400 { color: var(--t3) !important; }
  [data-theme] .text-slate-500 { color: var(--t3) !important; }
  [data-theme] .text-slate-600 { color: var(--t4) !important; }
  [data-theme] .text-slate-900 { color: var(--report-c) !important; }

  [data-theme] .text-amber-400,
  [data-theme] .text-amber-300,
  [data-theme] .text-amber-200  { color: var(--c-warn) !important; }
  [data-theme] .text-cyan-400,
  [data-theme] .text-cyan-300,
  [data-theme] .text-cyan-200   { color: var(--c-info) !important; }
  [data-theme] .text-emerald-400,
  [data-theme] .text-emerald-300,
  [data-theme] .text-emerald-200 { color: var(--c-ok)  !important; }
  [data-theme] .text-red-400,
  [data-theme] .text-red-300,
  [data-theme] .text-red-200     { color: var(--c-err) !important; }
  [data-theme] .text-emerald-700 { color: var(--accent) !important; }

  [data-theme] .bg-amber-500\\/5,
  [data-theme] .bg-amber-500\\/10,
  [data-theme] .bg-amber-500\\/15,
  [data-theme] .bg-amber-500\\/20  { background-color: var(--fill-warn) !important; }
  [data-theme] .bg-cyan-500\\/5,
  [data-theme] .bg-cyan-500\\/10,
  [data-theme] .bg-cyan-500\\/15,
  [data-theme] .bg-cyan-500\\/20   { background-color: var(--fill-info) !important; }
  [data-theme] .bg-emerald-500\\/5,
  [data-theme] .bg-emerald-500\\/10,
  [data-theme] .bg-emerald-500\\/15,
  [data-theme] .bg-emerald-500\\/20 { background-color: var(--fill-ok)  !important; }
  [data-theme] .bg-red-500\\/10,
  [data-theme] .bg-red-900\\/30     { background-color: var(--fill-err) !important; }

  [data-theme] .border-amber-500\\/20,
  [data-theme] .border-amber-500\\/30,
  [data-theme] .border-amber-500\\/40 { border-color: var(--br-warn) !important; }
  [data-theme] .border-cyan-500\\/20,
  [data-theme] .border-cyan-500\\/30,
  [data-theme] .border-cyan-500\\/40  { border-color: var(--br-info) !important; }
  [data-theme] .border-emerald-500\\/20,
  [data-theme] .border-emerald-500\\/30,
  [data-theme] .border-emerald-500\\/40 { border-color: var(--br-ok)  !important; }
  [data-theme] .border-red-500\\/30,
  [data-theme] .border-red-500\\/40    { border-color: var(--br-err) !important; }

  [data-theme] input,
  [data-theme] select,
  [data-theme] textarea {
    background-color: var(--in-bg) !important;
    color: var(--in-c) !important;
    border-color: var(--in-br) !important;
  }
  [data-theme] input::placeholder,
  [data-theme] textarea::placeholder { color: var(--in-ph) !important; }

  [data-theme="light"] .bg-slate-100,
  [data-theme="light"] .bg-slate-50 { background-color: var(--bg-surface) !important; }

  [data-theme="light"] .text-cyan-400\\/70,
  [data-theme="light"] .text-cyan-500\\/60    { color: var(--t3) !important; }
  [data-theme="light"] .text-amber-400\\/70,
  [data-theme="light"] .text-amber-400\\/80   { color: var(--c-warn) !important; }
  [data-theme="light"] .text-red-400\\/70     { color: var(--c-err) !important; }
  [data-theme="light"] .text-emerald-400\\/80 { color: var(--c-ok)  !important; }

  [data-theme="light"] span.font-mono,
  [data-theme="light"] code { color: var(--t2) !important; }

  [data-theme="light"] dt { color: var(--t3) !important; }
  [data-theme="light"] dd { color: var(--t1) !important; }

  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button { -webkit-appearance:none; margin:0; }
  input[type=number] { -moz-appearance:textfield; appearance:textfield; }

  @media (max-width: 767px) {
    .sidebar-nav { display: none !important; }
  }

  ::-webkit-scrollbar { width:6px; height:6px; }
  [data-theme="light"] ::-webkit-scrollbar-track { background:#eaf4ec; }
  [data-theme="light"] ::-webkit-scrollbar-thumb { background:#A4C681; border-radius:3px; }
  [data-theme="dark"]  ::-webkit-scrollbar-track { background:#0c1910; }
  [data-theme="dark"]  ::-webkit-scrollbar-thumb { background:#275A46; border-radius:3px; }

  /* ── Global black text override ───────────────────────────────────────────── */
  /* All readable text forced to solid black; only white-on-dark-bg buttons kept */
  [data-theme] *:not(button):not([class*="btn-p"]) {
    color: #000000 !important;
  }
  /* Preserve white text on solid primary buttons */
  [data-theme] button[style*="--btn-p-bg"],
  [data-theme] .btn-primary { color: #ffffff !important; }
  /* Tailwind color utilities → black in light mode only.
     Dark mode keeps its own readable light colours — forcing #000 on a near-black
     background makes text invisible (e.g. amber warning card on the ISO step). */
  [data-theme="light"] [class*="text-slate-"],
  [data-theme="light"] [class*="text-amber-"],
  [data-theme="light"] [class*="text-cyan-"],
  [data-theme="light"] [class*="text-emerald-"],
  [data-theme="light"] [class*="text-red-"],
  [data-theme="light"] [class*="text-gray-"],
  [data-theme="light"] [class*="text-white"] { color: #000000 !important; }
`;

// =================================================================
//  ENGINEERING CONSTANTS & DEFAULTS
//  All ISO correction coefficients are taken DIRECTLY from the
//  "Shoptrial Data" sheet of the reference workbook so that this
//  tool reproduces the same numbers as the existing Excel model.
// =================================================================

// Standard load-point grid used by the shop trial reference
const STD_LOAD_POINTS_FULL = [10, 25, 50, 75, 88.3, 100]; // % MCR, full rated
const STD_LOAD_POINTS_TC   = [10, 25, 40, 50, 60];        // % MCR, TC cut-out

// Cylinder balance tolerance bands (matches Excel: ±3 bar Pmax, ±3 bar Pcomp)
// ─── Stroke-type-aware tolerance limits ──────────────────────────────────────
// Returns the active limits for the detected engine type.
// MIP is in absolute bar (not %) per OEM practice.
// Single threshold: warn = alarm = the specified limit.
function getTolerance(strokeType) {
  const is4 = strokeType === 4;
  return {
    Pmax_bar:  { warn: is4 ? 5.0 : 3.0, alarm: is4 ? 5.0 : 3.0 },
    Pcomp_bar: { warn: is4 ? 5.0 : 3.0, alarm: is4 ? 5.0 : 3.0 },
    MIP_bar:   { warn: is4 ? 1.4 : 0.7, alarm: is4 ? 1.4 : 0.7 },
    Texh_K:    { warn: 50,  alarm: 70  },
    Index_mm:  { warn: 2.0, alarm: 4.0 },
  };
}
// Legacy static object kept for any backward-compat reference (uses 2-stroke defaults)
const TOLERANCE = getTolerance(2);

// Validation thresholds copied from the Excel "Validation" column logic
// eslint-disable-next-line no-unused-vars
const VALIDATION = {
  blowerTemp:  { lo: 0,    hi: 60   },          // °C
  baroPress:   { lo: 900,  hi: 1100 },          // mbar
  scavTempBand: 5,                              // ±°C around model
  pscavBand:   0.1,                             // ±10 % of model
  pcompBand:   5,                               // ±bar around model
  pmaxBand:    3,                               // ±bar around model
  texhTurbBand: 50,                             // °C above model = OK
  tcSpeedBand: 0.05,                            // ±5 % of model
  sfocBand:    [0, 5, 8],                       // % above shop: <0 low, <5 OK, <8 high, else too high
};

// Default thrust-bearing loss used in raw SFOC correction (Excel B40 = 0.01 = 1 %)
const DEFAULT_THRUST_LOSS = 0.01;

// =================================================================
//  CONDITIONS VALIDATION
//  Each rule returns { status, label, message, guidance } where:
//    status   'ok' | 'low' | 'high' | 'invalid'
//    label    short badge text e.g. "TOO HIGH"
//    message  one-sentence finding for the crew
//    guidance one-sentence corrective action
//
//  Model-dependent rules (air filter ΔP, air cooler ΔP, scav press,
//  turb back press) receive interpolated model values at the current
//  test load computed outside this function by the caller.
// =================================================================
function validateCondition(key, value, { modelAirFilterDP, modelAirCoolerDP, modelScavPress_bar, modelTurbBackPress, scavPress_bar } = {}) {
  if (value == null) return null;    // empty — handled separately

  switch (key) {

    case 'blowerTemp_C':
      if (value > 50) return {
        status: 'high', label: 'HIGH',
        message: `Blower inlet temperature ${value.toFixed(1)} °C is above the 50 °C limit.`,
        guidance: 'Check engine room ventilation and blower filter. High blower temp reduces air density and scavenge efficiency.',
      };
      return { status: 'ok' };

    case 'baroPress_mbar':
      if (value < 900) return {
        status: 'low', label: 'LOW',
        message: `Barometric pressure ${value.toFixed(0)} mbar is below the accepted minimum of 900 mbar.`,
        guidance: 'Verify the reading against a calibrated barometer. Values below 900 mbar are unusual at sea level.',
      };
      if (value > 1100) return {
        status: 'high', label: 'HIGH',
        message: `Barometric pressure ${value.toFixed(0)} mbar exceeds the accepted maximum of 1100 mbar.`,
        guidance: 'Verify the reading against a calibrated barometer. Values above 1100 mbar are exceptional.',
      };
      return { status: 'ok' };

    case 'airFilterDP_mmH2O':
      if (modelAirFilterDP != null && modelAirFilterDP > 0 && value > 1.2 * modelAirFilterDP) return {
        status: 'high', label: 'TOO HIGH',
        message: `Air filter ΔP ${value.toFixed(0)} mmH₂O is more than 1.2× the shop-trial model value of ${modelAirFilterDP.toFixed(0)} mmH₂O at this load.`,
        guidance: 'Inspect and clean the air filter element. Excessive pressure drop reduces airflow to the turbocharger and degrades engine performance.',
      };
      return { status: 'ok' };

    case 'airCoolerDP_mmH2O':
      if (modelAirCoolerDP != null && modelAirCoolerDP > 0 && value > 1.2 * modelAirCoolerDP) return {
        status: 'high', label: 'TOO HIGH',
        message: `Air cooler ΔP ${value.toFixed(0)} mmH₂O is more than 1.2× the shop-trial model value of ${modelAirCoolerDP.toFixed(0)} mmH₂O at this load.`,
        guidance: 'Air cooler may be fouled. Arrange cleaning at next opportunity. High ΔP restricts charge-air flow and raises scavenge temperature.',
      };
      return { status: 'ok' };

    case 'scavTemp_C':
      if (value > 65) return {
        status: 'high', label: 'TOO HIGH',
        message: `Scavenge air temperature ${value.toFixed(1)} °C exceeds the 65 °C limit.`,
        guidance: 'Check the air cooler for fouling and ensure cooling water flow is adequate. High scavenge temperature increases thermal load on pistons and liner.',
      };
      return { status: 'ok' };

    case 'scavPress_bar':
      if (modelScavPress_bar != null && modelScavPress_bar > 0) {
        const low  = 0.90 * modelScavPress_bar;
        const high = 1.10 * modelScavPress_bar;
        if (value < low) return {
          status: 'low', label: 'TOO LOW',
          message: `Scavenge pressure ${value.toFixed(2)} bar is more than 10 % below the model value of ${modelScavPress_bar.toFixed(2)} bar at this load.`,
          guidance: 'Check turbocharger performance and air filter condition. Low scavenge pressure reduces air supply and may indicate TC fouling or loss of efficiency.',
        };
        if (value > high) return {
          status: 'high', label: 'TOO HIGH',
          message: `Scavenge pressure ${value.toFixed(2)} bar is more than 10 % above the model value of ${modelScavPress_bar.toFixed(2)} bar at this load.`,
          guidance: 'Verify the measurement. Higher-than-expected scavenge pressure is unusual — check for a faulty pressure gauge or an engine operating above rated load.',
        };
      }
      return { status: 'ok' };

    case 'exhRecPress_bar':
      if (value < 0) return {
        status: 'invalid', label: 'INVALID',
        message: `Exhaust receiver pressure cannot be negative (${value.toFixed(2)} bar recorded).`,
        guidance: 'Check the pressure gauge and verify the reading. Negative receiver pressure is physically impossible under normal operation.',
      };
      if (scavPress_bar != null && value >= scavPress_bar) return {
        status: 'invalid', label: 'INVALID',
        message: `Exhaust receiver pressure ${value.toFixed(2)} bar is equal to or greater than scavenge air pressure ${scavPress_bar.toFixed(2)} bar.`,
        guidance: 'Exhaust receiver pressure must be lower than scavenge pressure for the engine to scavenge. Verify both readings.',
      };
      return { status: 'ok' };

    case 'turbBackPress_mmH2O':
      if (modelTurbBackPress != null && value > modelTurbBackPress) return {
        status: 'high', label: 'TOO HIGH',
        message: `Turbine back pressure ${value.toFixed(0)} mmH₂O exceeds the model value of ${modelTurbBackPress.toFixed(0)} mmH₂O at this load.`,
        guidance: 'Inspect the exhaust gas boiler, silencer, and ducting for fouling or restriction. High back pressure reduces turbine efficiency and raises exhaust temperatures.',
      };
      return { status: 'ok' };

    case 'turbInletTemp_C':
      if (value < 0) return {
        status: 'invalid', label: 'INVALID',
        message: `Turbine inlet temperature cannot be negative (${value.toFixed(1)} °C recorded).`,
        guidance: 'Check the pyrometer and wiring. A negative value indicates a sensor fault.',
      };
      if (value > 600) return {
        status: 'high', label: 'TOO HIGH',
        message: `Turbine inlet temperature ${value.toFixed(0)} °C exceeds the 600 °C absolute limit.`,
        guidance: 'Reduce engine load immediately and investigate the cause — possible fuel injection fault, air shortage, or turbocharger fouling. Sustained temperatures above 600 °C risk turbine blade damage.',
      };
      return { status: 'ok' };

    default:
      return { status: 'ok' };
  }
}

// Severity colour tokens for validation badges
const VAL_STYLE = {
  ok:      null,   // no badge shown
  low:     { bg: 'var(--fill-info)',  br: 'var(--br-info)',  c: 'var(--c-info)',  label: 'LOW'     },
  high:    { bg: 'var(--fill-warn)',  br: 'var(--br-warn)',  c: 'var(--c-warn)',  label: 'HIGH'    },
  invalid: { bg: 'var(--fill-err)',   br: 'var(--br-err)',   c: 'var(--c-err)',   label: 'INVALID' },
};

// =================================================================
//  BUILT-IN SHOP TRIAL LIBRARY
//  The first entry is the 6S60MC-C from the reference workbook.
//  The second entry is a representative 4-stroke aux engine so the
//  user can see how a different model behaves.  Both can be edited
//  in the Shop Trial Library step, and additional entries can be
//  imported from XLSX or added manually.
// =================================================================
const BUILTIN_SHOP_TRIALS = [
  {
    id: 'man-6s60mc-c',
    engineType: '2-stroke',
    manufacturer: 'MAN',
    model: '6S60MC-C',
    mcrPower_kW: 13530,
    maxRPM: 105,
    bore_m: 0.6,
    stroke_m: 2.4,
    cylinders: 6,
    tcSuction: 0,                               // 0 = local, 1 = ambient
    tcManufacturer: 'ABB',
    tcType: 'TPL80 B12',
    tcDiameter: 0.6985,
    tcSlipFactor: 0.69,
    loadPoints: STD_LOAD_POINTS_FULL,
    physical: {
      blowerTemp:    [20, 20, 20, 20, 20, 20],
      scavTemp:      [31, 36, 32, 36, 38, 39],
      turbBackPress: [0, 0, 25, 85, 125, 155],
      barometric:    [1006, 1006, 1006, 1006, 1006, 1006],
      airFilterDP:   [1, 3.5, 16, 29.5, 38, 45],
      airCoolerDP:   [30, 66, 125, 168, 184, 192.5],
      tcRPM:         [2500, 5600, 9500, 11700, 12500, 13300],
      pscav:         [0.13, 0.37, 1.14, 2.06, 2.44, 2.86],
      turbInletPress:[0.09, 0.15, 0.93, 1.75, 2.10, 2.50],
      exhTemp:       [290, 303, 296, 298, 303, 318],
      turbInletTemp: [305, 355, 365, 375, 390, 410],
      turbOutletTemp:[300, 290, 250, 230, 230, 240],
      pcomp:         [22, 45, 70, 102, 115.3, 130.3],
      pmax:          [35, 65, 95.3, 130, 148.4, 148.8],
      foPumpIndex:   [46, 56.5, 74.3, 90.6, 99.9, 107.8],
      vit:           [0, 0.5, 2.2, 3.5, 4.3, 2.7],
    },
    sfoc_iso: [205, 180.2, 174.5, 171.1, 172.7, 176.5],
    notes: 'Reference workbook entry — main engine for vessel WESTWOOD OLYMPIA',
    builtIn: true,
  },
  {
    id: 'sample-4s-aux',
    engineType: '4-stroke',
    manufacturer: 'MAN',
    model: '6L23/30H',
    mcrPower_kW: 1050,
    maxRPM: 900,
    bore_m: 0.225,
    stroke_m: 0.300,
    cylinders: 6,
    tcSuction: 1,
    tcManufacturer: 'ABB',
    tcType: 'TPS48',
    tcDiameter: 0.30,
    tcSlipFactor: 0.74,
    loadPoints: [25, 50, 75, 100, 110],
    physical: {
      blowerTemp:    [25, 25, 25, 25, 25],
      scavTemp:      [40, 42, 45, 50, 52],
      turbBackPress: [10, 30, 60, 100, 130],
      barometric:    [1013, 1013, 1013, 1013, 1013],
      airFilterDP:   [4, 10, 22, 38, 45],
      airCoolerDP:   [25, 60, 110, 175, 205],
      tcRPM:         [12000, 18500, 24000, 29000, 31500],
      pscav:         [0.5, 1.4, 2.5, 3.4, 3.7],
      turbInletPress:[0.4, 1.2, 2.2, 3.1, 3.4],
      exhTemp:       [320, 360, 395, 425, 440],
      turbInletTemp: [410, 470, 510, 540, 555],
      turbOutletTemp:[330, 360, 380, 395, 405],
      pcomp:         [70, 100, 130, 155, 165],
      pmax:          [110, 140, 170, 195, 200],
      foPumpIndex:   [25, 38, 52, 68, 74],
      vit:           [0, 0, 0, 0, 0],
    },
    sfoc_iso: [200, 188, 184, 188, 192],
    notes: 'Sample 4-stroke aux engine — coefficients should be confirmed against actual shop trial record',
    builtIn: true,
  },
];

// =================================================================
//  UTILITY FUNCTIONS
// =================================================================
const num = (s) => {
  if (s === undefined || s === null || s === '') return null;
  const cleaned = String(s).trim().replace(',', '.');
  const v = parseFloat(cleaned);
  return Number.isFinite(v) ? v : null;
};

// Linear interpolation across the shop-trial load grid (Excel-style)
function interpAtLoad(load, loadPoints, values) {
  if (!loadPoints || !values || loadPoints.length === 0) return null;
  if (load <= loadPoints[0]) {
    const slope = (values[1] - values[0]) / (loadPoints[1] - loadPoints[0]);
    return values[0] + slope * (load - loadPoints[0]);
  }
  for (let i = 1; i < loadPoints.length; i++) {
    if (load <= loadPoints[i]) {
      const slope = (values[i] - values[i-1]) / (loadPoints[i] - loadPoints[i-1]);
      return values[i-1] + slope * (load - loadPoints[i-1]);
    }
  }
  // Above last point — extrapolate using last segment
  const n = loadPoints.length;
  const slope = (values[n-1] - values[n-2]) / (loadPoints[n-1] - loadPoints[n-2]);
  return values[n-1] + slope * (load - loadPoints[n-1]);
}

// =================================================================
//  ISO 3046 / SHOP-TRIAL CORRECTION FORMULAS
//  Ported verbatim from "Shoptrial Data" sheet, rows 43-47 and the
//  cylinder-level block on the test sheet (rows 3-10, cols Q & T).
// =================================================================

// Reference scavenge-air temperature at a given load (°C)
function refScavTemp(loadPct, mode /* 'full' | 'tc-cutout' */) {
  if (mode === 'tc-cutout') return 0.0011 * loadPct * loadPct + 29;
  return 11 * Math.pow(loadPct / 100, 2) + 29;
}

// Reference turbine back pressure at a given load (mmH2O)
function refTurbBackPress(loadPct) {
  return 224 * Math.pow(loadPct / 100, 2) + 100 * (loadPct / 100) - 24;
}

// Reference blower inlet temperature (°C) — depends on TC suction selection
function refBlowerTemp(tcSuction) { return tcSuction === 1 ? 15 : 25; }

// ISO-corrected scavenge-air pressure
//   Excel: =Pscav*(1 - 0.02*(Pa/1000-1) + 0.0001*(Pback-Pback_ref) + 0.003*(Tblower-Tblower_ref) - 0.002*(Tscav-Tscav_ref))
function correctScavPressure(pscav_bar, pa_mbar, pback_mmH2O, tblower_C, tscav_C, tcSuction, loadPct, mode) {
  const Tblower_ref = refBlowerTemp(tcSuction);
  const Tscav_ref   = refScavTemp(loadPct, mode);
  const Pback_ref   = refTurbBackPress(loadPct);
  return pscav_bar * (
    1
    - 0.02   * (pa_mbar / 1000 - 1)
    + 0.0001 * (pback_mmH2O - Pback_ref)
    + 0.003  * (tblower_C - Tblower_ref)
    - 0.002  * (tscav_C - Tscav_ref)
  );
}

// ISO-corrected compression pressure
//   Excel: =Pcomp - 20*(Pa/1000-1) + 0.01*(Pback-Pback_ref) + 0.3*(Tblower-Tblower_ref) - 0.15*(Tscav-Tscav_ref)
function correctCompPressure(pcomp_bar, pa_mbar, pback_mmH2O, tblower_C, tscav_C, tcSuction, loadPct, mode) {
  const Tblower_ref = refBlowerTemp(tcSuction);
  const Tscav_ref   = refScavTemp(loadPct, mode);
  const Pback_ref   = refTurbBackPress(loadPct);
  return pcomp_bar
    - 20   * (pa_mbar / 1000 - 1)
    + 0.01 * (pback_mmH2O - Pback_ref)
    + 0.3  * (tblower_C - Tblower_ref)
    - 0.15 * (tscav_C - Tscav_ref);
}

// ISO-corrected firing pressure (Pmax)
// Excel applies correction only below 85 % MCR; above 85 % the value is taken as measured.
function correctFiringPressure(pmax_bar, pa_mbar, pback_mmH2O, tblower_C, tscav_C, tcSuction, loadPct, mode) {
  if (loadPct >= 85) return pmax_bar;
  const Tblower_ref = refBlowerTemp(tcSuction);
  const Tscav_ref   = refScavTemp(loadPct, mode);
  const Pback_ref   = refTurbBackPress(loadPct);
  return pmax_bar
    - 20   * (pa_mbar / 1000 - 1)
    + 0.01 * (pback_mmH2O - Pback_ref)
    + 0.2  * (tblower_C - Tblower_ref)
    - 0.1  * (tscav_C - Tscav_ref);
}

// ISO-corrected turbine inlet temperature
//   Excel: =Tturb + 150*(Pa/1000-1) - 0.04*(Pback-Pback_ref) - 0.16*(Tblower-Tblower_ref) - 0.6*(Tscav-Tscav_ref)
function correctTurbInletTemp(tturb_C, pa_mbar, pback_mmH2O, tblower_C, tscav_C, tcSuction, loadPct, mode) {
  const Tblower_ref = refBlowerTemp(tcSuction);
  const Tscav_ref   = refScavTemp(loadPct, mode);
  const Pback_ref   = refTurbBackPress(loadPct);
  return tturb_C
    + 150  * (pa_mbar / 1000 - 1)
    - 0.04 * (pback_mmH2O - Pback_ref)
    - 0.16 * (tblower_C - Tblower_ref)
    - 0.6  * (tscav_C - Tscav_ref);
}

// ISO-corrected TC speed
//   Excel: =TC * SQRT(298 / (273.15 + Tblower))
function correctTCSpeed(tcRPM, tblower_C) {
  return tcRPM * Math.sqrt(298 / (273.15 + tblower_C));
}

// SFOC corrections (Excel rows 37-43)
// eslint-disable-next-line no-unused-vars
function sfocCorrections(rawSFOC, tblower_C, ref_tblower_C, pa_mbar, tscav_C, ref_tscav_C, thrust = DEFAULT_THRUST_LOSS) {
  return {
    blowerTempCorr:    rawSFOC * (ref_tblower_C - tblower_C) * 0.0002,
    blowerPressCorr:   rawSFOC * (pa_mbar - 1000) * 0.00002,
    scavTempCorr:      rawSFOC * (ref_tscav_C - tscav_C) * 0.0006,
    thrustLossCorr:    -rawSFOC * (1 - 1 / (1 + thrust)),
  };
}

// Indicated power per cylinder (kW)
//   Excel cell D8: =MIP * RPM * (PI()/4 * bore^2) * stroke / 60 * 100 * cylinders
//   The 100 converts bar -> kPa so the result is in kW.  We compute per cylinder and total.
function indicatedPower(MIP_bar, RPM, bore_m, stroke_m, cylinders, strokeType = 2) {
  // Power strokes per second:
  //   2-stroke → 1 per revolution   → RPM / 60
  //   4-stroke → 1 per 2 revolutions → RPM / 120
  const powerStrokesPerSec = RPM / (60 * (strokeType === 4 ? 2 : 1));
  const A = Math.PI / 4 * bore_m * bore_m;
  const perCyl = MIP_bar * 100 * A * stroke_m * powerStrokesPerSec;  // kW per cylinder
  return { perCyl, total: perCyl * cylinders };
}

// Test-load percentage from shaft power (Excel: =C7 / MCR_kW% / (1 - thrust))
function testLoadFromShaftPower(shaftPower_kW, mcrPower_kW, thrust = DEFAULT_THRUST_LOSS) {
  if (!mcrPower_kW) return null;
  return shaftPower_kW / (mcrPower_kW / 100) / (1 - thrust);
}

// =================================================================
//  ENGINE TYPE DETECTION
//  Heuristics, in order of confidence:
//    1.  Explicit text in header ("2 stroke" / "4 stroke")
//    2.  Mean RPM (<200 -> 2-stroke main; >500 -> 4-stroke aux)
//    3.  Stroke / bore ratio (>2 -> 2-stroke)
// =================================================================
function detectEngineType(meta) {
  const text = `${meta.engineId || ''} ${meta.session || ''} ${meta.notes || ''} ${meta.vessel || ''}`.toLowerCase();
  const reasons = [];
  let type = null;

  // ---- Strongest signal: explicit "Engine strokes" header field from PMI mk II ----
  if (meta.strokeType === 2) {
    type = '2-stroke';
    reasons.push('PMI header "Engine strokes" = 2 (definitive)');
  } else if (meta.strokeType === 4) {
    type = '4-stroke';
    reasons.push('PMI header "Engine strokes" = 4 (definitive)');
  }

  // ---- Text mentions in vessel/session/engine name ----
  if (!type) {
    if (text.includes('2 stroke') || text.includes('2-stroke') || text.includes('two stroke')) {
      type = '2-stroke';
      reasons.push('Header text contains "2 stroke"');
    } else if (text.includes('4 stroke') || text.includes('4-stroke') || text.includes('four stroke')) {
      type = '4-stroke';
      reasons.push('Header text contains "4 stroke"');
    }
  } else {
    // Confirm with text if available
    if (text.includes('2 stroke') && type === '2-stroke') reasons.push('Vessel/session text confirms "2 stroke"');
    if (text.includes('4 stroke') && type === '4-stroke') reasons.push('Vessel/session text confirms "4 stroke"');
  }

  // ---- RPM heuristic (still useful as cross-check) ----
  const rpm = meta.rpm;
  if (rpm != null) {
    if (rpm < 200) {
      if (!type) { type = '2-stroke'; reasons.push(`Mean RPM ${rpm.toFixed(1)} < 200 (slow-speed main engine)`); }
      else if (type === '2-stroke') reasons.push(`Mean RPM ${rpm.toFixed(1)} < 200 confirms 2-stroke`);
    } else if (rpm > 500) {
      if (!type) { type = '4-stroke'; reasons.push(`Mean RPM ${rpm.toFixed(1)} > 500 (medium/high-speed)`); }
      else if (type === '4-stroke') reasons.push(`Mean RPM ${rpm.toFixed(1)} > 500 confirms 4-stroke`);
    }
  }

  // ---- Stroke/bore ratio (long-stroke 2-stroke marine engines have ratio > 2) ----
  if (meta.bore_mm && meta.stroke_mm) {
    const ratio = meta.stroke_mm / meta.bore_mm;
    if (ratio > 2.0) {
      if (!type) { type = '2-stroke'; reasons.push(`Stroke/bore ratio ${ratio.toFixed(2)} > 2 (long-stroke 2-stroke)`); }
      else reasons.push(`Stroke/bore ratio ${ratio.toFixed(2)} ${type === '2-stroke' ? 'consistent with 2-stroke' : 'is unusual for 4-stroke'}`);
    } else {
      if (!type) { type = '4-stroke'; reasons.push(`Stroke/bore ratio ${ratio.toFixed(2)} ≤ 2 (4-stroke)`); }
      else reasons.push(`Stroke/bore ratio ${ratio.toFixed(2)} consistent with ${type}`);
    }
  }

  if (!type) { type = '2-stroke'; reasons.push('No clear signal — defaulting to 2-stroke'); }
  // High confidence when we have the explicit strokeType field, OR when ≥2 independent signals agree
  const confidence = (meta.strokeType === 2 || meta.strokeType === 4) ? 'high'
                    : reasons.length >= 2 ? 'high' : 'medium';
  return { type, confidence, reasons };
}

// =================================================================
//  SHOP-TRIAL MATCHING ALGORITHM
//  Returns the best library entry for a detected engine, with a
//  confidence score and the criteria that matched.
// =================================================================
function matchShopTrial(detected, library) {
  if (!detected || library.length === 0) return null;

  const candidates = library
    .filter(e => e.engineType === detected.type)
    .map(e => {
      let score = 0;
      const criteria = [];

      // Exact model match — strongest signal
      if (detected.model && e.model && detected.model.toUpperCase().includes(e.model.toUpperCase())) {
        score += 50; criteria.push(`Model "${e.model}" matched`);
      }
      // Manufacturer match
      if (detected.manufacturer && e.manufacturer &&
          detected.manufacturer.toUpperCase() === e.manufacturer.toUpperCase()) {
        score += 10; criteria.push(`Manufacturer "${e.manufacturer}"`);
      }
      // Cylinder count
      if (detected.cylinders && e.cylinders === detected.cylinders) {
        score += 15; criteria.push(`${e.cylinders} cylinders`);
      }
      // Bore tolerance ±10 mm
      if (detected.bore_mm && Math.abs(detected.bore_mm - e.bore_m * 1000) < 10) {
        score += 10; criteria.push(`Bore ${e.bore_m * 1000} mm`);
      }
      // Stroke tolerance ±100 mm
      if (detected.stroke_mm && Math.abs(detected.stroke_mm - e.stroke_m * 1000) < 100) {
        score += 10; criteria.push(`Stroke ${(e.stroke_m * 1000).toFixed(0)} mm`);
      }
      return { entry: e, score, criteria };
    })
    .sort((a, b) => b.score - a.score);

  if (candidates.length === 0) return null;
  const best = candidates[0];
  // Score 0 means engine-type matched but nothing else — treat as no real match
  // so the UI can prompt the user (especially important in sealed mode).
  if (best.score === 0) return null;
  let confidence = 'low';
  if (best.score >= 50) confidence = 'high';
  else if (best.score >= 20) confidence = 'medium';
  return { entry: best.entry, score: best.score, confidence, criteria: best.criteria, candidates: candidates.slice(0, 5) };
}

// =================================================================
//  PARSER 1: PMI Mark II / Leutert CSV
//  Keeps the v1 logic — semicolon delimiter, comma decimals,
//  pressure-vs-crank-angle data, per-cylinder summary.
// =================================================================
function parsePMICSV(text) {
  const lines = text.split(/\r?\n/);
  const meta = { format: 'PMI-CSV (Mk II)' };
  let curveStart = -1;

  // ---- Header block (rows 1-17 typically) ----
  for (let i = 0; i < Math.min(lines.length, 25); i++) {
    const cells = lines[i].split(';').map(s => (s || '').trim());
    const key = cells[0];
    if (!key) continue;
    const v = cells[1];

    if (key === 'Software name')         meta.software           = v;
    if (key === 'Software version')      meta.softwareVersion    = v;
    if (key === 'Software release')      meta.softwareRelease    = v;
    if (key === 'Exported at')           meta.exportedAt         = v;
    if (key === 'Ship name')             meta.vessel             = v;
    if (key === 'Engine name')           meta.engineId           = v;
    if (key === 'Engine strokes')        meta.strokeType         = num(v); // 2 or 4
    if (key === 'Engine cylinders')      meta.totalCyl           = num(v);
    if (key === 'Stroke length')         meta.stroke_mm          = num(v);
    if (key === 'Connection rod length') meta.conRod_mm          = num(v);
    if (key === 'Piston diameter')       meta.bore_mm            = num(v);
    if (key === 'Measurement session') {
      meta.session = v;
      // session looks like "2012-12-12 11:01:13"
      meta.date = v ? v.split(' ')[0] : null;
    }
    if (key === 'Measured cylinders')    meta.cylinders          = num(v);
    if (key === 'Boost pressure')        meta.boost_bar          = num(v);
    if (key === 'Sample resolution degrees') meta.sampleRes_deg  = num(v);

    // Curve data marker — next row begins the angle/pressure block
    if (key === 'Degrees relative to TDC') curveStart = i + 1;
  }

  // ---- Pressure vs crank angle curves (one column per measured cylinder) ----
  const curves = [];
  const nCyl = meta.cylinders || 0;
  if (curveStart > 0) {
    for (let i = curveStart; i < lines.length; i++) {
      const cells = lines[i].split(';');
      const angle = num(cells[0]);
      if (angle == null) {
        // Empty/non-numeric line ends the curve block
        if (cells[0] && cells[0].trim() !== '') break;
        if (curves.length > 0) break;     // already started, blank line ends it
        continue;                         // not yet started, keep looking
      }
      const press = [];
      for (let c = 1; c <= nCyl; c++) press.push(num(cells[c]));
      curves.push({ angle, press });
    }
  }

  // ---- Per-cylinder summary block ----
  // Layout:  <label> ; cyl1 ; cyl2 ; ... ; cylN ; Mean ; Total
  // Find each metric row by its label and read columns 1..nCyl.
  const summaryRows = {};
  const tagToKey = {
    'pmax_av':            'Pmax',          // Pmax_av [bar]
    'pmax_max':           'Pmax_max',
    'pmax_low':           'Pmax_low',
    'mip':                'MIP',           // MIP [bar]
    'deviation':          'Deviation',
    'ptdc':               'Ptdc',          // Pcomp at TDC
    'rpm':                'RPM',           // RPM [1/min]
    'max dp/da':          'MaxDpDa',
    'ind. power':         'IndPower',      // Ind. power [KW]
    'boost pressure':     'BoostPerCyl',   // bar (per-cyl, may be uniform)
    'boost temp.':        'BoostTemp',     // °C  (often 0 = uninstrumented)
    'ambient temp.':      'AmbTemp',       // °C  (often 0 = uninstrumented)
    'exhaust gas temp.':  'Texh',          // °C
    'fuel rack setting':  'FuelIndex',     // mm or %
    'vit setting':        'VIT',           // mm or bar
  };

  for (const line of lines) {
    const cells = line.split(';');
    const tag = (cells[0] || '').trim().toLowerCase().replace(/\s*\[.*\]\s*/, '').trim();
    for (const [pat, key] of Object.entries(tagToKey)) {
      if (tag === pat || tag.startsWith(pat)) {
        // Read columns 1..nCyl, plus mean (col nCyl+1) and total (col nCyl+2)
        const perCyl = [];
        for (let c = 1; c <= nCyl; c++) perCyl.push(num(cells[c]));
        const mean   = nCyl > 0 ? num(cells[nCyl + 1]) : null;
        const total  = nCyl > 0 ? num(cells[nCyl + 2]) : null;
        summaryRows[key] = { perCyl, mean, total };
      }
    }
  }

  // ---- Build per-cylinder objects ----
  const cylinders = [];
  for (let c = 0; c < nCyl; c++) {
    const cyl = { idx: c + 1 };
    for (const key of Object.values(tagToKey)) {
      cyl[key] = summaryRows[key]?.perCyl?.[c] ?? null;
    }
    cylinders.push(cyl);
  }

  // ---- Mean RPM (from summary, more reliable than scattered values) ----
  if (summaryRows.RPM?.mean != null) meta.rpm = summaryRows.RPM.mean;
  else if (summaryRows.RPM?.perCyl?.length) {
    const v = summaryRows.RPM.perCyl.filter(x => x != null);
    if (v.length) meta.rpm = v.reduce((a, b) => a + b, 0) / v.length;
  }

  // ---- Mean MIP, total Indicated Power for the upload step ----
  meta.MIP_overall_bar = summaryRows.MIP?.mean ?? null;
  meta.indPower_total_kW = summaryRows.IndPower?.total ?? null;

  // ---- Detect test load % from engine-name string (e.g. "Main Engine 50%") ----
  const ldMatch = (meta.engineId || '').match(/(\d{2,3})\s*%/);
  meta.testLoadFromName = ldMatch ? num(ldMatch[1]) : null;

  // ---- Conditions block: PMI exports per-cyl ambient/boost/Texh/fuel/VIT.
  // If a metric is uniformly 0 across all cylinders, treat it as "not measured".
  const meanIfMeasured = (key) => {
    const arr = summaryRows[key]?.perCyl || [];
    const valid = arr.filter(v => v != null);
    if (valid.length === 0) return { value: null, measured: false };
    const allZero = valid.every(v => Math.abs(v) < 0.001);
    if (allZero) return { value: null, measured: false };
    const mean = valid.reduce((a, b) => a + b, 0) / valid.length;
    return { value: mean, measured: true };
  };
  const tEnv   = meanIfMeasured('AmbTemp');
  const tBoost = meanIfMeasured('BoostTemp');
  const tExh   = meanIfMeasured('Texh');
  const conditions = { sources: {} };
  if (tEnv.measured)   { conditions.blowerTemp_C    = tEnv.value;   conditions.sources.blowerTemp_C    = 'FROM FILE'; }
  if (tBoost.measured) { conditions.scavTemp_C      = tBoost.value; conditions.sources.scavTemp_C      = 'FROM FILE'; }
  if (tExh.measured)   { conditions.turbInletTemp_C = tExh.value;   conditions.sources.turbInletTemp_C = 'FROM FILE'; }
  if (meta.boost_bar != null && Math.abs(meta.boost_bar) > 0.05) {
    conditions.scavPress_bar = meta.boost_bar;
    conditions.sources.scavPress_bar = 'FROM FILE';
  }

  return { meta, cylinders, curves, conditions };
}

// =================================================================
//  PARSER 2: Marine engine test-report XLSX
//  Recognises sheets named like "Speed test*" and reads the layout
//  used by the reference workbook (One Atlas / MAJD).
// =================================================================
function parseTestReportXLSX(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  let testSheetName = wb.SheetNames.find(n => /speed\s*test|trial|sea\s*trial|me\s*test/i.test(n));
  if (!testSheetName) testSheetName = wb.SheetNames[0];
  const ws = wb.Sheets[testSheetName];
  const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });

  const get = (r, c) => grid[r] && grid[r][c] != null ? grid[r][c] : null;

  const meta = {
    format:  'TEST-REPORT-XLSX',
    sheetName: testSheetName,
  };

  // --- Top metadata block ---
  // Look for rows containing "Date of measurement", "Vessel", "Applies to:" anywhere in cols A or H
  for (let r = 0; r < Math.min(grid.length, 12); r++) {
    const a = String(get(r, 0) || '');
    if (a.startsWith('Applies to')) {
      meta.engineId = a.replace(/^Applies to:\s*/, '');
      // Extract IMO if present
      const im = a.match(/IMO\s*No[:\s]*([0-9]+)/i);
      if (im) meta.imo = im[1];
    }
    if (a === 'Date of measurement' && get(r, 1) != null) meta.date = get(r, 1);
    if (a === 'Vessel' && get(r, 1) != null) meta.vessel = get(r, 1);
  }

  // --- Power, MIP, RPM ---
  for (let r = 6; r < Math.min(grid.length, 12); r++) {
    const a = String(get(r, 0) || '');
    if (a.toLowerCase().includes('power average')) meta.shaftPower_kW = num(get(r, 2));
    if (a.toLowerCase().includes('indicated power') && a.toLowerCase().includes('mip')) meta.MIP_overall_bar = num(get(r, 2));
    if (a.toLowerCase().includes('rpm average')) meta.rpm = num(get(r, 2));
  }

  // --- Ambient & charge-air conditions (with provenance) ---
  // These come straight from the Excel's INPUT column (col C, index 2)
  const conditions = { sources: {} };
  const condRows = [
    ['blowerTemp_C',     'blower inlet temperature'],
    ['baroPress_mbar',   'ambient pressure'],
    ['airFilterDP_mmH2O','press.drop air filter'],
    ['airCoolerDP_mmH2O','pressure drop air cooler'],
    ['scavTemp_C',       'scavenging air temperature'],
    ['scavPress_bar',    'scavenging air pressure'],
    ['exhRecPress_bar',  'exhaust receiver pressure'],
    ['turbBackPress_mmH2O','turbine back pressure'],
    ['turbInletTemp_C',  'temperature before turbine'],
    ['tcRPM',            'turbocharger speed'],
  ];
  for (let r = 11; r < Math.min(grid.length, 30); r++) {
    const a = String(get(r, 0) || '').toLowerCase();
    for (const [key, label] of condRows) {
      if (a.includes(label)) {
        const v = num(get(r, 2));
        if (v != null) {
          conditions[key] = v;
          conditions.sources[key] = 'FROM FILE';
        }
      }
    }
  }

  // --- Cylinder-level block (cols O-AC = indices 14-28) ---
  const cylinders = [];
  // Header row 2 (index 1) has labels.  Data rows start at index 2 and run while col O has a number.
  for (let r = 2; r < grid.length; r++) {
    const idx = num(get(r, 14));
    if (idx == null) break;
    cylinders.push({
      idx,
      Pcomp_meas: num(get(r, 15)),
      Pmax_meas:  num(get(r, 18)),
      MIP:        num(get(r, 21)),
      fuelIndex:  num(get(r, 22)),
      vit_mm:     num(get(r, 25)),
      Texh:       num(get(r, 28)),
    });
  }

  meta.cylinders = cylinders.length;

  // --- Engine specs from the Shoptrial Data sheet (if present in the same workbook) ---
  const stSheetName = wb.SheetNames.find(n => /shop\s*trial|shoptrial/i.test(n));
  if (stSheetName) {
    const stWS = wb.Sheets[stSheetName];
    const stGrid = XLSX.utils.sheet_to_json(stWS, { header: 1, defval: null, raw: true });
    const stGet = (r, c) => stGrid[r] && stGrid[r][c] != null ? stGrid[r][c] : null;

    for (let r = 0; r < Math.min(stGrid.length, 14); r++) {
      const a = String(stGet(r, 0) || '').toLowerCase();
      if (a.includes('engine type') || a.includes('mcr rating')) {
        const t = stGet(r, 3);
        if (t) meta.model = String(t);
        const mcr = num(stGet(r, 6));
        if (mcr) meta.mcrPower_kW = mcr;
      }
      if (a.includes('nominal max rpm')) meta.maxRPM = num(stGet(r, 6));
      if (a.includes('cylinder diameter')) {
        meta.bore_mm = num(stGet(r, 2)) * 1000;
        meta.stroke_mm = num(stGet(r, 6)) * 1000;
      }
      if (a.includes('number of cylinders')) meta.shopCyl = num(stGet(r, 6));
      if (a.includes('engine manufacturer')) meta.manufacturer = stGet(r, 6);
    }
  }

  return { meta, cylinders, conditions, _workbook: wb };
}

// =================================================================
//  AUTO-DETECT FILE FORMAT & DISPATCH
// =================================================================
async function parseUpload(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) {
    const text = await file.text();
    return { kind: 'PMI-CSV', data: parsePMICSV(text) };
  }
  if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.xlsm')) {
    const buf = await file.arrayBuffer();
    return { kind: 'TEST-REPORT-XLSX', data: parseTestReportXLSX(buf) };
  }
  // Unknown extension — try CSV first
  const text = await file.text();
  return { kind: 'PMI-CSV', data: parsePMICSV(text) };
}

// =================================================================
//  DIAGNOSTIC ENGINE (per cylinder)
// =================================================================
// ─── Per-cylinder diagnostics ────────────────────────────────────────────────
// All deviations are from the fleet mean of complete cylinders.
// MIP deviation is in absolute bar (not %) — consistent with OEM tolerance sheets.
// strokeType selects the correct tolerance band (2-stroke vs 4-stroke).
function diagnoseCyl(cyl, mean, conditions, shopRef, strokeType = 2) {
  const TOL = getTolerance(strokeType);
  const insights = [];

  const dPmax  = (cyl.Pmax_iso  != null && mean.Pmax_iso  != null) ? cyl.Pmax_iso  - mean.Pmax_iso  : null;
  const dPcomp = (cyl.Pcomp_iso != null && mean.Pcomp_iso != null) ? cyl.Pcomp_iso - mean.Pcomp_iso : null;
  const dMIP_bar = (cyl.MIP    != null && mean.MIP        != null) ? cyl.MIP       - mean.MIP       : null;
  const dTexh  = (cyl.Texh     != null && mean.Texh       != null) ? cyl.Texh      - mean.Texh      : null;

  const pmaxLim  = TOL.Pmax_bar.warn;   // same warn & alarm for single-threshold rules
  const pcompLim = TOL.Pcomp_bar.warn;
  const mipLim   = TOL.MIP_bar.warn;

  // ── 1. Low compression ────────────────────────────────────────────────────
  if (dPcomp != null && dPcomp < -pcompLim) {
    insights.push({
      severity: 'warning',
      title: 'Low compression pressure',
      finding: `Pcomp ISO is ${Math.abs(dPcomp).toFixed(1)} bar below the fleet mean (limit: ±${pcompLim.toFixed(0)} bar for ${strokeType}-stroke engine). Indicates reduced compression.`,
      causes: ['Worn or stuck piston rings', 'Leaking exhaust valve', 'Fouled scavenge ports', 'Cylinder liner wear'],
      actions: ['Boroscope inspection of liner and rings', 'Check exhaust valve seat and spindle', 'Inspect scavenge ports for fouling', 'Compare with last known-good Pcomp reading'],
    });
  }

  // ── 2. High Pmax with low MIP (advanced / sharp injection) ───────────────
  if (dPmax != null && dMIP_bar != null && dPmax > pmaxLim && dMIP_bar < -mipLim) {
    insights.push({
      severity: 'warning',
      title: 'High Pmax, low MIP — abnormal combustion',
      finding: `Pmax is ${dPmax.toFixed(1)} bar above mean (limit ±${pmaxLim.toFixed(0)} bar) while MIP is ${Math.abs(dMIP_bar).toFixed(2)} bar below mean (limit ±${mipLim.toFixed(2)} bar). Suggests advanced or sharp ignition followed by poor burn.`,
      causes: ['Advanced fuel injection timing', 'VIT actuator drift', 'Dribbling injector (early start of injection)', 'Worn injector seat'],
      actions: ['Verify injection timing against shop record', 'Check VIT setting on this unit', 'Pop-test injector and check spray pattern', 'Inspect cam follower wear'],
    });
  }

  // ── 3. Low Pmax with high MIP (retarded injection / late combustion) ──────
  if (dPmax != null && dMIP_bar != null && dPmax < -pmaxLim && dMIP_bar > mipLim) {
    insights.push({
      severity: 'warning',
      title: 'Low Pmax, high MIP — retarded combustion',
      finding: `Pmax is ${Math.abs(dPmax).toFixed(1)} bar below mean (limit ±${pmaxLim.toFixed(0)} bar) while MIP is ${dMIP_bar.toFixed(2)} bar above mean (limit ±${mipLim.toFixed(2)} bar). Combustion is occurring late in the expansion stroke.`,
      causes: ['Retarded injection timing', 'Worn or eroded nozzle holes (long burn)', 'Fuel quality / poor ignition', 'Low charge-air pressure on this unit'],
      actions: ['Verify injection timing', 'Replace injector and inspect nozzle holes', 'Check fuel viscosity at injection', 'Check scavenge box for soot — sign of after-burning'],
    });
  }

  // ── 3b. MIP deficit with balanced Pmax (under-fuelled cylinder) ──────────
  if (dMIP_bar != null && dPmax != null && dMIP_bar < -mipLim && Math.abs(dPmax) <= pmaxLim) {
    insights.push({
      severity: 'warning',
      title: 'Under-fuelled cylinder — MIP deficit with balanced Pmax',
      finding: `MIP is ${Math.abs(dMIP_bar).toFixed(2)} bar below the fleet mean (limit: ±${mipLim.toFixed(2)} bar for ${strokeType}-stroke engine). Pmax is within tolerance. This cylinder is producing less work per cycle.`,
      causes: ['Worn fuel pump plunger / barrel', 'Fuel rack indication wrong (fuel index reads OK but delivery is low)', 'Governor link mis-set on this unit', 'Suction valve leakage in fuel pump'],
      actions: ['Compare measured fuel index vs shop reference', 'Calibration test of fuel pump', 'Check governor linkage on this cylinder', 'Plan fuel pump overhaul at next opportunity'],
    });
  }

  // ── 3c. MIP surplus with balanced Pmax (over-fuelled cylinder) ───────────
  if (dMIP_bar != null && dPmax != null && dMIP_bar > mipLim && Math.abs(dPmax) <= pmaxLim) {
    insights.push({
      severity: 'warning',
      title: 'Over-fuelled cylinder — MIP surplus with balanced Pmax',
      finding: `MIP is ${dMIP_bar.toFixed(2)} bar above the fleet mean (limit: ±${mipLim.toFixed(2)} bar for ${strokeType}-stroke engine). This cylinder is doing more than its share, accelerating thermal load.`,
      causes: ['Fuel pump delivering more than spec', 'Dribbling injector after main injection', 'Fuel rack out of step with neighbours'],
      actions: ['Check fuel index against shop reference', 'Pop-test injector for tight closure', 'Re-balance fuel rack linkage'],
    });
  }

  // ── 4. Exhaust temperature deviation (only if Texh available) ────────────
  if (dTexh != null) {
    if (dTexh > TOL.Texh_K.warn) {
      insights.push({
        severity: dTexh > TOL.Texh_K.alarm ? 'critical' : 'warning',
        title: 'High exhaust gas temperature',
        finding: `Texh is ${dTexh.toFixed(0)} K above the fleet mean (warn: +${TOL.Texh_K.warn} K, alarm: +${TOL.Texh_K.alarm} K). Likely after-burning or overload on this cylinder.`,
        causes: ['Late combustion / long burn', 'Over-fuelling', 'Reduced air supply (fouled cooler, blocked filter)', 'Worn injector nozzle'],
        actions: ['Check injector spray pattern', 'Verify charge-air supply to this unit', 'Compare with previous voyages — is this a trend?'],
      });
    } else if (dTexh < -TOL.Texh_K.warn) {
      insights.push({
        severity: dTexh < -TOL.Texh_K.alarm ? 'critical' : 'warning',
        title: 'Low exhaust gas temperature',
        finding: `Texh is ${Math.abs(dTexh).toFixed(0)} K below the fleet mean (warn: −${TOL.Texh_K.warn} K, alarm: −${TOL.Texh_K.alarm} K). Likely under-firing on this cylinder.`,
        causes: ['Insufficient fuel delivery', 'Late ignition / mis-firing cycle', 'Cold injector (clogged nozzle holes)'],
        actions: ['Check fuel pump output', 'Test injector', 'Look for cycle-to-cycle stability with PMI trace'],
      });
    }
  }

  // ── 5. Fuel index drift vs shop reference ─────────────────────────────────
  if (cyl.fuelIndex != null && shopRef?.foPumpIndex_atLoad != null) {
    const dIdx = cyl.fuelIndex - shopRef.foPumpIndex_atLoad;
    if (Math.abs(dIdx) > TOL.Index_mm.warn) {
      insights.push({
        severity: Math.abs(dIdx) > TOL.Index_mm.alarm ? 'critical' : 'warning',
        title: `Fuel index ${dIdx > 0 ? 'above' : 'below'} shop reference`,
        finding: `Fuel index ${cyl.fuelIndex.toFixed(1)} mm vs shop ${shopRef.foPumpIndex_atLoad.toFixed(1)} mm at this load (Δ ${dIdx >= 0 ? '+' : ''}${dIdx.toFixed(1)} mm, limit: ±${TOL.Index_mm.warn.toFixed(1)} mm).`,
        causes: dIdx > 0
          ? ['Fuel pump wear → more rack travel needed for same delivery', 'Increased fuel viscosity / poor combustion', 'Engine-wide load reduction not reflected in shop curve']
          : ['Fuel pump delivering more than spec', 'Injector leakage', 'Wrong shop reference selected'],
        actions: ['Verify shop trial reference is correct for this engine', 'Schedule fuel pump inspection', 'Cross-check governor and fuel limiters'],
      });
    }
  }

  return { dPmax, dPcomp, dMIP_bar, dTexh, insights };
}

// ─── Health scoring ────────────────────────────────────────────────────────────
// CYLINDER HEALTH (0–100)
//   Starts at 100 points.  Each diagnostic finding deducts:
//     WARNING  → −10 points
//     CRITICAL → −25 points
//   Clamped to [0, 100].  Cylinders with incomplete data (null) are excluded
//   from the engine mean and shown as "—".
//
// ENGINE HEALTH (0–100)
//   Simple arithmetic mean of the health scores of ALL complete cylinders.
//   A cylinder is "complete" when Pcomp, Pmax, and MIP are all present.
//   Rounded to the nearest integer.
const healthScore = (insights) => {
  let s = 100;
  for (const i of insights) {
    if (i.severity === 'critical') s -= 25;
    else if (i.severity === 'warning') s -= 10;
  }
  return Math.max(0, s);
};

// =================================================================
//  SEALED-MODE HELPERS
// =================================================================
const isSealedMode = () =>
  VESSEL_CONFIG.sealed === true &&
  Array.isArray(VESSEL_CONFIG.engines) &&
  VESSEL_CONFIG.engines.length > 0;

// Translate VESSEL_CONFIG.engines into shop-trial library entries.
// All entries are marked builtIn so the LibraryTable hides delete actions.
const sealedLibrary = () =>
  (VESSEL_CONFIG.engines || []).map((e, i) => ({
    ...e.shopTrial,
    id: e.id || `vessel-engine-${i}`,
    _vesselEngineName: e.name,
    _vesselEngineRole: e.role,
    builtIn: true,
    notes: `${e.shopTrial?.notes ? e.shopTrial.notes + ' · ' : ''}Configured for ${VESSEL_CONFIG.vessel || 'vessel'} by ${VESSEL_CONFIG.configuredBy || 'Zeroqu Marine Solutions'}`,
  }));

// =================================================================
//  LOCAL STORAGE for shop trial library
//  In sealed mode this is bypassed entirely so the crew cannot add
//  or remove entries from the library.
// =================================================================
async function loadLibrary() {
  if (isSealedMode()) return sealedLibrary();
  try {
    if (typeof window !== 'undefined' && window.storage) {
      const r = await window.storage.get('shopTrialLibrary');
      if (r && r.value) return JSON.parse(r.value);
    }
  } catch (e) { /* fall through to defaults */ }
  return BUILTIN_SHOP_TRIALS;
}
async function saveLibrary(library) {
  if (isSealedMode()) return;
  try {
    if (typeof window !== 'undefined' && window.storage) {
      await window.storage.set('shopTrialLibrary', JSON.stringify(library));
    }
  } catch (e) { console.error('saveLibrary failed', e); }
}

// =================================================================
//  HISTORICAL REPORT STORAGE
//  Dual-layer: configService (localStorage + window.storage) is the
//  primary store. All four functions delegate to configService so
//  reports persist across page refreshes and browser sessions.
// =================================================================

async function loadReportIndex() {
  try { return await cs_loadReportIndex(); } catch (_) {}
  // Fallback: legacy window.storage keys (artifact sandbox)
  try {
    if (typeof window !== 'undefined' && window.storage) {
      const r = await window.storage.get('reportIndex');
      if (r && r.value) return JSON.parse(r.value);
    }
  } catch (_) {}
  return [];
}

async function saveReportToHistory(snapshot) {
  // Primary: configService persists to localStorage (survives page refresh)
  try { await cs_saveReport(snapshot); } catch (e) {
    console.error('saveReportToHistory (cs) failed', e);
  }
  // Fallback: also write to window.storage if available (artifact sandbox)
  try {
    if (typeof window !== 'undefined' && window.storage) {
      await window.storage.set(`report:${snapshot.id}`, JSON.stringify(snapshot));
      const raw = await window.storage.get('reportIndex').catch(() => null);
      const idx = (raw && raw.value) ? JSON.parse(raw.value) : [];
      const summary = {
        id: snapshot.id, savedAt: snapshot.savedAt,
        vessel: snapshot.vessel, imo: snapshot.imo,
        vesselKey: snapshot.vesselKey || '',
        engineLabel: snapshot.engineLabel, engineType: snapshot.engineType,
        testLoadPct: snapshot.testLoadPct, engineHealth: snapshot.engineHealth,
        findingsCount: snapshot.findingsCount, criticalCount: snapshot.criticalCount,
        warningCount: snapshot.warningCount,
      };
      const existing = idx.findIndex(r => r.id === snapshot.id);
      if (existing >= 0) idx[existing] = summary; else idx.unshift(summary);
      await window.storage.set('reportIndex', JSON.stringify(idx));
    }
  } catch (_) {}
}

async function loadReportFromHistory(id) {
  // Primary: configService
  try {
    const snap = await cs_loadReport(id);
    if (snap) return snap;
  } catch (_) {}
  // Fallback: window.storage (artifact sandbox legacy keys)
  try {
    if (typeof window !== 'undefined' && window.storage) {
      const r = await window.storage.get(`report:${id}`);
      if (r && r.value) return JSON.parse(r.value);
    }
  } catch (_) {}
  return null;
}

async function deleteReportFromHistory(id) {
  try { await cs_deleteReport(id); } catch (e) {
    console.error('deleteReportFromHistory (cs) failed', e);
  }
  // Also remove from window.storage fallback layer
  try {
    if (typeof window !== 'undefined' && window.storage) {
      await window.storage.delete(`report:${id}`);
      const raw = await window.storage.get('reportIndex').catch(() => null);
      if (raw && raw.value) {
        const idx = JSON.parse(raw.value).filter(r => r.id !== id);
        await window.storage.set('reportIndex', JSON.stringify(idx));
      }
    }
  } catch (_) {}
}

// =================================================================
//  SHOP TRIAL TEMPLATE GENERATOR
//  Builds a blank XLSX whose layout exactly matches what
//  ImportShopTrial expects, so users can fill in numbers manually
//  without worrying about row positions.
// =================================================================
function buildShopTrialTemplate(engineType = '2-stroke') {
  const isFour = engineType === '4-stroke';
  const loadGrid = isFour ? [25, 50, 75, 100, 110, null] : [10, 25, 50, 75, 88.3, 100];

  // Build a 60-row, 8-column grid (col A = label, col B-G = load-point values, col H = unit/notes)
  const grid = Array.from({ length: 60 }, () => Array(8).fill(null));

  const setRow = (r, cells) => { cells.forEach((v, i) => { grid[r][i] = v; }); };

  // Header block (rows 0-12) — matches importer's expectations
  setRow(0,  ['SHOP TRIAL DATA TEMPLATE', null, null, null, null, null, null, null]);
  setRow(2,  ['Fill in the values in the highlighted cells. Do not delete or reorder rows.']);
  setRow(4,  ['Engine identification']);
  setRow(5,  ['Engine type / MCR rating', null, null, '<Model e.g. 6S60MC-C>', null, null, '<MCR kW>', 'kW']);
  setRow(6,  ['Nominal max RPM',           null, null, null,                   null, null, null,        'rpm']);
  setRow(7,  ['Cylinder diameter / stroke (m)', null, '<bore m>',          null, null, null, '<stroke m>', 'm']);
  setRow(8,  ['Number of cylinders',       null, null, null,                   null, null, null,        '']);
  setRow(11, ['Engine manufacturer',       null, null, null,                   null, null, '<MAN/WinGD/etc.>', '']);

  // Load grid (row 15)
  setRow(14, ['Load point grid (% MCR) — fill all 6 columns; for 4-stroke leave 6th blank if unused']);
  setRow(15, ['Load point %', ...loadGrid, '%']);

  // TC suction (row 16)
  setRow(16, ['TC suction (0 = local, 1 = ambient)', 0, null, null, null, null, null, '']);

  // Section header for physical conditions
  setRow(20, ['Physical conditions per load point — values shown at the corresponding load %']);
  setRow(21, ['Parameter', 'Load 1', 'Load 2', 'Load 3', 'Load 4', 'Load 5', 'Load 6', 'Unit']);

  // Physical conditions block (rows 23-38)
  // Order matches the importer's `fields` array exactly.
  const physRows = [
    [23, 'Blower inlet temperature',     '°C'],
    [24, 'Scavenge air temperature',     '°C'],
    [25, 'Turbine back pressure',        'mmH2O'],
    [26, 'Barometric pressure',          'mbar'],
    [27, 'Air filter ΔP',                'mmH2O'],
    [28, 'Air cooler ΔP',                'mmH2O'],
    [29, 'TC speed',                     'RPM'],
    [30, 'Scavenge air pressure',        'bar'],
    [31, 'Turbine inlet pressure',       'bar'],
    [32, 'Exhaust temperature (cyl out)', '°C'],
    [33, 'Turbine inlet temperature',    '°C'],
    [34, 'Turbine outlet temperature',   '°C'],
    [35, 'Compression pressure (Pcomp)', 'bar'],
    [36, 'Firing pressure (Pmax)',       'bar'],
    [37, 'Fuel pump index',              'mm'],
    [38, 'VIT setting',                  'mm or bar'],
  ];
  physRows.forEach(([r, label, unit]) => {
    setRow(r, [label, null, null, null, null, null, null, unit]);
  });

  // SFOC ISO row (row 47)
  setRow(45, ['Specific fuel oil consumption (ISO)']);
  setRow(47, ['SFOC ISO', null, null, null, null, null, null, 'g/kWh']);

  // TC info (rows 52-56)
  setRow(50, ['Turbocharger details']);
  setRow(52, ['TC type',                   null, '<TPL80 B12>', null, null, null, null, '']);
  setRow(53, ['Manufacturer (turbocharger)', null, null, null, null, null, '<ABB/MHI/Napier>', '']);
  setRow(54, ['Diameter (Mitsubishi conv.)', null, null, null, null, null, null, 'm']);
  setRow(55, ['Slip factor',               null, null, null, null, null, null, '']);

  // Convert to a worksheet
  const ws = XLSX.utils.aoa_to_sheet(grid);

  // Set column widths for readability
  ws['!cols'] = [
    { wch: 38 }, { wch: 11 }, { wch: 11 }, { wch: 11 }, { wch: 11 }, { wch: 11 }, { wch: 14 }, { wch: 12 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Shoptrial Data');

  // Add a brief instructions sheet
  const instr = XLSX.utils.aoa_to_sheet([
    ['Shop Trial Library — Import Template'],
    [],
    ['How to use:'],
    ['1.  Fill in the engine identification block (model, MCR, RPM, bore, stroke, cylinders).'],
    ['2.  Set the load-point grid in row 16. Use 10/25/50/75/88.3/100 for full-rated 2-strokes,'],
    ['    or 25/50/75/100/110 for 4-stroke aux engines (leave the 6th column blank).'],
    ['3.  Fill the physical conditions block (rows 24-39) with shop trial reference values'],
    ['    at each load point. Leave a row blank if not applicable.'],
    ['4.  Fill the SFOC ISO row (48) with grams of fuel per kWh at each load.'],
    ['5.  Fill turbocharger details in rows 53-56 (model, manufacturer, slip factor).'],
    ['6.  Save the file and import it via "Import from XLSX" in the tool.'],
    [],
    ['Required fields (import will fail if missing):'],
    ['   - Model name'],
    ['   - MCR rating in kW'],
    ['   - Bore in m'],
    ['   - At least one Pmax value in the load grid'],
    [],
    ['Notes:'],
    ['   - All physical-condition values are at the load points defined in row 16.'],
    ['   - Engine type (2-stroke / 4-stroke) is auto-detected from RPM.'],
    ['   - Do not delete or reorder rows; the importer reads by row position.'],
  ]);
  instr['!cols'] = [{ wch: 90 }];
  XLSX.utils.book_append_sheet(wb, instr, 'Instructions');

  return wb;
}

function downloadShopTrialTemplate(engineType = '2-stroke') {
  const wb = buildShopTrialTemplate(engineType);
  const filename = `Shop_Trial_Template_${engineType}.xlsx`;
  XLSX.writeFile(wb, filename);
}

// Empty entry factory used by the manual-entry form
function emptyShopTrialEntry(engineType = '2-stroke') {
  const isFour = engineType === '4-stroke';
  const loadPoints = isFour ? [25, 50, 75, 100, 110] : [...STD_LOAD_POINTS_FULL];
  const blank = () => Array(loadPoints.length).fill(null);
  return {
    id: `custom-${Date.now()}`,
    name: '',                          // user-facing display name e.g. "Main Engine", "Aux Engine No.1"
    role: isFour ? 'auxiliary' : 'main',  // 'main' | 'auxiliary' — drives report title
    engineType,
    manufacturer: '',
    model: '',
    mcrPower_kW: null,
    maxRPM: null,
    bore_m: null,
    stroke_m: null,
    cylinders: null,
    tcSuction: 0,
    tcManufacturer: '',
    tcType: '',
    tcDiameter: null,
    tcSlipFactor: null,
    loadPoints,
    physical: {
      blowerTemp:    blank(),
      scavTemp:      blank(),
      turbBackPress: blank(),
      barometric:    blank(),
      airFilterDP:   blank(),
      airCoolerDP:   blank(),
      tcRPM:         blank(),
      pscav:         blank(),
      turbInletPress:blank(),
      exhTemp:       blank(),
      turbInletTemp: blank(),
      turbOutletTemp:blank(),
      pcomp:         blank(),
      pmax:          blank(),
      foPumpIndex:   blank(),
      vit:           blank(),
    },
    sfoc_iso: blank(),
    notes: '',
    builtIn: false,
  };
}

// ─── Engine label / role helpers ──────────────────────────────────────────────
// Used by every step header and the report title to consistently name the engine.
//
// Priority for name:   _vesselEngineName (sealed) → entry.name → manufacturer model
// Priority for role:   _vesselEngineRole (sealed) → entry.role → infer from engine type
function getEngineLabel(shopRef) {
  if (!shopRef) return '';
  return (
    shopRef._vesselEngineName ||
    shopRef.name ||
    `${shopRef.manufacturer} ${shopRef.model}`.trim() ||
    shopRef.model ||
    'Engine'
  );
}
function getEngineRole(shopRef) {
  if (!shopRef) return 'main';
  if (shopRef._vesselEngineRole) return shopRef._vesselEngineRole;
  if (shopRef.role)              return shopRef.role;
  // Fallback: 4-stroke engines are typically auxiliary in marine context
  return shopRef.engineType === '4-stroke' ? 'auxiliary' : 'main';
}
function getReportTitle(shopRef) {
  const role = getEngineRole(shopRef);
  return role === 'auxiliary'
    ? 'Auxiliary Engine Performance Report'
    : 'Main Engine Performance Report';
}

// =================================================================
//  MAIN COMPONENT
// =================================================================
const ALL_STEPS = [
  { id: 'upload',     label: 'Upload',           icon: Upload },
  { id: 'engine',     label: 'Engine Profile',   icon: Cpu },
  { id: 'testload',   label: 'Test Load',        icon: Gauge },
  { id: 'shopmatch',  label: 'Shop Trial',       icon: BookOpen },
  { id: 'conditions', label: 'Conditions',       icon: Wind },
  { id: 'iso',        label: 'ISO Correction',   icon: ShieldCheck },
  { id: 'analysis',   label: 'Analysis',         icon: BarChart3 },
  { id: 'report',     label: 'Report',           icon: FileText },
];

// All 8 steps are present in both modes.  Sealed mode locks the
// Shop Trial step to a read-only picker (no add/edit/import).
// Fleet-role users previously skipped the Upload step, but now that they can
// start a new analysis from the vessel browser, they need the upload step too.
const stepsForMode = (_role) => ALL_STEPS;

export default function MarineEnginePerformanceAnalyzer({ initialRole = 'user', onLogout }) {
  const realSealedMode = isSealedMode();
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const stored = localStorage.getItem('zq-theme');
      return stored ? stored === 'dark' : false; // default light
    } catch { return false; }
  });
  const toggleDark = () => setDarkMode(d => {
    const next = !d;
    try { localStorage.setItem('zq-theme', next ? 'dark' : 'light'); } catch {}
    return next;
  });
  // Role-based initial mode:
  //   admin  → Fleet Configurator
  //   fleet  → Analyser (full view, Preview as crew only)
  //   vessel → Analyser (crew/sealed view, no config buttons)
  const [mode, setMode] = useState(
  initialRole === 'admin'  ? 'fleet-configurator' :
  initialRole === 'fleet'  ? 'fleet-vessel-browser' :
  'analyzer'
);
  // Tracks which fleet was last saved so we can return to its edit form
  const [activeFleetKey, setActiveFleetKey] = useState(null);
  const [activeVesselKey, setActiveVesselKey] = useState(null); // vessel to pre-load in VesselConfigurator
  // Tracks the vessel key that the analyser is currently working on (set when
  // a report is opened from the fleet browser so snapshots can be tagged).
  const [analyserVesselKey, setAnalyserVesselKey] = useState(
    () => (initialRole === 'vessel' ? (sessionStorage.getItem('auth_vessel_key') || '') : '')
  );
  const [fleetBrowserVesselKeys, setFleetBrowserVesselKeys] = useState([]);
  const [fleetBrowserVessels,    setFleetBrowserVessels]    = useState({});
  const [fleetBrowserEngines,    setFleetBrowserEngines]    = useState({});
  const [fleetBrowserName,       setFleetBrowserName]       = useState('');
  const [sessionReady, setSessionReady] = useState(
    () => initialRole !== 'fleet' // vessel/admin/user are ready immediately
  );
  const [fleetBrowserReportVesselKey, setFleetBrowserReportVesselKey] = useState(null);
  const [pendingPdfDownload, setPendingPdfDownload] = useState(false);
  const [silentPdfMode, setSilentPdfMode] = useState(false); // true while generating PDF silently in background
  const pdfDownloadResolveRef = useRef(null); // resolve fn for the fleet-browser download promise
  // vessel role always starts in crew view (sealed-like, no config buttons)
  const [previewAsCrew, setPreviewAsCrew] = useState(initialRole === 'vessel');
  const [configuratorEngines, setConfiguratorEngines] = useState([]); // engines from "Configure Vessel" draft

  // ---- Lifted configurator state so it survives mode switches ----
  // Synchronous initialiser reads from the localStorage draft (fastest path).
  // An async useEffect below then layers in data from the config JSON files
  // so that previously saved vessels are loaded on first open.
  const [vesselInfo, setVesselInfo] = useState(() => {
    try {
      const raw = localStorage.getItem('zq-configuratorDraft');
      if (raw) {
        const d = JSON.parse(raw);
        if (d?.info) return d.info;
      }
    } catch (e) { /* ignore */ }
    return {
      vessel:       VESSEL_CONFIG.vessel       || '',
      imo:          VESSEL_CONFIG.imo          || '',
      flag:         VESSEL_CONFIG.flag         || '',
      configuredBy: VESSEL_CONFIG.configuredBy || '',
      configuredOn: VESSEL_CONFIG.configuredOn || new Date().toISOString().split('T')[0],
      notes:        VESSEL_CONFIG.notes        || '',
      mainEngineCount: '',
      auxEngineCount:  '',
    };
  });
  const [vesselEngines, setVesselEngines] = useState(() => {
    try {
      const raw = localStorage.getItem('zq-configuratorDraft');
      if (raw) {
        const d = JSON.parse(raw);
        if (d?.engines?.length > 0) return d.engines;
      }
    } catch (e) { /* ignore */ }
    return (VESSEL_CONFIG.engines || []).map(e => ({ ...e }));
  });

  // Load from config files on mount so data persisted via "Save to config files"
  // is automatically restored even when there is no localStorage draft.
  useEffect(() => {
    (async () => {
      try {
        // Only hydrate if we don't already have meaningful state from localStorage
        if (vesselInfo.vessel || vesselEngines.length > 0) return;
        const draft = await loadConfiguratorDraft();
        if (draft?.info?.vessel) {
          setVesselInfo(v => ({ ...v, ...draft.info }));
        }
        if (draft?.engines?.length > 0) {
          setVesselEngines(draft.engines);
        }
      } catch (e) { /* ignore */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [stepIdx, setStepIdx]   = useState(0);
  const [maxUnlockedIdx, setMaxUnlockedIdx] = useState(0); // tracks highest step ever reached
  const [parsed, setParsed]     = useState(null);   // { kind, data: {meta, cylinders, ...} }
  const [detected, setDetected] = useState(null);   // engine type detection
  const [library, setLibrary]   = useState(realSealedMode ? sealedLibrary() : BUILTIN_SHOP_TRIALS);
  const [shopRef, setShopRef]   = useState(null);   // selected shop trial entry
  const [shopMode, setShopMode] = useState('full'); // 'full' | 'tc-cutout'
  const [testLoad, setTestLoad] = useState(null);   // % MCR
  const [testLoadSource, setTestLoadSource] = useState('default');
  const [conditions, setConditions] = useState(null); // {value, source} per field
  const [cylInputs, setCylInputs]   = useState([]);   // per-cyl manual entries
  const [parseError, setParseError] = useState(null);
  const [isHistorical, setIsHistorical] = useState(false); // true when a saved report is open (view-only)
  const [savedComputedCyls, setSavedComputedCyls] = useState(null); // pre-computed cyl deviations from snapshot

  // ---- Effective sealed mode: real OR admin-preview ----
  const sealedMode = realSealedMode || previewAsCrew;

  // ---- Auto-persist configurator draft whenever lifted state changes ----
  useEffect(() => {
    saveConfiguratorDraft({ info: vesselInfo, engines: vesselEngines });
  }, [vesselInfo, vesselEngines]);

  // ---- Load configurator draft on mount (and after returning from configurator) ----
  // so preview mode always shows the engines the admin just configured.
  const refreshConfiguratorEngines = useCallback(async () => {
    const draft = await loadConfiguratorDraft();
    setConfiguratorEngines(draft?.engines || []);
  }, []);

  useEffect(() => { refreshConfiguratorEngines(); }, [refreshConfiguratorEngines]); // eslint-disable-line react-hooks/exhaustive-deps
  // ---- Load fleet vessel/engine data for fleet-role users ----
useEffect(() => {
  if (initialRole !== 'fleet') return;
  (async () => {
    try {
      // Find which fleet this user belongs to by matching credentials
      const allFleets = await loadAllFleets();
      // The fleet-role username from .env is the default 'fleet';
      // for dynamic per-fleet credentials the Login sets sessionStorage.auth_role = 'fleet'.
      // We resolve the fleet by the stored fleetKey if available, or scan all fleets.
      const storedFleetKey = sessionStorage.getItem('auth_fleet_key') || null;
      let matchedFleet = null;
      let matchedFleetKey = storedFleetKey;

      if (storedFleetKey && allFleets[storedFleetKey]) {
        matchedFleet = allFleets[storedFleetKey];
      } else {
        // Fallback: pick the first active fleet (single-fleet deployment)
        const entries = Object.entries(allFleets);
        if (entries.length > 0) {
          [matchedFleetKey, matchedFleet] = entries[0];
        }
      }

      if (!matchedFleet) { setSessionReady(true); return; } 

      const vKeys = Array.isArray(matchedFleet.vessels) ? matchedFleet.vessels : [];
      const [allV, allE] = await Promise.all([loadAllVessels(), loadAllEngines()]);

      // Filter to only this fleet's vessels
      const filteredVessels = {};
      vKeys.forEach(k => { if (allV[k]) filteredVessels[k] = allV[k]; });

      setFleetBrowserName(matchedFleet.companyName || matchedFleetKey || 'Fleet');
      setFleetBrowserVesselKeys(vKeys.filter(k => !!allV[k]));
      setFleetBrowserVessels(filteredVessels);
      setFleetBrowserEngines(allE);
      setSessionReady(true);
    } catch (e) {
      console.error('Failed to load fleet vessel data:', e);
    }
  })();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [initialRole]);

  // ---- Load vessel-specific engines for vessel-role users ----
  // When a vessel crew member logs in with their vessel credentials, Login
  // stores auth_vessel_key in sessionStorage.  We load ONLY that vessel's
  // engines from engineConfig.json into the shop-trial library so the
  // Shop Trial step shows the correct engines and the cylinder-count check passes.
  useEffect(() => {
    if (initialRole !== 'vessel') return;
    (async () => {
      try {
        const storedVesselKey = sessionStorage.getItem('auth_vessel_key') || null;
        if (!storedVesselKey) return;

        // Tag the analyser so reports are saved under this vessel
        setAnalyserVesselKey(storedVesselKey);

        const allE = await loadAllEngines();
        const vesselEngineEntries = allE[storedVesselKey];
        if (!Array.isArray(vesselEngineEntries) || vesselEngineEntries.length === 0) return;

        // Convert engineConfig entries into the shop-trial library shape
        const engineLibrary = vesselEngineEntries.map((e, i) => ({
          ...e.shopTrial,
          id: e.id || ('vessel-engine-' + i),
          _vesselEngineName: e.name,
          _vesselEngineRole: e.role,
          builtIn: true,
          notes: (e.shopTrial?.notes ? e.shopTrial.notes + ' \xb7 ' : '') + 'Configured for vessel ' + storedVesselKey,
        }));

        setLibrary(engineLibrary);

        // Also populate configuratorEngines with this vessel's engines so that
        // effectiveLibrary (which uses configuratorEngines when previewAsCrew=true)
        // shows the correct vessel engines and not a stale admin draft.
        // vesselEngineEntries already has the right shape: { id, name, role, shopTrial }
        setConfiguratorEngines(vesselEngineEntries);
      } catch (e) {
        console.error('Failed to load vessel engine library:', e);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRole]);

  // ---- Effective library ----
  // • Real sealed build  → sealedLibrary() from VESSEL_CONFIG.engines (already in library)
  // • Preview as crew    → translate configurator-draft engines into the same shape as
  //                        sealedLibrary() so the SealedShopMatchStep cards look right
  // • Admin (unsealed)   → raw shop trial library, pass through
  const effectiveLibrary = useMemo(() => {
    if (realSealedMode) return library;
    if (!previewAsCrew)  return library;
    if (configuratorEngines.length === 0) return library; // no draft yet — fall back
    // Mirror what sealedLibrary() does but using the draft instead of VESSEL_CONFIG
    return configuratorEngines.map((e, i) => ({
      ...e.shopTrial,
      id: e.id || `preview-engine-${i}`,
      _vesselEngineName: e.name,
      _vesselEngineRole: e.role,
      builtIn: true,   // prevents edit/delete buttons in any table views
      notes: `${e.shopTrial?.notes ? e.shopTrial.notes + ' · ' : ''}[Preview — configured via Vessel Configurator]`,
    }));
  }, [library, realSealedMode, previewAsCrew, configuratorEngines]);

  // Load shop trial library on mount (sealed mode loads VESSEL_CONFIG.engines).
  // Skip for vessel-role logins: their engine library is already loaded above
  // from engineConfig.json and must not be overwritten with the built-in trials.
  useEffect(() => {
    if (initialRole === 'vessel') return;
    (async () => setLibrary(await loadLibrary()))();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- file upload ----------
  const handleUpload = useCallback(async (file) => {
    setParseError(null);
    setIsHistorical(false);
    autoSaveIdRef.current = null;
    setSavedComputedCyls(null);
    try {
      const result = await parseUpload(file);
      setParsed(result);

      // Engine type detection
      const det = detectEngineType(result.data.meta);
      // Add bore/stroke/cyl info from parser
      Object.assign(det, {
        bore_mm: result.data.meta.bore_mm,
        stroke_mm: result.data.meta.stroke_mm,
        cylinders: result.data.meta.cylinders,
        manufacturer: result.data.meta.manufacturer,
        model: result.data.meta.model || result.data.meta.engineId,
      });
      setDetected(det);

      // Auto match shop trial
      let m = matchShopTrial(det, library);
      // Sealed-mode helpful fallback: if the vessel has exactly one engine of
      // the detected stroke type, auto-select it even without a strong match —
      // there is no ambiguity to resolve.
      if (!m && sealedMode) {
        const sameType = library.filter(e => e.engineType === det.type);
        if (sameType.length === 1) {
          m = {
            entry: sameType[0],
            score: 0,
            confidence: 'low',
            criteria: ['Single engine of this stroke type configured for the vessel'],
          };
        }
      }
      if (m) setShopRef(m.entry);

      // Auto test load
      let detectedLoad = null;
      let loadSource = 'default';
      if (result.data.meta.shaftPower_kW && (m?.entry?.mcrPower_kW || result.data.meta.mcrPower_kW)) {
        detectedLoad = testLoadFromShaftPower(
          result.data.meta.shaftPower_kW,
          m?.entry?.mcrPower_kW || result.data.meta.mcrPower_kW
        );
        loadSource = 'computed-from-shaft-power';
      } else if (result.data.meta.testLoadFromName) {
        detectedLoad = result.data.meta.testLoadFromName;
        loadSource = 'parsed-from-session-name';
      } else {
        detectedLoad = 50;
        loadSource = 'default';
      }
      setTestLoad(detectedLoad);
      setTestLoadSource(loadSource);

      // Initial conditions — start with anything found in file, then sensible defaults
      const fileConds = result.data.conditions || {};
      // ---- Conditions initialisation -----------------------------------------
      // Rule: only pre-fill a value if it actually came from the uploaded file.
      // Everything else is left null so the crew knows they must enter it.
      // Two exceptions: fuelLCV and thrustLoss are engineering constants, not
      // measurements — they are pre-filled and tagged 'CONSTANT'.
      const fromFile = (key) => ({
        value:  fileConds[key]  ?? null,
        source: fileConds.sources?.[key] || null,   // null = not from file
      });

      const cs = {
        blowerTemp_C:        fromFile('blowerTemp_C'),
        baroPress_mbar:      fromFile('baroPress_mbar'),
        airFilterDP_mmH2O:   fromFile('airFilterDP_mmH2O'),
        airCoolerDP_mmH2O:   fromFile('airCoolerDP_mmH2O'),
        scavTemp_C:          fromFile('scavTemp_C'),
        // Scavenge pressure: PMI CSV carries a single boost_bar header value
        scavPress_bar: result.data.meta.boost_bar
          ? { value: result.data.meta.boost_bar, source: 'FROM FILE' }
          : fromFile('scavPress_bar'),
        exhRecPress_bar:     fromFile('exhRecPress_bar'),
        turbBackPress_mmH2O: fromFile('turbBackPress_mmH2O'),
        turbInletTemp_C:     fromFile('turbInletTemp_C'),
        tcRPM:               fromFile('tcRPM'),
        shaftPower_kW: result.data.meta.shaftPower_kW
          ? { value: result.data.meta.shaftPower_kW, source: 'FROM FILE' }
          : { value: null, source: null },
        rpm: result.data.meta.rpm
          ? { value: result.data.meta.rpm, source: 'FROM FILE' }
          : { value: null, source: null },
        // Engineering constants — always pre-filled, never treated as measurements
        fuelLCV_kJkg: { value: 42700, source: 'CONSTANT' },
        thrustLoss:   { value: DEFAULT_THRUST_LOSS, source: 'CONSTANT' },
      };
      setConditions(cs);

      // Per-cylinder manual inputs — pre-fill from file where available
      const ci = result.data.cylinders.map(c => ({
        idx: c.idx,
        Pcomp_meas: c.Pcomp_meas ?? c.Ptdc ?? null,
        Pmax_meas:  c.Pmax_meas  ?? c.Pmax ?? null,
        MIP:        c.MIP        ?? null,
        Texh:       c.Texh       ?? null,
        fuelIndex:  c.fuelIndex  ?? c.FuelIndex ?? null,
        vit_mm:     c.vit_mm     ?? c.VIT       ?? null,
        MaxDpDa:    c.MaxDpDa    ?? null,
        IndPower:   c.IndPower   ?? null,
      }));
      setCylInputs(ci);

      setStepIdx(1);
    } catch (e) {
      console.error(e);
      setParseError(e.message || 'Failed to parse file');
    }
  }, [library, sealedMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- ISO correction & per-cyl computed values ----------
  const computed = useMemo(() => {
    if (!parsed || !conditions || !cylInputs.length || !shopRef || testLoad == null) return null;

    const c = (k) => conditions[k]?.value;
    const Pa     = c('baroPress_mbar');
    const Pback  = c('turbBackPress_mmH2O');
    const Tblow  = c('blowerTemp_C');
    const Tscav  = c('scavTemp_C');
    const tcSuc  = shopRef.tcSuction;
    const mode   = shopMode;

    // Keys that cannot be 0 on a running engine — same set as PerCylTable.noZero
    const NON_ZERO_KEYS = ['Pcomp_meas', 'Pmax_meas', 'MIP', 'Texh', 'fuelIndex'];
    const normVal = (key, val) =>
      (NON_ZERO_KEYS.includes(key) && val === 0) ? null : val;

    // Keys where ANY null/zero means we can't run diagnostics for that cylinder
    const CRITICAL_KEYS = ['Pcomp_meas', 'Pmax_meas', 'MIP'];

    // All four ambient values are needed for ISO corrections.
    // If any are missing the corrections are skipped (iso = null).
    const canCorrect = Pa != null && Pback != null && Tblow != null && Tscav != null;

    // Per cylinder — normalize zeros then compute ISO corrections
    const cyls = cylInputs.map(ci => {
      // Build a normalised copy — 0s replaced with null where physically invalid
      const n = { ...ci };
      NON_ZERO_KEYS.forEach(k => { n[k] = normVal(k, ci[k]); });

      const Pcomp_iso = (n.Pcomp_meas != null && canCorrect)
        ? correctCompPressure(n.Pcomp_meas, Pa, Pback, Tblow, Tscav, tcSuc, testLoad, mode) : null;
      const Pmax_iso  = (n.Pmax_meas != null && canCorrect)
        ? correctFiringPressure(n.Pmax_meas, Pa, Pback, Tblow, Tscav, tcSuc, testLoad, mode) : null;

      // A cylinder is "incomplete" if any critical value is missing after normalization
      const dataIncomplete = CRITICAL_KEYS.some(k => n[k] == null);

      return { ...n, Pcomp_iso, Pmax_iso, dataIncomplete };
    });

    // Means — exclude null AND incomplete cylinders so zeros don't skew averages
    const meanOf = (key) => {
      const vals = cyls
        .filter(c => !c.dataIncomplete)
        .map(c => c[key])
        .filter(v => v != null && v !== 0);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    };
    const means = {
      Pcomp_meas: meanOf('Pcomp_meas'),
      Pcomp_iso:  meanOf('Pcomp_iso'),
      Pmax_meas:  meanOf('Pmax_meas'),
      Pmax_iso:   meanOf('Pmax_iso'),
      MIP:        meanOf('MIP'),
      Texh:       meanOf('Texh'),
      fuelIndex:  meanOf('fuelIndex'),
    };

    // Shop trial reference values at test load (after ISO scaling already applied in shop curve)
    const shopAtLoad = {
      pscav:           interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.pscav),
      pcomp:           interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.pcomp),
      pmax:            interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.pmax),
      tcRPM:           interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.tcRPM),
      turbInletTemp:   interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.turbInletTemp),
      exhTemp:         interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.exhTemp),
      airFilterDP:     interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.airFilterDP),
      airCoolerDP:     interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.airCoolerDP),
      foPumpIndex:     interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.foPumpIndex),
      vit:             interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.vit),
      sfoc:            interpAtLoad(testLoad, shopRef.loadPoints, shopRef.sfoc_iso),
    };

    // Stroke type must be resolved BEFORE diagnostics so getTolerance() uses the right limits.
    // Primary source: explicit "Engine strokes" field in the PMI CSV header (2 or 4).
    // Fallback: shop trial engine type string.
    const strokeType = parsed?.data?.meta?.strokeType
      || (shopRef?.engineType === '4-stroke' ? 4 : 2);

    // Diagnostics — incomplete cylinders get a special advisory instead of rules
    const cylDiag = cyls.map(cyl => {
      if (cyl.dataIncomplete) {
        const missing = ['Pcomp_meas', 'Pmax_meas', 'MIP']
          .filter(k => cyl[k] == null)
          .map(k => ({ Pcomp_meas: 'Pcomp', Pmax_meas: 'Pmax', MIP: 'MIP' }[k]));
        return {
          ...cyl,
          dPmax: null, dPcomp: null, dMIP_bar: null, dTexh: null,
          insights: [{
            severity:  'incomplete',
            title:     'Data not recorded — analysis skipped for this cylinder',
            finding:   `Missing: ${missing.join(', ')}.  This cylinder has been excluded from the fleet mean and from all diagnostic rules.`,
            causes:    ['Value was 0 or absent in the uploaded file'],
            actions:   [
              'Return to the Conditions step → Per-cylinder measured values table',
              'Enter the correct value from the engine room indicator card or PMI printout',
              'Re-run the analysis — this cylinder will be included once data is present',
            ],
          }],
          health: null,   // null = unknown (not 0 or 100)
        };
      }
      const d = diagnoseCyl(cyl, means, conditions, { foPumpIndex_atLoad: shopAtLoad.foPumpIndex }, strokeType);
      return { ...cyl, ...d, health: healthScore(d.insights) };
    });

    const indPow = (means.MIP != null && c('rpm') && shopRef.bore_m && shopRef.stroke_m)
      ? indicatedPower(means.MIP, c('rpm'), shopRef.bore_m, shopRef.stroke_m, shopRef.cylinders, strokeType)
      : null;

    // Mechanical efficiency = shaft power / indicated power
    // Only shown when both values are available AND the result is physically
    // plausible (0–100%). Values above 100% indicate a data mismatch between
    // the shaft-power condition and the MIP measurement condition.
    const mechEffRaw = (c('shaftPower_kW') && indPow?.total)
      ? c('shaftPower_kW') / indPow.total * 100 : null;
    // const mechEff = (mechEffRaw != null && mechEffRaw > 0 && mechEffRaw <= 100)
     const mechEff = (mechEffRaw != null && mechEffRaw > 0)
      ? mechEffRaw : null;

    // ISO-corrected ambient values for reporting
    const isoCorrAmbient = {
      pa_iso:     Pa - (c('airFilterDP_mmH2O') || 0) * 0.0981,    // Excel D13
      tc_speed_iso: c('tcRPM') ? correctTCSpeed(c('tcRPM'), Tblow) : null,
      turb_inlet_iso: c('turbInletTemp_C')
        ? correctTurbInletTemp(c('turbInletTemp_C'), Pa, Pback, Tblow, Tscav, tcSuc, testLoad, mode)
        : null,
      pscav_iso: c('scavPress_bar')
        ? correctScavPressure(c('scavPress_bar'), Pa, Pback, Tblow, Tscav, tcSuc, testLoad, mode)
        : null,
    };

    // Historical report: merge saved deviation values so PDF charts always
    // reflect the figures computed at save-time, not a re-computation that
    // can go haywire when ambient conditions contain test/garbage values.
    // We overlay dPmax, dPcomp, dMIP_bar, dTexh, insights, and health from
    // the snapshot while keeping the freshly-computed Pmax_iso/Pcomp_iso.
    let finalCyls = cylDiag;
    if (savedComputedCyls && Array.isArray(savedComputedCyls) && savedComputedCyls.length > 0) {
      finalCyls = cylDiag.map(live => {
        const saved = savedComputedCyls.find(s => s.idx === live.idx);
        if (!saved) return live;
        return {
          ...live,
          dPmax:         ('dPmax'         in saved) ? saved.dPmax         : live.dPmax,
          dPcomp:        ('dPcomp'        in saved) ? saved.dPcomp        : live.dPcomp,
          dMIP_bar:      ('dMIP_bar'      in saved) ? saved.dMIP_bar      : live.dMIP_bar,
          dTexh:         ('dTexh'         in saved) ? saved.dTexh         : live.dTexh,
          insights:      ('insights'      in saved) ? saved.insights      : live.insights,
          health:        ('health'        in saved) ? saved.health        : live.health,
          dataIncomplete:('dataIncomplete' in saved) ? saved.dataIncomplete: live.dataIncomplete,
        };
      });
    }

    // Engine health (mean cyl health)
    const completeCyls = finalCyls.filter(c => c.health != null);
    const engineHealth = completeCyls.length
      ? Math.round(completeCyls.reduce((a, c) => a + c.health, 0) / completeCyls.length)
      : 0;

    return { cyls: finalCyls, means, shopAtLoad, indPow, mechEff, mechEffRaw, isoCorrAmbient, engineHealth, strokeType };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed, conditions, cylInputs, shopRef, testLoad, shopMode, savedComputedCyls]);

  // STEPS list adapts to mode — sealed mode hides Shop Trial step
  const STEPS = useMemo(() => stepsForMode(initialRole), [initialRole]);
  // Step-id-based navigation so indices don't break when steps are added/removed
  const stepIdOf = (idx) => STEPS[idx]?.id;
  const indexOfStep = (id) => {
    const i = STEPS.findIndex(s => s.id === id);
    return i >= 0 ? i : 0;
  };
  const goToStep = (id) => {
    const idx = indexOfStep(id);
    setStepIdx(idx);
    setMaxUnlockedIdx(prev => Math.max(prev, idx));
  };
  const goToNext = () => {
    const id = stepIdOf(stepIdx);
    const order = STEPS.map(s => s.id);
    const here = order.indexOf(id);
    if (here >= 0 && here < order.length - 1) goToStep(order[here + 1]);
  };
  const goToPrev = () => {
    const id = stepIdOf(stepIdx);
    const order = STEPS.map(s => s.id);
    const here = order.indexOf(id);
    if (here > 0) goToStep(order[here - 1]);
  };

  const reset = () => {
    setParsed(null); setDetected(null);
    if (!sealedMode) setShopRef(null);  // sealed mode keeps the auto-matched ref
    setTestLoad(null);
    setConditions(null); setCylInputs([]); setStepIdx(0); setParseError(null);
    setIsHistorical(false);
    setSavedComputedCyls(null);
  };

  // Reload a previously-saved historical report into all working state and
  // jump straight to the Report step.
  const loadHistoricalReport = (snapshot, targetStep = 'report') => {
    setParsed(snapshot.parsed);
    setDetected(snapshot.detected);
    setShopRef(snapshot.shopRef);
    setShopMode(snapshot.shopMode || 'full');
    setTestLoad(snapshot.testLoad);
    setTestLoadSource(snapshot.testLoadSource || 'reloaded-from-history');
    setConditions(snapshot.conditions);
    setCylInputs(snapshot.cylInputs);
    // Restore pre-computed cylinder deviations saved with the snapshot so the
    // PDF charts always reflect what was computed at save-time, not a re-computation
    // that may go haywire when ambient conditions contain garbage/test values.
    setSavedComputedCyls(snapshot.computedCyls || null);
    setParseError(null);
    // Historical reports always open in crew (preview) mode regardless of role
    setPreviewAsCrew(true);
    // Mark as view-only — all editable fields become read-only
    setIsHistorical(true);
    // Unlock ALL steps so user can freely navigate after loading from history
    setMaxUnlockedIdx(999);
    autoSaveIdRef.current = snapshot.id;
    goToStep(targetStep);
  };

  // Build a snapshot and save it to history — called explicitly by the user
  // pressing "Save to history" in the Report step. Also auto-fires once each
  // time a new analysis reaches the Report step (idempotent: re-saves update
  // the existing record rather than creating a duplicate).
  const saveSnapshot = useCallback(() => {
    if (!parsed || !computed || !shopRef) return;
    // Reuse the same stable ID the auto-save already wrote (or the ID of the
    // historical report that was loaded), so the server treats this as an upsert.
    // If somehow no ID exists yet, generate a fresh one.
    const stableId = autoSaveIdRef.current || `report-${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const snapshot = {
      id: stableId,
      savedAt: new Date().toISOString(),
      vessel: parsed.data.meta.vessel || VESSEL_CONFIG.vessel || '—',
      imo:    parsed.data.meta.imo || VESSEL_CONFIG.imo || '',
      vesselKey: analyserVesselKey || vesselKey(VESSEL_CONFIG) || '',
      engineLabel: getEngineLabel(shopRef),
      engineType: shopRef.engineType,
      testLoadPct: testLoad,
      engineHealth: computed.engineHealth,
      findingsCount: computed.cyls.reduce((a, c) => a + (c.insights?.length || 0), 0),
      criticalCount: computed.cyls.reduce((a, c) => a + (c.insights?.filter(i => i.severity === 'critical').length || 0), 0),
      warningCount: computed.cyls.reduce((a, c) => a + (c.insights?.filter(i => i.severity === 'warning').length || 0), 0),
      parsed, detected, shopRef, shopMode, testLoad, testLoadSource,
      conditions, cylInputs,
      computedCyls: computed.cyls,
    };
    saveReportToHistory(snapshot);
  }, [parsed, computed, shopRef, shopMode, testLoad, testLoadSource, conditions, cylInputs, detected, analyserVesselKey]);

  // Auto-save whenever the user first lands on the Report step with a valid
  // analysis. Uses a stable snapshot ID for the current session so
  // navigating back-and-forth doesn't create duplicate entries.
  // When a historical report is loaded, autoSaveIdRef is pre-set to that
  // report's ID so saves update the existing record instead of creating a new one.
  const autoSaveIdRef = useRef(null);
  useEffect(() => {
    if (stepIdOf(stepIdx) !== 'report') return;
    if (!parsed || !computed || !shopRef) return;
    // If we already have an ID for this session (either from a loaded historical
    // report or from a prior auto-save in this same session), reuse it so we
    // don't create a duplicate. Otherwise generate a brand-new unique ID.
    if (!autoSaveIdRef.current) {
      autoSaveIdRef.current = `report-${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    }
    const stableId = autoSaveIdRef.current;
    const snapshot = {
      id: stableId,
      savedAt: new Date().toISOString(),
      vessel: parsed.data.meta.vessel || VESSEL_CONFIG.vessel || '—',
      imo:    parsed.data.meta.imo || VESSEL_CONFIG.imo || '',
      vesselKey: analyserVesselKey || vesselKey(VESSEL_CONFIG) || '',
      engineLabel: getEngineLabel(shopRef),
      engineType: shopRef.engineType,
      testLoadPct: testLoad,
      engineHealth: computed.engineHealth,
      findingsCount: computed.cyls.reduce((a, c) => a + (c.insights?.length || 0), 0),
      criticalCount: computed.cyls.reduce((a, c) => a + (c.insights?.filter(i => i.severity === 'critical').length || 0), 0),
      warningCount: computed.cyls.reduce((a, c) => a + (c.insights?.filter(i => i.severity === 'warning').length || 0), 0),
      parsed, detected, shopRef, shopMode, testLoad, testLoadSource,
      conditions, cylInputs,
      computedCyls: computed.cyls,
    };
    saveReportToHistory(snapshot);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx]);
  if (mode === 'fleet-vessel-browser') {
  return (
  <>
    <FleetVesselBrowser
      darkMode={darkMode}
      fleetName={fleetBrowserName}
      vesselKeys={fleetBrowserVesselKeys}
      allVessels={fleetBrowserVessels}
      allEngineMap={fleetBrowserEngines}
      loadReportIndex={loadReportIndex}
      loadReportFromHistory={loadReportFromHistory}
      deleteReportFromHistory={deleteReportFromHistory}
      onDownloadReportPdf={async (summary) => {
        const snapshot = await loadReportFromHistory(summary.id);
        if (!snapshot) return;
        const snapVesselKey = snapshot.vesselKey || fleetBrowserReportVesselKey || '';
        if (snapVesselKey && fleetBrowserEngines[snapVesselKey]) {
          const vesselEngineEntries = fleetBrowserEngines[snapVesselKey];
          if (Array.isArray(vesselEngineEntries) && vesselEngineEntries.length > 0) {
            const engineLibrary = vesselEngineEntries.map((e, i) => ({
              ...e.shopTrial,
              id: e.id || ('vessel-engine-' + i),
              _vesselEngineName: e.name,
              _vesselEngineRole: e.role,
              builtIn: true,
              notes: (e.shopTrial?.notes ? e.shopTrial.notes + ' · ' : '')
                     + 'Configured for vessel ' + snapVesselKey,
            }));
            setLibrary(engineLibrary);
            setConfiguratorEngines(vesselEngineEntries);
          }
        }
        setAnalyserVesselKey(snapVesselKey);
        // Return a Promise that stays pending until onAutoDownloadDone fires,
        // so FleetVesselBrowser's downloadingId spinner stays visible the whole time.
        return new Promise((resolve) => {
          pdfDownloadResolveRef.current = resolve;
          setSilentPdfMode(true);
          loadHistoricalReport(snapshot, 'report');
          setPendingPdfDownload(true);
        });
      }}
      onOpenReport={(snapshot, stepId) => {
        // Tag the analyser with the vessel key so subsequent saves are keyed correctly
        setAnalyserVesselKey(snapshot.vesselKey || fleetBrowserReportVesselKey || '');
        // Load this vessel's engines into the shop-trial library so matchShopTrial
        // works correctly when the report's Shop Trial step is rendered.
        const snapVesselKey = snapshot.vesselKey || fleetBrowserReportVesselKey || '';
        if (snapVesselKey && fleetBrowserEngines[snapVesselKey]) {
          const vesselEngineEntries = fleetBrowserEngines[snapVesselKey];
          if (Array.isArray(vesselEngineEntries) && vesselEngineEntries.length > 0) {
            const engineLibrary = vesselEngineEntries.map((e, i) => ({
              ...e.shopTrial,
              id: e.id || ('vessel-engine-' + i),
              _vesselEngineName: e.name,
              _vesselEngineRole: e.role,
              builtIn: true,
              notes: (e.shopTrial?.notes ? e.shopTrial.notes + ' \xb7 ' : '')
                     + 'Configured for vessel ' + snapVesselKey,
            }));
            setLibrary(engineLibrary);
            setConfiguratorEngines(vesselEngineEntries);
          }
        }
        setMode('analyzer');
        // Pass target step directly — no setTimeout flash
        loadHistoricalReport(snapshot, stepId || 'report');
      }}
      onViewReports={(vesselKey) => {
        // Navigate into that vessel's report list inside FleetVesselBrowser
        setFleetBrowserReportVesselKey(vesselKey);
      }}
      reportVesselKey={fleetBrowserReportVesselKey}
      onClearReportVessel={() => setFleetBrowserReportVesselKey(null)}
      onLogout={onLogout}
      onToggleDark={toggleDark}
      onStartNewAnalysis={(selectedVesselKey) => {
        // Fleet user clicked "New Analysis" on a vessel row.
        // 1. Tag the analyser with this vessel so saved reports are keyed correctly.
        setAnalyserVesselKey(selectedVesselKey);

        // 2. Load this vessel's configured engines into the shop-trial library.
        //    This is the fix for the ss1 bug: without this step, handleUpload runs
        //    matchShopTrial against the built-in sample library instead of the
        //    vessel's actual engines, so the Shop Trial step shows no match (ss1).
        const vesselEngineEntries = fleetBrowserEngines[selectedVesselKey];
        if (Array.isArray(vesselEngineEntries) && vesselEngineEntries.length > 0) {
          const engineLibrary = vesselEngineEntries.map((e, i) => ({
            ...e.shopTrial,
            id: e.id || ('vessel-engine-' + i),
            _vesselEngineName: e.name,
            _vesselEngineRole: e.role,
            builtIn: true,
            notes: (e.shopTrial?.notes ? e.shopTrial.notes + ' \xb7 ' : '')
                   + 'Configured for vessel ' + selectedVesselKey,
          }));
          setLibrary(engineLibrary);
          setConfiguratorEngines(vesselEngineEntries);
        }

        // 3. Reset analyser state and switch to the upload step.
        reset();
        setMode('analyzer');
      }}
    />

    {/* ── Silent PDF generator ────────────────────────────────────────────────
        Rendered off-screen (invisible) only while silentPdfMode is true.
        ReportStep auto-triggers generatePdf() via autoDownloadPdf, then calls
        onAutoDownloadDone which clears both flags and resets analyser state so
        the fleet browser is left completely undisturbed.
    ─────────────────────────────────────────────────────────────────────── */}
    {silentPdfMode && computed && parsed && shopRef && (
      <div style={{ position: 'fixed', top: '-9999px', left: '-9999px', visibility: 'hidden', pointerEvents: 'none', width: '900px', height: 'auto', overflow: 'visible' }}>
        <ReportStep
          computed={computed}
          parsed={parsed}
          shopRef={shopRef}
          testLoad={testLoad}
          conditions={conditions}
          detected={detected}
          onSave={() => {}}
          onPrev={() => {}}
          initialRole={initialRole}
          isHistorical={true}
          autoDownloadPdf={pendingPdfDownload}
          onAutoDownloadDone={() => {
            setPendingPdfDownload(false);
            setSilentPdfMode(false);
            // Reset analyser state so it doesn't bleed into next real analysis
            setParsed(null);
            setDetected(null);
            setShopRef(null);
            setConditions(null);
            setCylInputs([]);
            setTestLoad(null);
            setMaxUnlockedIdx(0);
            setStepIdx(0);
            // Resolve the promise returned to FleetVesselBrowser — this is what
            // finally clears the downloadingId spinner in the fleet browser.
            pdfDownloadResolveRef.current?.();
            pdfDownloadResolveRef.current = null;
          }}
        />
      </div>
    )}
  </>
  );
}
  // Fleet Configurator mode: shown first when admin logs in
  if (mode === 'fleet-configurator') {
    return (
      <FleetConfigurator
        darkMode={darkMode}
        onClose={() => setMode('analyzer')}
        initialRole={initialRole}
        onLogout={onLogout}
        initialFleetKey={activeFleetKey}
        onGoToVesselConfigurator={(fKey, vKey) => {
          setActiveFleetKey(fKey || null);
          if (vKey) {
            // Pre-load an existing vessel so VesselConfigurator opens in edit view for it
            setActiveVesselKey(vKey);
          } else {
            // Reset to blank so VesselConfigurator opens a fresh new-vessel form
            setVesselInfo({
              vessel: '', imo: '', flag: '',
              configuredBy: '',
              configuredOn: new Date().toISOString().split('T')[0],
              notes: '', mainEngineCount: '', auxEngineCount: '',
              userAccess: '', userPassword: '',
            });
            setVesselEngines([]);
            setActiveVesselKey(null);
          }
          setMode('configurator');
        }}
      />
    );
  }

  if (mode === 'configurator') {
    return (
      <VesselConfigurator
        darkMode={darkMode}
        vesselInfo={vesselInfo}
        setVesselInfo={setVesselInfo}
        vesselEngines={vesselEngines}
        setVesselEngines={setVesselEngines}
        onClose={() => { setMode('analyzer'); refreshConfiguratorEngines(); }}
        onBackToFleet={activeFleetKey ? () => { setActiveVesselKey(null); setMode('fleet-configurator'); } : null}
        initialRole={initialRole}
        onLogout={onLogout}
        fleetKey={activeFleetKey}
        initialView={activeFleetKey ? 'edit' : 'list'}
        initialVesselKey={activeVesselKey}
      />
    );
  }

  
  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-page)', color: 'var(--t1)', display: 'flex', flexDirection: 'column' }} data-theme={darkMode ? 'dark' : 'light'}>
      {/* ── ZEROQU BRAND THEME ─────────────────────────────────────────────── */}
      <style>{`
        @import url('https://fonts.zeroquhq.com/steviewsans/stylesheet.css');

        *, *::before, *::after { font-family: 'StevieSans', system-ui, sans-serif; }
        h1, h2, h3 { font-family: 'StevieSans', system-ui, sans-serif !important; }
        .font-mono, code, pre { font-family: 'StevieSans', system-ui, sans-serif !important; }

        /* ══ LIGHT MODE (default — brand-aligned) ══════════════════════════ */
        [data-theme="light"] {
          --bg-page:      #f2f8f3;
          --bg-panel:     #ffffff;
          --bg-surface:   #eaf4ec;
          --bg-input:     #ffffff;
          --br:           #c3dccb;
          --br-mid:       #a5c9b2;
          --t1:           #1a2e1f;
          --t2:           #265858;
          --t3:           #2e5252;
          --t4:           #4a7070;
          --accent:       #275A46;
          --accent-lt:    #A4C681;
          /* semantic fills — solid enough to differentiate, not so heavy they clash */
          --fill-info:    rgba(39,90,70,0.08);
          --fill-warn:    rgba(180,160,0,0.10);
          --fill-err:     rgba(203,94,100,0.08);
          --fill-ok:      rgba(164,198,129,0.18);
          --br-info:      rgba(39,90,70,0.30);
          --br-warn:      rgba(140,120,0,0.40);
          --br-err:       rgba(203,94,100,0.38);
          --br-ok:        rgba(39,90,70,0.35);
          --c-info:       #1e4a38;
          --c-warn:       #4a3d00;
          --c-err:        #7a1820;
          --c-ok:         #1e4a38;
          /* tags — solid fills with dark text */
          --tag-file-bg:  rgba(39,90,70,0.12); --tag-file-c: #1e4a38; --tag-file-b: rgba(39,90,70,0.32);
          --tag-man-bg:   rgba(180,160,0,0.14); --tag-man-c:  #4a3d00; --tag-man-b:  rgba(140,120,0,0.40);
          --tag-ok-bg:    rgba(164,198,129,0.22); --tag-ok-c: #1e4a38; --tag-ok-b:  rgba(39,90,70,0.35);
          --tag-err-bg:   rgba(203,94,100,0.11); --tag-err-c: #7a1820; --tag-err-b: rgba(203,94,100,0.38);
          --tag-sl-bg:    rgba(38,88,88,0.09);  --tag-sl-c:  #265858; --tag-sl-b:  rgba(38,88,88,0.28);
          /* inputs */
          --in-bg: #ffffff; --in-c: #1a2e1f; --in-br: #c3dccb; --in-ph: #6a9898;
          /* buttons */
          --btn-p-bg: #275A46; --btn-p-c: #ffffff; --btn-p-b: #1e4434;
          --btn-g-c: #2e5252;  --btn-g-b: #c3dccb;
          /* report white surface */
          --report-bg: #ffffff; --report-c: #1a2e1f;
          /* source-card backgrounds — explicit so they don't look grey */
          --card-file-bg: rgba(39,90,70,0.06);   --card-file-br: rgba(39,90,70,0.25);
          --card-man-bg:  rgba(180,160,0,0.08);  --card-man-br:  rgba(140,120,0,0.30);
          --card-const-bg:#f8fcf9;               --card-const-br:#c3dccb;
          --card-empty-bg:rgba(200,210,202,0.30); --card-empty-br:#b0cabb;
        }

        /* ══ DARK MODE (crew toggle) ════════════════════════════════════════ */
        [data-theme="dark"] {
          --bg-page:      #050d08;
          --bg-panel:     #0c1910;
          --bg-surface:   #142019;
          --bg-input:     #050d08;
          --br:           #1a2e1f;
          --br-mid:       #23382a;
          --t1:           #D0DCDC;
          --t2:           #afc8c8;
          --t3:           #7eaaaa;
          --t4:           #426464;
          --accent:       #275A46;
          --accent-lt:    #A4C681;
          --fill-info:    rgba(164,198,129,0.10);
          --fill-warn:    rgba(235,232,156,0.10);
          --fill-err:     rgba(203,94,100,0.12);
          --fill-ok:      rgba(39,90,70,0.25);
          --br-info:      rgba(164,198,129,0.35);
          --br-warn:      rgba(235,232,156,0.30);
          --br-err:       rgba(203,94,100,0.40);
          --br-ok:        rgba(164,198,129,0.40);
          --c-info:       #A4C681;
          --c-warn:       #EBE89F;
          --c-err:        #CB5E64;
          --c-ok:         #A4C681;
          --tag-file-bg:  rgba(164,198,129,0.12); --tag-file-c: #A4C681; --tag-file-b: rgba(164,198,129,0.35);
          --tag-man-bg:   rgba(235,232,156,0.12); --tag-man-c:  #EBE89F; --tag-man-b:  rgba(235,232,156,0.35);
          --tag-ok-bg:    rgba(39,90,70,0.28);   --tag-ok-c:   #A4C681; --tag-ok-b:   rgba(164,198,129,0.40);
          --tag-err-bg:   rgba(203,94,100,0.14); --tag-err-c:  #CB5E64; --tag-err-b:  rgba(203,94,100,0.40);
          --tag-sl-bg:    rgba(45,69,52,0.28);   --tag-sl-c:   #7eaaaa; --tag-sl-b:   rgba(45,69,52,0.50);
          --in-bg: #050d08; --in-c: #D0DCDC; --in-br: #1a2e1f; --in-ph: #426464;
          --btn-p-bg: rgba(39,90,70,0.35); --btn-p-c: #A4C681; --btn-p-b: rgba(164,198,129,0.45);
          --btn-g-c: #7eaaaa; --btn-g-b: #1a2e1f;
          --report-bg: #0c1910; --report-c: #D0DCDC;
          /* source-card backgrounds */
          --card-file-bg: rgba(164,198,129,0.10); --card-file-br: rgba(164,198,129,0.30);
          --card-man-bg:  rgba(235,232,156,0.10); --card-man-br:  rgba(235,232,156,0.28);
          --card-const-bg:#142019;                --card-const-br:#23382a;
          --card-empty-bg:rgba(20,32,25,0.60);    --card-empty-br:#23382a;
        }

        /* ══ Map Tailwind utility classes to CSS variables ════════════════ */
        [data-theme] .bg-slate-950  { background-color: var(--bg-page)    !important; }
        [data-theme] .bg-slate-900  { background-color: var(--bg-panel)   !important; }
        [data-theme] .bg-slate-800  { background-color: var(--bg-surface) !important; }
        [data-theme] .bg-slate-900/40 { background-color: color-mix(in srgb,var(--bg-panel) 40%,transparent) !important; }
        [data-theme] .bg-slate-900/60 { background-color: color-mix(in srgb,var(--bg-panel) 60%,transparent) !important; }
        [data-theme] .bg-slate-800/30 { background-color: color-mix(in srgb,var(--bg-surface) 30%,transparent) !important; }
        [data-theme] .bg-slate-800/40 { background-color: color-mix(in srgb,var(--bg-surface) 40%,transparent) !important; }
        [data-theme] .bg-slate-700/30 { background-color: color-mix(in srgb,var(--bg-surface) 25%,transparent) !important; }
        [data-theme] .bg-slate-950/60 { background-color: color-mix(in srgb,var(--bg-page) 60%,transparent) !important; }
        [data-theme] .bg-white         { background-color: var(--report-bg) !important; }

        [data-theme] .border-slate-800    { border-color: var(--br)     !important; }
        [data-theme] .border-slate-700    { border-color: var(--br-mid) !important; }
        [data-theme] .border-slate-600    { border-color: var(--br-mid) !important; }
        [data-theme] .border-slate-800/50 { border-color: color-mix(in srgb,var(--br) 50%,transparent) !important; }
        [data-theme] .border-slate-600/50 { border-color: color-mix(in srgb,var(--br-mid) 50%,transparent) !important; }

        [data-theme] .text-slate-100 { color: var(--t1) !important; }
        [data-theme] .text-slate-200 { color: var(--t1) !important; }
        [data-theme] .text-slate-300 { color: var(--t2) !important; }
        [data-theme] .text-slate-400 { color: var(--t3) !important; }
        [data-theme] .text-slate-500 { color: var(--t3) !important; }
        [data-theme] .text-slate-600 { color: var(--t4) !important; }
        [data-theme] .text-slate-900 { color: var(--report-c) !important; }

        [data-theme] .text-amber-400,
        [data-theme] .text-amber-300,
        [data-theme] .text-amber-200  { color: var(--c-warn) !important; }
        [data-theme] .text-cyan-400,
        [data-theme] .text-cyan-300,
        [data-theme] .text-cyan-200   { color: var(--c-info) !important; }
        [data-theme] .text-emerald-400,
        [data-theme] .text-emerald-300,
        [data-theme] .text-emerald-200 { color: var(--c-ok)  !important; }
        [data-theme] .text-red-400,
        [data-theme] .text-red-300,
        [data-theme] .text-red-200     { color: var(--c-err) !important; }
        [data-theme] .text-emerald-700 { color: var(--accent) !important; }

        [data-theme] .bg-amber-500/5,
        [data-theme] .bg-amber-500/10,
        [data-theme] .bg-amber-500/15,
        [data-theme] .bg-amber-500/20  { background-color: var(--fill-warn) !important; }
        [data-theme] .bg-cyan-500/5,
        [data-theme] .bg-cyan-500/10,
        [data-theme] .bg-cyan-500/15,
        [data-theme] .bg-cyan-500/20   { background-color: var(--fill-info) !important; }
        [data-theme] .bg-emerald-500/5,
        [data-theme] .bg-emerald-500/10,
        [data-theme] .bg-emerald-500/15,
        [data-theme] .bg-emerald-500/20 { background-color: var(--fill-ok)  !important; }
        [data-theme] .bg-red-500/10,
        [data-theme] .bg-red-900/30     { background-color: var(--fill-err) !important; }

        [data-theme] .border-amber-500/20,
        [data-theme] .border-amber-500/30,
        [data-theme] .border-amber-500/40 { border-color: var(--br-warn) !important; }
        [data-theme] .border-cyan-500/20,
        [data-theme] .border-cyan-500/30,
        [data-theme] .border-cyan-500/40  { border-color: var(--br-info) !important; }
        [data-theme] .border-emerald-500/20,
        [data-theme] .border-emerald-500/30,
        [data-theme] .border-emerald-500/40 { border-color: var(--br-ok)  !important; }
        [data-theme] .border-red-500/30,
        [data-theme] .border-red-500/40    { border-color: var(--br-err) !important; }

        /* ── All form elements pick up theme variables automatically ── */
        [data-theme] input,
        [data-theme] select,
        [data-theme] textarea {
          background-color: var(--in-bg) !important;
          color: var(--in-c) !important;
          border-color: var(--in-br) !important;
        }
        [data-theme] input::placeholder,
        [data-theme] textarea::placeholder { color: var(--in-ph) !important; }

        /* ── Misc light-only overrides ── */
        [data-theme="light"] .bg-slate-100,
        [data-theme="light"] .bg-slate-50 { background-color: var(--bg-surface) !important; }

        /* Opacity-modified text classes lose their contrast — force readable values */
        [data-theme="light"] .text-cyan-400/70,
        [data-theme="light"] .text-cyan-500/60    { color: var(--t3) !important; }
        [data-theme="light"] .text-amber-400/70,
        [data-theme="light"] .text-amber-400/80   { color: var(--c-warn) !important; }
        [data-theme="light"] .text-red-400/70     { color: var(--c-err) !important; }
        [data-theme="light"] .text-emerald-400/80 { color: var(--c-ok)  !important; }

        /* Ensure all mono/code spans inherit readable text color */
        [data-theme="light"] span.font-mono,
        [data-theme="light"] code { color: var(--t2) !important; }

        /* DefList, DefRow labels should be readable */
        [data-theme="light"] dt { color: var(--t3) !important; }
        [data-theme="light"] dd { color: var(--t1) !important; }

        /* ── Number input: remove spinners (both themes) ── */
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance:none; margin:0; }
        input[type=number] { -moz-appearance:textfield; appearance:textfield; }

        /* ── Sidebar responsive ── */
        @media (max-width: 767px) {
          .sidebar-nav { display: none !important; }
        }

        /* ── Scrollbar ── */
        ::-webkit-scrollbar { width:6px; height:6px; }
        [data-theme="light"] ::-webkit-scrollbar-track { background:#eaf4ec; }
        [data-theme="light"] ::-webkit-scrollbar-thumb { background:#A4C681; border-radius:3px; }
        [data-theme="dark"]  ::-webkit-scrollbar-track { background:#0c1910; }
        [data-theme="dark"]  ::-webkit-scrollbar-thumb { background:#275A46; border-radius:3px; }
      `}</style>
      <Header reset={reset} sealedMode={sealedMode} realSealedMode={realSealedMode}
        previewAsCrew={previewAsCrew}
        onTogglePreview={() => { if (initialRole !== 'vessel') { setPreviewAsCrew(!previewAsCrew); setShopRef(null); } }}
        onConfigure={() => setMode('configurator')}
        darkMode={darkMode} onToggleDark={toggleDark}
        onLogout={onLogout}
        initialRole={initialRole}
        onBackToFleet={initialRole === 'fleet' ? () => setMode('fleet-vessel-browser') : null} />

      {/* Body: sidebar + main content */}
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 64px)', alignItems: 'stretch' }}>

        {/* ── SIDEBAR NAV ─────────────────────────────────────────────── */}
        <aside style={{
          width: '220px',
          flexShrink: 0,
          background: 'var(--bg-panel)',
          borderRight: '1px solid var(--br)',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '16px',
          paddingBottom: '16px',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflowY: 'auto',
        }} className="hidden md:flex">
          <div style={{ padding: '0 12px 12px', borderBottom: '1px solid var(--br)', marginBottom: '8px' }}>
            <p style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--t4)', fontWeight: 600 }}>
              Workflow steps
            </p>
          </div>
          {STEPS.map((s, i) => {
            const Ic = s.icon;
            const done = parsed && i < stepIdx;
            const active = i === stepIdx;
            const accessible = i <= Math.max(stepIdx, maxUnlockedIdx);
            return (
              <button
                key={s.id}
                onClick={() => accessible && setStepIdx(i)}
                disabled={!accessible}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '9px 16px',
                  margin: '1px 8px',
                  borderRadius: '7px',
                  border: active ? '1px solid var(--br-ok)' : '1px solid transparent',
                  background: active ? 'var(--fill-ok)' : 'transparent',
                  color: active ? 'var(--c-ok)' : done ? 'var(--c-info)' : accessible ? 'var(--t3)' : 'var(--t4)',
                  cursor: accessible ? 'pointer' : 'not-allowed',
                  opacity: accessible ? 1 : 0.4,
                  textAlign: 'left',
                  fontFamily: 'StevieSans, sans-serif',
                  fontSize: '12px',
                  fontWeight: active ? 600 : 400,
                  transition: 'all 0.15s',
                }}
              >
                <span style={{
                  width: '20px', height: '20px', borderRadius: '5px', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: active ? 'var(--accent)' : done ? 'var(--fill-ok)' : 'var(--bg-surface)',
                  border: active ? 'none' : '1px solid var(--br)',
                }}>
                  {done && !active
                    ? <Check style={{ width: '10px', height: '10px', color: 'var(--accent)' }} />
                    : <Ic style={{ width: '11px', height: '11px', color: active ? '#fff' : 'var(--t3)' }} />
                  }
                </span>
                {s.label}
              </button>
            );
          })}
        </aside>

        {/* ── MAIN CONTENT ─────────────────────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>

          {/* Mobile step strip (shown below md) */}
          <div className="md:hidden"
            style={{ background: 'var(--bg-panel)', borderBottom: '1px solid var(--br)', overflowX: 'auto' }}>
            <div style={{ display: 'flex', gap: '2px', padding: '8px 12px' }}>
              {STEPS.map((s, i) => {
                const Ic = s.icon;
                const active = i === stepIdx;
                const accessible = i <= Math.max(stepIdx, maxUnlockedIdx);
                return (
                  <button
                    key={s.id}
                    onClick={() => accessible && setStepIdx(i)}
                    disabled={!accessible}
                    style={active
                      ? { background:'var(--fill-ok)', border:'1px solid var(--br-ok)', color:'var(--c-ok)', borderRadius:'6px', padding:'5px 10px', fontSize:'11px', fontWeight:600, display:'flex', alignItems:'center', gap:'5px', whiteSpace:'nowrap' }
                      : { color: accessible ? 'var(--t3)' : 'var(--t4)', background:'transparent', border:'1px solid transparent', borderRadius:'6px', padding:'5px 10px', fontSize:'11px', display:'flex', alignItems:'center', gap:'5px', whiteSpace:'nowrap', opacity: accessible ? 1 : 0.45 }
                    }
                  >
                    <Ic style={{ width:'12px', height:'12px' }} />
                    {s.label}
                  </button>
                );
              })}
            </div>
            <div style={{ height:'2px', background:'linear-gradient(to right,#265858,#275A46 50%,rgba(164,198,129,0.4))' }} />
          </div>

          {/* Page content */}
          <div style={{ flex: 1, padding: '24px', width: '100%', boxSizing: 'border-box' }}>
            {parseError && (
              <div className="mb-4 bg-red-900/30 border border-red-500/40 text-red-300 px-4 py-3 rounded flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div><div className="font-semibold">Parse error</div><div className="text-sm opacity-90">{parseError}</div></div>
              </div>
            )}

            {stepIdOf(stepIdx) === 'upload' && (
              <UploadStep
                onUpload={handleUpload}
                onLoadHistorical={loadHistoricalReport}
                onNavigate={goToStep}
                sessionRole={initialRole}
                sessionVesselKey={analyserVesselKey}
                sessionFleetVesselKeys={initialRole === 'fleet' ? fleetBrowserVesselKeys : null}
                sessionReady={sessionReady}
              />
            )}
            {stepIdOf(stepIdx) === 'engine' && (
              <EngineStep parsed={parsed} detected={detected} shopRef={shopRef} sealedMode={sealedMode}
                isHistorical={isHistorical}
                onNext={goToNext} onPrev={goToPrev} />
            )}
            {stepIdOf(stepIdx) === 'testload' && (
              <TestLoadStep
                parsed={parsed} testLoad={testLoad} setTestLoad={setTestLoad}
                testLoadSource={testLoadSource} setTestLoadSource={setTestLoadSource}
                shopRef={shopRef} shopMode={shopMode} setShopMode={setShopMode}
                isHistorical={isHistorical}
                onNext={goToNext} onPrev={goToPrev}
              />
            )}
            {stepIdOf(stepIdx) === 'shopmatch' && (
              <ShopMatchStep
                parsed={parsed} detected={detected}
                library={effectiveLibrary} setLibrary={(lib) => { setLibrary(lib); saveLibrary(lib); }}
                shopRef={shopRef} setShopRef={setShopRef}
                sealedMode={sealedMode}
                isHistorical={isHistorical}
                onNext={goToNext} onPrev={goToPrev}
              />
            )}
            {stepIdOf(stepIdx) === 'conditions' && (
              <ConditionsStep
                conditions={conditions} setConditions={setConditions}
                cylInputs={cylInputs} setCylInputs={setCylInputs}
                shopRef={shopRef} testLoad={testLoad} shopMode={shopMode}
                isHistorical={isHistorical}
                onNext={goToNext} onPrev={goToPrev}
              />
            )}
            {stepIdOf(stepIdx) === 'iso' && (
              <ISOStep computed={computed} conditions={conditions} shopRef={shopRef} testLoad={testLoad} shopMode={shopMode}
                isHistorical={isHistorical}
                onNext={goToNext} onPrev={goToPrev} />
            )}
            {stepIdOf(stepIdx) === 'analysis' && (
              <AnalysisStep computed={computed} parsed={parsed} shopRef={shopRef} testLoad={testLoad}
                conditions={conditions}
                isHistorical={isHistorical}
                onNext={goToNext} onPrev={goToPrev} />
            )}
            {stepIdOf(stepIdx) === 'report' && (
              <ReportStep computed={computed} parsed={parsed} shopRef={shopRef} testLoad={testLoad}
                conditions={conditions} detected={detected}
                onSave={saveSnapshot}
                onPrev={goToPrev}
                initialRole={initialRole}
                isHistorical={isHistorical}
                autoDownloadPdf={!silentPdfMode && pendingPdfDownload}
                onAutoDownloadDone={() => setPendingPdfDownload(false)} />
            )}
          </div>

          <Footer />
        </div>
      </div>
    </div>
  );
}

// =================================================================
//  HEADER & FOOTER
// =================================================================
// ── ZEROQU LOGO ────────────────────────────────────────────────────────────
function ZerroquWordmark({ height = 28 }) {
  return (
    <img
      src="/assets/Zeroqu-Logo.png"
      alt="Zeroqu"
      style={{ height: `${height}px`, width: 'auto', display: 'block' }}
    />
  );
}

// ── GRAPHIC ANCHOR ──────────────────────────────────────────────────────────
// "Lines moving from darker shade of green to light imply decarbonisation."
function GraphicAnchor() {
  return (
    <div
      style={{
        height: '3px',
        background: 'linear-gradient(to right, #265858 0%, #275A46 35%, #A4C681 75%, #EBE89F 100%)',
      }}
      aria-hidden="true"
    />
  );
}

function Header({ reset, sealedMode, realSealedMode, previewAsCrew, onTogglePreview, onConfigure, darkMode, onToggleDark, onLogout, initialRole, onBackToFleet }) {
  // Button visibility by role:
  //   admin  → both "Preview as crew" and "Configure Vessel" shown
  //   fleet  → "Back to Reports" + "Preview as crew" only (no Configure Vessel)
  //   vessel → neither button (crew view, no config access)
  const showPreviewBtn   = initialRole !== 'vessel' && initialRole !== 'fleet';
  const showConfigureBtn = initialRole === 'admin';

  const hdrBg = darkMode
    ? 'linear-gradient(135deg,#050d08,#0c1910,#142019)'
    : '#ffffff';

  const DarkToggle = () => (
    <button onClick={onToggleDark}
      title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{ border:'1px solid var(--br-mid)', color:'var(--t3)', borderRadius:'6px', padding:'5px 10px', background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px', fontSize:'11px' }}>
      {darkMode ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
      <span className="hidden sm:inline">{darkMode ? 'Light' : 'Dark'}</span>
    </button>
  );

  if (realSealedMode) {
    return (
      <>
        <header style={{ background: hdrBg, borderBottom: '1px solid var(--br)' }}>
          <div style={{ padding: "0 24px", height: "64px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="flex items-center gap-4">
              <ZerroquWordmark height={26} />
              <div style={{ width:'1px', height:'28px', background:'var(--br-mid)' }} />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color:'var(--t1)', fontFamily:'StevieSans,sans-serif' }}>
                    {VESSEL_CONFIG.vessel || 'Vessel'}
                  </span>
                  {VESSEL_CONFIG.imo && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                      style={{ background:'var(--fill-info)', color:'var(--c-info)', border:'1px solid var(--br-info)' }}>
                      IMO {VESSEL_CONFIG.imo}
                    </span>
                  )}
                  <Lock className="w-3 h-3" style={{ color:'var(--c-warn)' }} />
                  <span className="text-[10px] uppercase tracking-wider" style={{ color:'var(--c-warn)' }}>Sealed</span>
                </div>
                <p className="text-[10px]" style={{ color:'var(--t4)' }}>
                  {(VESSEL_CONFIG.engines||[]).length} engine{VESSEL_CONFIG.engines.length!==1?'s':''} configured
                  {VESSEL_CONFIG.configuredBy ? ` · by ${VESSEL_CONFIG.configuredBy}` : ''}
                  {VESSEL_CONFIG.configuredOn ? ` · ${VESSEL_CONFIG.configuredOn}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <DarkToggle />
              <button onClick={reset}
                style={{ border:'1px solid var(--br)', color:'var(--t3)', borderRadius:'6px', padding:'5px 10px', background:'transparent', cursor:'pointer', fontSize:'11px', display:'flex', alignItems:'center', gap:'5px' }}>
                <RotateCcw className="w-3 h-3" />Reset
              </button>
            </div>
          </div>
        </header>
        <GraphicAnchor />
      </>
    );
  }

  return (
    <>
      <header style={{ background: hdrBg, borderBottom: '1px solid var(--br)', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: '100%', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          {/* LEFT: Logo + title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
            <ZerroquWordmark height={30} />
            <div style={{ width: '1px', height: '32px', background: 'var(--br-mid)', flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--t1)', fontFamily: 'StevieSans, sans-serif', margin: 0, lineHeight: 1.2 }}>
                Engine Performance Analyser
              </p>
              <p style={{ fontSize: '10px', color: 'var(--t4)', margin: 0, lineHeight: 1.4 }}>
                Shop-trial ISO correction · Cylinder balance · Diagnostics
              </p>
            </div>
          </div>

          {/* RIGHT: Action buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <DarkToggle />

            <button onClick={onTogglePreview}
              style={previewAsCrew
                ? { background:'var(--fill-warn)', border:'1px solid var(--br-warn)', color:'var(--c-warn)', borderRadius:'6px', padding:'6px 12px', cursor:'pointer', fontSize:'12px', display: showPreviewBtn ? 'flex' : 'none', alignItems:'center', gap:'5px', fontWeight: 500 }
                : { background:'var(--fill-info)', border:'1px solid var(--br-info)', color:'var(--c-info)', borderRadius:'6px', padding:'6px 12px', cursor:'pointer', fontSize:'12px', display: showPreviewBtn ? 'flex' : 'none', alignItems:'center', gap:'5px', fontWeight: 500 }
              }
              title="Preview the crew-side UI without rebuilding">
              <Eye style={{ width: '13px', height: '13px' }} />
              <span className="hidden sm:inline">{previewAsCrew ? 'Exit crew preview' : 'Preview as crew'}</span>
            </button>

            <button onClick={onConfigure}
              style={{ background:'var(--btn-p-bg)', color:'var(--btn-p-c)', border:'1px solid var(--btn-p-b)', borderRadius:'6px', padding:'6px 12px', cursor:'pointer', fontSize:'12px', display: showConfigureBtn ? 'flex' : 'none', alignItems:'center', gap:'5px', fontWeight: 600 }}>
              <UserCog style={{ width: '13px', height: '13px' }} />
              <span className="hidden sm:inline">Configure Vessel</span>
            </button>

            {onBackToFleet && (
              <button onClick={onBackToFleet}
                style={{ background:'var(--fill-ok)', border:'1px solid var(--br-ok)', color:'var(--c-ok)', borderRadius:'6px', padding:'6px 12px', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:'5px', fontWeight: 600 }}>
                <ChevronLeft style={{ width: '13px', height: '13px' }} />
                <span className="hidden sm:inline">Report List</span>
              </button>
            )}
            
            
            {initialRole !== 'fleet' && (
            <button onClick={reset}
              style={{ border:'1px solid var(--br)', color:'var(--t3)', borderRadius:'6px', padding:'6px 12px', background:'transparent', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:'5px' }}>
              <RotateCcw style={{ width: '13px', height: '13px' }} />
              <span className="hidden sm:inline">Reset</span>
            </button>
            )}

            {onLogout && (
              <button onClick={onLogout}
                style={{ border:'1px solid var(--br-err)', color:'var(--c-err)', borderRadius:'6px', padding:'6px 12px', background:'var(--fill-err)', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:'5px' }}>
                <span className="hidden sm:inline">Log out</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <GraphicAnchor />

      {/* {previewAsCrew && (
        <div style={{ background:'var(--fill-warn)', borderBottom:'1px solid var(--br-warn)', color:'var(--c-warn)', padding:'7px 16px', fontSize:'11px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
          <Eye className="w-3.5 h-3.5" />
          <span><strong>Crew preview mode</strong> — UI is rendering as the ship's crew would see it.
          Library entries are read-only; Configure Vessel and edit actions are hidden.
          Click <em>Exit crew preview</em> to return to admin view.</span>
        </div>
      )} */}
    </>
  );
}
function Footer() {
  return (
    <>
      {/* <GraphicAnchor />
      <footer style={{ borderTop: '1px solid var(--br)', background: 'var(--bg-panel)', marginTop: 'auto' }} className="py-4">
        <div style={{ maxWidth: '100%', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ZerroquWordmark height={18} />
            <span style={{ fontSize: '10px', color: 'var(--t4)' }}>
              Engine Performance Analyser
            </span>
          </div>
          <span style={{ fontSize: '10px', color: 'var(--t4)' }}>
            ISO correction · Shop-trial reference · Cylinder diagnostics · v2.0
          </span>
        </div>
      </footer> */}
    </>
  );
}

// =================================================================
//  STEP 1 — UPLOAD
// =================================================================
function UploadStep({ onUpload, onLoadHistorical, onNavigate, sessionRole, sessionVesselKey, sessionFleetVesselKeys, sessionReady = true }) {
  const inputRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const [tab, setTab] = useState('upload');  // 'upload' | 'history'
  const [reports, setReports] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    // Don't load until session context is resolved — prevents a flash of
    // unfiltered reports before vessel/fleet keys arrive from the DB.
    if (!sessionReady) return;

    (async () => {
      const all = await loadReportIndex();
      let filtered = all;
      if (sessionRole === 'vessel' && sessionVesselKey) {
        filtered = all.filter(r => r.vesselKey === sessionVesselKey);
      } else if (sessionRole === 'fleet' && sessionFleetVesselKeys?.length) {
        filtered = all.filter(r => sessionFleetVesselKeys.includes(r.vesselKey));
      }
      setReports(filtered);
    })();
  }, [refreshTick, tab, sessionRole, sessionVesselKey, sessionFleetVesselKeys, sessionReady]);

  const handleDelete = async (id) => {
    await deleteReportFromHistory(id);
    setRefreshTick(t => t + 1);
  };

  const handleReopen = async (id, stepId) => {
    const snap = await loadReportFromHistory(id);
    if (snap) {
      // Pass target step directly — no setTimeout flash
      onLoadHistorical(snap, stepId || 'report');
    } else {
      window.alert('Could not load report (it may have been deleted).');
    }
  };

  return (
    <div>
      <SectionHeader icon={Upload} title="Performance data" subtitle="Upload a new report or open a previously analyzed one" stepId="upload" />

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-slate-800">
        <TabButton active={tab === 'upload'} onClick={() => setTab('upload')} icon={Upload} label="Upload new" />
        <TabButton active={tab === 'history'} onClick={() => setTab('history')} icon={Database} label={`History${reports.length ? ` (${reports.length})` : ''}`} />
      </div>

      {tab === 'upload' && (
        <>
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault(); setDragOver(false);
              if (e.dataTransfer.files[0]) onUpload(e.dataTransfer.files[0]);
            }}
            className={`relative cursor-pointer rounded-lg border-2 border-dashed p-12 text-center transition-all ${
              dragOver ? 'border-amber-500 bg-amber-500/5' : 'border-slate-700 hover:border-slate-600 hover:bg-slate-900/50'
            }`}
          >
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls,.xlsm" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} className="hidden" />
            <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-500 mb-3" />
            <div className="text-sm font-medium text-slate-200">Drop file here or click to browse</div>
            <div className="text-xs text-slate-500 mt-1">Supported: PMI CSV · Test report XLSX</div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormatCard
              icon={Database} title="PMI Mark II / Leutert CSV"
              items={['Engine ID, session, bore/stroke', 'Per-cylinder pressure-vs-crank-angle', 'Pmax, MIP, Ptdc, dp/dα, IndPower']}
            />
            <FormatCard
              icon={FileSpreadsheet} title="Test Report XLSX"
              items={['Speed test sheet (PMI-style layout)', 'Ambient & charge-air conditions from file', 'Per-cylinder Pmax, Pcomp, MIP, Texh, Index, VIT']}
            />
          </div>
        </>
      )}

      {tab === 'history' && (
        <HistoryList reports={reports} onReopen={handleReopen} onDelete={handleDelete} onSwitchToUpload={() => setTab('upload')} />
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Ic, label }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
        active ? 'border-amber-500 text-amber-300' : 'border-transparent text-slate-500 hover:text-slate-300'
      }`}>
      <Ic className="w-3.5 h-3.5" />{label}
    </button>
  );
}

function HistoryList({ reports, onReopen, onDelete, onSwitchToUpload }) {
  if (reports.length === 0) {
    return (
      <div style={{ textAlign:'center', padding:'48px 16px', border:'2px dashed var(--br-mid)', borderRadius:'8px', background:'var(--bg-surface)' }}>
        <Database style={{ width:'36px', height:'36px', margin:'0 auto 8px', color:'var(--t3)' }} />
        <div style={{ fontSize:'14px', color:'var(--t1)', fontWeight:500, marginBottom:'4px' }}>No reports saved yet</div>
        <div style={{ fontSize:'12px', color:'var(--t3)', marginBottom:'16px' }}>Reports are saved automatically when you reach the Report step.</div>
        <button onClick={onSwitchToUpload}
          style={{ background:'var(--fill-ok)', border:'1px solid var(--br-ok)', color:'var(--c-ok)', borderRadius:'6px', padding:'6px 12px', fontSize:'12px', cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'6px' }}>
          <Upload style={{ width:'12px', height:'12px' }} />Upload your first file
        </button>
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'8px', width:'100%', boxSizing:'border-box' }}>
      <div style={{ fontSize:'12px', color:'var(--t3)', marginBottom:'4px' }}>
        {reports.length} saved report{reports.length === 1 ? '' : 's'} — most recent first. Click a row or a step button to open.
      </div>
      {reports.map(r => (
        <HistoryRow key={r.id} report={r}
          onReopen={(stepId) => onReopen(r.id, stepId)}
          onDelete={() => onDelete(r.id)} />
      ))}
    </div>
  );
}

// Step quick-jump buttons shown on each history card
// const HISTORY_STEP_BTNS = [
//   { id:'engine',     label:'Engine',    icon: Cpu      },
//   { id:'conditions', label:'Cond.',     icon: Wind     },
//   { id:'iso',        label:'ISO',       icon: ShieldCheck },
//   { id:'analysis',   label:'Analysis',  icon: BarChart3 },
//   { id:'report',     label:'Report',    icon: FileText  },
// ];

function HistoryRow({ report, onReopen, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dt = new Date(report.savedAt);
  const dateStr = isNaN(dt.getTime()) ? report.savedAt : dt.toLocaleString();

  const score = report.engineHealth;
  const badgeBg    = score > 80 ? 'rgba(39,90,70,0.12)'    : score > 50 ? 'rgba(180,160,0,0.12)'    : 'rgba(203,94,100,0.10)';
  const badgeBr    = score > 80 ? 'rgba(39,90,70,0.35)'    : score > 50 ? 'rgba(140,120,0,0.40)'    : 'rgba(203,94,100,0.38)';
  const badgeColor = score > 80 ? 'var(--c-ok)'             : score > 50 ? 'var(--c-warn)'            : 'var(--c-err)';

  return (
    <div style={{
      background:'var(--bg-panel)', border:'1px solid var(--br)', borderRadius:'8px',
      overflow:'hidden', transition:'border-color 0.15s', width:'100%', boxSizing:'border-box',
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--br)'}
    >
      {/* ── Main row (clickable → opens report step) ── */}
      <div
        onClick={() => onReopen('report')}
        style={{
          padding:'12px', display:'flex', alignItems:'center', gap:'12px',
          cursor:'pointer',
        }}
      >
        {/* Health badge */}
        <div style={{
          width:'56px', height:'56px', flexShrink:0, borderRadius:'8px',
          background: badgeBg, border: `1px solid ${badgeBr}`,
          display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        }}>
          <div style={{ fontSize:'20px', fontFamily: 'StevieSans,sans-serif', fontWeight:700, color: badgeColor, lineHeight:1 }}>{score ?? '—'}</div>
          <div style={{ fontSize:'8px', textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--t3)', marginTop:'2px' }}>/ 100</div>
        </div>

        {/* Summary text */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap', marginBottom:'3px' }}>
            <span style={{ fontSize:'14px', fontFamily:'StevieSans,sans-serif', fontWeight:700, color:'var(--t1)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {report.engineLabel || '—'}
            </span>
            {report.engineType && (
              <Tag tone={report.engineType === '2-stroke' ? 'amber' : 'cyan'} small>{report.engineType}</Tag>
            )}
            {report.testLoadPct != null && (
              <span style={{ fontSize:'11px', fontFamily: 'StevieSans,sans-serif', color:'var(--c-info)', fontWeight:600 }}>
                {Number(report.testLoadPct).toFixed(1)} % MCR
              </span>
            )}
          </div>
          <div style={{ fontSize:'11px', color:'var(--t3)', marginBottom:'4px' }}>
            {[report.vessel, report.imo ? `IMO ${report.imo}` : null, dateStr].filter(Boolean).join(' · ')}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', fontSize:'11px' }}>
            {report.criticalCount > 0 && (
              <span style={{ color:'var(--c-err)', display:'flex', alignItems:'center', gap:'4px', fontWeight:600 }}>
                <AlertCircle style={{ width:'11px', height:'11px' }} />{report.criticalCount} critical
              </span>
            )}
            {report.warningCount > 0 && (
              <span style={{ color:'var(--c-warn)', display:'flex', alignItems:'center', gap:'4px', fontWeight:600 }}>
                <AlertTriangle style={{ width:'11px', height:'11px' }} />{report.warningCount} warning{report.warningCount !== 1 ? 's' : ''}
              </span>
            )}
            {report.findingsCount === 0 && (
              <span style={{ color:'var(--c-ok)', display:'flex', alignItems:'center', gap:'4px' }}>
                <CheckCircle2 style={{ width:'11px', height:'11px' }} />no findings
              </span>
            )}
          </div>
        </div>

        {/* Right-side controls */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'4px', flexShrink:0 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Open button — goes directly to Engine Profile */}
          <button
            onClick={() => onReopen('engine')}
            title="Open at Engine Profile"
            style={{ background:'var(--btn-p-bg)', border:'1px solid var(--btn-p-b)', color:'var(--btn-p-c)', borderRadius:'5px', padding:'5px 14px', fontSize:'11px', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px', whiteSpace:'nowrap', fontWeight:600 }}>
            <ChevronRight style={{ width:'12px', height:'12px' }} />Open
          </button>

          {/* Delete */}
          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)}
              style={{ background:'var(--fill-err)', border:'1px solid var(--br-err)', color:'var(--c-err)', borderRadius:'5px', padding:'4px 8px', fontSize:'10px', cursor:'pointer', display:'flex', alignItems:'center', gap:'4px', whiteSpace:'nowrap' }}>
              <Trash2 style={{ width:'11px', height:'11px' }} />Delete
            </button>
          ) : (
            <div style={{ display:'flex', gap:'3px' }}>
              <button onClick={onDelete}
                style={{ background:'var(--c-err)', border:'1px solid var(--c-err)', color:'#fff', borderRadius:'5px', padding:'4px 8px', fontSize:'10px', cursor:'pointer', fontWeight:700 }}>
                Confirm
              </button>
              <button onClick={() => setConfirmDelete(false)}
                style={{ background:'var(--bg-surface)', border:'1px solid var(--br)', color:'var(--t3)', borderRadius:'5px', padding:'4px 8px', fontSize:'10px', cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>


    </div>
  );
}

function FormatCard({ icon: Ic, title, items }) {
  return (
    <div style={{ padding:'14px', borderRadius:'8px', border:'1px solid var(--br)', background:'var(--bg-panel)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
        <Ic style={{ width:'16px', height:'16px', color:'var(--accent)', flexShrink:0 }} />
        <h4 style={{ fontSize:'13px', fontWeight:600, color:'var(--t1)', margin:0, fontFamily:'StevieSans,sans-serif' }}>{title}</h4>
      </div>
      <ul style={{ margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:'4px' }}>
        {items.map((it, i) => (
          <li key={i} style={{ display:'flex', alignItems:'flex-start', gap:'6px', fontSize:'12px', color:'var(--t2)' }}>
            <span style={{ color:'var(--accent-lt)', marginTop:'2px', flexShrink:0 }}>·</span>{it}
          </li>
        ))}
      </ul>
    </div>
  );
}

// =================================================================
//  STEP 2 — ENGINE PROFILE
// =================================================================
// =================================================================
//  READ-ONLY BANNER — shown on every step when viewing a historical report
// =================================================================
function ReadOnlyBanner() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '8px 14px', marginBottom: '14px',
      borderRadius: '7px',
      background: 'var(--fill-info)', border: '1px solid var(--br-info)',
      fontSize: '12px', color: 'var(--c-info)', fontWeight: 500,
    }}>
      <Eye style={{ width: '14px', height: '14px', flexShrink: 0 }} />
      <span>
        <strong>View-only</strong> — this is a saved historical report. All fields are read-only.
        Upload a new file to start a fresh analysis.
      </span>
    </div>
  );
}

function EngineStep({ parsed, detected, shopRef, sealedMode, isHistorical, onNext, onPrev }) {
  if (!parsed) return null;
  const m = parsed.data.meta;
  return (
    <div>
      {isHistorical && <ReadOnlyBanner />}
      <SectionHeader icon={Cpu}
        title="Engine profile"
        subtitle={shopRef ? `${getEngineRole(shopRef) === 'auxiliary' ? 'Auxiliary' : 'Main'} engine — ${getEngineLabel(shopRef)} — auto-extracted from uploaded file` : 'Auto-extracted from uploaded file'}
        stepId="engine" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Identification" icon={Ship}>
          <DefList items={[
            ['Vessel',         m.vessel || '—'],
            ['IMO No.',        m.imo || '—'],
            ['Engine ID',      m.engineId || '—'],
            ['Model',          m.model || '—'],
            ['Manufacturer',   m.manufacturer || '—'],
            ['Date',           m.date ? String(m.date) : '—'],
            ['File format',    m.format],
          ]} />
        </Panel>
        <Panel title="Specifications" icon={Settings}>
          <DefList items={[
            ['Stroke type',    m.strokeType ? `${m.strokeType}-stroke` : '—'],
            ['Bore',           m.bore_mm ? `${m.bore_mm} mm` : '—'],
            ['Stroke',         m.stroke_mm ? `${m.stroke_mm} mm` : '—'],
            ['Conrod length',  m.conRod_mm ? `${m.conRod_mm} mm` : '—'],
            ['Cylinders (measured)', m.cylinders ?? '—'],
            ['Cylinders (total)',    m.totalCyl ?? '—'],
            ['MCR rating',     m.mcrPower_kW ? `${m.mcrPower_kW} kW` : '—'],
            ['Max RPM',        m.maxRPM ? `${m.maxRPM} rpm` : '—'],
            ['Mean RPM (test)',m.rpm ? m.rpm.toFixed(1) : '—'],
            ['Mean MIP (test)',m.MIP_overall_bar != null ? `${m.MIP_overall_bar.toFixed(2)} bar` : '—'],
            ['Indicated power',m.indPower_total_kW != null ? `${m.indPower_total_kW.toFixed(0)} kW` : '—'],
          ]} />
        </Panel>
        <Panel title="Engine type detection" icon={Layers}>
          <div className="mb-3">
            <Tag tone={detected?.type === '2-stroke' ? 'amber' : 'cyan'} large>
              {detected?.type?.toUpperCase()}
            </Tag>
            <span className="ml-2 text-xs text-slate-500">confidence: <span className={detected?.confidence === 'high' ? 'text-emerald-400' : 'text-amber-400'}>{detected?.confidence}</span></span>
          </div>
          <ul className="text-xs text-slate-300 space-y-1.5">
            {(detected?.reasons || []).map((r, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />{r}
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <NavRow onPrev={onPrev} onNext={onNext} canNext={true} />
    </div>
  );
}

// =================================================================
//  STEP 3 — TEST LOAD (separate tab as requested)
// =================================================================
function TestLoadStep({ parsed, testLoad, setTestLoad, testLoadSource, setTestLoadSource, shopRef, shopMode, setShopMode, isHistorical, onNext, onPrev }) {
  const m = parsed?.data?.meta;
  const recompute = () => {
    if (m?.shaftPower_kW && shopRef?.mcrPower_kW) {
      setTestLoad(testLoadFromShaftPower(m.shaftPower_kW, shopRef.mcrPower_kW));
      setTestLoadSource('computed-from-shaft-power');
    }
  };
  return (
    <div>
      {isHistorical && <ReadOnlyBanner />}
      <SectionHeader icon={Gauge}
        title='Test Load — % MCR'
        subtitle={shopRef ? `${getEngineLabel(shopRef)} — detected from data; can be overridden` : 'Detected from data; can be overridden by the user'}
        stepId="testload" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title={`Test condition: ${testLoad != null ? testLoad.toFixed(1) + ' % MCR' : '—'}`} icon={Gauge}>
          <div className="text-center py-6">
            <div className="text-6xl font-mono font-bold text-amber-300">
              {testLoad != null ? testLoad.toFixed(1) : '—'}
              <span className="text-2xl text-slate-500 ml-2">%</span>
            </div>
            <div className="mt-2 text-xs text-slate-400">of Maximum Continuous Rating</div>
            <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 roundedtext-xs">
              <Info className="w-3 h-3 text-cyan-400" />
              <span className="text-slate-300">source:</span>
              <span className="text-cyan-400 font-mono">{testLoadSource}</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-800">
            <label className="text-xs text-slate-400">Override (% MCR)</label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="number" step="0.1" min="0" max="120"
                value={testLoad ?? ''}
                onChange={(e) => { if (!isHistorical) { setTestLoad(parseFloat(e.target.value) || 0); setTestLoadSource('user-override'); } }}
                readOnly={isHistorical}
                disabled={isHistorical}
                className="flex-1 bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm font-mono"
                style={isHistorical ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
              />
              {m?.shaftPower_kW && shopRef?.mcrPower_kW && !isHistorical && (
                <button onClick={recompute} className="text-xs px-2 py-1.5 rounded border border-slate-700 hover:bg-slate-800">
                  Recompute
                </button>
              )}
            </div>
            <div className="mt-2 text-[10px] text-slate-500">
              Formula (Excel): <span className="font-mono text-slate-400">load = ShaftPower_kW / (MCR_kW / 100) / (1 - thrust_loss)</span>
            </div>
          </div>
        </Panel>

        <Panel title="Calculation inputs" icon={ClipboardList}>
          <DefList items={[
            ['Shaft power (file)',   m?.shaftPower_kW ? `${m.shaftPower_kW} kW` : '—'],
            ['MCR (shop ref)',       shopRef?.mcrPower_kW ? `${shopRef.mcrPower_kW} kW` : '—'],
            ['Mean MIP (file)',      m?.MIP_overall_bar != null ? `${m.MIP_overall_bar} bar` : '—'],
            ['Mean RPM (file)',      m?.rpm != null ? m.rpm.toFixed(1) : '—'],
            ['Thrust loss',          `${(DEFAULT_THRUST_LOSS * 100).toFixed(1)} %`],
            ['Session name',         m?.session || '—'],
          ]} />

          <div className="mt-4 pt-4 border-t border-slate-800">
            <div className="text-xs font-semibold text-slate-300 mb-2">Reference grid</div>
            <div className="flex gap-2">
              <button
                onClick={() => { if (!isHistorical) setShopMode('full'); }}
                disabled={isHistorical}
                style={isHistorical ? { cursor: 'not-allowed', opacity: 0.65 } : {}}
                className={`flex-1 px-3 py-2 rounded text-xs ${shopMode === 'full' ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300' : 'border border-slate-700 text-slate-400'}`}>
                Full rated
                <div className="text-[10px] mt-0.5" style={{ color: 'var(--t2)' }}>{STD_LOAD_POINTS_FULL.join(' / ')} %</div>
              </button>
              <button
                onClick={() => { if (!isHistorical) setShopMode('tc-cutout'); }}
                disabled={isHistorical}
                style={isHistorical ? { cursor: 'not-allowed', opacity: 0.65 } : {}}
                className={`flex-1 px-3 py-2 rounded text-xs ${shopMode === 'tc-cutout' ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300' : 'border border-slate-700 text-slate-400'}`}>
                TC cut-out
                <div className="text-[10px] mt-0.5" style={{ color: 'var(--t2)' }}>{STD_LOAD_POINTS_TC.join(' / ')} %</div>
              </button>
            </div>
          </div>
        </Panel>
      </div>

      {/* Visualisation: position on shop trial curve */}
      {shopRef && testLoad != null && (
        <Panel title="Position on shop trial curve" icon={TrendingUp} className="mt-4">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={shopRef.loadPoints.map((lp, i) => ({ load: lp, pmax: shopRef.physical.pmax[i], pcomp: shopRef.physical.pcomp[i] }))}>
              <CartesianGrid stroke="var(--br)" strokeDasharray="3 3" />
              <XAxis dataKey="load" type="number" domain={['dataMin', 'dataMax']} stroke="var(--t4)" tick={{ fill:'var(--t3)', fontSize: 11 }} label={{ value: '% MCR', position: 'bottom', offset: -5, fill:'var(--t3)', fontSize: 11 }} />
              <YAxis stroke="var(--t4)" tick={{ fill:'var(--t3)', fontSize: 11 }} label={{ value: 'bar', angle: -90, position: 'insideLeft', fill:'var(--t3)', fontSize: 11 }} />
              <Tooltip contentStyle={{ background:'var(--bg-panel)', border:'1px solid var(--br)', fontSize: 11, color:'var(--t1)' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine x={testLoad} stroke="#c45000" strokeWidth={3} label={{ value: `Test load ${testLoad.toFixed(0)}%`, position: 'top', fill: '#c45000', fontSize: 11, fontWeight: 700 }} />
              <Line type="monotone" dataKey="pmax"  stroke="#1a6b8a" strokeWidth={2.5} name="Pmax shop"  dot={{ r: 4, fill:'#1a6b8a', strokeWidth:0 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="pcomp" stroke="#5a3ea8" strokeWidth={2.5} name="Pcomp shop" dot={{ r: 4, fill:'#5a3ea8', strokeWidth:0 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <NavRow onPrev={onPrev} onNext={onNext} canNext={testLoad != null && testLoad > 0} />
    </div>
  );
}

// =================================================================
//  STEP 4 — SHOP TRIAL MATCH / LIBRARY
// =================================================================
// =================================================================
//  ENGINE-MATCH VALIDATION
//  Checks whether a user-selected shop trial entry is compatible
//  with the engine auto-detected from the uploaded file.
//  Returns { ok, severity, errors, warnings } where:
//    - severity 'block' means the user MUST resolve before proceeding
//    - severity 'warn'  means a non-blocking caution
//    - severity 'ok'    means a clean match
// =================================================================
function validateEngineMatch(detected, picked) {
  if (!detected || !picked) return { ok: false, severity: 'block', errors: ['No engine selected.'], warnings: [] };

  const errors = [];
  const warnings = [];

  // 1. Stroke type — hard block (4-stroke shop trial cannot analyze 2-stroke data, ever)
  if (detected.type && picked.engineType && detected.type !== picked.engineType) {
    errors.push(
      `Stroke type mismatch — uploaded data is from a ${detected.type} engine, but you selected a ${picked.engineType} entry.  Pick the matching engine.`
    );
  }

  // 2. Cylinder count — hard block (per-cylinder analysis is meaningless if counts differ)
  if (detected.cylinders && picked.cylinders && detected.cylinders !== picked.cylinders) {
    errors.push(
      `Cylinder count mismatch — uploaded data has ${detected.cylinders} measured cylinder${detected.cylinders === 1 ? '' : 's'}, but selected engine has ${picked.cylinders} cylinder${picked.cylinders === 1 ? '' : 's'}.`
    );
  }

  // 3. Bore — warn if more than 10 mm off (not a hard block; allows for slight unit/precision differences)
  if (detected.bore_mm && picked.bore_m) {
    const diff = Math.abs(detected.bore_mm - picked.bore_m * 1000);
    if (diff > 10) {
      warnings.push(
        `Bore differs significantly — uploaded ${detected.bore_mm} mm vs selected ${picked.bore_m * 1000} mm (Δ ${diff.toFixed(0)} mm).  Verify this is the right engine.`
      );
    }
  }

  // 4. Stroke length — warn if more than 100 mm off
  if (detected.stroke_mm && picked.stroke_m) {
    const diff = Math.abs(detected.stroke_mm - picked.stroke_m * 1000);
    if (diff > 100) {
      warnings.push(
        `Stroke length differs — uploaded ${detected.stroke_mm} mm vs selected ${(picked.stroke_m * 1000).toFixed(0)} mm.  Verify this is the right engine.`
      );
    }
  }

  const severity = errors.length > 0 ? 'block' : warnings.length > 0 ? 'warn' : 'ok';
  return { ok: severity === 'ok', severity, errors, warnings };
}

// =================================================================
//  SEALED-MODE SHOP TRIAL PICKER
//  Read-only — cannot add/edit/import/delete entries.
//  Selecting the wrong engine triggers blocking validation.
// =================================================================
function SealedShopMatchStep({ parsed, detected, library, shopRef, setShopRef, recommended, onNext, onPrev }) {
  // Acknowledge-mismatch toggle: when the validation produces a soft warning,
  // the user can opt to proceed.  Hard errors (stroke type, cyl count) cannot
  // be overridden — those would cause incorrect analysis.
  const [acknowledgedWarn, setAcknowledgedWarn] = useState(false);

  // Reset acknowledgement whenever the selection changes
  useEffect(() => { setAcknowledgedWarn(false); }, [shopRef?.id]);

  const validation = useMemo(() => validateEngineMatch(detected, shopRef), [detected, shopRef]);

  const canProceed =
    !!shopRef && (
      validation.severity === 'ok' ||
      (validation.severity === 'warn' && acknowledgedWarn)
    );

  const recId = recommended?.entry?.id;

  return (
    <div>
      <SectionHeader icon={BookOpen} title="Select shop trial reference"
        subtitle={`Choose the engine that matches your uploaded data.  ${library.length} engine${library.length !== 1 ? 's are' : ' is'} configured for this vessel.`}
        stepId="shopmatch" />

      {/* Guide admin when no engines are configured yet in the draft */}
      {library.length === 0 && (
        <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-amber-200 mb-1">
                No engines configured for this vessel yet
              </div>
              <div className="text-xs text-amber-300/80 space-y-1">
                <div>You are in <strong>crew preview mode</strong> but your vessel has no engines configured. This is what the crew would see if the tool was deployed without any engine setup.</div>
                <div className="mt-2">To add engines:</div>
                <ol className="list-decimal list-inside space-y-1 mt-1 text-amber-200/90">
                  <li>Click <strong>Exit crew preview</strong> at the top</li>
                  <li>Click <strong>Configure Vessel</strong> in the header</li>
                  <li>Add engines using <strong>Add engine</strong></li>
                  <li>Click <strong>Back to analyzer</strong> (you don&apos;t need to export yet)</li>
                  <li>Click <strong>Preview as crew</strong> again to see your configured engines here</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}
      <Panel title="Detected from uploaded file" icon={Eye} className="mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-x-8 gap-y-6 text-xs">
          {/* Engine Type */}
          <div>
            <div className="text-gray-500 mb-1">Engine type</div>
            <Tag tone={detected?.type === '2-stroke' ? 'amber' : 'cyan'}>
              {detected?.type || '—'}
            </Tag>
          </div>

          {/* Cylinders */}
          <div>
            <div className="text-gray-500 mb-1">Cylinders</div>
            <div className="font-medium text-base">{detected?.cylinders ?? '—'}</div>
          </div>

          {/* Bore */}
          <div>
            <div className="text-gray-500 mb-1">Bore</div>
            <div className="font-medium text-base">
              {detected?.bore_mm ? `${detected.bore_mm} mm` : '—'}
            </div>
          </div>

          {/* Stroke */}
          <div>
            <div className="text-gray-500 mb-1">Stroke</div>
            <div className="font-medium text-base">
              {detected?.stroke_mm ? `${detected.stroke_mm} mm` : '—'}
            </div>
          </div>

          {/* Mean RPM */}
          <div>
            <div className="text-gray-500 mb-1">Mean RPM</div>
            <div className="font-medium text-base">
              {parsed?.data?.meta?.rpm ? parsed.data.meta.rpm.toFixed(1) : '—'}
            </div>
          </div>
        </div>
      </Panel>

      {/* Recommendation banner */}
      {recommended && (
        <div className="mb-4 rounded-lg p-3 flex items-start gap-3" style={{ border:'1px solid var(--br-info)', background:'var(--fill-info)' }}>
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color:'var(--accent)' }} />
          <div className="flex-1 text-xs" style={{ color:'#000000' }}>
            <span style={{ fontWeight:700, color:'#000000' }}>Recommended: </span>
            <span style={{ fontFamily: 'StevieSans,sans-serif', fontWeight:600, color:'#000000' }}>{recommended.entry._vesselEngineName || recommended.entry.model}</span>
            <span style={{ color:'#000000', opacity:0.7 }}> — match score {recommended.score} ({recommended.confidence})</span>
            <div className="mt-0.5" style={{ fontSize:'11px', color:'#000000', opacity:0.8 }}>
              Click the recommended card below to select it, or pick a different engine if you know better.
            </div>
          </div>
        </div>
      )}

      {/* Engine cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {library.map(e => {
          const isRec = e.id === recId;
          const isSel = shopRef?.id === e.id;
          const cardValidation = validateEngineMatch(detected, e);
          const wouldBlock = cardValidation.severity === 'block';

          let border = 'border-slate-700';
          let bg = 'bg-slate-900/40';
          if (isSel)       { border = 'border-amber-500/60'; bg = 'bg-amber-500/10'; }
          else if (isRec)  { border = 'border-cyan-500/40';  bg = 'bg-cyan-500/5';   }
          else if (wouldBlock) { border = 'border-red-500/30 opacity-60'; }

          return (
            <button
              key={e.id}
              onClick={() => setShopRef(e)}
              className={`text-left rounded-lg border ${border} ${bg} p-3 hover:border-slate-500 transition-colors`}
            >
              {/* Card header with badges */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color:'#000000' }}>
                    {e._vesselEngineName || e.model}
                  </div>
                  <div className="text-[11px] truncate" style={{ color:'#000000', opacity:0.7 }}>{e.manufacturer} {e.model}</div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {isSel && <Tag tone="amber" small>Selected</Tag>}
                  {!isSel && isRec && <Tag tone="cyan" small>Recommended</Tag>}
                  {!isSel && wouldBlock && <Tag tone="red" small>Won&apos;t match</Tag>}
                </div>
              </div>

              {/* Quick specs */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] mt-2" style={{ color:'#000000' }}>
                <DefRow label="Type" value={<Tag tone={e.engineType === '2-stroke' ? 'amber' : 'cyan'} small>{e.engineType}</Tag>} />
                <DefRow label="Cyl" value={e.cylinders} />
                <DefRow label="MCR" value={`${e.mcrPower_kW} kW`} />
                <DefRow label="RPM" value={e.maxRPM ?? '—'} />
                <DefRow label="Bore" value={`${e.bore_m * 1000} mm`} />
                <DefRow label="Stroke" value={`${(e.stroke_m * 1000).toFixed(0)} mm`} />
              </div>

              {e._vesselEngineRole && (
                <div className="mt-2 pt-2 text-[10px]" style={{ borderTop:'1px solid rgba(0,0,0,0.15)', color:'#000000', opacity:0.6 }}>
                  Role: {e._vesselEngineRole === 'main' ? 'Main propulsion' : 'Auxiliary'}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Validation panel — only after a selection is made */}
      {shopRef && (
        <Panel title="Selection validation" icon={validation.ok ? CheckCircle2 : validation.severity === 'block' ? XCircle : AlertCircle} className="mb-4">
          {validation.severity === 'ok' && (
            <div className="rounded p-3 flex items-start gap-2" style={{ background:'var(--fill-ok)', border:'1px solid var(--br-ok)' }}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color:'var(--accent)' }} />
              <div className="text-xs" style={{ color:'#000000' }}>
                <div className="font-semibold mb-0.5">Selection is consistent with the uploaded file.</div>
                <div className="text-[11px]" style={{ color:'#000000', opacity:0.75 }}>Stroke type, cylinder count, bore, and stroke all match.  Click Next to continue.</div>
              </div>
            </div>
          )}

          {validation.severity === 'block' && (
            <div className="rounded p-3" style={{ background:'var(--fill-err)', border:'1px solid var(--br-err)' }}>
              <div className="flex items-start gap-2 mb-2">
                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color:'var(--c-err)' }} />
                <div className="text-xs" style={{ color:'#000000' }}>
                  <div className="font-semibold mb-1">Cannot proceed — selected engine does not match the uploaded data.</div>
                  <div className="text-[11px]" style={{ color:'#000000', opacity:0.75 }}>
                    Pick the engine that matches the uploaded file, or upload the correct file for this engine.
                  </div>
                </div>
              </div>
              <ul className="text-[11px] space-y-1 ml-6" style={{ color:'#000000' }}>
                {validation.errors.map((er, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="mt-0.5" style={{ color:'var(--c-err)' }}>•</span>{er}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {validation.severity === 'warn' && (
            <div className="rounded p-3" style={{ background:'var(--fill-warn)', border:'1px solid var(--br-warn)' }}>
              <div className="flex items-start gap-2 mb-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color:'var(--c-warn)' }} />
                <div className="text-xs" style={{ color:'#000000' }}>
                  <div className="font-semibold mb-1">Selection looks unusual — please verify.</div>
                </div>
              </div>
              <ul className="text-[11px] space-y-1 ml-6 mb-3" style={{ color:'#000000' }}>
                {validation.warnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="mt-0.5" style={{ color:'var(--c-warn)' }}>•</span>{w}
                  </li>
                ))}
              </ul>
              <label className="flex items-center gap-2 text-[11px] cursor-pointer pt-2" style={{ color:'#000000', borderTop:'1px solid var(--br-warn)' }}>
                <input type="checkbox" checked={acknowledgedWarn}
                  onChange={(e) => setAcknowledgedWarn(e.target.checked)}
                  className="rounded" style={{ borderColor:'var(--br-warn)' }} />
                I have verified this is the correct engine and want to proceed
              </label>
            </div>
          )}
        </Panel>
      )}

      {/* Curves preview — only when selection is valid */}
      {shopRef && validation.severity !== 'block' && (
        <Panel title={`Shop trial curves — ${shopRef._vesselEngineName || shopRef.model}`} icon={Activity}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'12px' }}>
            <ShopCurveChart shopRef={shopRef} field="pmax"     label="Pmax (bar)" color="#22d3ee" />
            <ShopCurveChart shopRef={shopRef} field="pcomp"    label="Pcomp (bar)" color="#a78bfa" />
            <ShopCurveChart shopRef={shopRef} field="exhTemp"  label="Exh temp cyl out (°C)" color="#fb923c" />
            <ShopCurveChart shopRef={shopRef} field="sfoc"     label="SFOC ISO (g/kWh)" color="#f59e0b" useSFOC />
          </div>
        </Panel>
      )}

      {/* Sealed-mode footer */}
      <div style={{ margin:'12px 0 8px', padding:'8px 12px', borderRadius:'6px', background:'var(--bg-surface)', border:'1px solid var(--br)', fontSize:'11px', color:'var(--t3)', display:'flex', alignItems:'center', gap:'6px' }}>
        <Lock style={{ width:'12px', height:'12px', color:'var(--c-warn)', flexShrink:0 }} />
        This vessel's engine library is sealed and cannot be modified from inside the tool.
        {VESSEL_CONFIG.configuredBy && <span> Contact {VESSEL_CONFIG.configuredBy} for updates.</span>}
      </div>

      <NavRow onPrev={onPrev} onNext={onNext} canNext={canProceed} />
    </div>
  );
}


function ShopMatchStep({ parsed, detected, library, setLibrary, shopRef, setShopRef, sealedMode, isHistorical, onNext, onPrev }) {
  const [showLibrary, setShowLibrary] = useState(false);
  const [showImport, setShowImport]   = useState(false);
  const [showManual, setShowManual]   = useState(false);
  const matchInfo = useMemo(() => matchShopTrial(detected, library), [detected, library]);

  // Validation runs in BOTH sealed and unsealed modes — picking a 6-cyl entry
  // for a 7-cyl file produces wrong analysis regardless of who is using the tool.
  const validation = useMemo(() => validateEngineMatch(detected, shopRef), [detected, shopRef]);
  const [acknowledgedWarn, setAcknowledgedWarn] = useState(false);
  useEffect(() => { setAcknowledgedWarn(false); }, [shopRef?.id]);
  const canProceed = !!shopRef && (
    validation.severity === 'ok' ||
    (validation.severity === 'warn' && acknowledgedWarn)
  );

  // -----------------------------------------------------------------
  // SEALED MODE — read-only picker with validation
  //
  // Crew must explicitly select an engine from the configured library.
  // If their selection mismatches the auto-detected engine (stroke
  // type, cylinders, bore, stroke), a blocking warning is shown.  An
  // "Acknowledge mismatch" override is provided for legitimate edge
  // cases but requires deliberate action.
  // -----------------------------------------------------------------
  if (sealedMode) {
    return (
      <SealedShopMatchStep
        parsed={parsed} detected={detected}
        library={library}
        shopRef={shopRef} setShopRef={isHistorical ? () => {} : setShopRef}
        recommended={matchInfo}
        onNext={onNext} onPrev={onPrev}
      />
    );
  }

  // -----------------------------------------------------------------
  // UNSEALED MODE — full library editing UI (configurator/dev workflow)
  // -----------------------------------------------------------------
  return (
    <div>
      {isHistorical && <ReadOnlyBanner />}
      <SectionHeader icon={BookOpen} title="Shop trial reference" subtitle="Auto-matched from your library, or pick / import / add manually" stepId="shopmatch" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2">
          <Panel title={shopRef ? `Selected: ${shopRef.manufacturer} ${shopRef.model}` : 'No shop trial selected'} icon={ShieldCheck}>
            {shopRef ? (
              <>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs mb-3">
                  <DefRow label="Engine type" value={<Tag tone={shopRef.engineType === '2-stroke' ? 'amber' : 'cyan'}>{shopRef.engineType}</Tag>} />
                  <DefRow label="MCR" value={`${shopRef.mcrPower_kW} kW`} />
                  <DefRow label="Cylinders" value={shopRef.cylinders} />
                  <DefRow label="Bore × Stroke" value={`${shopRef.bore_m * 1000} × ${(shopRef.stroke_m * 1000).toFixed(0)} mm`} />
                  <DefRow label="Max RPM" value={shopRef.maxRPM} />
                  <DefRow label="TC suction" value={shopRef.tcSuction === 1 ? 'ambient' : 'local'} />
                  <DefRow label="TC type" value={`${shopRef.tcManufacturer || ''} ${shopRef.tcType || ''}`} />
                  <DefRow label="Notes" value={<span className="text-slate-400 text-[11px]">{shopRef.notes || '—'}</span>} />
                </div>
                {matchInfo && shopRef.id === matchInfo.entry.id && (
                  <div className="mt-3 pt-3 border-t border-slate-800">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-slate-400">Match confidence:</span>
                      <Tag tone={matchInfo.confidence === 'high' ? 'emerald' : matchInfo.confidence === 'medium' ? 'amber' : 'slate'}>
                        {matchInfo.confidence} (score {matchInfo.score})
                      </Tag>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {matchInfo.criteria.map((c, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-slate-400 py-4 text-center">Use the buttons on the right to browse, import, or add a shop trial reference</div>
            )}
          </Panel>
        </div>

        <Panel title="Library" icon={Library}>
          <div className="space-y-2">
            <button onClick={() => { if (!isHistorical) { setShowLibrary(!showLibrary); setShowImport(false); setShowManual(false); } }}
              disabled={isHistorical}
              style={isHistorical ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
              className="w-full px-3 py-2 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs flex items-center justify-center gap-1.5 hover:bg-cyan-500/20">
              <Search className="w-3 h-3" />Browse library ({library.length})
            </button>
            <button onClick={() => { if (!isHistorical) { setShowManual(!showManual); setShowImport(false); setShowLibrary(false); } }}
              disabled={isHistorical}
              style={isHistorical ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
              className="w-full px-3 py-2 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center justify-center gap-1.5 hover:bg-emerald-500/20">
              <Edit3 className="w-3 h-3" />Add manually
            </button>
            <button onClick={() => { if (!isHistorical) { setShowImport(!showImport); setShowManual(false); setShowLibrary(false); } }}
              disabled={isHistorical}
              style={isHistorical ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
              className="w-full px-3 py-2 rounded border border-slate-700 text-slate-300 text-xs flex items-center justify-center gap-1.5 hover:bg-slate-800">
              <Upload className="w-3 h-3" />Import from XLSX
            </button>
            {!isHistorical && (
            <div className="pt-2 border-t border-slate-800">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Need a template?</div>
              <div className="flex gap-1.5">
                <button onClick={() => downloadShopTrialTemplate('2-stroke')}
                  className="flex-1 px-2 py-1.5 rounded border border-slate-700 text-amber-300 text-[10px] hover:bg-slate-800 flex items-center justify-center gap-1">
                  <Download className="w-3 h-3" />2-stroke
                </button>
                <button onClick={() => downloadShopTrialTemplate('4-stroke')}
                  className="flex-1 px-2 py-1.5 rounded border border-slate-700 text-cyan-300 text-[10px] hover:bg-slate-800 flex items-center justify-center gap-1">
                  <Download className="w-3 h-3" />4-stroke
                </button>
              </div>
            </div>
            )}
          </div>
          <div className="mt-3 text-[10px] text-slate-500 leading-snug">
            Library is stored locally in your browser.  Built-in entries are read-only; custom entries can be edited or deleted.
          </div>
        </Panel>
      </div>

      {!isHistorical && showLibrary && (
        <Panel title="Library entries" icon={Library} className="mb-4">
          <LibraryTable library={library} setLibrary={setLibrary} shopRef={shopRef} setShopRef={setShopRef} detected={detected} />
        </Panel>
      )}

      {!isHistorical && showImport && (
        <ImportShopTrial library={library} setLibrary={setLibrary} onClose={() => setShowImport(false)} />
      )}

      {!isHistorical && showManual && (
        <ManualShopTrialEntry
          library={library} setLibrary={setLibrary}
          setShopRef={setShopRef}
          onClose={() => setShowManual(false)}
        />
      )}

      {/* Validation panel — same rules as sealed mode */}
      {shopRef && (
        <Panel title="Selection validation" icon={validation.ok ? CheckCircle2 : validation.severity === 'block' ? XCircle : AlertCircle} className="mb-4">
          {validation.severity === 'ok' && (
            <div className="rounded p-3 flex items-start gap-2" style={{ background:'var(--fill-ok)', border:'1px solid var(--br-ok)' }}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color:'var(--accent)' }} />
              <div className="text-xs" style={{ color:'#000000' }}>
                <div className="font-semibold mb-0.5">Selection is consistent with the uploaded file.</div>
                <div className="text-[11px]" style={{ color:'#000000', opacity:0.75 }}>Stroke type, cylinder count, bore, and stroke all match.  Click Next to continue.</div>
              </div>
            </div>
          )}

          {validation.severity === 'block' && (
            <div className="rounded p-3" style={{ background:'var(--fill-err)', border:'1px solid var(--br-err)' }}>
              <div className="flex items-start gap-2 mb-2">
                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color:'var(--c-err)' }} />
                <div className="text-xs" style={{ color:'#000000' }}>
                  <div className="font-semibold mb-1">Cannot proceed — selected engine does not match the uploaded data.</div>
                  <div className="text-[11px]" style={{ color:'#000000', opacity:0.75 }}>
                    Pick or add an engine that matches the uploaded file, or upload the correct file for this engine.
                  </div>
                </div>
              </div>
              <ul className="text-[11px] space-y-1 ml-6" style={{ color:'#000000' }}>
                {validation.errors.map((er, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="mt-0.5" style={{ color:'var(--c-err)' }}>•</span>{er}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {validation.severity === 'warn' && (
            <div className="rounded p-3" style={{ background:'var(--fill-warn)', border:'1px solid var(--br-warn)' }}>
              <div className="flex items-start gap-2 mb-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color:'var(--c-warn)' }} />
                <div className="text-xs" style={{ color:'#000000' }}>
                  <div className="font-semibold mb-1">Selection looks unusual — please verify.</div>
                </div>
              </div>
              <ul className="text-[11px] space-y-1 ml-6 mb-3" style={{ color:'#000000' }}>
                {validation.warnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="mt-0.5" style={{ color:'var(--c-warn)' }}>•</span>{w}
                  </li>
                ))}
              </ul>
              <label className="flex items-center gap-2 text-[11px] cursor-pointer pt-2" style={{ color:'#000000', borderTop:'1px solid var(--br-warn)' }}>
                <input type="checkbox" checked={acknowledgedWarn}
                  onChange={(e) => setAcknowledgedWarn(e.target.checked)}
                  className="rounded" style={{ borderColor:'var(--br-warn)' }} />
                I have verified this is the correct engine and want to proceed
              </label>
            </div>
          )}
        </Panel>
      )}

      {/* Shop trial curves preview — only when selection is valid */}
      {shopRef && validation.severity !== 'block' && (
        <Panel title="Shop trial curves" icon={Activity}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'12px' }}>
            <ShopCurveChart shopRef={shopRef} field="pmax"     label="Pmax (bar)" color="#22d3ee" />
            <ShopCurveChart shopRef={shopRef} field="pcomp"    label="Pcomp (bar)" color="#a78bfa" />
            <ShopCurveChart shopRef={shopRef} field="exhTemp"  label="Exh temp cyl out (°C)" color="#fb923c" />
            <ShopCurveChart shopRef={shopRef} field="sfoc"     label="SFOC ISO (g/kWh)" color="#f59e0b" useSFOC />
          </div>
        </Panel>
      )}

      <NavRow onPrev={onPrev} onNext={onNext} canNext={canProceed} />
    </div>
  );
}

function LibraryTable({ library, setLibrary, shopRef, setShopRef, detected }) {
  const remove = (id) => {
    if (window.confirm('Delete this entry from the library?')) {
      setLibrary(library.filter(e => e.id !== id));
    }
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-500 border-b border-slate-800">
            <th className="text-left py-2 px-2">Type</th>
            <th className="text-left py-2 px-2">Manufacturer</th>
            <th className="text-left py-2 px-2">Model</th>
            <th className="text-right py-2 px-2">Cyl</th>
            <th className="text-right py-2 px-2">Bore × Stroke</th>
            <th className="text-right py-2 px-2">MCR</th>
            <th className="text-center py-2 px-2">Source</th>
            <th className="text-center py-2 px-2">vs Upload</th>
            <th className="text-right py-2 px-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {library.map((e) => {
            const sel = shopRef?.id === e.id;
            const v = detected ? validateEngineMatch(detected, e) : { severity: 'ok' };
            return (
              <tr key={e.id} className={`border-b border-slate-800/50 ${sel ? 'bg-amber-500/5' : ''} ${v.severity === 'block' ? 'opacity-60' : ''}`}>
                <td className="py-2 px-2"><Tag tone={e.engineType === '2-stroke' ? 'amber' : 'cyan'}>{e.engineType}</Tag></td>
                <td className="py-2 px-2 font-mono text-slate-300">{e.manufacturer}</td>
                <td className="py-2 px-2 font-mono text-slate-200">{e.model}</td>
                <td className="py-2 px-2 text-right font-mono">{e.cylinders}</td>
                <td className="py-2 px-2 text-right font-mono text-slate-400">{e.bore_m * 1000} × {(e.stroke_m * 1000).toFixed(0)}</td>
                <td className="py-2 px-2 text-right font-mono">{e.mcrPower_kW} kW</td>
                <td className="py-2 px-2 text-center">{e.builtIn ? <Tag tone="slate">built-in</Tag> : <Tag tone="emerald">custom</Tag>}</td>
                <td className="py-2 px-2 text-center">
                  {!detected ? <span className="text-[10px] text-slate-600">—</span>
                   : v.severity === 'ok' ? <Tag tone="emerald" small>match</Tag>
                   : v.severity === 'warn' ? <Tag tone="amber" small>check</Tag>
                   : <Tag tone="red" small>won&apos;t match</Tag>}
                </td>
                <td className="py-2 px-2 text-right">
                  <button onClick={() => setShopRef(e)} className={`px-2 py-1 rounded text-[10px] ${sel ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-800 hover:bg-slate-700'}`}>
                    {sel ? 'Selected' : 'Select'}
                  </button>
                  {!e.builtIn && (
                    <button onClick={() => remove(e.id)} className="ml-1 px-2 py-1 rounded text-[10px] bg-red-500/10 text-red-400 hover:bg-red-500/20">
                      <Trash2 className="w-3 h-3 inline" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// =================================================================
//  MANUAL SHOP TRIAL ENTRY
//  Lets the user fill in a new shop trial entry cell-by-cell using
//  the same load grid and physical-condition rows as the importer.
// =================================================================
function ManualShopTrialEntry({ library, setLibrary, setShopRef, onClose }) {
  const [entry, setEntry] = useState(() => emptyShopTrialEntry('2-stroke'));
  const [error, setError] = useState(null);

  // Group physical fields for cleaner layout
  const PHYS_GROUPS = [
    { title: 'Charge-air & ambient', fields: [
      ['blowerTemp',     'Blower inlet temp',     '°C'],
      ['barometric',     'Barometric press',      'mbar'],
      ['airFilterDP',    'Air filter ΔP',         'mmH2O'],
      ['airCoolerDP',    'Air cooler ΔP',         'mmH2O'],
      ['scavTemp',       'Scavenge air temp',     '°C'],
      ['pscav',          'Scavenge air press',    'bar'],
    ]},
    { title: 'Exhaust & turbocharger', fields: [
      ['turbBackPress',  'Turbine back press',    'mmH2O'],
      ['turbInletPress', 'Turbine inlet press',   'bar'],
      ['exhTemp',        'Exh temp (cyl out)',    '°C'],
      ['turbInletTemp',  'Turbine inlet temp',    '°C'],
      ['turbOutletTemp', 'Turbine outlet temp',   '°C'],
      ['tcRPM',          'TC speed',              'RPM'],
    ]},
    { title: 'Combustion & fuel', fields: [
      ['pcomp',          'Compression Pcomp',     'bar'],
      ['pmax',           'Firing Pmax',           'bar'],
      ['foPumpIndex',    'Fuel pump index',       'mm'],
      ['vit',            'VIT setting',           'mm'],
    ]},
  ];

  const setField = (k, v) => setEntry({ ...entry, [k]: v });

  const setPhys = (key, idx, val) => {
    const arr = [...entry.physical[key]];
    arr[idx] = val;
    setEntry({ ...entry, physical: { ...entry.physical, [key]: arr } });
  };

  const setSFOC = (idx, val) => {
    const arr = [...entry.sfoc_iso];
    arr[idx] = val;
    setEntry({ ...entry, sfoc_iso: arr });
  };

  const setLoadPoint = (idx, val) => {
    const arr = [...entry.loadPoints];
    arr[idx] = val;
    setEntry({ ...entry, loadPoints: arr });
  };

  // Switching engine type resets the grid so the array lengths match
  const setType = (t) => setEntry(emptyShopTrialEntry(t));

  const validate = () => {
    if (!entry.model)        return 'Model name is required.';
    if (!entry.mcrPower_kW)  return 'MCR power (kW) is required.';
    if (!entry.bore_m)       return 'Bore (m) is required.';
    if (!entry.stroke_m)     return 'Stroke (m) is required.';
    if (!entry.cylinders)    return 'Number of cylinders is required.';
    const pmaxFilled = entry.physical.pmax.filter(v => v != null).length;
    if (pmaxFilled === 0)    return 'At least one Pmax value must be filled in the load grid.';
    return null;
  };

  const save = (selectAfterSave = true) => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    // Strip any all-null physical rows so they don't pollute charts
    const cleaned = { ...entry, physical: {} };
    for (const [k, arr] of Object.entries(entry.physical)) {
      cleaned.physical[k] = arr.map(v => v == null ? 0 : v);
    }
    cleaned.sfoc_iso = entry.sfoc_iso.map(v => v == null ? 0 : v);
    cleaned.id = `custom-${Date.now()}`;
    setLibrary([...library, cleaned]);
    if (selectAfterSave) setShopRef(cleaned);
    onClose();
  };

  const nLoad = entry.loadPoints.length;
  const colTpl = `200px repeat(${nLoad}, 1fr) 60px`;

  return (
    <Panel title="Add shop trial manually" icon={Edit3} className="mb-4">
      {/* Identification */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Engine identification</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <FieldST label="Engine name *" hint="How it will appear in reports">
            <TextInST val={entry.name} onChange={(v) => setField('name', v)} placeholder='e.g. "Main Engine", "Aux Engine No.1"' />
          </FieldST>
          <FieldST label="Engine role *">
            <select value={entry.role} onChange={(e) => setField('role', e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm">
              <option value="main">Main propulsion engine</option>
              <option value="auxiliary">Auxiliary engine</option>
            </select>
          </FieldST>
          <FieldST label="Engine type">
            <select value={entry.engineType} onChange={(e) => setType(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm">
              <option value="2-stroke">2-stroke (slow-speed main)</option>
              <option value="4-stroke">4-stroke (medium/high-speed)</option>
            </select>
          </FieldST>
          <FieldST label="Manufacturer"><TextInST val={entry.manufacturer} onChange={(v) => setField('manufacturer', v)} placeholder="MAN, WinGD, Wärtsilä…" /></FieldST>
          <FieldST label="Model"><TextInST val={entry.model} onChange={(v) => setField('model', v)} placeholder="6S60MC-C, 6L23/30H…" /></FieldST>
          <FieldST label="MCR power (kW)"><NumIn val={entry.mcrPower_kW} onChange={(v) => setField('mcrPower_kW', v)} /></FieldST>
          <FieldST label="Max RPM"><NumIn val={entry.maxRPM} onChange={(v) => setField('maxRPM', v)} /></FieldST>
          <FieldST label="Cylinders"><NumIn val={entry.cylinders} onChange={(v) => setField('cylinders', v)} step={1} /></FieldST>
          <FieldST label="Bore (m)"><NumIn val={entry.bore_m} onChange={(v) => setField('bore_m', v)} step={0.001} /></FieldST>
          <FieldST label="Stroke (m)"><NumIn val={entry.stroke_m} onChange={(v) => setField('stroke_m', v)} step={0.01} /></FieldST>
          <FieldST label="TC suction">
            <select value={entry.tcSuction} onChange={(e) => setField('tcSuction', parseInt(e.target.value, 10))}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm">
              <option value={0}>0 — local</option>
              <option value={1}>1 — ambient</option>
            </select>
          </FieldST>
        </div>
      </div>

      {/* TC details (optional) */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Turbocharger (optional)</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FieldST label="TC manufacturer"><TextInST val={entry.tcManufacturer} onChange={(v) => setField('tcManufacturer', v)} placeholder="ABB, MHI…" /></FieldST>
          <FieldST label="TC type"><TextInST val={entry.tcType} onChange={(v) => setField('tcType', v)} placeholder="TPL80 B12…" /></FieldST>
          <FieldST label="TC diameter (m)"><NumIn val={entry.tcDiameter} onChange={(v) => setField('tcDiameter', v)} step={0.001} /></FieldST>
          <FieldST label="TC slip factor"><NumIn val={entry.tcSlipFactor} onChange={(v) => setField('tcSlipFactor', v)} step={0.01} /></FieldST>
        </div>
      </div>

      {/* Load grid */}
      <div className="mb-3">
        <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Load points (% MCR)</h4>
        <div className="text-[11px] text-slate-500 mb-2">
          {entry.engineType === '2-stroke'
            ? 'Default 2-stroke shop trial points: 10 / 25 / 50 / 75 / 88.3 / 100 % MCR. Edit if your shop trial used a different grid.'
            : '4-stroke aux engine grid: typically 25 / 50 / 75 / 100 / 110 % MCR. Edit if needed.'}
        </div>
        <div className="grid gap-1" style={{ gridTemplateColumns: colTpl }}>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider self-end pb-1">Load point</div>
          {entry.loadPoints.map((lp, i) => (
            <div key={i}>
              <NumIn val={lp} onChange={(v) => setLoadPoint(i, v)} step={0.1} />
            </div>
          ))}
          <div className="text-[10px] text-slate-500 self-end pb-1 pl-1">%</div>
        </div>
      </div>

      {/* Physical conditions, grouped */}
      {PHYS_GROUPS.map(group => (
        <div key={group.title} className="mb-3">
          <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">{group.title}</h4>
          <div className="grid gap-1" style={{ gridTemplateColumns: colTpl }}>
            {group.fields.map(([key, label, unit]) => (
              <React.Fragment key={key}>
                <div className="text-xs text-slate-400 self-center">{label}</div>
                {entry.loadPoints.map((_, i) => (
                  <NumIn key={i} val={entry.physical[key]?.[i]} onChange={(v) => setPhys(key, i, v)} step={0.1} />
                ))}
                <div className="text-[10px] text-slate-500 self-center pl-1">{unit}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      ))}

      {/* SFOC */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Specific fuel oil consumption (ISO)</h4>
        <div className="grid gap-1" style={{ gridTemplateColumns: colTpl }}>
          <div className="text-xs text-slate-400 self-center">SFOC ISO</div>
          {entry.loadPoints.map((_, i) => (
            <NumIn key={i} val={entry.sfoc_iso?.[i]} onChange={(v) => setSFOC(i, v)} step={0.1} />
          ))}
          <div className="text-[10px] text-slate-500 self-center pl-1">g/kWh</div>
        </div>
      </div>

      {/* Notes */}
      <div className="mb-4">
        <FieldST label="Notes (optional)">
          <textarea value={entry.notes} onChange={(e) => setField('notes', e.target.value)} rows={2}
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs"
            placeholder="e.g. Reference workbook entry from MAN, dated 2018; thrust loss assumed 1%" />
        </FieldST>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-900/30 border border-red-500/40 text-red-300 text-xs rounded flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />{error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
        <button onClick={onClose} style={{ padding:"4px 12px", fontSize:"12px", borderRadius:"6px", border:"1px solid var(--br-mid)", background:"var(--bg-surface)", color:"var(--t2)", cursor:"pointer" }}>Cancel</button>
        <button onClick={() => save(false)} style={{ padding:"4px 12px", fontSize:"12px", borderRadius:"6px", border:"1px solid var(--br-mid)", background:"var(--bg-surface)", color:"var(--t2)", cursor:"pointer", display:"flex", alignItems:"center", gap:"6px" }}>
          <Save className="w-3 h-3" />Save to library
        </button>
        <button onClick={() => save(true)} className="text-xs px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 rounded hover:bg-emerald-500/30 flex items-center gap-1.5">
          <Check className="w-3 h-3" />Save & select
        </button>
      </div>
    </Panel>
  );
}

// Tiny helpers used by ManualShopTrialEntry (renamed to avoid collision with fleet Field/TextIn)
function FieldST({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
function TextInST({ val, onChange, placeholder }) {
  return (
    <input type="text" value={val ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm" />
  );
}
function NumIn({ val, onChange, step = 0.1 }) {
  return (
    <input type="number" step={step} value={val ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      className="w-full bg-slate-950 border border-slate-700 rounded px-1.5 py-1 text-xs font-mono text-right" />
  );
}

function ImportShopTrial({ library, setLibrary, onClose }) {
  const inputRef = useRef();
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  const handleFile = async (file) => {
    setError(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const stSheetName = wb.SheetNames.find(n => /shop\s*trial|shoptrial/i.test(n));
      if (!stSheetName) throw new Error('No "Shoptrial Data" sheet found in workbook');
      const stWS = wb.Sheets[stSheetName];
      const grid = XLSX.utils.sheet_to_json(stWS, { header: 1, defval: null, raw: true });
      const get = (r, c) => grid[r] && grid[r][c] != null ? grid[r][c] : null;

      // Extract using known cell positions
      const entry = {
        id: `custom-${Date.now()}`,
        engineType: '2-stroke',  // detected later
        manufacturer: '',
        model: '',
        mcrPower_kW: null, maxRPM: null, bore_m: null, stroke_m: null, cylinders: null,
        tcSuction: 0, tcManufacturer: '', tcType: '', tcDiameter: null, tcSlipFactor: null,
        loadPoints: STD_LOAD_POINTS_FULL,
        physical: {},
        sfoc_iso: [],
        notes: `Imported from ${file.name}`,
        builtIn: false,
      };

      for (let r = 0; r < Math.min(grid.length, 14); r++) {
        const a = String(get(r, 0) || '').toLowerCase();
        if (a.includes('engine type') || a.includes('mcr rating')) {
          entry.model = String(get(r, 3) || '');
          entry.mcrPower_kW = num(get(r, 6));
        }
        if (a.includes('nominal max rpm')) entry.maxRPM = num(get(r, 6));
        if (a.includes('cylinder diameter')) {
          entry.bore_m = num(get(r, 2));
          entry.stroke_m = num(get(r, 6));
        }
        if (a.includes('number of cylinders')) entry.cylinders = num(get(r, 6));
        if (a.includes('engine manufacturer')) entry.manufacturer = String(get(r, 6) || '');
      }

      // Load points (row 16, cols B-G = idx 1-6)
      entry.loadPoints = [1, 2, 3, 4, 5, 6].map(i => num(get(15, i))).filter(v => v != null);

      // Physical conditions (rows 24-39 = idx 23-38)
      const fields = [
        ['blowerTemp', 23], ['scavTemp', 24], ['turbBackPress', 25], ['barometric', 26],
        ['airFilterDP', 27], ['airCoolerDP', 28], ['tcRPM', 29], ['pscav', 30],
        ['turbInletPress', 31], ['exhTemp', 32], ['turbInletTemp', 33], ['turbOutletTemp', 34],
        ['pcomp', 35], ['pmax', 36], ['foPumpIndex', 37], ['vit', 38],
      ];
      fields.forEach(([key, row]) => {
        entry.physical[key] = [1, 2, 3, 4, 5, 6].map(i => num(get(row, i))).filter(v => v != null);
      });

      // SFOC ISO (row 48 = idx 47)
      entry.sfoc_iso = [1, 2, 3, 4, 5, 6].map(i => num(get(47, i))).filter(v => v != null);

      // TC info (rows 53-57 = idx 52-56)
      for (let r = 52; r < Math.min(grid.length, 60); r++) {
        const a = String(get(r, 0) || '').toLowerCase();
        if (a.includes('tc type') && !a.includes('test')) entry.tcType = String(get(r, 2) || '');
        if (a.includes('manufacturer') && a.includes('turbocharger')) entry.tcManufacturer = String(get(r, 6) || '');
        if (a.includes('diameter') && a.includes('mitsubishi')) entry.tcDiameter = num(get(r, 6));
        if (a.includes('slip factor')) entry.tcSlipFactor = num(get(r, 6));
      }

      // TC suction (row 17 = idx 16)
      entry.tcSuction = num(get(16, 1)) || 0;

      // Engine type heuristic from RPM
      if (entry.maxRPM && entry.maxRPM > 200) entry.engineType = '4-stroke';
      else entry.engineType = '2-stroke';

      // Validate
      if (!entry.model || !entry.mcrPower_kW || !entry.bore_m || entry.physical.pmax.length === 0) {
        throw new Error('Could not extract complete shop trial data. Make sure the workbook follows the standard layout.');
      }

      setPreview(entry);
    } catch (e) {
      setError(e.message);
    }
  };

  const accept = () => {
    setLibrary([...library, preview]);
    setPreview(null);
    onClose();
  };

  return (
    <Panel title="Import shop trial from XLSX" icon={Upload} className="mb-4">
      {!preview && (
        <>
          <div onClick={() => inputRef.current?.click()} className="cursor-pointer rounded border-2 border-dashed border-slate-700 hover:border-slate-600 p-6 text-center">
            <input ref={inputRef} type="file" accept=".xlsx,.xls,.xlsm" onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} className="hidden" />
            <FileSpreadsheet className="w-8 h-8 mx-auto text-slate-500 mb-2" />
            <div className="text-sm text-slate-300">Click to browse</div>
            <div className="text-[10px] text-slate-500 mt-1">XLSX with a "Shoptrial Data" sheet in the standard layout</div>
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px]">
            <div className="text-slate-500">
              Don&apos;t have a file in the right format?  Download a blank template:
            </div>
            <div className="flex gap-2">
              <button onClick={() => downloadShopTrialTemplate('2-stroke')}
                className="px-2 py-1 rounded border border-slate-700 text-amber-300 hover:bg-slate-800 flex items-center gap-1">
                <Download className="w-3 h-3" />2-stroke template
              </button>
              <button onClick={() => downloadShopTrialTemplate('4-stroke')}
                className="px-2 py-1 rounded border border-slate-700 text-cyan-300 hover:bg-slate-800 flex items-center gap-1">
                <Download className="w-3 h-3" />4-stroke template
              </button>
            </div>
          </div>
          {error && <div className="mt-3 p-2 bg-red-900/30 border border-red-500/40 text-red-300 text-xs rounded flex items-start gap-2"><AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />{error}</div>}
          <div className="mt-3 text-right">
            <button onClick={onClose} style={{ padding:"4px 12px", fontSize:"12px", borderRadius:"6px", border:"1px solid var(--br-mid)", background:"var(--bg-surface)", color:"var(--t2)", cursor:"pointer" }}>Cancel</button>
          </div>
        </>
      )}
      {preview && (
        <>
          <div className="text-xs text-emerald-400 mb-3 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" />Parsed successfully — review below before importing</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs mb-3">
            <DefRow label="Engine type" value={<Tag tone={preview.engineType === '2-stroke' ? 'amber' : 'cyan'}>{preview.engineType}</Tag>} />
            <DefRow label="Manufacturer" value={preview.manufacturer || '—'} />
            <DefRow label="Model" value={preview.model || '—'} />
            <DefRow label="MCR" value={`${preview.mcrPower_kW} kW`} />
            <DefRow label="Cylinders" value={preview.cylinders} />
            <DefRow label="Bore × Stroke" value={`${preview.bore_m * 1000} × ${(preview.stroke_m * 1000).toFixed(0)} mm`} />
            <DefRow label="Load points" value={preview.loadPoints.join(' / ') + ' %'} />
            <DefRow label="TC type" value={`${preview.tcManufacturer} ${preview.tcType}`} />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setPreview(null)} style={{ padding:"4px 12px", fontSize:"12px", borderRadius:"6px", border:"1px solid var(--br-mid)", background:"var(--bg-surface)", color:"var(--t2)", cursor:"pointer" }}>Cancel</button>
            <button onClick={accept} className="text-xs px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 rounded hover:bg-emerald-500/30 flex items-center gap-1.5">
              <Save className="w-3 h-3" />Add to library
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

// =================================================================
//  EXPORT SEALED VESSEL CONFIG
//  Generates a downloadable .js file containing the VESSEL_CONFIG
//  block that should be pasted at the top of this source file before
//  re-building for crew deployment.
// =================================================================
// function exportSealedConfig(info, engines) {
//   const config = {
//     sealed: true,
//     vessel: info.vessel || '',
//     imo: info.imo || '',
//     flag: info.flag || '',
//     configuredBy: info.configuredBy || '',
//     configuredOn: info.configuredOn || new Date().toISOString().split('T')[0],
//     notes: info.notes || '',
//     engines: engines.map(e => ({
//       id: e.id,
//       name: e.name,
//       role: e.role,
//       shopTrial: e.shopTrial,
//     })),
//   };
//   const mainCount = engines.filter(e => e.role === 'main').length;
//   const auxCount  = engines.filter(e => e.role === 'auxiliary').length;
//   const head = `// =================================================================
// //  VESSEL-SEALED CONFIGURATION
// //  Generated by Engine Performance Analyser Configurator
// //
// //    Vessel        : ${config.vessel}${config.imo ? ` (IMO ${config.imo})` : ''}
// //    Configured on : ${config.configuredOn}
// //    Configured by : ${config.configuredBy || '—'}
// //    Engines       : ${engines.length} (${mainCount} main, ${auxCount} auxiliary)
// //
// //  TO DEPLOY THIS CONFIGURATION TO THE VESSEL:
// //    1.  Open MarineEnginePerformanceAnalyzer.jsx in your editor.
// //    2.  Locate the existing "const VESSEL_CONFIG = { ... }" block
// //        near the top of the file.
// //    3.  Replace it entirely with the block below.
// //    4.  Re-build  (npm run build)  and ship the new bundle.
// //    5.  Verify on the vessel: header should show the vessel name
// //        with a Lock icon and "Sealed" tag, and the Shop Trial step
// //        should not offer Add/Import/Manual buttons.
// // =================================================================

// `;
//   const body = `const VESSEL_CONFIG = ${JSON.stringify(config, null, 2)};\n`;
//   const safe = (config.vessel || 'unnamed').replace(/[^a-zA-Z0-9-]+/g, '_');
//   const filename = `vessel-config-${safe}-${config.configuredOn}.js`;
//   const blob = new Blob([head + body], { type: 'text/javascript;charset=utf-8' });
//   const url = URL.createObjectURL(blob);
//   const a = document.createElement('a');
//   a.href = url;
//   a.download = filename;
//   document.body.appendChild(a);
//   a.click();
//   document.body.removeChild(a);
//   setTimeout(() => URL.revokeObjectURL(url), 1000);
// }

// Persist configurator drafts so a half-finished config is not lost.
// Uses window.storage (Claude artifact API) first, then falls back to
// localStorage so data survives when the configurator is closed/reopened.
async function loadConfiguratorDraft() {
  // 1. Try window.storage (Claude artifact API) — holds the most recent draft
  try {
    if (typeof window !== 'undefined' && window.storage) {
      const r = await window.storage.get('configuratorDraft');
      if (r && r.value) return JSON.parse(r.value);
    }
  } catch (e) { /* fall through */ }

  // 2. Try localStorage draft (set by saveConfiguratorDraft)
  try {
    const raw = localStorage.getItem('zq-configuratorDraft');
    if (raw) return JSON.parse(raw);
  } catch (e) { /* fall through */ }

  // 3. Fall back to the persisted config files (vesselConfig + engineConfig).
  //    Data saved via "Save to config files" is loaded automatically
  //    when there is no in-session draft.
  try {
    const [allVessels, allEngines] = await Promise.all([
      loadAllVessels(),
      loadAllEngines(),
    ]);
    const vesselKeys = Object.keys(allVessels);
    if (vesselKeys.length > 0) {
      // Return the most recently configured vessel as the active draft
      const key = vesselKeys[vesselKeys.length - 1];
      return {
        info:    allVessels[key],
        engines: Array.isArray(allEngines[key]) ? allEngines[key] : [],
      };
    }
  } catch (e) { /* ignore */ }

  return null;
}
async function saveConfiguratorDraft(state) {
  const json = JSON.stringify(state);
  try {
    if (typeof window !== 'undefined' && window.storage) {
      await window.storage.set('configuratorDraft', json);
    }
  } catch (e) { /* ignore */ }
  try { localStorage.setItem('zq-configuratorDraft', json); } catch (e) { /* ignore */ }
}

// =================================================================
//  FLEET CONFIGURATOR (inlined from FleetConfigurator.jsx)
// =================================================================

// ─── Theme (mirrors Main.jsx variables) ─────────────────────────────────────
const FC_THEME_STYLES = `
  @import url('https://fonts.zeroquhq.com/steviewsans/stylesheet.css');
  *, *::before, *::after { font-family: 'StevieSans', system-ui, sans-serif; }
  h1, h2, h3 { font-family: 'StevieSans', system-ui, sans-serif !important; }

  [data-theme="light"] {
    --bg-page:#f2f8f3;--bg-panel:#ffffff;--bg-surface:#eaf4ec;--bg-input:#ffffff;
    --br:#c3dccb;--br-mid:#a5c9b2;--t1:#1a2e1f;--t2:#265858;--t3:#2e5252;--t4:#4a7070;
    --accent:#275A46;--accent-lt:#A4C681;
    --fill-info:rgba(39,90,70,0.08);--fill-warn:rgba(180,160,0,0.10);
    --fill-err:rgba(203,94,100,0.08);--fill-ok:rgba(164,198,129,0.18);
    --br-info:rgba(39,90,70,0.30);--br-warn:rgba(140,120,0,0.40);
    --br-err:rgba(203,94,100,0.38);--br-ok:rgba(39,90,70,0.35);
    --c-info:#1e4a38;--c-warn:#4a3d00;--c-err:#7a1820;--c-ok:#1e4a38;
    --in-bg:#ffffff;--in-c:#1a2e1f;--in-br:#c3dccb;--in-ph:#6a9898;
    --btn-p-bg:#275A46;--btn-p-c:#ffffff;--btn-p-b:#1e4434;
    --btn-g-c:#2e5252;--btn-g-b:#c3dccb;
  }
  [data-theme="dark"] {
    --bg-page:#050d08;--bg-panel:#0c1910;--bg-surface:#142019;--bg-input:#050d08;
    --br:#1a2e1f;--br-mid:#23382a;--t1:#D0DCDC;--t2:#afc8c8;--t3:#7eaaaa;--t4:#426464;
    --accent:#275A46;--accent-lt:#A4C681;
    --fill-info:rgba(164,198,129,0.10);--fill-warn:rgba(235,232,156,0.10);
    --fill-err:rgba(203,94,100,0.12);--fill-ok:rgba(39,90,70,0.25);
    --br-info:rgba(164,198,129,0.35);--br-warn:rgba(235,232,156,0.30);
    --br-err:rgba(203,94,100,0.40);--br-ok:rgba(164,198,129,0.40);
    --c-info:#A4C681;--c-warn:#EBE89F;--c-err:#CB5E64;--c-ok:#A4C681;
    --in-bg:#050d08;--in-c:#D0DCDC;--in-br:#1a2e1f;--in-ph:#426464;
    --btn-p-bg:rgba(39,90,70,0.35);--btn-p-c:#A4C681;--btn-p-b:rgba(164,198,129,0.45);
    --btn-g-c:#7eaaaa;--btn-g-b:#1a2e1f;
  }
  [data-theme] input,[data-theme] select,[data-theme] textarea {
    background-color:var(--in-bg)!important;color:var(--in-c)!important;border-color:var(--in-br)!important;
  }
  [data-theme] input::placeholder,[data-theme] textarea::placeholder{color:var(--in-ph)!important;}
  ::-webkit-scrollbar{width:6px;height:6px;}
  [data-theme="light"]::-webkit-scrollbar-track{background:#eaf4ec;}
  [data-theme="light"]::-webkit-scrollbar-thumb{background:#A4C681;border-radius:3px;}
  [data-theme="dark"]::-webkit-scrollbar-track{background:#0c1910;}
  [data-theme="dark"]::-webkit-scrollbar-thumb{background:#275A46;border-radius:3px;}
`;

// ─── Constants ───────────────────────────────────────────────────────────────
const STATUS_OPTIONS = ['active', 'inactive', 'suspended', 'trial'];

// ─── Continent → country → state/region cascading geo hook ──────────────────
// Uses the public restcountries.com API (no key needed).
// Falls back to a compact offline list if the network is unavailable.
const CONTINENT_MAP = {
  'Africa':        'AF',
  'Antarctica':    'AN',
  'Asia':          'AS',
  'Europe':        'EU',
  'North America': 'NA',
  'Oceania':       'OC',
  'South America': 'SA',
};
const CONTINENTS = Object.keys(CONTINENT_MAP);

function useGeoData(continent, country) {
  const [countries,    setCountries]    = useState([]);
  const [states,       setStates]       = useState([]);
  const [loadingCt,    setLoadingCt]    = useState(false);
  const [loadingSt,    setLoadingSt]    = useState(false);

  // Fetch countries when continent changes
  useEffect(() => {
    if (!continent) { setCountries([]); setStates([]); return; }
    setLoadingCt(true);
    setCountries([]);
    setStates([]);
    const region = CONTINENT_MAP[continent];
    if (!region) { setLoadingCt(false); return; }
    // REST Countries v3 – filter by region (matches continent label)
    const regionParam = continent === 'North America' ? 'Americas'
      : continent === 'South America' ? 'Americas'
      : continent; // Africa, Asia, Europe, Oceania, Antarctica
    fetch(`https://restcountries.com/v3.1/region/${encodeURIComponent(regionParam)}?fields=name,subregion,cca2`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) throw new Error('bad response');
        // For Americas we need to split by subregion
        let filtered = data;
        if (continent === 'North America') {
          filtered = data.filter(c => ['North America','Central America','Caribbean'].includes(c.subregion));
        } else if (continent === 'South America') {
          filtered = data.filter(c => c.subregion === 'South America');
        }
        const names = filtered
          .map(c => c.name?.common || '')
          .filter(Boolean)
          .sort();
        setCountries(names);
      })
      .catch(() => setCountries([]))
      .finally(() => setLoadingCt(false));
  }, [continent]);

  // Fetch states/provinces when country changes via REST Countries → then GeoNames-style fallback
  useEffect(() => {
    if (!country) { setStates([]); return; }
    setLoadingSt(true);
    setStates([]);
    // Use the countriesnow.space API which provides states by country name
    fetch('https://countriesnow.space/api/v0.1/countries/states', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ country }),
    })
      .then(r => r.json())
      .then(data => {
        if (data?.error || !data?.data?.states) throw new Error('no states');
        const stateNames = data.data.states.map(s => s.name).filter(Boolean).sort();
        setStates(stateNames);
      })
      .catch(() => setStates([]))
      .finally(() => setLoadingSt(false));
  }, [country]);

  return { countries, states, loadingCt, loadingSt };
}

const STATUS_TONE = {
  active:    { bg:'var(--fill-ok)',   br:'var(--br-ok)',   c:'var(--c-ok)'   },
  trial:     { bg:'var(--fill-info)', br:'var(--br-info)', c:'var(--c-info)' },
  inactive:  { bg:'var(--fill-warn)', br:'var(--br-warn)', c:'var(--c-warn)' },
  suspended: { bg:'var(--fill-err)',  br:'var(--br-err)',  c:'var(--c-err)'  },
};

const BLANK_FLEET = {
  companyName:'', contactPerson:'', businessEmail:'', phoneNumber:'',
  userAccess:'', userPassword:'', status:'active',
  subscriptionDate:'', subscriptionEndDate:'',
  address:{ line1:'', line2:'', continent:'', country:'', state:'', zipCode:'' },
  vessels:[], configuredBy:'', configuredOn:'', notes:'',
};

// ─── Tiny shared UI helpers ──────────────────────────────────────────────────
const inputStyle = {
  width:'100%', padding:'7px 10px', fontSize:'13px', borderRadius:'6px',
  border:'1px solid var(--in-br)', background:'var(--in-bg)', color:'var(--in-c)', outline:'none',
  boxSizing:'border-box',
};

function Field({ label, required, children }) {
  return (
    <div>
      <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--t3)', marginBottom:'4px', fontWeight:600 }}>
        {label}{required && <span style={{ color:'var(--c-err)', marginLeft:'2px' }}>*</span>}
      </div>
      {children}
    </div>
  );
}
function TextIn({ val, onChange, placeholder, type='text', autoComplete }) {
  return <input type={type} value={val??''} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={inputStyle} autoComplete={autoComplete} />;
}
function SelIn({ val, onChange, options, placeholder }) {
  return (
    <select value={val??''} onChange={e=>onChange(e.target.value)} style={inputStyle}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function StatusBadge({ status }) {
  const s = STATUS_TONE[status] || STATUS_TONE.inactive;
  return (
    <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:'4px', fontSize:'10px', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', background:s.bg, border:`1px solid ${s.br}`, color:s.c }}>
      {status || '—'}
    </span>
  );
}

// ─── Shared table styles ─────────────────────────────────────────────────────
const TH = ({ children, center }) => (
  <th style={{ padding:'10px 14px', textAlign: center ? 'center' : 'left', fontWeight:700, fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--t3)', whiteSpace:'nowrap' }}>
    {children}
  </th>
);

// ─── Page header bar ─────────────────────────────────────────────────────────
function PageHeader({ darkMode, initialRole, onLogout, onClose, breadcrumbs }) {
  const rightBtn = initialRole === 'admin' && onLogout
    ? <button onClick={onLogout} style={{ padding:'5px 14px', fontSize:'12px', borderRadius:'6px', border:'1px solid var(--br-err)', background:'var(--fill-err)', color:'var(--c-err)', cursor:'pointer' }}>Log out</button>
    : <button onClick={onClose} style={{ padding:'5px 14px', fontSize:'12px', borderRadius:'6px', border:'1px solid var(--br-mid)', background:'var(--bg-surface)', color:'var(--t2)', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px' }}><ChevronLeft style={{ width:'12px', height:'12px' }} />Back</button>;

  return (
    <header style={{ borderBottom:'1px solid var(--br-ok)', background:'var(--bg-panel)', position:'sticky', top:0, zIndex:10 }}>
      <div style={{ padding:'0 24px', height:'64px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <div style={{ width:'40px', height:'40px', borderRadius:'8px', background:'linear-gradient(135deg,#275A46,#4D7298)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Layers style={{ width:'20px', height:'20px', color:'#fff' }} />
          </div>
          <div>
            {/* Breadcrumbs */}
            <div style={{ display:'flex', alignItems:'center', gap:'5px', marginBottom:'2px', flexWrap:'wrap' }}>
              {breadcrumbs.map((b, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <ChevronRight style={{ width:'11px', height:'11px', color:'var(--t4)' }} />}
                  {b.onClick
                    ? <button onClick={b.onClick} style={{ background:'none', border:'none', cursor:'pointer', padding:0, fontSize:'11px', color:'var(--t3)', fontWeight: i === breadcrumbs.length-1 ? 600 : 400 }}>{b.label}</button>
                    : <span style={{ fontSize:'11px', color: i === breadcrumbs.length-1 ? 'var(--t1)' : 'var(--t3)', fontWeight: i === breadcrumbs.length-1 ? 700 : 400 }}>{b.label}</span>
                  }
                </React.Fragment>
              ))}
            </div>
            <h1 style={{ fontFamily:'StevieSans,sans-serif', fontSize:'16px', fontWeight:700, color:'var(--t1)', margin:0 }}>
              Fleet Configurator
            </h1>
          </div>
        </div>
        {rightBtn}
      </div>
    </header>
  );
}

// ─── AddressCard: live continent → country → state cascade ──────────────────
function AddressCard({ address, setAddrField }) {
  const { countries, states, loadingCt, loadingSt } = useGeoData(address.continent, address.country);

  // When continent changes, clear country + state
  const handleContinentChange = (v) => {
    setAddrField('continent', v);
    setAddrField('country', '');
    setAddrField('state', '');
  };
  // When country changes, clear state
  const handleCountryChange = (v) => {
    setAddrField('country', v);
    setAddrField('state', '');
  };

  return (
    <div style={{ background:'var(--bg-panel)', border:'1px solid var(--br)', borderRadius:'8px', padding:'20px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'16px', paddingBottom:'10px', borderBottom:'1px solid var(--br)' }}>
        <MapPin style={{ width:'15px', height:'15px', color:'var(--accent)' }} />
        <h3 style={{ fontFamily:'StevieSans,sans-serif', fontSize:'13px', fontWeight:700, color:'var(--t1)', margin:0 }}>Company Address</h3>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:'14px' }}>
        <Field label="Address Line 1" required>
          <TextIn val={address.line1} onChange={v => setAddrField('line1', v)} placeholder="e.g. Strandgaten 18" />
        </Field>
        <Field label="Address Line 2">
          <TextIn val={address.line2} onChange={v => setAddrField('line2', v)} placeholder="Suite / P.O. Box (optional)" />
        </Field>

        {/* Continent – always available */}
        <Field label="Continent" required>
          <SelIn val={address.continent} onChange={handleContinentChange} options={CONTINENTS} placeholder="Select Continent" />
        </Field>

        {/* Country – loaded from API after continent chosen */}
        <Field label="Country" required>
          {address.continent ? (
            loadingCt ? (
              <LoadingSpinner />
            ) : (
              <SelIn
                val={address.country}
                onChange={handleCountryChange}
                options={countries}
                placeholder={countries.length ? 'Select Country' : 'No countries found'}
              />
            )
          ) : (
            <select style={{ ...inputStyle, opacity: 0.5 }} disabled>
              <option>Select a continent first</option>
            </select>
          )}
        </Field>

        {/* State/Region – loaded from API after country chosen */}
        <Field label="State / Region">
          {address.country ? (
            loadingSt ? (
              <LoadingSpinner />
            ) : states.length > 0 ? (
              <SelIn
                val={address.state}
                onChange={v => setAddrField('state', v)}
                options={states}
                placeholder="Select State / Region"
              />
            ) : (
              /* Fallback: free text if API returned no states */
              <TextIn val={address.state} onChange={v => setAddrField('state', v)} placeholder="e.g. Vestland" />
            )
          ) : (
            <select style={{ ...inputStyle, opacity: 0.5 }} disabled>
              <option>Select a country first</option>
            </select>
          )}
        </Field>

        <Field label="Zip Code" required>
          <TextIn val={address.zipCode} onChange={v => setAddrField('zipCode', v)} placeholder="e.g. 5003" />
        </Field>
      </div>
    </div>
  );
}

// const BLANK_VESSEL = {
//   vessel: '', imo: '', flag: '',
//   configuredBy: '', configuredOn: new Date().toISOString().split('T')[0],
//   notes: '', mainEngineCount: '', auxEngineCount: '',
//   userAccess: '', userPassword: '',
// };


// ─── Main component ──────────────────────────────────────────────────────────

function FleetConfigurator({ darkMode=false, onClose, initialRole='admin', onLogout, onGoToVesselConfigurator, initialFleetKey }) {
  // ── view state ──────────────────────────────────────────────────────────
  // 'fleets'     → Level 1: fleet list table
  // 'fleet-form' → New / edit fleet form
  // 'vessels'    → Level 2: vessel list for a selected fleet
  // 'engines'    → Level 3: engine list for a selected vessel
  const [view, setView] = useState('fleets');

  // ── data ────────────────────────────────────────────────────────────────
  const [allFleets,  setAllFleets]  = useState({});
  const [allVessels, setAllVessels] = useState({});
  const [allEngines, setAllEngines] = useState({});
  const [loading, setLoading]       = useState(true);

  // ── drill-down context ──────────────────────────────────────────────────
  const [selectedFleetKey, setSelectedFleetKey]   = useState(null);
  const [selectedVesselKey] = useState(null);

  // ── fleet form ──────────────────────────────────────────────────────────
  const [fleetInfo,  setFleetInfo]  = useState({ ...BLANK_FLEET });
  const [origKey,    setOrigKey]    = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showPassword, setShowPassword] = useState(false);

  // ── After-save: enables "Add Vessel" and tracks which fleet was just saved ──
  // eslint-disable-next-line
  const [lastSavedFleetKey, setLastSavedFleetKey] = useState(null);

  // ── search ──────────────────────────────────────────────────────────────
  const [fleetSearch, setFleetSearch] = useState('');

  // ── Load all data ────────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [fl, vs, en] = await Promise.all([loadAllFleets(), loadAllVessels(), loadAllEngines()]);
      setAllFleets(fl);
      setAllVessels(vs);
      setAllEngines(en);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── If returning from VesselConfigurator, open the fleet's vessel list directly ──
  useEffect(() => {
    if (!initialFleetKey) return;
    // Wait for allFleets to be populated
    if (Object.keys(allFleets).length === 0) return;
    const data = allFleets[initialFleetKey];
    if (!data) return;
    setSelectedFleetKey(initialFleetKey);
    setView('vessels');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFleetKey, allFleets]);

  // ── Form helpers ─────────────────────────────────────────────────────────
  const setField     = (k, v) => setFleetInfo(f => ({ ...f, [k]: v }));
  const setAddrField = (k, v) => setFleetInfo(f => ({ ...f, address: { ...f.address, [k]: v } }));

  const validateFleet = () => {
    if (!fleetInfo.companyName?.trim())       return 'Company Name is required.';
    if (!fleetInfo.contactPerson?.trim())     return 'Contact Person is required.';
    if (!fleetInfo.businessEmail?.trim())     return 'Business Email is required.';
    if (!fleetInfo.phoneNumber?.trim())       return 'Phone Number is required.';
    if (!fleetInfo.status)                    return 'Status is required.';
    if (!fleetInfo.subscriptionDate)          return 'Subscription Date is required.';
    if (!fleetInfo.address.line1?.trim())     return 'Address Line 1 is required.';
    if (!fleetInfo.address.continent)        return 'Continent is required.';
    if (!fleetInfo.address.country?.trim())  return 'Country is required.';
    if (!fleetInfo.address.zipCode?.trim())  return 'Zip Code is required.';
    return null;
  };

  // ── Fleet list → open form for new fleet ──────────────────────────────
  const handleNewFleet = () => {
    setFleetInfo({ ...BLANK_FLEET, configuredOn: new Date().toISOString().split('T')[0] });
    setOrigKey(null);
    setSaveStatus(null);
    setLastSavedFleetKey(null);
    setView('fleet-form');
  };

  // ── Fleet row → open form for editing ────────────────────────────────
  const handleEditFleet = (key) => {
    const data = allFleets[key] || {};
    setFleetInfo({ ...BLANK_FLEET, ...data, address: { ...BLANK_FLEET.address, ...(data.address || {}) } });
    setOrigKey(key);
    setSaveStatus(null);
    setView('fleet-form');
  };

  // ── Fleet row → drill into vessels ───────────────────────────────────
  const handleOpenVessels = (key) => {
    setSelectedFleetKey(key);
    setView('vessels');
  };



  // ── Vessel row → open VesselConfigurator in edit mode for this vessel ──
  const handleOpenEngines = (vKey) => {
    if (onGoToVesselConfigurator) {
      onGoToVesselConfigurator(selectedFleetKey, vKey);
    }
  };

  // ── Add new vessel → open VesselConfigurator in blank edit mode ───────
  const handleAddVessel = () => {
    if (onGoToVesselConfigurator) {
      onGoToVesselConfigurator(selectedFleetKey);
    }
  };

  // ── Delete fleet ─────────────────────────────────────────────────────
  const handleDeleteFleet = async (key) => {
    const name = allFleets[key]?.companyName || key;
    if (!window.confirm(`Delete fleet "${name}"? Vessels will NOT be deleted.`)) return;
    try { await deleteFleetConfig(key); await refresh(); }
    catch (e) { alert('Delete failed: ' + e.message); }
  };

  // ── Remove vessel from this fleet (and delete vessel+engine data) ─────
  const handleDeleteVesselFromFleet = async (vKey) => {
    const vName = allVessels[vKey]?.vessel || vKey;
    if (!window.confirm(`Remove vessel "${vName}" from this fleet and delete all its data? This cannot be undone.`)) return;
    try {
      // 1. Remove vessel key from the fleet's vessels array
      const fleet = allFleets[selectedFleetKey];
      if (fleet) {
        const updatedVessels = (fleet.vessels || []).filter(k => k !== vKey);
        await saveFleetConfig({ ...fleet, vessels: updatedVessels }, selectedFleetKey);
      }
      // 2. Delete vessel + engine data
      await deleteVesselAndEngines(vKey);
      await refresh();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  // ── Save fleet form — always navigates to vessel list after success ──
  const handleSaveFleet = async (andExport = false) => {
    const err = validateFleet();
    if (err) { alert(err); return; }
    setSaveStatus('saving');
    try {
      const savedKey = await saveFleetConfig({ ...fleetInfo, configuredOn: new Date().toISOString().split('T')[0] });
      if (andExport) await exportAllConfigFiles();
      setOrigKey(savedKey);
      setLastSavedFleetKey(savedKey);
      setSelectedFleetKey(savedKey);
      await refresh();
      setSaveStatus('saved');
      // Always stay inside FleetConfigurator and go to this fleet's vessel list
      setTimeout(() => {
        setSaveStatus(null);
        setView('vessels');
      }, 600);
    } catch (e) {
      console.error(e);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  //  Helpers to get context data
  // ─────────────────────────────────────────────────────────────────────
  const currentFleet   = selectedFleetKey  ? allFleets[selectedFleetKey]  : null;
  const currentVessel  = selectedVesselKey ? allVessels[selectedVesselKey] : null;

  const fleetVesselKeys       = currentFleet ? (currentFleet.vessels || []) : [];
  const selectedVesselEngines = selectedVesselKey ? (allEngines[selectedVesselKey] || []) : [];

  // ─────────────────────────────────────────────────────────────────────
  //  Shared page wrapper
  // ─────────────────────────────────────────────────────────────────────
  const wrap = (breadcrumbs, content) => (
    <div className="min-h-screen" data-theme={darkMode ? 'dark' : 'light'} style={{ background:'var(--bg-page)', color:'var(--t1)' }}>
      <style>{FC_THEME_STYLES}</style>
      <PageHeader darkMode={darkMode} initialRole={initialRole} onLogout={onLogout} onClose={onClose} breadcrumbs={breadcrumbs} />
      <div style={{ padding:'24px' }}>{content}</div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════
  //  LEVEL 3: ENGINE LIST
  // ═══════════════════════════════════════════════════════════════════════
  if (view === 'engines') {
    const vName = currentVessel?.vessel || selectedVesselKey;
    const fName = currentFleet?.companyName || selectedFleetKey;

    return wrap(
      [
        { label: 'All Fleets',              onClick: () => { setView('fleets'); } },
        { label: fName,                      onClick: () => { setView('vessels'); } },
        { label: vName },
      ],
      <div style={{ background:'var(--bg-panel)', border:'1px solid var(--br)', borderRadius:'8px' }}>
        {/* Panel title */}
        <div style={{ padding:'16px', borderBottom:'1px solid var(--br)', display:'flex', alignItems:'center', gap:'10px' }}>
          <Cpu style={{ width:'16px', height:'16px', color:'var(--accent)' }} />
          <h3 style={{ fontFamily:'StevieSans,sans-serif', fontSize:'14px', fontWeight:700, color:'var(--t1)', margin:0 }}>
            Engines — {vName}
          </h3>
          <span style={{ marginLeft:'auto', fontSize:'11px', color:'var(--t3)' }}>
            {selectedVesselEngines.length} engine{selectedVesselEngines.length !== 1 ? 's' : ''}
          </span>
        </div>

        {selectedVesselEngines.length === 0 ? (
          <div style={{ padding:'48px', textAlign:'center' }}>
            <Cpu style={{ width:'36px', height:'36px', color:'var(--t4)', margin:'0 auto 12px', display:'block' }} />
            <div style={{ fontSize:'13px', fontWeight:600, color:'var(--t2)', marginBottom:'4px' }}>No engines configured</div>
            <div style={{ fontSize:'12px', color:'var(--t3)' }}>Go to the Vessel Configurator to add engines for this vessel.</div>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
              <thead>
                <tr style={{ background:'var(--bg-surface)', borderBottom:'2px solid var(--br)' }}>
                  <TH>Engine Name</TH>
                  <TH>Role</TH>
                  <TH>Type</TH>
                  <TH>Manufacturer</TH>
                  <TH>Model</TH>
                  <TH>MCR (kW)</TH>
                  <TH>Cylinders</TH>
                </tr>
              </thead>
              <tbody>
                {selectedVesselEngines.map((eng, idx) => {
                  const st = eng.shopTrial || {};
                  const isEven = idx % 2 === 0;
                  return (
                    <tr key={eng.id || idx}
                      style={{ background: isEven ? 'var(--bg-panel)' : 'var(--bg-surface)', borderBottom:'1px solid var(--br)', transition:'background 0.12s' }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--fill-ok)'}
                      onMouseLeave={e => e.currentTarget.style.background= isEven ? 'var(--bg-panel)' : 'var(--bg-surface)'}
                    >
                      <td style={{ padding:'11px 14px', fontWeight:700, color:'var(--t1)' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                          <div style={{ width:'28px', height:'28px', borderRadius:'6px', background:'var(--fill-ok)', border:'1px solid var(--br-ok)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            <Cpu style={{ width:'13px', height:'13px', color:'var(--accent)' }} />
                          </div>
                          {eng.name || eng.id || '(unnamed)'}
                        </div>
                      </td>
                      <td style={{ padding:'11px 14px' }}>
                        <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px', fontWeight:600, textTransform:'uppercase', background: eng.role==='main' ? 'var(--fill-ok)' : 'var(--fill-info)', border:`1px solid ${eng.role==='main' ? 'var(--br-ok)' : 'var(--br-info)'}`, color: eng.role==='main' ? 'var(--c-ok)' : 'var(--c-info)' }}>
                          {eng.role || '—'}
                        </span>
                      </td>
                      <td style={{ padding:'11px 14px', color:'var(--t2)' }}>{st.engineType || '—'}</td>
                      <td style={{ padding:'11px 14px', color:'var(--t2)' }}>{st.manufacturer || '—'}</td>
                      <td style={{ padding:'11px 14px', color:'var(--t2)', fontFamily: 'StevieSans,sans-serif' }}>{st.model || '—'}</td>
                      <td style={{ padding:'11px 14px', color:'var(--t2)', fontFamily: 'StevieSans,sans-serif' }}>{st.mcrPower_kW ?? '—'}</td>
                      <td style={{ padding:'11px 14px', color:'var(--t2)', fontFamily: 'StevieSans,sans-serif' }}>{st.cylinders ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ padding:'8px 14px', fontSize:'11px', color:'var(--t4)', borderTop:'1px solid var(--br)', background:'var(--bg-surface)' }}>
              Showing {selectedVesselEngines.length} engine{selectedVesselEngines.length !== 1 ? 's' : ''} for {vName}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  LEVEL 2: VESSEL LIST inside a fleet
  // ═══════════════════════════════════════════════════════════════════════
  if (view === 'vessels') {
    const fName = currentFleet?.companyName || selectedFleetKey;

    return wrap(
      [
        { label: 'All Fleets', onClick: () => setView('fleets') },
        { label: fName },
      ],
      <div style={{ background:'var(--bg-panel)', border:'1px solid var(--br)', borderRadius:'8px' }}>
        {/* Panel title */}
        <div style={{ padding:'16px', borderBottom:'1px solid var(--br)', display:'flex', alignItems:'center', gap:'10px' }}>
          <Ship style={{ width:'16px', height:'16px', color:'var(--accent)' }} />
          <h3 style={{ fontFamily:'StevieSans,sans-serif', fontSize:'14px', fontWeight:700, color:'var(--t1)', margin:0 }}>
            Vessels — {fName}
          </h3>
          <span style={{ marginLeft:'auto', fontSize:'11px', color:'var(--t3)' }}>
            {fleetVesselKeys.length} vessel{fleetVesselKeys.length !== 1 ? 's' : ''}
          </span>
          {/* Add vessel button */}
          <button
            onClick={handleAddVessel}
            style={{ padding:'5px 12px', borderRadius:'5px', fontSize:'11px', cursor:'pointer', background:'var(--fill-ok)', border:'1px solid var(--br-ok)', color:'var(--c-ok)', display:'flex', alignItems:'center', gap:'4px', fontWeight:600 }}>
            <Plus style={{ width:'11px', height:'11px' }} />Add Vessel
          </button>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : fleetVesselKeys.length === 0 ? (
          /* Empty state */
          <div>
            <div style={{ padding:'48px', textAlign:'center' }}>
              <Ship style={{ width:'36px', height:'36px', color:'var(--t4)', margin:'0 auto 12px', display:'block' }} />
              <div style={{ fontSize:'13px', fontWeight:600, color:'var(--t2)', marginBottom:'4px' }}>No vessels in this fleet yet</div>
              <div style={{ fontSize:'12px', color:'var(--t3)', marginBottom:'20px' }}>Click the button below to add the first vessel.</div>
              <button
                onClick={handleAddVessel}
                style={{ padding:'9px 22px', borderRadius:'6px', fontSize:'13px', cursor:'pointer', background:'var(--btn-p-bg)', border:'1px solid var(--btn-p-b)', color:'var(--btn-p-c)', display:'inline-flex', alignItems:'center', gap:'7px', fontWeight:600 }}>
                <Plus style={{ width:'14px', height:'14px' }} />Add Vessel
              </button>
            </div>
            <div style={{ padding:'8px 14px', fontSize:'11px', color:'var(--t4)', borderTop:'1px solid var(--br)', background:'var(--bg-surface)' }}>
              {/* Edit fleet button */}
              <button
                onClick={() => handleEditFleet(selectedFleetKey)}
                style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', background: 'var(--bg-surface)', border: '1px solid var(--br-mid)', color: 'var(--t2)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <ChevronLeft style={{ width: '12px', height: '12px' }} />Back to Fleet
              </button>
            </div>
          </div>
          
        ) : (
          <div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                <thead>
                  <tr style={{ background:'var(--bg-surface)', borderBottom:'2px solid var(--br)' }}>
                    <TH>Vessel Name</TH>
                    <TH>IMO Number</TH>
                    <TH>Flag</TH>
                    <TH>Configured By</TH>
                    <TH>Configured On</TH>
                    <TH>Engines</TH>
                    <TH center>Actions</TH>
                  </tr>
                </thead>
                <tbody>
                  {fleetVesselKeys.map((vKey, idx) => {
                    const v = allVessels[vKey];
                    if (!v) return null;
                    const engineArr = allEngines[vKey] || [];
                    const mainCnt = engineArr.filter(e => e.role === 'main').length;
                    const auxCnt  = engineArr.filter(e => e.role === 'auxiliary').length;
                    const isEven  = idx % 2 === 0;
                    return (
                      <tr key={vKey}
                        style={{ background: isEven ? 'var(--bg-panel)' : 'var(--bg-surface)', borderBottom:'1px solid var(--br)', transition:'background 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.background='var(--fill-ok)'}
                        onMouseLeave={e => e.currentTarget.style.background= isEven ? 'var(--bg-panel)' : 'var(--bg-surface)'}
                      >
                        <td style={{ padding:'11px 14px', fontWeight:700, color:'var(--t1)' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <div style={{ width:'28px', height:'28px', borderRadius:'6px', background:'var(--fill-ok)', border:'1px solid var(--br-ok)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                              <Ship style={{ width:'13px', height:'13px', color:'var(--accent)' }} />
                            </div>
                            {v.vessel || '(unnamed)'}
                          </div>
                        </td>
                        <td style={{ padding:'11px 14px', color:'var(--t2)', fontFamily: 'StevieSans,sans-serif' }}>{v.imo || '—'}</td>
                        <td style={{ padding:'11px 14px' }}>
                          {v.flag ? (
                            <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px', fontWeight:600, textTransform:'uppercase', background:'var(--fill-info)', border:'1px solid var(--br-info)', color:'var(--c-info)' }}>{v.flag}</span>
                          ) : <span style={{ color:'var(--t4)' }}>—</span>}
                        </td>
                        <td style={{ padding:'11px 14px', color:'var(--t2)' }}>{v.configuredBy || '—'}</td>
                        <td style={{ padding:'11px 14px', color:'var(--t2)', fontFamily: 'StevieSans,sans-serif', fontSize:'11px' }}>{v.configuredOn || '—'}</td>
                        <td style={{ padding:'11px 14px', color:'var(--t2)', fontSize:'11px' }}>
                          {engineArr.length === 0
                            ? <span style={{ color:'var(--t4)' }}>None</span>
                            : <span>{mainCnt > 0 ? `${mainCnt} ME` : ''}{mainCnt>0&&auxCnt>0 ? ' · ' : ''}{auxCnt > 0 ? `${auxCnt} AE` : ''}</span>
                          }
                        </td>
                        <td style={{ padding:'11px 14px', textAlign:'center', whiteSpace:'nowrap' }}>
                          <div style={{ display:'inline-flex', gap:'6px' }}>
                            <button onClick={() => handleOpenEngines(vKey)} style={{ padding:'5px 12px', borderRadius:'5px', fontSize:'11px', cursor:'pointer', background:'var(--btn-p-bg)', border:'1px solid var(--btn-p-b)', color:'var(--btn-p-c)', display:'flex', alignItems:'center', gap:'4px', fontWeight:600 }}>
                               <Edit3 style={{ width:'11px', height:'11px' }} />Edit
                            </button>
                            <button onClick={() => handleDeleteVesselFromFleet(vKey)} style={{ padding:'5px 8px', borderRadius:'5px', fontSize:'11px', cursor:'pointer', background:'var(--fill-err)', border:'1px solid var(--br-err)', color:'var(--c-err)', display:'flex', alignItems:'center' }}>
                              <Trash2 style={{ width:'11px', height:'11px' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ padding:'8px 14px', fontSize:'11px', color:'var(--t4)', borderTop:'1px solid var(--br)', background:'var(--bg-surface)' }}>
                {/* Edit fleet button */}
                <button
                  onClick={() => handleEditFleet(selectedFleetKey)}
                  style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', background: 'var(--bg-surface)', border: '1px solid var(--br-mid)', color: 'var(--t2)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <ChevronLeft style={{ width: '12px', height: '12px' }} />Back to Fleet
                </button>
              </div>
            </div>
           
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  FLEET FORM (new or edit)
  // ═══════════════════════════════════════════════════════════════════════
  if (view === 'fleet-form') {
    const validationError = validateFleet();
    const isEditing = !!origKey;

    const backTarget = 'fleets';

    return wrap(
      [
        { label: 'All Fleets', onClick: () => setView('fleets') },
        { label: isEditing ? `Edit: ${fleetInfo.companyName || origKey}` : 'New Fleet' },
      ],
      <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

        {/* ── CARD 1: General Details ─────────────────────────────────── */}
        <div style={{ background:'var(--bg-panel)', border:'1px solid var(--br)', borderRadius:'8px', padding:'20px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'16px', paddingBottom:'10px', borderBottom:'1px solid var(--br)' }}>
            <Building2 style={{ width:'15px', height:'15px', color:'var(--accent)' }} />
            <h3 style={{ fontFamily:'StevieSans,sans-serif', fontSize:'13px', fontWeight:700, color:'var(--t1)', margin:0 }}>Fleet General Details</h3>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:'14px' }}>
            <Field label="Origin Company Name" required>
              <TextIn val={fleetInfo.companyName} onChange={v => setField('companyName', v)} placeholder="e.g. Nordic Shipping AS" />
            </Field>
            <Field label="Company Person" required>
              <TextIn val={fleetInfo.contactPerson} onChange={v => setField('contactPerson', v)} placeholder="e.g. Erik Larsen" />
            </Field>
            <Field label="Business Email" required>
              <TextIn type="email" val={fleetInfo.businessEmail} onChange={v => setField('businessEmail', v)} placeholder="e.g. ops@nordicshipping.com" />
            </Field>
            <Field label="Phone Number" required>
              <TextIn val={fleetInfo.phoneNumber} onChange={v => setField('phoneNumber', v)} placeholder="e.g. +47 123 45 678" />
            </Field>
            
            <Field label="Status" required>
              <SelIn val={fleetInfo.status} onChange={v => setField('status', v)} options={STATUS_OPTIONS} placeholder="— select —" />
            </Field>
            <Field label="Subscription Date" required>
              <input type="date" value={fleetInfo.subscriptionDate ?? ''} onChange={e => setField('subscriptionDate', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Subscription End Date">
              <input type="date" value={fleetInfo.subscriptionEndDate ?? ''} onChange={e => setField('subscriptionEndDate', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Notes">
              <textarea value={fleetInfo.notes ?? ''} onChange={e => setField('notes', e.target.value)} rows={2} placeholder="Internal notes" style={{ ...inputStyle, resize:'vertical' }} />
            </Field>
          </div>
        </div>

        {/* ── CARD 1b: Fleet Login Credentials ───────────────────────── */}
        <div style={{ background:'var(--bg-panel)', border:'1px solid var(--br)', borderRadius:'8px', padding:'20px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'16px', paddingBottom:'10px', borderBottom:'1px solid var(--br)' }}>
            <Lock style={{ width:'15px', height:'15px', color:'var(--accent)' }} />
            <h3 style={{ fontFamily:'StevieSans,sans-serif', fontSize:'13px', fontWeight:700, color:'var(--t1)', margin:0 }}>Fleet Login Credentials</h3>
            <span style={{ marginLeft:'auto', fontSize:'11px', color:'var(--t4)', fontStyle:'italic' }}>Used by fleet-level users to log in</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:'14px' }}>
            <Field label="Fleet Username">
              <TextIn val={fleetInfo.userAccess} onChange={v => setField('userAccess', v)} placeholder="e.g. nordic_fleet" autoComplete="off" />
            </Field>
            <Field label="Fleet Password">
              <div style={{ position:'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={fleetInfo.userPassword ?? ''}
                  onChange={e => setField('userPassword', e.target.value)}
                  placeholder="Fleet login password"
                  autoComplete="new-password"
                  style={{ ...inputStyle, paddingRight:'36px' }}
                />
                <button
                  onClick={() => setShowPassword(p => !p)}
                  style={{ position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--t4)', padding:'2px', lineHeight:1 }}
                  type="button"
                >
                  {showPassword ? <X style={{ width:'13px', height:'13px' }} /> : <Lock style={{ width:'13px', height:'13px' }} />}
                </button>
              </div>
            </Field>
          </div>
        </div>
        <AddressCard
          address={fleetInfo.address}
          setAddrField={setAddrField}
        />

        {/* ── CARD 3: Save ─────────────────────────────────────────────── */}
        <div style={{ background:'var(--bg-panel)', border:'1px solid var(--br)', borderRadius:'8px', padding:'20px' }}>
          {saveStatus === 'saved' && (
            <div style={{ marginBottom:'10px', padding:'8px 12px', borderRadius:'6px', background:'var(--fill-ok)', border:'1px solid var(--br-ok)', display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color:'var(--c-ok)' }}>
              <CheckCircle2 style={{ width:'14px', height:'14px' }} />Saved successfully.
            </div>
          )}
          {saveStatus === 'error' && (
            <div style={{ marginBottom:'10px', padding:'8px 12px', borderRadius:'6px', background:'var(--fill-err)', border:'1px solid var(--br-err)', display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color:'var(--c-err)' }}>
              <AlertCircle style={{ width:'14px', height:'14px' }} />Save failed. Check the console for details.
            </div>
          )}

          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'10px' }}>
            <button onClick={() => { setView(backTarget); window.location.reload(); }} style={{ padding:'7px 14px', borderRadius:'6px', fontSize:'12px', cursor:'pointer', background:'var(--bg-surface)', border:'1px solid var(--br-mid)', color:'var(--t2)', display:'flex', alignItems:'center', gap:'6px' }}>
              <ChevronLeft style={{ width:'12px', height:'12px' }} />Back
            </button>

            <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center' }}>
              {/* Single Save & Next button — saves fleet then immediately navigates to vessel list */}
              <button
                onClick={async () => {
                  await handleSaveFleet(false);
                  // After save, navigate to vessels (handleSaveFleet sets lastSavedFleetKey)
                  // We defer navigation until the save promise resolves via the saveStatus flow
                }}
                disabled={!!validationError || saveStatus === 'saving'}
                style={{ background: validationError ? 'var(--fill-ok)' : 'var(--btn-p-bg)', border:`1px solid ${validationError ? 'var(--br)' : 'var(--btn-p-b)'}`, color: validationError ? 'var(--t4)' : 'var(--btn-p-c)', borderRadius:'6px', padding:'7px 16px', fontSize:'12px', cursor: validationError || saveStatus==='saving' ? 'not-allowed' : 'pointer', opacity: validationError || saveStatus==='saving' ? 0.55 : 1, display:'flex', alignItems:'center', gap:'6px', fontWeight:700 }}>
                {saveStatus === 'saving'
                  ? <><RotateCcw style={{ width:'12px', height:'12px' }} />Saving…</>
                  : <><Save style={{ width:'12px', height:'12px' }} />Save &amp; Next <ChevronRight style={{ width:'13px', height:'13px' }} /></>}
              </button>
            </div>
          </div>

          {validationError && (
            <div style={{ marginTop:'8px', textAlign:'right', fontSize:'11px', color:'var(--c-warn)' }}>
              ⚠ {validationError}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  LEVEL 1: FLEET LIST (default view)
  // ═══════════════════════════════════════════════════════════════════════
  const fleetKeys = Object.keys(allFleets);
  const filtered  = fleetKeys.filter(k => {
    if (!fleetSearch) return true;
    const f = allFleets[k];
    const q = fleetSearch.toLowerCase();
    return (
      (f.companyName   || '').toLowerCase().includes(q) ||
      (f.contactPerson || '').toLowerCase().includes(q) ||
      (f.businessEmail || '').toLowerCase().includes(q)
    );
  });

  return wrap(
    [{ label: 'All Fleets' }],
    <div style={{ background:'var(--bg-panel)', border:'1px solid var(--br)', borderRadius:'8px', padding:'16px' }}>

      {/* Toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px', flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:'200px', position:'relative' }}>
          <Search style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', width:'13px', height:'13px', color:'var(--t4)' }} />
          <input type="text" placeholder="Search by company, contact, email…" value={fleetSearch} onChange={e => setFleetSearch(e.target.value)} style={{ ...inputStyle, paddingLeft:'32px' }} />
        </div>
        <button onClick={handleNewFleet} style={{ padding:'7px 16px', borderRadius:'6px', fontSize:'12px', cursor:'pointer', background:'var(--btn-p-bg)', border:'1px solid var(--btn-p-b)', color:'var(--btn-p-c)', display:'flex', alignItems:'center', gap:'6px', fontWeight:600, whiteSpace:'nowrap', flexShrink:0 }}>
          <Plus style={{ width:'13px', height:'13px' }} />New fleet
        </button>
      </div>

      {/* Panel heading */}
      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px', paddingBottom:'10px', borderBottom:'1px solid var(--br)' }}>
        <Layers style={{ width:'15px', height:'15px', color:'var(--accent)' }} />
        <h3 style={{ fontFamily:'StevieSans,sans-serif', fontSize:'13px', fontWeight:700, color:'var(--t1)', margin:0 }}>
          All fleets ({fleetKeys.length})
        </h3>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : fleetKeys.length === 0 ? (
        <div style={{ padding:'48px', textAlign:'center' }}>
          <Building2 style={{ width:'36px', height:'36px', color:'var(--t4)', margin:'0 auto 12px', display:'block' }} />
          <div style={{ fontSize:'13px', fontWeight:600, color:'var(--t2)', marginBottom:'4px' }}>No fleets configured yet</div>
          <div style={{ fontSize:'12px', color:'var(--t3)', marginBottom:'16px' }}>Click "New fleet" above to add the first fleet.</div>
          <button onClick={handleNewFleet} style={{ padding:'7px 18px', borderRadius:'6px', fontSize:'12px', cursor:'pointer', background:'var(--btn-p-bg)', border:'1px solid var(--btn-p-b)', color:'var(--btn-p-c)', display:'inline-flex', alignItems:'center', gap:'6px' }}>
            <Plus style={{ width:'12px', height:'12px' }} />Add first fleet
          </button>
        </div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
            <thead>
              <tr style={{ background:'var(--bg-surface)', borderBottom:'2px solid var(--br)' }}>
                <TH>Company Name</TH>
                <TH>Contact Person</TH>
                <TH>Email</TH>
                <TH>Status</TH>
                <TH>Subscription</TH>
                <TH>Vessels</TH>
                <TH center>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding:'32px', textAlign:'center', color:'var(--t3)', fontSize:'12px' }}>No fleets match your search.</td>
                </tr>
              ) : filtered.map((key, idx) => {
                const f = allFleets[key];
                const vesselCount = (f.vessels || []).length;
                const isEven = idx % 2 === 0;
                return (
                  <tr key={key}
                    style={{ background: isEven ? 'var(--bg-panel)' : 'var(--bg-surface)', borderBottom:'1px solid var(--br)', transition:'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--fill-ok)'}
                    onMouseLeave={e => e.currentTarget.style.background= isEven ? 'var(--bg-panel)' : 'var(--bg-surface)'}
                  >
                    {/* Company */}
                    <td style={{ padding:'11px 14px', fontWeight:700, color:'var(--t1)', whiteSpace:'nowrap', maxWidth:'220px', overflow:'hidden', textOverflow:'ellipsis' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                        <div style={{ width:'28px', height:'28px', borderRadius:'6px', background:'var(--fill-ok)', border:'1px solid var(--br-ok)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          <Building2 style={{ width:'13px', height:'13px', color:'var(--accent)' }} />
                        </div>
                        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.companyName || '(unnamed)'}</span>
                      </div>
                    </td>
                    {/* Contact */}
                    <td style={{ padding:'11px 14px', color:'var(--t2)', whiteSpace:'nowrap' }}>{f.contactPerson || <span style={{ color:'var(--t4)' }}>—</span>}</td>
                    {/* Email */}
                    <td style={{ padding:'11px 14px', color:'var(--t2)', whiteSpace:'nowrap', maxWidth:'200px', overflow:'hidden', textOverflow:'ellipsis' }}>{f.businessEmail || <span style={{ color:'var(--t4)' }}>—</span>}</td>
                    {/* Status */}
                    <td style={{ padding:'11px 14px', whiteSpace:'nowrap' }}><StatusBadge status={f.status} /></td>
                    {/* Subscription */}
                    <td style={{ padding:'11px 14px', color:'var(--t2)', fontFamily: 'StevieSans,sans-serif', whiteSpace:'nowrap', fontSize:'11px' }}>
                      {f.subscriptionDate || <span style={{ color:'var(--t4)' }}>—</span>}
                      {f.subscriptionEndDate ? <span style={{ color:'var(--t3)' }}> → {f.subscriptionEndDate}</span> : ''}
                    </td>
                    {/* Vessels count — clickable to drill in */}
                    <td style={{ padding:'11px 14px', whiteSpace:'nowrap' }}>
                      <button
                        onClick={() => handleOpenVessels(key)}
                        style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center', gap:'4px', color: vesselCount === 0 ? 'var(--t4)' : 'var(--accent)', fontWeight:600, fontSize:'12px' }}>
                        <Ship style={{ width:'12px', height:'12px' }} />
                        {vesselCount} <span style={{ fontSize:'10px', color:'var(--t3)', fontWeight:400 }}>vessel{vesselCount !== 1 ? 's' : ''}</span>
                      </button>
                    </td>
                    {/* Actions */}
                    <td style={{ padding:'11px 14px', textAlign:'center', whiteSpace:'nowrap' }}>
                      <div style={{ display:'inline-flex', gap:'6px' }}>
                        <button onClick={() => handleEditFleet(key)} style={{ padding:'5px 12px', borderRadius:'5px', fontSize:'11px', cursor:'pointer', background:'var(--btn-p-bg)', border:'1px solid var(--btn-p-b)', color:'var(--btn-p-c)', display:'flex', alignItems:'center', gap:'4px', fontWeight:600 }}>
                          <Edit3 style={{ width:'11px', height:'11px' }} />Edit
                        </button>
                        <button onClick={() => handleDeleteFleet(key)} style={{ padding:'5px 8px', borderRadius:'5px', fontSize:'11px', cursor:'pointer', background:'var(--fill-err)', border:'1px solid var(--br-err)', color:'var(--c-err)', display:'flex', alignItems:'center' }}>
                          <Trash2 style={{ width:'11px', height:'11px' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <div style={{ padding:'8px 14px', fontSize:'11px', color:'var(--t4)', borderTop:'1px solid var(--br)', background:'var(--bg-surface)' }}>
              Showing {filtered.length} of {fleetKeys.length} fleet{fleetKeys.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// =================================================================
//  VESSEL CONFIGURATOR
//  Full-screen UI that lets the configurator (e.g. Zeroqu) build the
//  vessel profile that gets baked into the crew's deployment.
// =================================================================
function VesselConfigurator({ onClose, onBackToFleet, darkMode = false, vesselInfo, setVesselInfo, vesselEngines, setVesselEngines, initialRole = 'user', onLogout, fleetKey: linkedFleetKey = null, initialView = 'list', initialVesselKey = null }) {
  // ─── Sub-view state ───────────────────────────────────────────────────────────
  // view: 'list'   → multi-vessel home screen (vessel selector + import)
  //       'edit'   → 3-card edit view for the currently selected vessel
  const [view, setView] = useState(initialView); // 'list' | 'edit'

  // ─── All vessels in runtime storage ──────────────────────────────────────────
  const [allVessels, setAllVessels] = useState({}); // { [key]: vesselInfo }
  const [allEngineMap, setAllEngineMap] = useState({}); // { [key]: engine[] }
  const [loadingVessels, setLoadingVessels] = useState(true);

  // ─── Currently-edited vessel (lifted to parent for draft persistence) ─────────
  const info    = vesselInfo;
  const engines = vesselEngines;
  const setInfo    = setVesselInfo;
  const setEngines = setVesselEngines;

  // ─── Engine-editor index within the edit view ─────────────────────────────────
  const [editingIdx, setEditingIdx] = useState(null); // null=engine list, -1=new, n=edit nth
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  // ─── Import JSON state ────────────────────────────────────────────────────────
  const [ setImportStatus] = useState(null); // null | {ok, msg}
  // const vesselFileRef = useRef(null);
  // const engineFileRef = useRef(null);
  const [pendingVesselJson, setPendingVesselJson] = useState(null);
  const [pendingEngineJson, setPendingEngineJson] = useState(null);
  const [importSearch, setImportSearch] = useState('');

  // ─── Load all saved vessels on mount and after saves ─────────────────────────
  const refreshVesselList = useCallback(async () => {
    setLoadingVessels(true);
    try {
      const [vessels, engineMap] = await Promise.all([loadAllVessels(), loadAllEngines()]);
      setAllVessels(vessels);
      setAllEngineMap(engineMap);
    } catch (e) { /* ignore */ }
    setLoadingVessels(false);
  }, []);

  useEffect(() => { refreshVesselList(); }, [refreshVesselList]);

  // ─── Fleet-scoped vessel keys (only vessels belonging to the linked fleet) ─────
  const [fleetScopedKeys, setFleetScopedKeys] = useState([]);
  const [linkedFleetName, setLinkedFleetName] = useState('');
  useEffect(() => {
    if (!linkedFleetKey) { setFleetScopedKeys([]); setLinkedFleetName(''); return; }
    let cancelled = false;
    (async () => {
      try {
        const allFleets = await loadAllFleets();
        const fleet = allFleets[linkedFleetKey];
        if (!cancelled) {
          setFleetScopedKeys(fleet ? (fleet.vessels || []) : []);
          setLinkedFleetName(fleet?.companyName || linkedFleetKey);
        }
      } catch { if (!cancelled) { setFleetScopedKeys([]); setLinkedFleetName(''); } }
    })();
    return () => { cancelled = true; };
  }, [linkedFleetKey, allVessels]);
  useEffect(() => {
    if (!initialVesselKey || !draftLoaded) return;
    // allVessels may not be loaded yet; wait until it has data
    if (Object.keys(allVessels).length === 0) return;
    const vInfo = allVessels[initialVesselKey];
    if (!vInfo) return;
    const vEngines = allEngineMap[initialVesselKey] || [];
    setInfo({
      vessel: '', imo: '', flag: '',
      configuredBy: '',
      configuredOn: new Date().toISOString().split('T')[0],
      notes: '', mainEngineCount: '', auxEngineCount: '',
      ...vInfo,
    });
    setEngines(vEngines.map(e => ({ ...e })));
    setView('edit');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVesselKey, allVessels, allEngineMap, draftLoaded]);

  // ─── Draft restore from window.storage ───────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (typeof window !== 'undefined' && window.storage) {
          const r = await window.storage.get('configuratorDraft');
          if (!cancelled && r && r.value) {
            const draft = JSON.parse(r.value);
            if (draft && draft.engines?.length > 0 && engines.length === 0) {
              if (draft.info)    setInfo(draft.info);
              if (draft.engines) setEngines(draft.engines);
            }
          }
        }
      } catch (e) { /* ignore */ }
      if (!cancelled) setDraftLoaded(true);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Auto-save draft whenever edit-view data changes ─────────────────────────
  useEffect(() => {
    if (!draftLoaded) return;
    saveConfiguratorDraft({ info, engines });
  }, [info, engines, draftLoaded]);

  const setInfoField = (k, v) => setInfo({ ...info, [k]: v });
  const [showVesselPassword, setShowVesselPassword] = useState(false);

  // ─── Select a vessel from the list view → switch to edit view ────────────────
  const handleSelectVessel = (key) => {
    const vInfo   = allVessels[key] || {};
    const vEngines = allEngineMap[key] || [];
    setInfo({
      vessel: '', imo: '', flag: '',
      configuredBy: '',
      configuredOn: new Date().toISOString().split('T')[0],
      notes: '', mainEngineCount: '', auxEngineCount: '',
      ...vInfo,
    });
    setEngines(vEngines.map(e => ({ ...e })));
    setView('edit');
  };

  // ─── New vessel → blank edit view ────────────────────────────────────────────
  const handleNewVessel = () => {
    setInfo({
      vessel: '', imo: '', flag: '',
      configuredBy: '',
      configuredOn: new Date().toISOString().split('T')[0],
      notes: '', mainEngineCount: '', auxEngineCount: '',
    });
    setEngines([]);
    setView('edit');
  };

  // ─── Delete a vessel from the list ───────────────────────────────────────────
  const handleDeleteVessel = async (key) => {
    const name = allVessels[key]?.vessel || key;
    if (!window.confirm(`Delete vessel "${name}" and all its engine data? This cannot be undone.`)) return;
    try {
      await deleteVesselAndEngines(key);
      await refreshVesselList();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  // ─── Import JSON files ────────────────────────────────────────────────────────
  const readJsonFile = (file) => new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try { res(JSON.parse(e.target.result)); } catch { rej(new Error('Invalid JSON')); }
    };
    reader.onerror = () => rej(new Error('File read error'));
    reader.readAsText(file);
  });

  // eslint-disable-next-line 
  const handleVesselFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { setPendingVesselJson(await readJsonFile(file)); }
    catch { setImportStatus({ ok: false, msg: 'Could not parse vessel JSON file.' }); }
    e.target.value = '';
  };

  // eslint-disable-next-line 
  const handleEngineFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { setPendingEngineJson(await readJsonFile(file)); }
    catch { setImportStatus({ ok: false, msg: 'Could not parse engine JSON file.' }); }
    e.target.value = '';
  };

  // eslint-disable-next-line 
  const handleImport = async () => {
    if (!pendingVesselJson && !pendingEngineJson) return;
    setImportStatus(null);
    try {
      const { vesselsImported, enginesImported } = await importFromJsonFiles(pendingVesselJson, pendingEngineJson);
      setPendingVesselJson(null);
      setPendingEngineJson(null);
      await refreshVesselList();
      setImportStatus({ ok: true, msg: `Imported ${vesselsImported} vessel${vesselsImported !== 1 ? 's' : ''} and ${enginesImported} vessel engine set${enginesImported !== 1 ? 's' : ''}.` });
      setTimeout(() => setImportStatus(null), 5000);
    } catch (e) {
      setImportStatus({ ok: false, msg: 'Import failed: ' + e.message });
    }
  };

  // ─── Engine CRUD within edit view ────────────────────────────────────────────
  const addEngine    = () => setEditingIdx(-1);
  const editEngine   = (i) => setEditingIdx(i);
  const removeEngine = (i) => {
    if (window.confirm(`Remove "${engines[i].name}" from the vessel profile?`)) {
      setEngines(engines.filter((_, j) => j !== i));
    }
  };
  const onEngineSave = (saved) => {
    setEngines(editingIdx === -1 ? [...engines, saved] : engines.map((e, i) => i === editingIdx ? saved : e));
    setEditingIdx(null);
  };

  // ─── Validate before save ─────────────────────────────────────────────────────
  const validateExport = () => {
    if (!info.vessel)         return 'Vessel name is required.';
    if (!info.configuredBy)   return 'Configured by (organisation) is required.';
    if (engines.length === 0) return 'Add at least one engine before saving.';
    return null;
  };

  // ─── Save current vessel ──────────────────────────────────────────────────────
  const handleSaveToConfig = async () => {
    const err = validateExport();
    if (err) { alert(err); return; }
    setSaveStatus('saving');
    try {
      const infoWithFleet = linkedFleetKey ? { ...info, fleetId: linkedFleetKey } : info;
      const savedKey = await saveFullConfig({ info: infoWithFleet, engines });
      // If this vessel is being created from within a fleet, link it automatically
      if (linkedFleetKey) await addVesselToFleet(linkedFleetKey, savedKey);
      await refreshVesselList();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e) {
      console.error('Save to config failed:', e);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
    }
  };

  const handleSaveAndExport = async () => {
    const err = validateExport();
    if (err) { alert(err); return; }
    setSaveStatus('saving');
    try {
      const infoWithFleet = linkedFleetKey ? { ...info, fleetId: linkedFleetKey } : info;
      const savedKey = await saveFullConfig({ info: infoWithFleet, engines });
      if (linkedFleetKey) await addVesselToFleet(linkedFleetKey, savedKey);
      await exportAllConfigFiles();
      await refreshVesselList();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e) {
      console.error('Save & export failed:', e);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
    }
  };

  const adminLogoutBtn = initialRole === 'admin'
    ? (onLogout ? <button onClick={onLogout} style={{ padding:'4px 12px', fontSize:'12px', borderRadius:'6px', border:'1px solid var(--br-err)', background:'var(--fill-err)', color:'var(--c-err)', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}>Log out</button> : null)
    : <button onClick={onClose} style={{ padding:'4px 12px', fontSize:'12px', borderRadius:'6px', border:'1px solid var(--br-mid)', background:'var(--bg-surface)', color:'var(--t2)', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}><ChevronLeft className="w-3 h-3" />Back to analyzer</button>;

  // ═══════════════════════════════════════════════════════════════════
  //  ENGINE EDITOR SUB-VIEW  (unchanged — shown when editingIdx != null)
  // ═══════════════════════════════════════════════════════════════════
  if (editingIdx !== null) {
    return (
      <VesselEngineEditor
        initial={editingIdx === -1 ? null : engines[editingIdx]}
        engineCount={engines.length}
        editingIndex={editingIdx}
        darkMode={darkMode}
        onSave={onEngineSave}
        onCancel={() => setEditingIdx(null)}
      />
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  //  LIST VIEW  — shows all saved vessels + import tools
  // ═══════════════════════════════════════════════════════════════════
  if (view === 'list') {
    // When opened from a fleet, vesselKeys is already filtered (see fleetScopedKeys below)
    const vesselKeys = linkedFleetKey ? fleetScopedKeys : Object.keys(allVessels);
    const filtered    = vesselKeys.filter(k => {
      if (!allVessels[k]) return false; // vessel data not loaded yet
      if (!importSearch) return true;
      const v = allVessels[k];
      const q = importSearch.toLowerCase();
      return (v.vessel || '').toLowerCase().includes(q) ||
             (v.imo    || '').toLowerCase().includes(q) ||
             (v.flag   || '').toLowerCase().includes(q);
    });

    return (
      <div className="min-h-screen" data-theme={darkMode ? 'dark' : 'light'} style={{ background: 'var(--bg-page)', color: 'var(--t1)' }}>
        <style>{THEME_STYLES}</style>

        {/* ── Header ── */}
        <header style={{ borderBottom: '1px solid var(--br-ok)', background: 'var(--bg-panel)', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ padding: "0 24px", height: "64px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <UserCog className="w-5 h-5 text-slate-950" />
              </div>
              <div>
                {onBackToFleet && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '1px' }}>
                    <button onClick={onBackToFleet} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '11px', color: 'var(--t3)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <ChevronLeft style={{ width: '11px', height: '11px' }} />Back to Fleet
                    </button>
                  </div>
                )}
                <h1 className="text-base font-bold tracking-tight">Vessel Configurator</h1>
                <p className="text-xs text-slate-400">Manage all vessels · Import JSON · Add or edit vessel profiles</p>
              </div>
            </div>
            {adminLogoutBtn}
          </div>
        </header>

        <div style={{ padding: "24px" }}>

          {/* ── Import JSON files ── */}
          {/* <Panel title="Import from JSON files" icon={Upload} className="mb-4">
            <p className="text-xs mb-3" style={{ color: 'var(--t3)', lineHeight: '1.6' }}>
              Upload previously exported <code style={{ color: 'var(--accent-lt)' }}>vesselConfig.json</code> and / or <code style={{ color: 'var(--accent-lt)' }}>engineConfig.json</code> to restore vessel profiles. Imported data is merged with any existing vessels — existing records with the same key are overwritten.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">

              <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-surface)', border: `1px ${pendingVesselJson ? 'solid' : 'dashed'} ${pendingVesselJson ? 'var(--br-ok)' : 'var(--br-mid)'}` }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--t2)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Ship style={{ width: '13px', height: '13px', color: 'var(--accent)' }} />
                  vesselConfig.json
                  {pendingVesselJson && (
                    <span style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--c-ok)', background: 'var(--fill-ok)', border: '1px solid var(--br-ok)', borderRadius: '4px', padding: '1px 7px' }}>
                      {Object.keys(pendingVesselJson.vessels || {}).length || 1} vessel{Object.keys(pendingVesselJson.vessels || {}).length !== 1 ? 's' : ''} ready
                    </span>
                  )}
                </div>
                <input ref={vesselFileRef} type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={handleVesselFileChange} />
                <button
                  onClick={() => vesselFileRef.current?.click()}
                  style={{ width: '100%', padding: '7px', borderRadius: '5px', fontSize: '12px', cursor: 'pointer', background: pendingVesselJson ? 'var(--fill-ok)' : 'var(--bg-panel)', border: `1px solid ${pendingVesselJson ? 'var(--br-ok)' : 'var(--br-mid)'}`, color: pendingVesselJson ? 'var(--c-ok)' : 'var(--t3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  {pendingVesselJson ? <><CheckCircle2 style={{ width: '12px', height: '12px' }} />File loaded — click to change</> : <><Upload style={{ width: '12px', height: '12px' }} />Choose vesselConfig.json</>}
                </button>
                {pendingVesselJson && (
                  <button onClick={() => setPendingVesselJson(null)} style={{ marginTop: '6px', width: '100%', padding: '4px', fontSize: '11px', borderRadius: '4px', border: '1px solid var(--br-err)', background: 'var(--fill-err)', color: 'var(--c-err)', cursor: 'pointer' }}>
                    <X style={{ width: '10px', height: '10px', display: 'inline', marginRight: '4px' }} />Clear
                  </button>
                )}
              </div>


              <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-surface)', border: `1px ${pendingEngineJson ? 'solid' : 'dashed'} ${pendingEngineJson ? 'var(--br-ok)' : 'var(--br-mid)'}` }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--t2)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Cpu style={{ width: '13px', height: '13px', color: 'var(--accent)' }} />
                  engineConfig.json
                  {pendingEngineJson && (
                    <span style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--c-ok)', background: 'var(--fill-ok)', border: '1px solid var(--br-ok)', borderRadius: '4px', padding: '1px 7px' }}>
                      {Object.keys(pendingEngineJson.engines || {}).length} vessel set{Object.keys(pendingEngineJson.engines || {}).length !== 1 ? 's' : ''} ready
                    </span>
                  )}
                </div>
                <input ref={engineFileRef} type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={handleEngineFileChange} />
                <button
                  onClick={() => engineFileRef.current?.click()}
                  style={{ width: '100%', padding: '7px', borderRadius: '5px', fontSize: '12px', cursor: 'pointer', background: pendingEngineJson ? 'var(--fill-ok)' : 'var(--bg-panel)', border: `1px solid ${pendingEngineJson ? 'var(--br-ok)' : 'var(--br-mid)'}`, color: pendingEngineJson ? 'var(--c-ok)' : 'var(--t3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  {pendingEngineJson ? <><CheckCircle2 style={{ width: '12px', height: '12px' }} />File loaded — click to change</> : <><Upload style={{ width: '12px', height: '12px' }} />Choose engineConfig.json</>}
                </button>
                {pendingEngineJson && (
                  <button onClick={() => setPendingEngineJson(null)} style={{ marginTop: '6px', width: '100%', padding: '4px', fontSize: '11px', borderRadius: '4px', border: '1px solid var(--br-err)', background: 'var(--fill-err)', color: 'var(--c-err)', cursor: 'pointer' }}>
                    <X style={{ width: '10px', height: '10px', display: 'inline', marginRight: '4px' }} />Clear
                  </button>
                )}
              </div>
            </div>


            {importStatus && (
              <div style={{ marginBottom: '10px', padding: '8px 12px', borderRadius: '6px', background: importStatus.ok ? 'var(--fill-ok)' : 'var(--fill-err)', border: `1px solid ${importStatus.ok ? 'var(--br-ok)' : 'var(--br-err)'}`, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: importStatus.ok ? 'var(--c-ok)' : 'var(--c-err)' }}>
                {importStatus.ok ? <CheckCircle2 style={{ width: '14px', height: '14px', flexShrink: 0 }} /> : <AlertCircle style={{ width: '14px', height: '14px', flexShrink: 0 }} />}
                {importStatus.msg}
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleImport}
                disabled={!pendingVesselJson && !pendingEngineJson}
                style={{
                  background: (!pendingVesselJson && !pendingEngineJson) ? 'var(--fill-ok)' : 'var(--btn-p-bg)',
                  border: `1px solid ${(!pendingVesselJson && !pendingEngineJson) ? 'var(--br)' : 'var(--btn-p-b)'}`,
                  color: (!pendingVesselJson && !pendingEngineJson) ? 'var(--t4)' : 'var(--btn-p-c)',
                  borderRadius: '6px', padding: '7px 16px', fontSize: '12px',
                  cursor: (!pendingVesselJson && !pendingEngineJson) ? 'not-allowed' : 'pointer',
                  opacity: (!pendingVesselJson && !pendingEngineJson) ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                <Download style={{ width: '12px', height: '12px' }} />Import &amp; merge
              </button>
            </div>
          </Panel> */}

          {/* ── All vessels ── */}
          <Panel title={`All vessels (${vesselKeys.length})`} icon={Ship} className="mb-4">
            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                <Search style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', width: '13px', height: '13px', color: 'var(--t4)' }} />
                <input
                  type="text"
                  placeholder="Search by name, IMO, flag…"
                  value={importSearch}
                  onChange={e => setImportSearch(e.target.value)}
                  style={{ width: '100%', paddingLeft: '32px', paddingRight: '10px', paddingTop: '7px', paddingBottom: '7px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--in-br)', background: 'var(--in-bg)', color: 'var(--in-c)' }}
                />
              </div>
              <button onClick={handleNewVessel}
                style={{ padding: '7px 16px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', background: 'var(--btn-p-bg)', border: '1px solid var(--btn-p-b)', color: 'var(--btn-p-c)', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', flexShrink: 0, fontWeight: 600 }}>
                <Plus style={{ width: '13px', height: '13px' }} />New vessel
              </button>
            </div>

            {loadingVessels ? (
              <LoadingSpinner />
            ) : vesselKeys.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center' }}>
                <Ship style={{ width: '36px', height: '36px', color: 'var(--t4)', margin: '0 auto 12px', display: 'block' }} />
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--t2)', marginBottom: '4px' }}>No vessels configured yet</div>
                <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '16px' }}>Click "New vessel" above to add the first vessel.</div>
                <button onClick={handleNewVessel}
                  style={{ padding: '7px 18px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', background: 'var(--btn-p-bg)', border: '1px solid var(--btn-p-b)', color: 'var(--btn-p-c)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <Plus style={{ width: '12px', height: '12px' }} />Add first vessel
                </button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-surface)', borderBottom: '2px solid var(--br)' }}>
                      {['Vessel Name', 'IMO Number', 'Flag', 'Configured By', 'Configured On', 'Engines', 'Actions'].map(col => (
                        <th key={col} style={{ padding: '10px 14px', textAlign: col === 'Actions' ? 'center' : 'left', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--t3)', whiteSpace: 'nowrap' }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: 'var(--t3)', fontSize: '12px' }}>
                          No vessels match your search. Try a different term.
                        </td>
                      </tr>
                    ) : filtered.map((key, idx) => {
                      const v = allVessels[key];
                      const engArr = allEngineMap[key] || [];
                      const mainCount = engArr.filter(e => e.role === 'main').length;
                      const auxCount  = engArr.filter(e => e.role === 'auxiliary').length;
                      const isEven = idx % 2 === 0;
                      return (
                        <tr key={key}
                          style={{ background: isEven ? 'var(--bg-panel)' : 'var(--bg-surface)', borderBottom: '1px solid var(--br)', transition: 'background 0.12s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--fill-ok)'}
                          onMouseLeave={e => e.currentTarget.style.background = isEven ? 'var(--bg-panel)' : 'var(--bg-surface)'}
                        >
                          {/* Vessel Name */}
                          <td style={{ padding: '11px 14px', fontWeight: 700, color: 'var(--t1)', whiteSpace: 'nowrap', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: 'var(--fill-ok)', border: '1px solid var(--br-ok)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Ship style={{ width: '14px', height: '14px', color: 'var(--accent)' }} />
                              </div>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.vessel || '(unnamed)'}</span>
                            </div>
                          </td>
                          {/* IMO Number */}
                          <td style={{ padding: '11px 14px', color: 'var(--t2)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                            {v.imo ? v.imo : <span style={{ color: 'var(--t4)' }}>—</span>}
                          </td>
                          {/* Flag */}
                          <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                            {v.flag ? <Tag tone="slate" small>{v.flag}</Tag> : <span style={{ color: 'var(--t4)' }}>—</span>}
                          </td>
                          {/* Configured By */}
                          <td style={{ padding: '11px 14px', color: 'var(--t2)', whiteSpace: 'nowrap', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {v.configuredBy || <span style={{ color: 'var(--t4)' }}>—</span>}
                          </td>
                          {/* Configured On */}
                          <td style={{ padding: '11px 14px', color: 'var(--t2)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                            {v.configuredOn || <span style={{ color: 'var(--t4)' }}>—</span>}
                          </td>
                          {/* Engines */}
                          <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                            {engArr.length === 0
                              ? <span style={{ color: 'var(--t4)' }}>None</span>
                              : <span style={{ color: 'var(--t2)' }}>
                                  {engArr.length} &nbsp;
                                  <span style={{ fontSize: '10px', color: 'var(--t3)' }}>({mainCount}M&nbsp;{auxCount}A)</span>
                                </span>
                            }
                          </td>
                          {/* Actions */}
                          <td style={{ padding: '11px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'inline-flex', gap: '6px' }}>
                              <button onClick={() => handleSelectVessel(key)}
                                style={{ padding: '5px 12px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', background: 'var(--btn-p-bg)', border: '1px solid var(--btn-p-b)', color: 'var(--btn-p-c)', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
                                <Edit3 style={{ width: '11px', height: '11px' }} />Edit
                              </button>
                              <button onClick={() => handleDeleteVessel(key)}
                                style={{ padding: '5px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', background: 'var(--fill-err)', border: '1px solid var(--br-err)', color: 'var(--c-err)', display: 'flex', alignItems: 'center' }}>
                                <Trash2 style={{ width: '11px', height: '11px' }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filtered.length > 0 && (
                  <div style={{ padding: '8px 14px', fontSize: '11px', color: 'var(--t4)', borderTop: '1px solid var(--br)', background: 'var(--bg-surface)' }}>
                    Showing {filtered.length} of {vesselKeys.length} vessel{vesselKeys.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}
          </Panel>

          {/* ── Back to Fleet button ── */}
          {onBackToFleet && (
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: '8px' }}>
              <button
                onClick={onBackToFleet}
                style={{ padding:'7px 16px', borderRadius:'6px', fontSize:'12px', cursor:'pointer', background:'var(--bg-surface)', border:'1px solid var(--br-mid)', color:'var(--t2)', display:'flex', alignItems:'center', gap:'6px' }}>
                <ChevronLeft style={{ width:'12px', height:'12px' }} />Back to Fleet
              </button>
            </div>
          )}

        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  //  EDIT VIEW  — 3 cards for one vessel (vessel info, engines, save)
  // ═══════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen" data-theme={darkMode ? 'dark' : 'light'} style={{ background: 'var(--bg-page)', color: 'var(--t1)' }}>
      <style>{THEME_STYLES}</style>
      <header style={{ borderBottom: "1px solid var(--br-ok)", background: "var(--bg-panel)", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ padding: "0 24px", height: "64px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <UserCog className="w-5 h-5 text-slate-950" />
            </div>
            <div>
              {/* Breadcrumb */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '1px' }}>
                <button onClick={() => linkedFleetKey && onBackToFleet ? onBackToFleet() : setView('list')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '11px', color: 'var(--t3)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <ChevronLeft style={{ width: '11px', height: '11px' }} />{linkedFleetKey ? (linkedFleetName || linkedFleetKey) : 'All vessels'}
                </button>
                <span style={{ fontSize: '11px', color: 'var(--t4)' }}>/</span>
                <span style={{ fontSize: '11px', color: 'var(--t2)', fontWeight: 600 }}>{info.vessel || 'New vessel'}</span>
              </div>
              <h1 className="text-base font-bold tracking-tight">
                {info.vessel ? `${info.vessel}` : 'New vessel'}
              </h1>
            </div>
          </div>
          {adminLogoutBtn}
        </div>
      </header>

      <div style={{ padding: "24px" }}>

        {/* Vessel info */}
        <Panel title="Vessel information" icon={Ship} className="mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Vessel name *">
              <TextIn val={info.vessel} onChange={(v) => setInfoField('vessel', v)} placeholder="e.g. MV Atlas" />
            </Field>
            <Field label="IMO number">
              <TextIn val={info.imo} onChange={(v) => setInfoField('imo', v)} placeholder="e.g. 9323027" />
            </Field>
            <Field label="Flag (optional)">
              <TextIn val={info.flag} onChange={(v) => setInfoField('flag', v)} placeholder="e.g. Liberia" />
            </Field>
            <Field label="Configured by *">
              <TextIn val={info.configuredBy} onChange={(v) => setInfoField('configuredBy', v)} placeholder="e.g. Zeroqu Marine Solutions" />
            </Field>
            <Field label="Configured on">
              <input type="date" value={info.configuredOn}
                onChange={(e) => setInfoField('configuredOn', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm" />
            </Field>
            <Field label="Notes (optional)">
              <textarea value={info.notes} onChange={(e) => setInfoField('notes', e.target.value)} rows={1}
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm"
                placeholder="Internal notes" />
            </Field>
            {/* Engine count declaration — helps validate configuration completeness */}
            <Field label="No. of main engines onboard *">
              <NumIn val={info.mainEngineCount} onChange={(v) => setInfoField('mainEngineCount', v)} step={1} placeholder="e.g. 1" />
            </Field>
            <Field label="No. of auxiliary engines onboard *">
              <NumIn val={info.auxEngineCount} onChange={(v) => setInfoField('auxEngineCount', v)} step={1} placeholder="e.g. 3" />
            </Field>
          </div>

          {/* Informational note — engine counts vs shop trial entries */}
          {(Number(info.mainEngineCount) > 0 || Number(info.auxEngineCount) > 0) && (
            <div style={{ marginTop:'10px', padding:'8px 12px', borderRadius:'6px', background:'var(--fill-info)', border:'1px solid var(--br-info)', display:'flex', gap:'8px', alignItems:'flex-start', fontSize:'11px', color:'var(--c-info)' }}>
              <Info style={{ width:'14px', height:'14px', flexShrink:0, marginTop:'1px' }} />
              <span>
                Vessel declared: <strong>{Number(info.mainEngineCount) || 0} main</strong> and <strong>{Number(info.auxEngineCount) || 0} auxiliary</strong> engines onboard.
                {' '}You only need <strong>one shop trial entry per unique model</strong> — if multiple engines share the same model and specifications, a single entry covers all of them.
                Add separate entries only when engines have different models, bore, stroke, or power ratings.
              </span>
            </div>
          )}

          {/* ── Vessel Login Credentials ── */}
          <div style={{ marginTop:'16px', background:'var(--bg-surface)', border:'1px solid var(--br)', borderRadius:'8px', padding:'16px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px', paddingBottom:'8px', borderBottom:'1px solid var(--br)' }}>
              <Lock style={{ width:'14px', height:'14px', color:'var(--accent)' }} />
              <span style={{ fontFamily:'StevieSans,sans-serif', fontSize:'13px', fontWeight:700, color:'var(--t1)' }}>Vessel Login Credentials</span>
              <span style={{ marginLeft:'auto', fontSize:'11px', color:'var(--t4)', fontStyle:'italic' }}>Used by vessel-level crew to log in</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:'12px' }}>
              <Field label="Vessel Username">
                <TextIn val={info.userAccess ?? ''} onChange={v => setInfoField('userAccess', v)} placeholder="e.g. imo_9999999" autoComplete="off" />
              </Field>
              <Field label="Vessel Password">
                <div style={{ position:'relative' }}>
                  <input
                    type={showVesselPassword ? 'text' : 'password'}
                    value={info.userPassword ?? ''}
                    onChange={e => setInfoField('userPassword', e.target.value)}
                    placeholder="Vessel login password"
                    autoComplete="new-password"
                    style={{
                      width:'100%', padding:'6px 34px 6px 8px',
                      background:'var(--in-bg)', border:'1px solid var(--in-br)',
                      borderRadius:'5px', color:'var(--in-c)', fontSize:'13px', outline:'none',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowVesselPassword(p => !p)}
                    style={{ position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--t4)', padding:'2px', lineHeight:1 }}
                  >
                    {showVesselPassword ? <X style={{ width:'13px', height:'13px' }} /> : <Lock style={{ width:'13px', height:'13px' }} />}
                  </button>
                </div>
              </Field>
            </div>
            <p style={{ marginTop:'8px', fontSize:'11px', color:'var(--t4)', lineHeight:'1.5' }}>
              These credentials allow crew to log in directly to this vessel’s analyser view.
              Leave blank if vessel-level login is not required.
            </p>
          </div>
        </Panel>
        {/* Engines list */}
        <Panel title={`Engines (${engines.length})`} icon={Cpu} className="mb-4">
          {engines.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              <Cpu className="w-8 h-8 mx-auto mb-2 text-slate-600" />
              No engines configured yet.  Add the vessel&apos;s main engine and any auxiliary engines below.
            </div>
          ) : (
            <div className="overflow-x-auto mb-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-800">
                    <th className="text-left py-2 px-2">#</th>
                    <th className="text-left py-2 px-2">Role</th>
                    <th className="text-left py-2 px-2">Name</th>
                    <th className="text-left py-2 px-2">Manufacturer · Model</th>
                    <th className="text-right py-2 px-2">MCR</th>
                    <th className="text-right py-2 px-2">Cyl</th>
                    <th className="text-center py-2 px-2">Type</th>
                    <th className="text-right py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {engines.map((e, i) => (
                    <tr key={e.id || i} className="border-b border-slate-800/50">
                      <td className="py-2 px-2 font-mono text-slate-500">{i + 1}</td>
                      <td className="py-2 px-2"><Tag tone={e.role === 'main' ? 'amber' : 'cyan'} small>{e.role}</Tag></td>
                      <td className="py-2 px-2 font-mono text-amber-300">{e.name}</td>
                      <td className="py-2 px-2 font-mono text-slate-300">{e.shopTrial?.manufacturer} {e.shopTrial?.model}</td>
                      <td className="py-2 px-2 text-right font-mono">{e.shopTrial?.mcrPower_kW ?? '—'} kW</td>
                      <td className="py-2 px-2 text-right font-mono">{e.shopTrial?.cylinders ?? '—'}</td>
                      <td className="py-2 px-2 text-center"><Tag tone={e.shopTrial?.engineType === '2-stroke' ? 'amber' : 'cyan'} small>{e.shopTrial?.engineType}</Tag></td>
                      <td className="py-2 px-2 text-right">
                        <button onClick={() => editEngine(i)} className="px-2 py-1 rounded text-[10px] bg-slate-800 hover:bg-slate-700 mr-1">
                          <Edit3 className="w-3 h-3 inline" /> Edit
                        </button>
                        <button onClick={() => removeEngine(i)} className="px-2 py-1 rounded text-[10px] bg-red-500/10 text-red-400 hover:bg-red-500/20">
                          <Trash2 className="w-3 h-3 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button onClick={addEngine} className="w-full px-3 py-2 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center justify-center gap-1.5 hover:bg-emerald-500/20">
            <Plus className="w-3 h-3" />Add engine
          </button>
        </Panel>

        {/* ── Save to config files ── */}
        <Panel title="Save configuration" icon={Save} className="mb-4">
          {/* Summary */}
          <div style={{ background:'var(--bg-surface)', border:'1px solid var(--br)', borderRadius:'6px', padding:'12px', marginBottom:'12px', fontSize:'11px' }}>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <DefRow label="Vessel"        value={info.vessel || <span style={{ color:'var(--c-warn)' }}>not set</span>} />
              <DefRow label="IMO"           value={info.imo || '—'} />
              <DefRow label="Configured by" value={info.configuredBy || <span style={{ color:'var(--c-warn)' }}>not set</span>} />
              <DefRow label="Configured on" value={info.configuredOn} />
              <DefRow label="Engines"       value={`${engines.length} (${engines.filter(e => e.role === 'main').length} main, ${engines.filter(e => e.role === 'auxiliary').length} aux)`} />
              <DefRow label="Vessel key"    value={vesselKey(info) || <span style={{ color:'var(--c-warn)' }}>not set</span>} />
            </div>
          </div>

          {/* Status banner */}
          {saveStatus === 'saved' && (
            <div style={{ marginBottom:'10px', padding:'8px 12px', borderRadius:'6px', background:'var(--fill-ok)', border:'1px solid var(--br-ok)', display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color:'var(--c-ok)' }}>
              <CheckCircle2 style={{ width:'14px', height:'14px' }} />
              Saved successfully.
            </div>
          )}
          {saveStatus === 'error' && (
            <div style={{ marginBottom:'10px', padding:'8px 12px', borderRadius:'6px', background:'var(--fill-err)', border:'1px solid var(--br-err)', display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color:'var(--c-err)' }}>
              <AlertCircle style={{ width:'14px', height:'14px' }} />
              Save failed.
            </div>
          )}

          {/* Explanation */}
          {/* <div className="text-xs mb-3" style={{ color:'var(--t3)' }}>
            <p style={{ marginBottom:'6px' }}>
              <strong style={{ color:'var(--t2)' }}>Save</strong> writes this vessel&apos;s info and engine data to browser storage (<code style={{ color:'var(--accent-lt)' }}>vesselConfig</code> / <code style={{ color:'var(--accent-lt)' }}>engineConfig</code>).
              Multiple vessels are stored under separate keys so they never conflict — Vessel A&apos;s engines cannot overwrite Vessel B&apos;s.
            </p>
            <p>
              <strong style={{ color:'var(--t2)' }}>Save &amp; Download files</strong> does the same AND downloads
              <code style={{ color:'var(--accent-lt)' }}> vesselConfig.json</code> and <code style={{ color:'var(--accent-lt)' }}>engineConfig.json</code> containing <em>all</em> configured vessels.
              Replace the files in <code style={{ color:'var(--accent-lt)' }}>src/config/</code> and rebuild so the data ships baked into the next deployment.
            </p>
          </div> */}

          {/* Buttons */}
          <div className="flex justify-end gap-2 flex-wrap">
            {/* Save only (runtime storage) */}
            <button
              onClick={handleSaveToConfig}
              disabled={!!validateExport() || saveStatus === 'saving'}
              style={{
                background: validateExport() ? 'var(--fill-ok)' : 'var(--btn-p-bg)',
                border: `1px solid ${validateExport() ? 'var(--br)' : 'var(--btn-p-b)'}`,
                color: validateExport() ? 'var(--t4)' : 'var(--btn-p-c)',
                borderRadius:'6px', padding:'7px 14px', fontSize:'12px',
                cursor: validateExport() || saveStatus === 'saving' ? 'not-allowed' : 'pointer',
                opacity: validateExport() || saveStatus === 'saving' ? 0.55 : 1,
                display:'flex', alignItems:'center', gap:'6px',
              }}>
              {saveStatus === 'saving'
                ? <><RotateCcw style={{ width:'12px', height:'12px' }} />Saving…</>
                : <><Save style={{ width:'12px', height:'12px' }} />Save</>}
            </button>

            {/* Save + download JSON files */}
            <button
              onClick={handleSaveAndExport}
              disabled={!!validateExport() || saveStatus === 'saving'}
              style={{
                background: validateExport() ? 'var(--fill-ok)' : 'var(--fill-ok)',
                border: `1px solid ${validateExport() ? 'var(--br)' : 'var(--br-ok)'}`,
                color: validateExport() ? 'var(--t4)' : 'var(--c-ok)',
                borderRadius:'6px', padding:'7px 14px', fontSize:'12px',
                cursor: validateExport() || saveStatus === 'saving' ? 'not-allowed' : 'pointer',
                opacity: validateExport() || saveStatus === 'saving' ? 0.55 : 1,
                display:'flex', alignItems:'center', gap:'6px',
              }}>
              <Download style={{ width:'12px', height:'12px' }} />Save &amp; Download files
            </button>
          </div>

          {validateExport() && (
            <div className="mt-2 text-[11px] text-right" style={{ color:'var(--c-warn)' }}>{validateExport()}</div>
          )}
        </Panel>

        {/* ── Export sealed .js build (existing feature) ── */}
        {/* <Panel title="Export sealed build (.js)" icon={Lock} className="mb-4">
          <div className="text-xs mb-3" style={{ color:'var(--t3)' }}>
            Downloads a <code style={{ color:'var(--accent-lt)' }}>.js</code> file containing the <code style={{ color:'var(--accent-lt)' }}>VESSEL_CONFIG</code> block.
            Replace the block at the top of <code style={{ color:'var(--accent-lt)' }}>MarineEnginePerformanceAnalyzer.jsx</code> and rebuild to produce a vessel-locked crew build.
          </div>
          <div className="flex justify-end">
            <button onClick={handleExport}
              disabled={!!validateExport()}
              style={{ background:'var(--btn-p-bg)', border:'1px solid var(--btn-p-b)', color:'var(--btn-p-c)', borderRadius:'6px', padding:'7px 14px', fontSize:'12px', cursor: validateExport() ? 'not-allowed' : 'pointer', opacity: validateExport() ? 0.5 : 1, display:'flex', alignItems:'center', gap:'6px' }}>
              <Download style={{ width:'12px', height:'12px' }} />Export sealed config (.js)
            </button>
          </div>
          {validateExport() && (
            <div className="mt-2 text-[11px] text-right" style={{ color:'var(--c-warn)' }}>{validateExport()}</div>
          )}
        </Panel> */}

        {/* ── Back to vessel list ── */}
        <div style={{ paddingTop: '8px', borderTop: '1px solid var(--br)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => linkedFleetKey && onBackToFleet ? onBackToFleet() : setView('list')}
            style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', background: 'var(--bg-surface)', border: '1px solid var(--br-mid)', color: 'var(--t2)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <ChevronLeft style={{ width: '12px', height: '12px' }} />Back to all vessels
          </button>
          <span style={{ fontSize: '11px', color: 'var(--t3)' }}>
            {(linkedFleetKey ? fleetScopedKeys.length : Object.keys(allVessels).length)} vessel{(linkedFleetKey ? fleetScopedKeys.length : Object.keys(allVessels).length) !== 1 ? 's' : ''} in storage
          </span>
        </div>
      </div>
    </div>
  );
}

// =================================================================
//  VESSEL ENGINE EDITOR
//  Used inside VesselConfigurator.  Same shop-trial form as the
//  analyzer's "Add manually" flow, plus engine name + role fields.
// =================================================================
function VesselEngineEditor({ initial, engineCount, editingIndex, onSave, onCancel, darkMode = false }) {
  const [name, setName] = useState(initial?.name || '');
  const [role, setRole] = useState(initial?.role || (engineCount === 0 ? 'main' : 'auxiliary'));
  const [engine, setEngine] = useState(() => initial?.shopTrial || emptyShopTrialEntry('2-stroke'));
  const [error, setError] = useState(null);

  const PHYS_GROUPS = [
    { title: 'Charge-air & ambient', fields: [
      ['blowerTemp',     'Blower inlet temp',     '°C'],
      ['barometric',     'Barometric press',      'mbar'],
      ['airFilterDP',    'Air filter ΔP',         'mmH2O'],
      ['airCoolerDP',    'Air cooler ΔP',         'mmH2O'],
      ['scavTemp',       'Scavenge air temp',     '°C'],
      ['pscav',          'Scavenge air press',    'bar'],
    ]},
    { title: 'Exhaust & turbocharger', fields: [
      ['turbBackPress',  'Turbine back press',    'mmH2O'],
      ['turbInletPress', 'Turbine inlet press',   'bar'],
      ['exhTemp',        'Exh temp (cyl out)',    '°C'],
      ['turbInletTemp',  'Turbine inlet temp',    '°C'],
      ['turbOutletTemp', 'Turbine outlet temp',   '°C'],
      ['tcRPM',          'TC speed',              'RPM'],
    ]},
    { title: 'Combustion & fuel', fields: [
      ['pcomp',          'Compression Pcomp',     'bar'],
      ['pmax',           'Firing Pmax',           'bar'],
      ['foPumpIndex',    'Fuel pump index',       'mm'],
      ['vit',            'VIT setting',           'mm'],
    ]},
  ];

  const setField = (k, v) => setEngine({ ...engine, [k]: v });
  const setPhys = (key, idx, val) => {
    const arr = [...engine.physical[key]];
    arr[idx] = val;
    setEngine({ ...engine, physical: { ...engine.physical, [key]: arr } });
  };
  const setSFOC = (idx, val) => {
    const arr = [...engine.sfoc_iso];
    arr[idx] = val;
    setEngine({ ...engine, sfoc_iso: arr });
  };
  const setLoadPoint = (idx, val) => {
    const arr = [...engine.loadPoints];
    arr[idx] = val;
    setEngine({ ...engine, loadPoints: arr });
  };
  // Switching engine type resets only the shop trial template (keeps name/role)
  const setType = (t) => setEngine(emptyShopTrialEntry(t));

  const validate = () => {
    if (!name.trim())          return 'Engine name is required (e.g. "Main Engine", "Aux Engine 1").';
    if (!engine.model)         return 'Model name is required.';
    if (!engine.mcrPower_kW)   return 'MCR power (kW) is required.';
    if (!engine.bore_m)        return 'Bore (m) is required.';
    if (!engine.stroke_m)      return 'Stroke (m) is required.';
    if (!engine.cylinders)     return 'Number of cylinders is required.';
    const pmaxFilled = engine.physical.pmax.filter(v => v != null).length;
    if (pmaxFilled === 0)      return 'At least one Pmax value must be filled in the load grid.';
    return null;
  };

  const save = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    // Replace nulls in physical/sfoc with 0 so charts behave
    const cleanedShopTrial = { ...engine, physical: {} };
    for (const [k, arr] of Object.entries(engine.physical)) {
      cleanedShopTrial.physical[k] = arr.map(v => v == null ? 0 : v);
    }
    cleanedShopTrial.sfoc_iso = engine.sfoc_iso.map(v => v == null ? 0 : v);
    cleanedShopTrial.id = engine.id || `vessel-engine-${Date.now()}`;
    onSave({
      id: initial?.id || `vessel-engine-${Date.now()}`,
      name: name.trim(),
      role,
      shopTrial: cleanedShopTrial,
    });
  };

  const nLoad = engine.loadPoints.length;
  const colTpl = `200px repeat(${nLoad}, 1fr) 60px`;

  return (
    <div className="min-h-screen" data-theme={darkMode ? 'dark' : 'light'} style={{ background: 'var(--bg-page)', color: 'var(--t1)' }}>
      <style>{THEME_STYLES}</style>
      <header style={{ borderBottom: "1px solid var(--br-ok)", background: "var(--bg-panel)", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ padding: "0 24px", height: "64px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Cpu className="w-5 h-5 text-slate-950" />
            </div>
            <div>
              <h1 className="text-base font-bold tracking-tight">{editingIndex === -1 ? 'Add engine to vessel' : `Edit engine ${(editingIndex ?? 0) + 1}`}</h1>
              <p className="text-xs text-slate-400">Engine identification + shop trial reference values</p>
            </div>
          </div>
          <button onClick={onCancel} style={{ padding:'4px 12px', fontSize:'12px', borderRadius:'6px', border:'1px solid var(--br-mid)', background:'var(--bg-surface)', color:'var(--t2)', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}>
            <ChevronLeft className="w-3 h-3" />Cancel
          </button>
        </div>
      </header>

      <div style={{ padding: "24px" }}>
        {/* Engine identity */}
        <Panel title="Engine identity (in this vessel's profile)" icon={Ship} className="mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Display name *">
              <TextIn val={name} onChange={setName} placeholder='e.g. "Main Engine", "Aux Engine 1"' />
            </Field>
            <Field label="Role *">
              <select value={role} onChange={(e) => setRole(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm">
                <option value="main">Main propulsion</option>
                <option value="auxiliary">Auxiliary engine</option>
              </select>
            </Field>
          </div>
        </Panel>

        {/* Shop trial — identification */}
        <Panel title="Engine identification (shop trial)" icon={Settings} className="mb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Engine type">
              <select value={engine.engineType} onChange={(e) => setType(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm">
                <option value="2-stroke">2-stroke (slow-speed main)</option>
                <option value="4-stroke">4-stroke (medium/high-speed)</option>
              </select>
            </Field>
            <Field label="Manufacturer"><TextIn val={engine.manufacturer} onChange={(v) => setField('manufacturer', v)} placeholder="MAN, WinGD, Wärtsilä…" /></Field>
            <Field label="Model"><TextIn val={engine.model} onChange={(v) => setField('model', v)} placeholder="6S60MC-C, 6L23/30H…" /></Field>
            <Field label="MCR power (kW)"><NumIn val={engine.mcrPower_kW} onChange={(v) => setField('mcrPower_kW', v)} /></Field>
            <Field label="Max RPM"><NumIn val={engine.maxRPM} onChange={(v) => setField('maxRPM', v)} /></Field>
            <Field label="Cylinders"><NumIn val={engine.cylinders} onChange={(v) => setField('cylinders', v)} step={1} /></Field>
            <Field label="Bore (m)"><NumIn val={engine.bore_m} onChange={(v) => setField('bore_m', v)} step={0.001} /></Field>
            <Field label="Stroke (m)"><NumIn val={engine.stroke_m} onChange={(v) => setField('stroke_m', v)} step={0.01} /></Field>
            <Field label="TC suction">
              <select value={engine.tcSuction} onChange={(e) => setField('tcSuction', parseInt(e.target.value, 10))}
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm">
                <option value={0}>0 — local</option>
                <option value={1}>1 — ambient</option>
              </select>
            </Field>
          </div>
        </Panel>

        {/* TC details (optional) */}
        <Panel title="Turbocharger (optional)" icon={Wind} className="mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="TC manufacturer"><TextIn val={engine.tcManufacturer} onChange={(v) => setField('tcManufacturer', v)} placeholder="ABB, MHI…" /></Field>
            <Field label="TC type"><TextIn val={engine.tcType} onChange={(v) => setField('tcType', v)} placeholder="TPL80 B12…" /></Field>
            <Field label="TC diameter (m)"><NumIn val={engine.tcDiameter} onChange={(v) => setField('tcDiameter', v)} step={0.001} /></Field>
            <Field label="TC slip factor"><NumIn val={engine.tcSlipFactor} onChange={(v) => setField('tcSlipFactor', v)} step={0.01} /></Field>
          </div>
        </Panel>

        {/* Load grid */}
        <Panel title="Load points (% MCR)" icon={Gauge} className="mb-4">
          <div className="text-[11px] text-slate-500 mb-2">
            {engine.engineType === '2-stroke'
              ? 'Default 2-stroke shop trial points: 10 / 25 / 50 / 75 / 88.3 / 100 % MCR.'
              : '4-stroke aux engine grid: typically 25 / 50 / 75 / 100 / 110 % MCR.'}
          </div>
          <div className="grid gap-1" style={{ gridTemplateColumns: colTpl }}>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider self-end pb-1">Load point</div>
            {engine.loadPoints.map((lp, i) => (
              <div key={i}><NumIn val={lp} onChange={(v) => setLoadPoint(i, v)} step={0.1} /></div>
            ))}
            <div className="text-[10px] text-slate-500 self-end pb-1 pl-1">%</div>
          </div>
        </Panel>

        {/* Physical conditions */}
        {PHYS_GROUPS.map(group => (
          <Panel key={group.title} title={group.title} icon={Activity} className="mb-3">
            <div className="grid gap-1" style={{ gridTemplateColumns: colTpl }}>
              {group.fields.map(([key, label, unit]) => (
                <React.Fragment key={key}>
                  <div className="text-xs text-slate-400 self-center">{label}</div>
                  {engine.loadPoints.map((_, i) => (
                    <NumIn key={i} val={engine.physical[key]?.[i]} onChange={(v) => setPhys(key, i, v)} step={0.1} />
                  ))}
                  <div className="text-[10px] text-slate-500 self-center pl-1">{unit}</div>
                </React.Fragment>
              ))}
            </div>
          </Panel>
        ))}

        {/* SFOC */}
        <Panel title="Specific fuel oil consumption (ISO)" icon={Flame} className="mb-4">
          <div className="grid gap-1" style={{ gridTemplateColumns: colTpl }}>
            <div className="text-xs text-slate-400 self-center">SFOC ISO</div>
            {engine.loadPoints.map((_, i) => (
              <NumIn key={i} val={engine.sfoc_iso?.[i]} onChange={(v) => setSFOC(i, v)} step={0.1} />
            ))}
            <div className="text-[10px] text-slate-500 self-center pl-1">g/kWh</div>
          </div>
        </Panel>

        {/* Notes */}
        <Panel title="Notes (optional)" icon={ClipboardList} className="mb-4">
          <textarea value={engine.notes || ''} onChange={(e) => setField('notes', e.target.value)} rows={2}
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs"
            placeholder="e.g. Reference workbook entry from MAN, dated 2018; thrust loss assumed 1%" />
        </Panel>

        {error && (
          <div className="mb-3 p-2 bg-red-900/30 border border-red-500/40 text-red-300 text-xs rounded flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />{error}
          </div>
        )}

        <div className="flex justify-between items-center pt-3 border-t border-slate-800">
          <button onClick={onCancel} style={{ padding:"4px 12px", fontSize:"12px", borderRadius:"6px", border:"1px solid var(--br-mid)", background:"var(--bg-surface)", color:"var(--t2)", cursor:"pointer" }}>Cancel</button>
          <button onClick={save} className="px-4 py-2 text-xs bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 rounded hover:bg-emerald-500/30 flex items-center gap-1.5">
            <Save className="w-3 h-3" />{editingIndex === -1 ? 'Add engine to vessel' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =================================================================
//  SHOP CURVE CHART
//  Shows raw shop-trial measurements (solid line) alongside the
//  ISO-corrected trend (dotted line) computed from the shop trial's
//  own ambient conditions at each load point.
//
//  Correction formulas are the same ones used in the ISO Correction
//  step — ported verbatim from the reference workbook.
//
//  SFOC is already ISO-corrected in the shop trial record, so only
//  one line is shown for that parameter.
//
//  Exhaust temperature at cylinder outlet has no direct ISO formula
//  in the reference — the turbine inlet correction is used as a proxy
//  (same exhaust gas stream, same driving variables) and labelled.
// =================================================================
function ShopCurveChart({ shopRef, field, label, color, useSFOC }) {
  const p = shopRef.physical;
  const mode = 'full'; // shop trial is always full-rated

  const data = shopRef.loadPoints.map((lp, i) => {
    const raw = useSFOC ? shopRef.sfoc_iso[i] : (p[field]?.[i] ?? null);

    // Ambient conditions recorded during shop trial at this load point
    const pa     = p.barometric?.[i]    ?? 1013;
    const pback  = p.turbBackPress?.[i] ?? 0;
    const tblw   = p.blowerTemp?.[i]    ?? 25;
    const tscav  = p.scavTemp?.[i]      ?? 40;
    const tcs    = shopRef.tcSuction    ?? 0;

    let iso = null;
    if (useSFOC) {
      // SFOC already ISO corrected — nothing to add
      iso = raw;
    } else if (field === 'pmax') {
      iso = raw != null ? correctFiringPressure(raw, pa, pback, tblw, tscav, tcs, lp, mode) : null;
    } else if (field === 'pcomp') {
      iso = raw != null ? correctCompPressure(raw, pa, pback, tblw, tscav, tcs, lp, mode) : null;
    } else if (field === 'exhTemp') {
      // No direct formula for per-cylinder Texh — use turbine inlet correction as proxy
      iso = raw != null ? correctTurbInletTemp(raw, pa, pback, tblw, tscav, tcs, lp, mode) : null;
    } else {
      iso = raw;
    }

    const delta = (raw != null && iso != null) ? iso - raw : null;

    return { load: lp, raw, iso, delta };
  });

  const isSFOC = useSFOC;
  const isExh  = field === 'exhTemp';

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label: load }) => {
    if (!active || !payload?.length) return null;
    const row = data.find(d => d.load === load);
    if (!row) return null;
    return (
      <div className="bg-slate-950 border border-slate-600 rounded p-2 text-[11px] shadow-xl">
        <div className="text-slate-300 font-semibold mb-1.5">{load} % MCR</div>
        <div className="flex items-center gap-2 mb-0.5">
          <span className="inline-block w-6 border-t-2" style={{ borderColor: color }} />
          <span className="text-slate-400">Shop measured:</span>
          <span className="font-mono text-slate-100">{row.raw != null ? row.raw.toFixed(2) : '—'}</span>
        </div>
        {!isSFOC && (
          <>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="inline-block w-6 border-t-2 border-dashed" style={{ borderColor: color }} />
              <span className="text-slate-400">ISO corrected:</span>
              <span className="font-mono text-cyan-300">{row.iso != null ? row.iso.toFixed(2) : '—'}</span>
            </div>
            {row.delta != null && (
              <div className="text-[10px] text-slate-500 mt-1 pt-1 border-t border-slate-800">
                Correction: <span className={`font-mono ${Math.abs(row.delta) < 0.01 ? 'text-slate-400' : row.delta > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {row.delta >= 0 ? '+' : ''}{row.delta.toFixed(2)}
                </span>
              </div>
            )}
            {isExh && (
              <div className="text-[10px] text-amber-400/80 mt-1 pt-1 border-t border-slate-800">
                ※ Turbine inlet correction used as proxy
              </div>
            )}
          </>
        )}
        {isSFOC && (
          <div className="text-[10px] text-emerald-400/80 mt-1">Already ISO corrected</div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-slate-300">{label}</div>
        {!isSFOC && (
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            <span className="flex items-center gap-1">
              <span className="inline-block w-4 border-t-2" style={{ borderColor: color }} />
              measured
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: color }} />
              ISO corrected
            </span>
          </div>
        )}
        {isSFOC && (
          <span className="text-[10px] text-emerald-400">ISO corrected</span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={data} margin={{ top: 5, right: 10, bottom: 20, left: 0 }}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
          <XAxis dataKey="load" type="number" domain={[0, 110]} stroke="#64748b"
            tick={{ fill: '#94a3b8', fontSize: 10 }}
            label={{ value: '% MCR', position: 'bottom', offset: -5, fill: '#64748b', fontSize: 10 }} />
          <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 10 }} />
          <Tooltip content={<CustomTooltip />} />
          {/* Solid line = raw shop trial measurement */}
          <Line type="monotone" dataKey="raw" stroke={color} dot={{ r: 3, fill: color }}
            strokeWidth={2} name="Measured" connectNulls />
          {/* Dotted line = ISO corrected — only when there's a correction to show */}
          {!isSFOC && (
            <Line type="monotone" dataKey="iso" stroke={color} dot={{ r: 2, fill: 'transparent', stroke: color }}
              strokeWidth={1.5} strokeDasharray="5 3" name="ISO corrected" connectNulls />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// =================================================================
//  STEP 5 — CONDITIONS
// =================================================================
// Responsive grid that watches its own width via ResizeObserver.
// This works correctly inside sidebar layouts where viewport width != panel width.
// Breakpoints: ≥950→5 cols, ≥760→4, ≥560→3, ≥360→2, else→1
function CondGrid({ children }) {
  const wrapRef = useRef(null);
  const [cols, setCols] = useState(5);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setCols(w >= 950 ? 4 : w >= 760 ? 3 : w >= 560 ? 2 : 1);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={wrapRef}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: '8px',
        alignItems: 'start',
      }}>
        {children}
      </div>
    </div>
  );
}

function ConditionsStep({ conditions, setConditions, cylInputs, setCylInputs, shopRef, testLoad, shopMode, isHistorical, onNext, onPrev }) {
  if (!conditions) return null;

  // When a value changes:
  //   • If it was FROM FILE → mark as MANUAL (crew override)
  //   • If it was null/empty → mark as MANUAL (new entry)
  //   • If it was CONSTANT → keep CONSTANT (crew shouldn't be overriding these, but allow it)
  const set = (key, v) => {
    if (isHistorical) return;
    const old = conditions[key];
    const newSource = (old?.source === 'CONSTANT') ? 'CONSTANT' : 'MANUAL';
    setConditions({ ...conditions, [key]: { value: v, source: v == null ? null : newSource } });
  };
  // Functional updater — each call chains off the latest state, so multiple
  // calls within the same event handler don't overwrite each other.
  const setCyl = (idx, key, v) => {
    if (isHistorical) return;
    setCylInputs(prev => prev.map(c => c.idx === idx ? { ...c, [key]: v } : c));
  };

  // Reference values at this load (ISO formula refs)
  const refs = shopRef && testLoad != null ? {
    blowerTemp:    refBlowerTemp(shopRef.tcSuction),
    scavTemp:      refScavTemp(testLoad, shopMode),
    turbBackPress: refTurbBackPress(testLoad),
  } : null;

  // Model values interpolated at the current test load — needed for the
  // model-dependent validation rules (ΔP, scav press, turb back press).
  const modelRefs = shopRef && testLoad != null ? {
    airFilterDP:     interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.airFilterDP),
    airCoolerDP:     interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.airCoolerDP),
    scavPress_bar:   interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.pscav),
    turbBackPress:   interpAtLoad(testLoad, shopRef.loadPoints, shopRef.physical.turbBackPress),
  } : {};

  // Run all validations upfront so CondInput just receives a result object
  const scavPressVal = conditions.scavPress_bar?.value;
  const valCtx = {
    modelAirFilterDP:   modelRefs.airFilterDP,
    modelAirCoolerDP:   modelRefs.airCoolerDP,
    modelScavPress_bar: modelRefs.scavPress_bar,
    modelTurbBackPress: testLoad != null ? refTurbBackPress(testLoad) : modelRefs.turbBackPress,
    scavPress_bar:      scavPressVal,
  };
  const validations = {};
  Object.keys(conditions).forEach(k => {
    validations[k] = validateCondition(k, conditions[k]?.value, valCtx);
  });

  // Count validations with problems for the banner
  const validationIssues = Object.values(validations).filter(v => v && v.status !== 'ok').length;

  // Fields required for ISO correction — warn if still empty
  const REQUIRED_FOR_ISO = [
    { key: 'blowerTemp_C',        label: 'Blower inlet temp' },
    { key: 'baroPress_mbar',      label: 'Barometric pressure' },
    { key: 'scavTemp_C',          label: 'Scavenge air temp' },
    { key: 'turbBackPress_mmH2O', label: 'Turbine back pressure' },
  ];
  const missing = REQUIRED_FOR_ISO.filter(f => conditions[f.key]?.value == null);
  const fromFileCount = Object.values(conditions).filter(c => c?.source === 'FROM FILE').length;
  const manualCount   = Object.values(conditions).filter(c => c?.source === 'MANUAL').length;
  const emptyCount    = Object.values(conditions).filter(c => c?.source == null || c?.source === '').length;

  return (
    <div>
      {isHistorical && <ReadOnlyBanner />}
      <SectionHeader icon={Wind} title="Operating conditions"
        subtitle="Values captured from the uploaded file are tagged FROM FILE.  Empty cells require manual entry."
        stepId="conditions" />

      {/* Status summary banner — compact inline row */}
      <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px', padding:'7px 12px', borderRadius:'6px', background:'var(--bg-surface)', border:'1px solid var(--br)', fontSize:'11px', flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'5px', color:'var(--c-info)' }}>
          <CheckCircle2 style={{ width:'13px', height:'13px', flexShrink:0 }} />
          <span style={{ fontWeight:600 }}>{fromFileCount}</span><span style={{ color:'var(--t3)' }}>from file</span>
        </div>
        <div style={{ width:'1px', height:'14px', background:'var(--br-mid)' }} />
        <div style={{ display:'flex', alignItems:'center', gap:'5px', color:'var(--c-warn)' }}>
          <Edit3 style={{ width:'13px', height:'13px', flexShrink:0 }} />
          <span style={{ fontWeight:600 }}>{manualCount}</span><span style={{ color:'var(--t3)' }}>manual</span>
        </div>
        <div style={{ width:'1px', height:'14px', background:'var(--br-mid)' }} />
        <div style={{ display:'flex', alignItems:'center', gap:'5px', color: emptyCount > 0 ? 'var(--c-err)' : 'var(--t3)' }}>
          <AlertCircle style={{ width:'13px', height:'13px', flexShrink:0 }} />
          <span style={{ fontWeight:600 }}>{emptyCount}</span><span style={{ color:'var(--t3)' }}>empty{emptyCount > 0 ? ' — enter manually' : ''}</span>
        </div>
      </div>

      {/* Advisory for ISO-critical missing fields */}
      {missing.length > 0 && (
        <div style={{ marginBottom:'16px', borderRadius:'8px', border:'1px solid var(--br-warn)', background:'var(--fill-warn)', padding:'12px' }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:'8px', marginBottom:'8px' }}>
            <AlertCircle style={{ width:'16px', height:'16px', color:'var(--c-warn)', flexShrink:0, marginTop:'2px' }} />
            <div>
              <div style={{ fontSize:'12px', fontWeight:600, color:'var(--c-warn)', marginBottom:'6px' }}>
                {missing.length} field{missing.length > 1 ? 's' : ''} required for ISO correction — please enter manually
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                {missing.map(f => (
                  <span key={f.key} style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px', background:'var(--card-man-bg)', border:'1px solid var(--card-man-br)', color:'var(--c-warn)', fontFamily: 'StevieSans,sans-serif' }}>
                    {f.label}
                  </span>
                ))}
              </div>
              <div style={{ fontSize:'11px', color:'var(--t2)', marginTop:'8px', lineHeight:'1.5' }}>
                These values are used directly in the ISO 3046 correction formulas. Without them the ISO correction step will produce reference-condition results only. Record the values from the engine room log or instruments at the time of measurement.
              </div>
            </div>
          </div>
        </div>
      )}

      <Panel title="Ambient & charge-air conditions" icon={Thermometer} className="mb-4">
        {validationIssues > 0 && (
          <div style={{ marginBottom:'12px', padding:'10px 12px', borderRadius:'6px', background:'var(--fill-warn)', border:'1px solid var(--br-warn)', display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color:'var(--c-warn)', fontWeight:500 }}>
            <AlertTriangle style={{ width:'14px', height:'14px', flexShrink:0 }} />
            {validationIssues} condition{validationIssues > 1 ? 's' : ''} outside expected range — see highlighted fields below
          </div>
        )}
        <CondGrid>
          <CondInput label="Blower inlet temp"     unit="°C"     cond={conditions.blowerTemp_C}        refVal={refs?.blowerTemp}    required validation={validations.blowerTemp_C}        onChange={(v) => set('blowerTemp_C', v)}        readOnly={isHistorical} />
          <CondInput label="Barometric pressure"   unit="mbar"   cond={conditions.baroPress_mbar}      refVal={1000}                required validation={validations.baroPress_mbar}      onChange={(v) => set('baroPress_mbar', v)}      readOnly={isHistorical} />
          <CondInput label="Air filter ΔP"         unit="mmH2O"  cond={conditions.airFilterDP_mmH2O}   refVal={modelRefs.airFilterDP != null ? `${modelRefs.airFilterDP.toFixed(0)} (model)` : null} validation={validations.airFilterDP_mmH2O}   onChange={(v) => set('airFilterDP_mmH2O', v)}  readOnly={isHistorical} />
          <CondInput label="Air cooler ΔP"         unit="mmH2O"  cond={conditions.airCoolerDP_mmH2O}   refVal={modelRefs.airCoolerDP != null ? `${modelRefs.airCoolerDP.toFixed(0)} (model)` : null} validation={validations.airCoolerDP_mmH2O}   onChange={(v) => set('airCoolerDP_mmH2O', v)}  readOnly={isHistorical} />
          <CondInput label="Scavenge air temp"     unit="°C"     cond={conditions.scavTemp_C}          refVal={refs?.scavTemp}      required validation={validations.scavTemp_C}          onChange={(v) => set('scavTemp_C', v)}          readOnly={isHistorical} />
          <CondInput label="Scavenge air pressure" unit="bar"    cond={conditions.scavPress_bar}        refVal={modelRefs.scavPress_bar != null ? `${modelRefs.scavPress_bar.toFixed(2)} (model)` : null} validation={validations.scavPress_bar}        onChange={(v) => set('scavPress_bar', v)}        readOnly={isHistorical} />
          <CondInput label="Exh receiver press"    unit="bar"    cond={conditions.exhRecPress_bar}      validation={validations.exhRecPress_bar}     onChange={(v) => set('exhRecPress_bar', v)}     readOnly={isHistorical} />
          <CondInput label="Turb back pressure"    unit="mmH2O"  cond={conditions.turbBackPress_mmH2O}  refVal={refs?.turbBackPress}  required validation={validations.turbBackPress_mmH2O} onChange={(v) => set('turbBackPress_mmH2O', v)} readOnly={isHistorical} />
          <CondInput label="Turb inlet temp"       unit="°C"     cond={conditions.turbInletTemp_C}      validation={validations.turbInletTemp_C}     onChange={(v) => set('turbInletTemp_C', v)}     readOnly={isHistorical} />
          <CondInput label="TC speed"              unit="RPM"    cond={conditions.tcRPM}                                                              onChange={(v) => set('tcRPM', v)}                readOnly={isHistorical} />
          <CondInput label="Shaft power"           unit="kW"     cond={conditions.shaftPower_kW}                                                      onChange={(v) => set('shaftPower_kW', v)}        readOnly={isHistorical} />
          <CondInput label="Engine RPM"            unit="rpm"    cond={conditions.rpm}                                                                onChange={(v) => set('rpm', v)}                  readOnly={isHistorical} />
          <CondInput label="Fuel LCV"              unit="kJ/kg"  cond={conditions.fuelLCV_kJkg}                                                       onChange={(v) => set('fuelLCV_kJkg', v)}         readOnly={isHistorical} />
          <CondInput label="Thrust loss"           unit="frac"   cond={conditions.thrustLoss}                                                          onChange={(v) => set('thrustLoss', v)} step={0.001} readOnly={isHistorical} />
        </CondGrid>
      </Panel>

      <Panel title="Per-cylinder measured values" icon={Cpu}>
        <PerCylTable cylInputs={cylInputs} setCyl={setCyl} setCylInputs={setCylInputs} readOnly={isHistorical} />
      </Panel>

      <NavRow onPrev={onPrev} onNext={onNext} canNext={true} />
    </div>
  );
}

// SOURCE_CONFIG drives visual treatment per data source.
// Uses CSS variable inline styles — not Tailwind classes —
// so they never get double-remapped through the theme system.
const SRC = {
  'FROM FILE': {
    bg: 'var(--card-file-bg)', br: 'var(--card-file-br)', dashed: false,
    labelC: 'var(--t1)', tagTone: 'cyan',
    inputBr: 'var(--br-info)', inputBg: 'var(--in-bg)',
  },
  'MANUAL': {
    bg: 'var(--card-man-bg)', br: 'var(--card-man-br)', dashed: false,
    labelC: 'var(--t1)', tagTone: 'amber',
    inputBr: 'var(--br-warn)', inputBg: 'var(--in-bg)',
  },
  'CONSTANT': {
    bg: 'var(--card-const-bg)', br: 'var(--card-const-br)', dashed: false,
    labelC: 'var(--t2)', tagTone: 'slate',
    inputBr: 'var(--br)', inputBg: 'var(--in-bg)',
  },
  null: {
    bg: 'var(--card-empty-bg)', br: 'var(--card-empty-br)', dashed: true,
    labelC: 'var(--t2)', tagTone: null,
    inputBr: 'var(--br-mid)', inputBg: 'var(--in-bg)',
  },
};

function CondInput({ label, unit, cond, refVal, required = false, validation = null, onChange, step = 0.1, readOnly = false }) {
  const src = cond?.source || null;
  const cfg = SRC[src] || SRC[null];
  const isEmpty = cond?.value == null;
  const val = validation && validation.status !== 'ok' ? validation : null;
  const vs  = val ? VAL_STYLE[val.status] : null;

  // Card border overrides to validation colour when flagged
  const cardBr = vs ? `1px solid ${vs.br}` : `${cfg.dashed ? '1.5px dashed' : '1px solid'} ${cfg.br}`;
  const cardBg = vs ? vs.bg : cfg.bg;

  return (
    <div style={{ background: cardBg, border: cardBr, borderRadius: '6px', padding: '6px 8px' }}>
      {/* Label row */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'4px', gap:'4px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'3px', minWidth:0 }}>
          <span style={{ fontSize:'9px', textTransform:'uppercase', letterSpacing:'0.06em', color: vs ? vs.c : cfg.labelC, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {label}
          </span>
          {required && isEmpty && (
            <span style={{ fontSize:'9px', fontWeight:700, color:'var(--c-err)', flexShrink:0 }}>*</span>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'3px', flexShrink:0 }}>
          {vs && (
            <span style={{ fontSize:'8px', fontWeight:700, padding:'1px 4px', borderRadius:'3px', background: vs.bg, border:`1px solid ${vs.br}`, color: vs.c, textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>
              {vs.label}
            </span>
          )}
          {src && <Tag tone={cfg.tagTone} small>{src}</Tag>}
        </div>
      </div>

      {/* Input row */}
      <div style={{ display:'flex', alignItems:'baseline', gap:'4px' }}>
        <input
          type="number"
          step={step}
          value={cond?.value ?? ''}
          placeholder={isEmpty ? 'Enter manually' : ''}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={(e) => { if (!readOnly) onChange(e.target.value === '' ? null : parseFloat(e.target.value)); }}
          style={{
            flex:1, minWidth:0,
            background: cfg.inputBg,
            color: isEmpty ? 'var(--t3)' : vs ? vs.c : 'var(--t1)',
            border: `1px ${cfg.dashed ? 'dashed' : 'solid'} ${vs ? vs.br : cfg.inputBr}`,
            borderRadius:'4px', padding:'3px 6px',
            fontSize:'12px', fontFamily: 'StevieSans,sans-serif', fontWeight: vs ? 600 : 400,
            cursor: readOnly ? 'not-allowed' : 'auto',
            opacity: readOnly ? 0.75 : 1,
          }}
        />
        <span style={{ fontSize:'9px', color:'var(--t3)', flexShrink:0 }}>{unit}</span>
      </div>

      {/* ISO / model reference */}
      {refVal != null && (
        <div style={{ marginTop:'3px', fontSize:'9px', color:'var(--t3)', display:'flex', gap:'3px' }}>
          <span>ref:</span>
          <span style={{ fontFamily: 'StevieSans,sans-serif', color:'var(--t2)', fontWeight:600 }}>
            {typeof refVal === 'number' ? refVal.toFixed(1) : refVal} {typeof refVal === 'number' ? unit : ''}
          </span>
        </div>
      )}

      {/* Validation finding + guidance */}
      {vs && val && (
        <div style={{ marginTop:'4px', paddingTop:'4px', borderTop:`1px solid ${vs.br}` }}>
          <div style={{ fontSize:'10px', color: vs.c, fontWeight:600, marginBottom:'2px', lineHeight:'1.3' }}>
            {val.message}
          </div>
          <div style={{ fontSize:'10px', color:'var(--t2)', lineHeight:'1.3' }}>
            {val.guidance}
          </div>
        </div>
      )}

      {/* Empty advisory — compact single line */}
      {isEmpty && !vs && (
        <div style={{ marginTop:'3px', fontSize:'9px', color:'var(--t4)', display:'flex', alignItems:'center', gap:'3px' }}>
          <AlertCircle style={{ width:'9px', height:'9px', flexShrink:0 }} />
          <span>Enter manually</span>
        </div>
      )}
    </div>
  );
}

// Per-cylinder table with source indicators per cell
function PerCylTable({ cylInputs, setCyl, setCylInputs, readOnly = false }) {
  // Columns where 0 is physically impossible on a running engine
  // and should be treated identically to null (not measured).
  // VIT = 0 is valid (no advance), so it is NOT in this set.
  const COLS = [
    { key: 'Pcomp_meas', label: 'Pcomp meas (bar)', step: 0.1,  noZero: true  },
    { key: 'Pmax_meas',  label: 'Pmax meas (bar)',  step: 0.1,  noZero: true  },
    { key: 'MIP',        label: 'MIP (bar)',         step: 0.01, noZero: true  },
    { key: 'Texh',       label: 'Texh (°C)',         step: 1,    noZero: true  },
    { key: 'fuelIndex',  label: 'Fuel index (mm)',   step: 0.1,  noZero: true  },
    { key: 'vit_mm',     label: 'VIT (mm)',          step: 0.1,  noZero: false },
  ];

  // A value is "effectively empty" when it is null,
  // OR when it is 0 in a column that cannot physically be 0.
  const isEmpty = (val, col) => val == null || (col.noZero && val === 0);

  // Column header advisory: show when every cylinder is effectively empty
  const allEmpty = (col) => cylInputs.every(c => isEmpty(c[col.key], col));

  // Which cylinders are missing critical data (Pcomp, Pmax, MIP)?
  // Used to highlight entire row.
  const CRITICAL = ['Pcomp_meas', 'Pmax_meas', 'MIP'];
  const cylMissingCritical = (c) =>
    CRITICAL.some(k => isEmpty(c[k], COLS.find(col => col.key === k)));

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-500 border-b border-slate-800">
              <th className="text-left py-2 px-2 w-10">Cyl</th>
              {COLS.map(col => (
                <th key={col.key} className="text-right py-2 px-2">
                  <div>{col.label}</div>
                  {allEmpty(col) && (
                    <div className="text-[9px] text-amber-400/80 font-normal normal-case mt-0.5">
                      ↑ enter manually
                    </div>
                  )}
                </th>
              ))}
              <th className="text-center py-2 px-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {cylInputs.map(c => {
              const critical = cylMissingCritical(c);
              return (
                <tr key={c.idx} className={`border-b border-slate-800/50 ${critical ? 'bg-amber-500/5' : ''}`}>
                  <td className="py-1.5 px-2 font-mono text-amber-400 font-semibold">{c.idx}</td>

                  {COLS.map(col => {
                    const rawVal   = c[col.key];
                    const empty    = isEmpty(rawVal, col);
                    const isZero   = rawVal === 0 && col.noZero;
                    const isManual = c[`${col.key}_src`] === 'MANUAL';
                    // Treat 0 as empty for display — show '' in input, not 0
                    const displayVal = empty ? '' : rawVal;
                    const fromFile = !empty && !isManual;

                    return (
                      <td key={col.key} className="py-1 px-2">
                        <div className="flex flex-col items-end gap-0.5">
                          <input
                            type="number"
                            step={col.step}
                            value={displayVal}
                            placeholder={isZero ? '0 → enter' : '—'}
                            readOnly={readOnly}
                            disabled={readOnly}
                            onChange={(e) => {
                              if (readOnly) return;
                              const v = e.target.value === '' ? null : parseFloat(e.target.value);
                              // Single atomic update — value + src in one setCylInputs call.
                              // This avoids the stale-closure bug where two sequential setCyl
                              // calls both read the old state and the second overwrites the first.
                              setCylInputs(prev => prev.map(row =>
                                row.idx === c.idx
                                  ? { ...row, [col.key]: v, [`${col.key}_src`]: v == null ? null : 'MANUAL' }
                                  : row
                              ));
                            }}
                            style={{ cursor: readOnly ? 'not-allowed' : 'auto', opacity: readOnly ? 0.75 : 1 }}
                            className={`w-20 rounded px-1.5 py-0.5 text-xs font-mono text-right transition-colors
                              ${empty
                                ? isZero
                                  ? 'bg-amber-950/30 border border-amber-500/40 text-amber-600 placeholder-amber-600/60'
                                  : 'bg-slate-950/60 border border-dashed border-slate-600/50 text-slate-600 placeholder-slate-700'
                                : isManual
                                ? 'bg-slate-950 border border-amber-500/40 text-amber-200'
                                : 'bg-slate-950 border border-cyan-500/20 text-slate-100'}
                            `}
                          />
                          {isZero && (
                            <span className="text-[8px] text-amber-500 uppercase tracking-wide">zero — enter</span>
                          )}
                          {!isZero && isManual && (
                            <span className="text-[8px] text-amber-400 uppercase tracking-wide">manual</span>
                          )}
                          {!isZero && fromFile && (
                            <span className="text-[8px] text-cyan-500/60 uppercase tracking-wide">file</span>
                          )}
                        </div>
                      </td>
                    );
                  })}

                  {/* Status column */}
                  <td className="py-1 px-2 text-center">
                    {critical
                      ? <Tag tone="amber" small>Incomplete</Tag>
                      : <Tag tone="emerald" small>OK</Tag>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend + advisory */}
      <div className="mt-3 space-y-1.5">
        <div className="flex items-center gap-4 text-[10px] text-slate-500">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-cyan-500/60 rounded" /> from file</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-amber-400 rounded" /> manual entry</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 border-t border-dashed border-slate-600" /> not available</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-amber-600/60 rounded" /> zero — invalid</span>
        </div>
        {cylInputs.some(c => cylMissingCritical(c)) && (
          <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-200">
            <AlertCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <span>
              <strong>Incomplete</strong> cylinders are missing Pcomp, Pmax, or MIP.  These will be excluded from Analysis and the tool will advise manual recording.  Enter the values from the engine room indicator card or PMI printout to enable full diagnostics.
            </span>
          </div>
        )}
      </div>
    </>
  );
}

// =================================================================
//  STEP 6 — ISO CORRECTION
// =================================================================
function ISOStep({ computed, conditions, shopRef, testLoad, shopMode, isHistorical, onNext, onPrev }) {
  if (!computed) return <div className="text-slate-400 text-sm">Provide all inputs first.</div>;
  const c = (k) => conditions[k]?.value;
  const Tblower_ref = refBlowerTemp(shopRef.tcSuction);
  const Tscav_ref   = refScavTemp(testLoad, shopMode);
  const Pback_ref   = refTurbBackPress(testLoad);

  const missingForISO = ['baroPress_mbar','turbBackPress_mmH2O','blowerTemp_C','scavTemp_C']
    .filter(k => c(k) == null);

  return (
    <div>
      {isHistorical && <ReadOnlyBanner />}
      <SectionHeader icon={ShieldCheck}
        title="ISO 3046 / shop-trial corrections"
        subtitle={shopRef ? `${getEngineLabel(shopRef)} · formulas from reference workbook` : 'Formulas reproduced from the reference workbook'}
        stepId="iso" />

      {missingForISO.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-200">
            <div className="font-semibold mb-1">ISO corrections are incomplete — {missingForISO.length} ambient value{missingForISO.length > 1 ? 's' : ''} missing</div>
            <div className="text-[11px] text-amber-300/80" style={{ color: 'var(--t2)' }}>
              Return to the Conditions step and enter: <span className="font-mono">{missingForISO.join(', ')}</span>.
              Per-cylinder Pcomp ISO and Pmax ISO will show as — until these are provided.
            </div>
          </div>
        </div>
      )}

      <Panel title="Reference conditions at this load" icon={Info} className="mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <RefBox label="Blower inlet temp ref" value={`${Tblower_ref} °C`} formula={`= IF(suction=1, 15, 25)`} />
          <RefBox label="Scavenge air temp ref" value={`${Tscav_ref.toFixed(2)} °C`} formula={shopMode === 'tc-cutout' ? `= 0.0011·load² + 29` : `= 11·(load/100)² + 29`} />
          <RefBox label="Turb back press ref"   value={`${Pback_ref.toFixed(1)} mmH₂O`} formula={`= 224·(load/100)² + 100·(load/100) − 24`} />
          <RefBox label="Barometric ref"        value="1000 mbar" formula="= constant" />
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Panel title="Engine-level corrections" icon={Settings}>
          <div className="space-y-2 text-xs">
            <CorrRow label="Pscav ISO"     measured={`${c('scavPress_bar')?.toFixed(2)} bar`}    iso={computed.isoCorrAmbient.pscav_iso?.toFixed(2)} unit="bar" />
            <CorrRow label="TC speed ISO"  measured={`${c('tcRPM') ?? '—'} RPM`}                  iso={computed.isoCorrAmbient.tc_speed_iso?.toFixed(0)} unit="RPM" />
            <CorrRow label="Turb inlet T ISO" measured={`${c('turbInletTemp_C') ?? '—'} °C`}       iso={computed.isoCorrAmbient.turb_inlet_iso?.toFixed(1)} unit="°C" />
            <CorrRow label="Pa (corrected)"   measured={`${c('baroPress_mbar')} mbar`}             iso={computed.isoCorrAmbient.pa_iso?.toFixed(1)} unit="mbar" />
          </div>
        </Panel>

        {/* Formula reference intentionally hidden from the UI — internal implementation detail */}
      </div>

      <Panel title="Per-cylinder ISO-corrected pressures" icon={BarChart3}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500 border-b border-slate-800">
                <th className="text-left py-2 px-2">Cyl</th>
                <th className="text-right py-2 px-2">Pcomp meas</th>
                <th className="text-right py-2 px-2">Pcomp ISO</th>
                <th className="text-right py-2 px-2">Pcomp ISO − Mean</th>
                <th className="text-right py-2 px-2">Pmax meas</th>
                <th className="text-right py-2 px-2">Pmax ISO</th>
                <th className="text-right py-2 px-2">Pmax ISO − Mean</th>
                <th className="text-right py-2 px-2">MIP</th>
                <th className="text-center py-2 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {computed.cyls.map(cyl => {
                const mPcomp = computed.means.Pcomp_iso || 0;
                const mPmax  = computed.means.Pmax_iso  || 0;
                const dPcomp = (cyl.Pcomp_iso ?? 0) - mPcomp;
                const dPmax  = (cyl.Pmax_iso  ?? 0) - mPmax;
                const flag = (Math.abs(dPcomp) > TOLERANCE.Pcomp_bar.warn) || (Math.abs(dPmax) > TOLERANCE.Pmax_bar.warn);
                return (
                  <tr key={cyl.idx} className={`border-b border-slate-800/50 ${flag ? 'bg-amber-500/5' : ''}`}>
                    <td className="py-1 px-2 font-mono text-amber-400">{cyl.idx}</td>
                    <td className="py-1 px-2 text-right font-mono">{cyl.Pcomp_meas?.toFixed(1)}</td>
                    <td className="py-1 px-2 text-right font-mono text-cyan-400">{cyl.Pcomp_iso?.toFixed(1)}</td>
                    <td className={`py-1 px-2 text-right font-mono ${Math.abs(dPcomp) > TOLERANCE.Pcomp_bar.alarm ? 'text-red-400' : Math.abs(dPcomp) > TOLERANCE.Pcomp_bar.warn ? 'text-amber-400' : 'text-slate-400'}`}>
                      {dPcomp >= 0 ? '+' : ''}{dPcomp.toFixed(1)}
                    </td>
                    <td className="py-1 px-2 text-right font-mono">{cyl.Pmax_meas?.toFixed(1)}</td>
                    <td className="py-1 px-2 text-right font-mono text-cyan-400">{cyl.Pmax_iso?.toFixed(1)}</td>
                    <td className={`py-1 px-2 text-right font-mono ${Math.abs(dPmax) > TOLERANCE.Pmax_bar.alarm ? 'text-red-400' : Math.abs(dPmax) > TOLERANCE.Pmax_bar.warn ? 'text-amber-400' : 'text-slate-400'}`}>
                      {dPmax >= 0 ? '+' : ''}{dPmax.toFixed(1)}
                    </td>
                    <td className="py-1 px-2 text-right font-mono">{cyl.MIP?.toFixed(2)}</td>
                    <td className="py-1 px-2 text-center">
                      {flag ? <Tag tone="amber" small>CHECK</Tag> : <Tag tone="emerald" small>OK</Tag>}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t border-slate-700 font-semibold">
                <td className="py-2 px-2 text-slate-400">Mean</td>
                <td className="py-2 px-2 text-right font-mono">{computed.means.Pcomp_meas?.toFixed(1)}</td>
                <td className="py-2 px-2 text-right font-mono text-cyan-400">{computed.means.Pcomp_iso?.toFixed(1)}</td>
                <td></td>
                <td className="py-2 px-2 text-right font-mono">{computed.means.Pmax_meas?.toFixed(1)}</td>
                <td className="py-2 px-2 text-right font-mono text-cyan-400">{computed.means.Pmax_iso?.toFixed(1)}</td>
                <td></td>
                <td className="py-2 px-2 text-right font-mono">{computed.means.MIP?.toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>

      <NavRow onPrev={onPrev} onNext={onNext} canNext={true} />
    </div>
  );
}

function RefBox({ label, value, formula }) {
  return (
    <div style={{ background:'var(--bg-surface)', border:'1px solid var(--br)', borderRadius:'6px', padding:'8px 10px' }}>
      <div style={{ fontSize:'9px', textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--t3)', fontWeight:600, marginBottom:'3px' }}>{label}</div>
      <div style={{ fontSize:'14px', fontFamily: 'StevieSans,sans-serif', fontWeight:700, color:'var(--accent)', marginBottom:'2px' }}>{value}</div>
      <div style={{ fontSize:'9px', fontFamily: 'StevieSans,sans-serif', color:'var(--t3)' }}>{formula}</div>
    </div>
  );
}
function CorrRow({ label, measured, iso, unit }) {
  return (
    <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', borderBottom:'1px solid var(--br)', padding:'7px 0' }}>
      <span style={{ color:'var(--t2)', fontSize:'13px' }}>{label}</span>
      <span style={{ fontFamily: 'StevieSans,sans-serif', fontSize:'13px' }}>
        <span style={{ color:'var(--t3)' }}>{measured}</span>
        <ChevronRight style={{ width:'12px', height:'12px', display:'inline', margin:'0 4px', color:'var(--br-mid)', verticalAlign:'middle' }} />
        <span style={{ color:'var(--accent)', fontWeight:700 }}>{iso != null ? `${iso} ${unit}` : '—'}</span>
      </span>
    </div>
  );
}

// =================================================================
//  STEP 7 — ANALYSIS
// =================================================================

function AnalysisStep({ computed, parsed, shopRef, testLoad, conditions, isHistorical, onNext, onPrev }) {
  console.log('Computed values:', computed);
  if (!computed) return <div style={{ color:'var(--t3)', fontSize:'14px' }}>Provide all inputs first.</div>;

  const TOL = getTolerance(computed.strokeType);
  const strokeLabel = `${computed.strokeType}-stroke`;

  
  // mechEff alert
  const mechEffVal = computed.mechEff;
  const mechEffRawVal = computed.mechEffRaw;
  const mechEffAlert = mechEffVal != null && mechEffVal < 89
    ? { status:'low',  msg:`${mechEffVal.toFixed(1)}% is below the 89% minimum — investigate friction losses (bearings, crosshead, rings) or verify shaft power measurement.` }
    : mechEffVal != null && mechEffVal > 100
    ? { status:'high', msg:`${mechEffVal.toFixed(1)}% is physically impossible — likely a mismatch between the shaft power condition and the MIP measurement condition.` }
    : null;

  return (
    <div>
      {isHistorical && <ReadOnlyBanner />}
      <SectionHeader icon={BarChart3}
        title="Performance analysis"
        subtitle={shopRef ? `${getReportTitle(shopRef)} — ${getEngineLabel(shopRef)} · cylinder balance · health · findings` : 'Cylinder balance · health · diagnostic findings'}
        stepId="analysis" />

      {/* Engine KPIs + Health gauge */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <HealthGauge score={computed.engineHealth} />
        <Panel title="Engine KPIs" icon={Gauge} className="lg:col-span-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <KPI label="Test load" value={`${testLoad?.toFixed(1)} %`} unit="MCR" />
            <KPI label="Indicated power" value={computed.indPow?.total?.toFixed(0) || '—'} unit="kW" />
            <KPI label="Mech. efficiency"
              value={mechEffVal != null ? `${mechEffVal.toFixed(1)} %` : mechEffRawVal != null ? '—' : '—'}
              unit=""
              alert={mechEffAlert}
              note={mechEffVal == null && computed.indPow?.total != null && conditions?.shaftPower_kW?.value != null
                ? 'Check shaft power vs MIP condition match' : null}
            />
            <KPI label="Pmax spread" value={(() => {
              const v = computed.cyls.filter(c => !c.dataIncomplete).map(c => c.Pmax_iso).filter(v => v != null);
              return v.length ? (Math.max(...v) - Math.min(...v)).toFixed(1) : '—';
            })()} unit="bar" />
          </div>
          {mechEffAlert && (
            <div style={{ marginTop:'10px', padding:'8px 12px', borderRadius:'6px',
              background: mechEffAlert.status === 'low' ? 'var(--fill-warn)' : 'var(--fill-err)',
              border: `1px solid ${mechEffAlert.status === 'low' ? 'var(--br-warn)' : 'var(--br-err)'}`,
              fontSize:'11px', color: mechEffAlert.status === 'low' ? 'var(--c-warn)' : 'var(--c-err)',
              display:'flex', gap:'8px', alignItems:'flex-start' }}>
              <AlertCircle style={{ width:'14px', height:'14px', flexShrink:0, marginTop:'1px' }} />
              <span><strong>Mechanical efficiency alert:</strong> {mechEffAlert.msg}</span>
            </div>
          )}
        </Panel>
      </div>

      {/* Cylinder health grid */}
      <Panel title="Cylinder health" icon={ShieldCheck} className="mb-4">
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${computed.cyls.length}, minmax(0, 1fr))` }}>
          {computed.cyls.map(c => {
            const incomplete = c.dataIncomplete;
            const realFindings = (c.insights || []).filter(i => i.severity !== 'incomplete');
            const healthColor = !incomplete && c.health > 80 ? 'var(--c-ok)' : !incomplete && c.health > 50 ? 'var(--c-warn)' : 'var(--c-err)';
            return (
              <div key={c.idx} style={{ borderRadius:'6px', padding:'8px', textAlign:'center',
                background: incomplete ? 'var(--bg-surface)' : 'var(--bg-panel)',
                border: `1px solid ${incomplete ? 'var(--br-mid)' : 'var(--br)'}` }}>
                <div style={{ fontSize:'10px', color:'var(--t3)', textTransform:'uppercase', letterSpacing:'0.06em' }}>CYL {c.idx}</div>
                {incomplete ? (
                  <>
                    <div style={{ fontSize:'20px', fontFamily: 'StevieSans,sans-serif', fontWeight:700, marginTop:'4px', color:'var(--t4)' }}>—</div>
                    <div style={{ fontSize:'10px', color:'var(--c-warn)', marginTop:'2px' }}>data missing</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize:'24px', fontFamily: 'StevieSans,sans-serif', fontWeight:700, marginTop:'4px', color: healthColor }}>{c.health}</div>
                    <div style={{ fontSize:'10px', color:'var(--t3)', marginTop:'2px' }}>{realFindings.length} {realFindings.length === 1 ? 'finding' : 'findings'}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {computed.cyls.some(c => c.dataIncomplete) && (
          <div style={{ marginTop:'12px', paddingTop:'12px', borderTop:'1px solid var(--br)', display:'flex', alignItems:'center', gap:'8px', fontSize:'11px', color:'var(--t3)' }}>
            <span style={{ display:'inline-block', width:'16px', height:'16px', borderRadius:'4px', background:'var(--bg-surface)', border:'1px solid var(--br-mid)', textAlign:'center', fontFamily: 'StevieSans,sans-serif', color:'var(--t4)', lineHeight:'16px', fontSize:'10px' }}>—</span>
            Cylinders showing — are excluded from the mean and scored as unknown. Enter missing values in the Conditions step to restore full diagnostics.
          </div>
        )}
      </Panel>

      {/* Deviation charts — stroke-type-aware tolerances */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'10px', marginBottom:'16px' }}>
        <DeviationChart cyls={computed.cyls.filter(c => !c.dataIncomplete)} field="dPmax"    mean={0} title={`ΔPmax ISO vs Mean (bar) — limit ±${TOL.Pmax_bar.warn} bar (${strokeLabel})`}  tol={TOL.Pmax_bar}  unit="bar" />
        <DeviationChart cyls={computed.cyls.filter(c => !c.dataIncomplete)} field="dPcomp"   mean={0} title={`ΔPcomp ISO vs Mean (bar) — limit ±${TOL.Pcomp_bar.warn} bar (${strokeLabel})`} tol={TOL.Pcomp_bar} unit="bar" />
        <DeviationChart cyls={computed.cyls.filter(c => !c.dataIncomplete)} field="dMIP_bar" mean={0} title={`ΔMIP vs Mean (bar) — limit ±${TOL.MIP_bar.warn} bar (${strokeLabel})`}          tol={TOL.MIP_bar}   unit="bar" />
        <DeviationChart cyls={computed.cyls.filter(c => !c.dataIncomplete)} field="dTexh"    mean={0} title="ΔTexh vs Mean (K) — warn >±20 K, alarm >±50 K"                                    tol={TOL.Texh_K}    unit="K"   />
      </div>

      {/* Interactive indicator diagram */}
      {parsed?.data?.curves && parsed.data.curves.length > 0 && (
        <IndicatorDiagram curves={parsed.data.curves} meta={parsed.data.meta} />
      )}

      {/* Health score methodology */}
      <Panel title="Health score methodology" icon={Info} className="mb-4">
        <div style={{ fontSize:'12px', color:'var(--t2)', lineHeight:'1.6' }}>
          <div style={{ fontWeight:600, color:'var(--t1)', marginBottom:'6px' }}>Cylinder health (0–100)</div>
          <p style={{ marginBottom:'8px' }}>Each cylinder starts at 100 points. Diagnostic findings deduct points based on severity:</p>
          <div style={{ display:'flex', gap:'16px', marginBottom:'10px' }}>
            <div style={{ padding:'6px 10px', borderRadius:'5px', background:'var(--fill-warn)', border:'1px solid var(--br-warn)', fontSize:'11px', color:'var(--c-warn)' }}>
              <strong>WARNING</strong> → −10 points
            </div>
            <div style={{ padding:'6px 10px', borderRadius:'5px', background:'var(--fill-err)', border:'1px solid var(--br-err)', fontSize:'11px', color:'var(--c-err)' }}>
              <strong>CRITICAL</strong> → −25 points
            </div>
          </div>
          <p style={{ marginBottom:'8px' }}>Findings are generated when deviations from the fleet mean cross the following limits:</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', marginBottom:'10px', fontSize:'11px' }}>
            {[
              ['ΔPmax ISO', `±${TOL.Pmax_bar.warn} bar (${strokeLabel})`],
              ['ΔPcomp ISO', `±${TOL.Pcomp_bar.warn} bar (${strokeLabel})`],
              ['ΔMIP', `±${TOL.MIP_bar.warn} bar absolute (${strokeLabel})`],
              ['ΔTexh', `warning >±20 K · alarm >±50 K`],
              ['Fuel index', `warning >±${getTolerance(2).Index_mm.warn} mm · alarm >±${getTolerance(2).Index_mm.alarm} mm`],
              ['Mech. efficiency', 'alert when <89% or >100%'],
            ].map(([k, v]) => (
              <div key={k} style={{ padding:'4px 8px', borderRadius:'4px', background:'var(--bg-surface)', border:'1px solid var(--br)' }}>
                <span style={{ fontWeight:600, color:'var(--t1)' }}>{k}:</span> <span style={{ color:'var(--t3)' }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ fontWeight:600, color:'var(--t1)', marginBottom:'4px' }}>Engine health (0–100)</div>
          <p>Simple arithmetic mean of all <em>complete</em> cylinder health scores. A cylinder is "complete" when Pcomp, Pmax, and MIP are all present. Cylinders with missing data are excluded from both the mean calculation and the engine health score.</p>
        </div>
      </Panel>

      {/* Diagnostic findings */}
      <Panel title="Diagnostic findings" icon={AlertCircle}>
        {(() => {
          const incompletes = computed.cyls.filter(c => c.dataIncomplete);
          const realFindings = computed.cyls.flatMap(c =>
            (c.insights || []).filter(i => i.severity !== 'incomplete').map((ins, j) => ({ cyl: c.idx, ins, key: `${c.idx}-${j}` }))
          );
          const noRealFindings = realFindings.length === 0;
          return (
            <>
              {incompletes.length > 0 && (
                <div className="mb-4">
                  <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--t3)', marginBottom:'8px', display:'flex', alignItems:'center', gap:'6px' }}>
                    <AlertCircle style={{ width:'12px', height:'12px', color:'var(--c-warn)' }} />
                    {incompletes.length} cylinder{incompletes.length > 1 ? 's' : ''} excluded — data not recorded
                  </div>
                  <div className="space-y-2">
                    {incompletes.map(c => <IncompleteCard key={c.idx} cylIdx={c.idx} insight={c.insights[0]} />)}
                  </div>
                  {realFindings.length > 0 && <div style={{ borderTop:'1px solid var(--br)', margin:'16px 0' }} />}
                </div>
              )}
              {noRealFindings && incompletes.length === 0 && (
                <div style={{ textAlign:'center', padding:'32px 0', color:'var(--c-ok)' }}>
                  <CheckCircle2 style={{ width:'40px', height:'40px', margin:'0 auto 8px' }} />
                  <div style={{ fontSize:'14px' }}>All cylinders within tolerance — no findings.</div>
                </div>
              )}
              {noRealFindings && incompletes.length > 0 && (
                <div style={{ textAlign:'center', padding:'16px 0', color:'var(--t3)', fontSize:'12px' }}>No diagnostic findings for cylinders with complete data.</div>
              )}
              {realFindings.length > 0 && (
                <div className="space-y-3">
                  {realFindings.map(({ cyl, ins, key }) => <FindingCard key={key} cylIdx={cyl} ins={ins} />)}
                </div>
              )}
            </>
          );
        })()}
      </Panel>

      <NavRow onPrev={onPrev} onNext={onNext} canNext={true} />
    </div>
  );
}

// ── Interactive Indicator Diagram ─────────────────────────────────────────────
// Cylinder toggle buttons let the user view individual cylinders or compare
// any combination simultaneously.
function IndicatorDiagram({ curves, meta }) {
  const n = meta.cylinders || 0;
  const cycleLabel = meta.strokeType === 4 ? '720° four-stroke cycle' : '360° two-stroke cycle';

  // Distinct colours per cylinder — brand-palette-safe, readable in both modes
  const CYL_COLORS = [
    '#275A46', '#A4C681', '#4D7298', '#CB5E64', '#7B73B1',
    '#EBE89F', '#B2D5F1', '#265858', '#d4845a', '#6aab9c',
  ];

  // Default: all cylinders selected
  const [selected, setSelected] = useState(() => Array.from({ length: n }, (_, i) => i));

  const toggleCyl = (i) => {
    setSelected(prev =>
      prev.includes(i)
        ? prev.length > 1 ? prev.filter(x => x !== i) : prev   // keep at least one selected
        : [...prev, i].sort((a, b) => a - b)
    );
  };
  const selectAll  = () => setSelected(Array.from({ length: n }, (_, i) => i));
  const selectOnly = (i) => setSelected([i]);

  // Down-sample to ~360 points for performance
  const step = Math.max(1, Math.floor(curves.length / 360));
  const sampled = curves.filter((_, i) => i % step === 0);
  const angleMin = Math.min(...curves.map(c => c.angle));
  const angleMax = Math.max(...curves.map(c => c.angle));

  return (
    <Panel title={`Indicator diagram — ${cycleLabel}`} icon={Activity} className="mb-4">
      {/* Cylinder selector */}
      <div style={{ marginBottom:'12px' }}>
        <div style={{ fontSize:'11px', color:'var(--t3)', marginBottom:'6px', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>
          Select cylinders to display
        </div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', alignItems:'center' }}>
          {Array.from({ length: n }, (_, i) => {
            const active = selected.includes(i);
            const col = CYL_COLORS[i % CYL_COLORS.length];
            return (
              <button
                key={i}
                onClick={() => toggleCyl(i)}
                onDoubleClick={() => selectOnly(i)}
                title={`Click to toggle · Double-click to isolate Cyl ${i + 1}`}
                style={{
                  padding:'4px 10px', borderRadius:'5px', fontSize:'12px', fontWeight:600,
                  cursor:'pointer', transition:'all 0.15s',
                  background: active ? col : 'var(--bg-surface)',
                  color: active ? '#fff' : 'var(--t3)',
                  border: `2px solid ${col}`,
                  opacity: active ? 1 : 0.45,
                }}
              >
                Cyl {i + 1}
              </button>
            );
          })}
          <div style={{ width:'1px', height:'20px', background:'var(--br-mid)', margin:'0 4px' }} />
          <button onClick={selectAll}
            style={{ padding:'4px 10px', borderRadius:'5px', fontSize:'11px', cursor:'pointer', background:'var(--fill-ok)', border:'1px solid var(--br-ok)', color:'var(--c-ok)', fontWeight:500 }}>
            All
          </button>
          <span style={{ fontSize:'10px', color:'var(--t4)' }}>Double-click a cylinder to isolate it</span>
        </div>
      </div>

      {/* Selected cylinder legend */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'8px' }}>
        {selected.map(i => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'11px', color:'var(--t2)' }}>
            <span style={{ display:'inline-block', width:'16px', height:'3px', borderRadius:'2px', background: CYL_COLORS[i % CYL_COLORS.length] }} />
            Cyl {i + 1}
          </div>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={sampled} margin={{ top: 5, right: 10, bottom: 25, left: 10 }}>
          <CartesianGrid stroke="var(--br)" strokeDasharray="3 3" />
          <XAxis dataKey="angle" type="number" domain={[angleMin, angleMax]}
            stroke="var(--t4)" tick={{ fill:'var(--t3)', fontSize:10 }}
            label={{ value:'Crank angle (° relative to TDC)', position:'bottom', offset:-5, fill:'var(--t3)', fontSize:10 }} />
          <YAxis stroke="var(--t4)" tick={{ fill:'var(--t3)', fontSize:10 }}
            label={{ value:'Pressure (bar)', angle:-90, position:'insideLeft', fill:'var(--t3)', fontSize:10 }} />
          <Tooltip
            contentStyle={{ background:'var(--bg-panel)', border:'1px solid var(--br)', fontSize:11, color:'var(--t1)' }}
            formatter={(v, name) => [`${v?.toFixed(1)} bar`, name]}
          />
          <ReferenceLine x={0} stroke="var(--t4)" strokeDasharray="3 3"
            label={{ value:'TDC', position:'top', fill:'var(--t3)', fontSize:10 }} />
          {selected.map(i => (
            <Line key={i}
              type="monotone"
              dataKey={(d) => d.press?.[i] ?? null}
              name={`Cyl ${i + 1}`}
              stroke={CYL_COLORS[i % CYL_COLORS.length]}
              dot={false}
              strokeWidth={selected.length === 1 ? 2 : 1.5}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      <div style={{ marginTop:'8px', fontSize:'10px', color:'var(--t4)', textAlign:'center' }}>
        Showing {selected.length} of {n} cylinder{n !== 1 ? 's' : ''}.
        {selected.length < n && ` ${n - selected.length} hidden — click the buttons above to add them.`}
      </div>
    </Panel>
  );
}

// ── Per-Cylinder Indicator Diagram Cards (Report tab section 3c) ─────────────
// Renders one chart card per cylinder — no click interaction needed.
function IndicatorDiagramPerCylinder({ curves, meta }) {
  const n = meta.cylinders || 0;
  const cycleLabel = meta.strokeType === 4 ? '720° four-stroke cycle' : '360° two-stroke cycle';
  const CYL_COLORS = [
    '#275A46', '#A4C681', '#4D7298', '#CB5E64', '#7B73B1',
    '#EBE89F', '#B2D5F1', '#265858', '#d4845a', '#6aab9c',
  ];

  const step = Math.max(1, Math.floor(curves.length / 360));
  const sampled = curves.filter((_, i) => i % step === 0);
  const angleMin = Math.min(...curves.map(c => c.angle));
  const angleMax = Math.max(...curves.map(c => c.angle));

  if (n === 0) return null;

  // Build rows of 2 cylinders each; each row is one indivisible PDF block
  const rows = [];
  for (let i = 0; i < n; i += 2) {
    rows.push([i, i + 1 < n ? i + 1 : null]);
  }

  // Helper to render a single cylinder card
  const CylCard = ({ i }) => {
    const col = CYL_COLORS[i % CYL_COLORS.length];
    return (
      <div style={{
        position: 'relative',
        background: 'var(--bg-panel)',
        border: '1px solid var(--br)',
        borderRadius: '8px',
        padding: '12px 16px 12px 16px',
        minWidth: 0,
        overflow: 'visible',
        boxSizing: 'border-box',
        alignSelf: 'start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: col, flexShrink: 0 }} />
          <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--t1)', fontFamily: 'StevieSans,sans-serif', whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'ellipsis' }}>
            Cylinder {i + 1} — {cycleLabel}
          </span>
        </div>
        {/* Reduced height to minimise blank space */}
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sampled} margin={{ top: 8, right: 20, bottom: 44, left: 20 }}>
              <CartesianGrid stroke="var(--br)" strokeDasharray="3 3" />
              <XAxis dataKey="angle" type="number" domain={[angleMin, angleMax]}
                stroke="#888" tick={{ fill: '#555', fontSize: 9 }}
                label={{ value: 'Crank angle (°)', position: 'insideBottom', offset: -30, fill: '#333', fontSize: 10, fontWeight: 500 }} />
              <YAxis stroke="#888" tick={{ fill: '#555', fontSize: 9 }} width={52}
                label={{ value: 'Press (bar)', angle: -90, position: 'insideLeft', offset: 0, dy: 40, fill: '#333', fontSize: 10, fontWeight: 500 }} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-panel)', border: '1px solid var(--br)', fontSize: 10, color: 'var(--t1)' }}
                formatter={(v) => [`${v?.toFixed(1)} bar`]}
              />
              <ReferenceLine x={0} stroke="#888" strokeDasharray="3 3"
                label={{ value: 'TDC', position: 'top', fill: '#333', fontSize: 9 }} />
              <Line
                type="monotone"
                dataKey={(d) => d.press?.[i] ?? null}
                name={`Cyl ${i + 1}`}
                stroke={col}
                dot={false}
                strokeWidth={2}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {rows.map((pair, rowIdx) => {
        const hasBoth = pair[1] !== null;
        return (
          <div
            key={rowIdx}
            data-pdf-block="cyl-card"
            style={{
              display: 'grid',
              // If only one cylinder in this row, constrain it to half-width so no blank space
              gridTemplateColumns: hasBoth ? 'repeat(2, 1fr)' : '1fr 1fr',
              gap: '8px',
              alignItems: 'start',
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
              WebkitColumnBreakInside: 'avoid',
            }}
          >
            {hasBoth ? (
              <>
                <CylCard i={pair[0]} />
                <CylCard i={pair[1]} />
              </>
            ) : (
              // Lone last cylinder: render in left half only, right stays empty (no card)
              <>
                <CylCard i={pair[0]} />
                <div /> {/* empty right cell — no visual gap/card */}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function KPI({ label, value, unit, note, alert }) {
  return (
    <div style={{ background:'var(--bg-surface)', border:'1px solid var(--br)', borderRadius:'6px', padding:'12px' }}>
      <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--t3)', fontWeight:600, marginBottom:'4px' }}>{label}</div>
      <div style={{ fontSize:'16px', fontFamily: 'StevieSans,sans-serif', fontWeight:700, color:'var(--t1)' }}>
        {value} <span style={{ fontSize:'11px', color:'var(--t3)', fontWeight:400 }}>{unit}</span>
      </div>
      {note && (
        <div style={{ fontSize:'10px', color:'var(--c-warn)', marginTop:'4px', lineHeight:'1.3' }}>{note}</div>
      )}
    </div>
  );
}
function HealthGauge({ score }) {
  const col = score > 80 ? '#2a8a5a' : score > 50 ? '#8a7000' : '#a02030';
  const data = [{ name: 'h', value: score, fill: col }];
  return (
    <Panel title="Engine health" icon={ShieldCheck}>
      <ResponsiveContainer width="100%" height={180}>
        <RadialBarChart cx="50%" cy="50%" innerRadius="65%" outerRadius="100%" data={data} startAngle={210} endAngle={-30}>
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar background={{ fill: 'var(--bg-surface)' }} dataKey="value" cornerRadius={4} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="text-center -mt-24 mb-12 pointer-events-none">
        <div style={{ fontSize:'36px', fontFamily: 'StevieSans,sans-serif', fontWeight:700, color: col }}>{score}</div>
        <div style={{ fontSize:'10px', color:'var(--t3)' }}>/ 100</div>
      </div>
    </Panel>
  );
}
function DeviationChart({ cyls, field, title, tol, unit }) {
  // Keep null as null — Recharts skips null bars rather than drawing zero-height ones.
  // || 0 was the old code and killed visibility of any cylinder whose ISO
  // correction hadn't run yet (all deviations came back null → rendered as 0).
  const data = cyls.map(c => {
    const raw = c[field];
    const val = (raw === null || raw === undefined) ? null : raw;
    return { name: `Cyl ${c.idx}`, val };
  });

  const hasAnyData = data.some(d => d.val !== null);

  const barFill = (v) => {
    if (v === null) return '#cccccc';
    return Math.abs(v) > tol.alarm ? '#a02030'
      : Math.abs(v) > tol.warn ? '#8a7000'
      : '#2a7a50';
  };

  return (
    <div style={{ background:'var(--bg-panel)', border:'1px solid var(--br)', borderRadius:'8px', padding:'12px', minWidth:0, overflow:'hidden' }}>
      <div style={{ fontSize:'11px', fontWeight:600, color:'var(--t1)', marginBottom:'8px', fontFamily:'StevieSans,sans-serif', lineHeight:'1.3' }}>{title}</div>
      {!hasAnyData ? (
        <div style={{ height:'160px', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'6px' }}>
          <div style={{ fontSize:'11px', color:'var(--t3)', textAlign:'center' }}>No cylinder data available</div>
          <div style={{ fontSize:'10px', color:'var(--t4)', textAlign:'center' }}>Enter per-cylinder values in the Conditions step</div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <ComposedChart data={data} barCategoryGap="15%" barSize={28}>
            <CartesianGrid stroke="var(--br)" strokeDasharray="3 3" />
            <XAxis dataKey="name" stroke="var(--t4)" tick={{ fill:'var(--t3)', fontSize:10 }} />
            <YAxis stroke="var(--t4)" tick={{ fill:'var(--t3)', fontSize:10 }} unit={unit} />
            <Tooltip
              contentStyle={{ background:'var(--bg-panel)', border:'1px solid var(--br)', fontSize:11, color:'var(--t1)' }}
              formatter={(value) => value === null ? ['—', 'Deviation'] : [`${value >= 0 ? '+' : ''}${Number(value).toFixed(2)} ${unit}`, 'Deviation']}
            />
            <ReferenceArea y1={-tol.warn} y2={tol.warn} fill="#2a7a50" fillOpacity={0.06} />
            <ReferenceLine y={tol.warn}   stroke="#8a7000" strokeDasharray="3 3" strokeWidth={1.2} />
            <ReferenceLine y={-tol.warn}  stroke="#8a7000" strokeDasharray="3 3" strokeWidth={1.2} />
            <ReferenceLine y={tol.alarm}  stroke="#a02030" strokeDasharray="3 3" strokeWidth={1.2} />
            <ReferenceLine y={-tol.alarm} stroke="#a02030" strokeDasharray="3 3" strokeWidth={1.2} />
            <ReferenceLine y={0}          stroke="var(--br-mid)" strokeWidth={1} />
            <Bar dataKey="val" radius={[2,2,0,0]} minPointSize={3} isAnimationActive={false}>
              {data.map((d, i) => <Cell key={i} fill={barFill(d.val)} fillOpacity={d.val === null ? 0.3 : 1} />)}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
// Card shown in Analysis for cylinders with missing data (not a diagnostic finding)
function IncompleteCard({ cylIdx, insight }) {
  return (
    <div style={{ borderRadius:'6px', border:'1px solid var(--br-mid)', background:'var(--bg-surface)', padding:'12px', display:'flex', alignItems:'flex-start', gap:'12px' }}>
      <span style={{ width:'36px', height:'36px', borderRadius:'50%', background:'var(--bg-panel)', border:'1px solid var(--br-mid)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily: 'StevieSans,sans-serif', fontSize:'13px', fontWeight:700, color:'var(--t3)', flexShrink:0 }}>
        {cylIdx}
      </span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
          <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px', background:'var(--fill-warn)', border:'1px solid var(--br-warn)', color:'var(--c-warn)', textTransform:'uppercase', letterSpacing:'0.05em', fontWeight:600 }}>
            data missing
          </span>
          <span style={{ fontSize:'13px', fontWeight:600, color:'var(--t1)' }}>{insight?.title}</span>
        </div>
        <div style={{ fontSize:'12px', color:'var(--t2)', marginBottom:'8px' }}>{insight?.finding}</div>
        <div>
          <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--t3)', marginBottom:'4px', fontWeight:600 }}>How to fix</div>
          <ol style={{ fontSize:'12px', color:'var(--t2)', margin:0, paddingLeft:'16px', lineHeight:'1.6' }}>
            {(insight?.actions || []).map((a, i) => <li key={i}>{a}</li>)}
          </ol>
        </div>
      </div>
    </div>
  );
}

function FindingCard({ cylIdx, ins }) {
  const isCrit = ins.severity === 'critical';
  const cardBg  = isCrit ? 'var(--fill-err)' : 'var(--fill-warn)';
  const cardBr  = isCrit ? 'var(--br-err)'   : 'var(--br-warn)';
  const headC   = isCrit ? 'var(--c-err)'    : 'var(--c-warn)';
  const badgeBr = isCrit ? 'var(--br-err)'   : 'var(--br-warn)';
  return (
    <div style={{ borderRadius:'6px', border:`1px solid ${cardBr}`, background: cardBg, padding:'12px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:'12px' }}>
        {/* Cylinder number badge */}
        <div style={{ flexShrink:0 }}>
          <span style={{
            display:'flex', width:'36px', height:'36px', borderRadius:'50%',
            background:'var(--bg-panel)', border:`1px solid ${badgeBr}`,
            alignItems:'center', justifyContent:'center',
            fontFamily: 'StevieSans,sans-serif', fontSize:'14px', fontWeight:700, color: headC,
          }}>{cylIdx}</span>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
            <Tag tone={isCrit ? 'red' : 'amber'} small>{ins.severity}</Tag>
            <h4 style={{ fontSize:'13px', fontWeight:600, color: headC, margin:0 }}>{ins.title}</h4>
          </div>
          <div style={{ fontSize:'12px', color:'var(--t2)', marginBottom:'10px', lineHeight:'1.5' }}>{ins.finding}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
            <div>
              <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--t3)', fontWeight:600, marginBottom:'4px' }}>Possible causes</div>
              <ul style={{ margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:'3px' }}>
                {ins.causes.map((c, i) => (
                  <li key={i} style={{ display:'flex', gap:'6px', fontSize:'12px', color:'var(--t2)' }}>
                    <span style={{ color:'var(--accent-lt)', flexShrink:0 }}>·</span>{c}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--t3)', fontWeight:600, marginBottom:'4px' }}>Recommended actions</div>
              <ul style={{ margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:'3px' }}>
                {ins.actions.map((a, i) => (
                  <li key={i} style={{ display:'flex', gap:'6px', fontSize:'12px', color:'var(--t2)', alignItems:'flex-start' }}>
                    <Wrench style={{ width:'12px', height:'12px', color:'var(--accent)', marginTop:'2px', flexShrink:0 }} />{a}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =================================================================
//  STEP 8 — REPORT
// =================================================================
function ReportStep({ computed, parsed, shopRef, testLoad, conditions, detected, onSave, onPrev, initialRole, autoDownloadPdf, onAutoDownloadDone, isHistorical }) {
  const [saved, setSaved] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const handleSave = () => {
    onSave?.();
    setSaved(true);
  };

  // Auto-trigger PDF download when opened via the fleet browser download button.
  // pdfFiredRef ensures we fire exactly once per autoDownloadPdf=true pulse,
  // even across re-renders, and avoids stale-closure / missing-dep warnings.
  const pdfFiredRef = useRef(false);
  useEffect(() => {
    if (!autoDownloadPdf) {
      pdfFiredRef.current = false; // reset for next time
      return;
    }
    if (pdfFiredRef.current) return; // already fired this pulse
    if (!computed || !parsed || !shopRef) return;
    pdfFiredRef.current = true;
    generatePdf().finally(() => {
      onAutoDownloadDone?.();
    });
  // generatePdf is recreated every render (defined inline in this component)
  // so we intentionally omit it — the ref guard makes it safe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDownloadPdf, computed, parsed, shopRef, onAutoDownloadDone]);

  // Reset "saved" badge if the user navigates away and comes back with new data
  useEffect(() => { setSaved(false); }, [parsed?.data?.meta?.session, parsed?.data?.meta?.exportedAt]);

  if (!computed) return null;
  const m = parsed.data.meta;
  const dateStr = m.date ? String(m.date) : new Date().toLocaleDateString();
  const allFindings = computed.cyls.flatMap(c => c.insights.map(i => ({ cyl: c.idx, ...i })));
  const critical = allFindings.filter(f => f.severity === 'critical');
  const warnings = allFindings.filter(f => f.severity === 'warning');

  // ---- Rebuild condition validations using computed model values ----
  // (Same logic as ConditionsStep — model values from computed.shopAtLoad)
  const condValCtx = {
    modelAirFilterDP:   computed.shopAtLoad.airFilterDP,
    modelAirCoolerDP:   computed.shopAtLoad.airCoolerDP,
    modelScavPress_bar: computed.shopAtLoad.pscav,
    modelTurbBackPress: computed.shopAtLoad.turbInletPress != null
      ? refTurbBackPress(testLoad) : null,    // use formula-derived ref
    scavPress_bar: conditions.scavPress_bar?.value,
  };
  // Override turbBackPress model with the formula reference (same as ConditionsStep refs)
  condValCtx.modelTurbBackPress = refTurbBackPress(testLoad);

  // All nine condition keys that have validation rules
  const COND_LABELS = {
    blowerTemp_C:        'Blower inlet temp',
    baroPress_mbar:      'Barometric pressure',
    airFilterDP_mmH2O:   'Air filter ΔP',
    airCoolerDP_mmH2O:   'Air cooler ΔP',
    scavTemp_C:          'Scavenge air temp',
    scavPress_bar:       'Scavenge air pressure',
    exhRecPress_bar:     'Exhaust receiver pressure',
    turbBackPress_mmH2O: 'Turbine back pressure',
    turbInletTemp_C:     'Turbine inlet temp',
  };
  const condValidations = Object.entries(COND_LABELS).map(([key, label]) => {
    const val = validateCondition(key, conditions[key]?.value, condValCtx);
    return { key, label, val, value: conditions[key]?.value, unit: {
      blowerTemp_C: '°C', baroPress_mbar: 'mbar', airFilterDP_mmH2O: 'mmH2O',
      airCoolerDP_mmH2O: 'mmH2O', scavTemp_C: '°C', scavPress_bar: 'bar',
      exhRecPress_bar: 'bar', turbBackPress_mmH2O: 'mmH2O', turbInletTemp_C: '°C',
    }[key] };
  }).filter(r => r.val && r.val.status !== 'ok' && r.value != null);

  const condInvalids = condValidations.filter(r => r.val.status === 'invalid');
  const condHighs    = condValidations.filter(r => r.val.status === 'high');
  const condLows     = condValidations.filter(r => r.val.status === 'low');

  // Colour mapping for the print-friendly report (uses explicit hex, not CSS vars)
  const ALERT_COLOR = {
    invalid: { bg: 'var(--fill-err)', border: 'var(--br-err)', text: 'var(--c-err)', badge: 'var(--fill-err)' },
    high:    { bg: 'var(--fill-warn)', border: 'var(--br-warn)', text: 'var(--c-warn)', badge: 'var(--fill-warn)' },
    low:     { bg: 'var(--fill-info)', border: 'var(--br-info)', text: 'var(--c-info)', badge: 'var(--fill-info)' },
  };

  const generatePdf = async () => {
    if (!computed || !parsed || !shopRef) return;
    setPdfGenerating(true);
    try {
    const m = parsed.data.meta;

              // ── Load jsPDF + autotable ───────────────────────────────────────
              const loadScript = (src, readyCheck) => new Promise((res, rej) => {
                // If already loaded and the global is available, resolve immediately
                if (readyCheck && readyCheck()) return res();
                // If the tag exists but the global isn't ready yet, wait for it
                const existing = document.querySelector(`script[src="${src}"]`);
                if (existing) {
                  const poll = setInterval(() => { if (readyCheck && readyCheck()) { clearInterval(poll); res(); } }, 30);
                  setTimeout(() => { clearInterval(poll); rej(new Error('Script load timeout: ' + src)); }, 10000);
                  return;
                }
                const s = document.createElement('script');
                s.src = src;
                s.onload = () => res();
                s.onerror = rej;
                document.head.appendChild(s);
              });
              try {
                await loadScript(
                  'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
                  () => !!(window.jspdf && window.jspdf.jsPDF)
                );
                await loadScript(
                  'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js',
                  () => {
                    try { const t = new window.jspdf.jsPDF(); return typeof t.autoTable === 'function'; } catch { return false; }
                  }
                );
              } catch(e) { alert('Could not load PDF libraries. Check internet connection.'); return; }

              const { jsPDF } = window.jspdf;
              const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
              const pageW = pdf.internal.pageSize.getWidth();
              const pageH = pdf.internal.pageSize.getHeight();
              const margin = 12;
              const contentW = pageW - margin * 2;

              // ── Load logo ────────────────────────────────────────────────────
              let logoDataUrl = null;
              let logoNatW = 1, logoNatH = 1;
              try {
                const resp = await fetch('/assets/Zeroqu-Logo.png');
                const blob = await resp.blob();
                logoDataUrl = await new Promise(res => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });
                const tmpImg = new Image();
                await new Promise(res => { tmpImg.onload = res; tmpImg.src = logoDataUrl; });
                logoNatW = tmpImg.naturalWidth; logoNatH = tmpImg.naturalHeight;
              } catch(_) {}

              // ── Colours ──────────────────────────────────────────────────────
              const C = {
                headerBg:   [242, 248, 243],
                headerLine: [195, 220, 203],
                accent:     [39,  90,  70],
                accentLt:   [164, 198, 129],
                tableBg:    [234, 244, 236],
                border:     [195, 220, 203],
                text:       [30,  30,  30],
                muted:      [120, 140, 130],
                warnBg:     [255, 251, 230],
                warnText:   [113, 63,  18],
                warnBadge:  [251, 191, 36],
                errBg:      [255, 241, 242],
                errText:    [153, 27,  27],
                okText:     [21,  128, 61],
                footerLine: [195, 220, 203],
                footerText: [140, 160, 150],
                white:      [255, 255, 255],
              };

              // ── Page decoration helpers ──────────────────────────────────────
              const logoH = 5;
              const logoAreaH = logoDataUrl ? logoH + 5 : 0;
              const footerH  = 10;

              const drawPageChrome = (pgNum, totalPages) => {
                // Header bar
                if (logoDataUrl) {
                  pdf.setFillColor(...C.headerBg);
                  pdf.rect(0, 0, pageW, logoAreaH, 'F');
                  pdf.setDrawColor(...C.headerLine);
                  pdf.setLineWidth(0.3);
                  pdf.line(0, logoAreaH, pageW, logoAreaH);
                  const logoW = logoH * (logoNatW / logoNatH);
                  pdf.addImage(logoDataUrl, 'PNG', margin, (logoAreaH - logoH) / 2, logoW, logoH);
                  pdf.setFontSize(5.5);
                  pdf.setTextColor(...C.accent);
                  pdf.text('Engine Performance Analyser', margin + logoW + 3, logoAreaH / 2 - 0.5);
                  pdf.text('Shop-trial ISO correction · Cylinder balance · Diagnostics', margin + logoW + 3, logoAreaH / 2 + 3);
                }
                // Footer line + text
                pdf.setDrawColor(...C.footerLine);
                pdf.setLineWidth(0.3);
                pdf.line(margin, pageH - footerH + 1, pageW - margin, pageH - footerH + 1);
                pdf.setFontSize(5.5);
                pdf.setTextColor(...C.footerText);
                const footerText = `Generated by Engine Performance Analyser v2.0 · Shop trial reference: ${shopRef.id} · Cylinder tolerances: Pmax \u00b1${TOLERANCE.Pmax_bar.warn}/\u00b1${TOLERANCE.Pmax_bar.alarm} bar, Pcomp \u00b1${TOLERANCE.Pcomp_bar.warn}/\u00b1${TOLERANCE.Pcomp_bar.alarm} bar`;
                pdf.text(footerText, margin, pageH - footerH + 4.5);
                pdf.text(`Page ${pgNum} of ${totalPages}`, pageW - margin, pageH - footerH + 4.5, { align: 'right' });
              };

              // ── Cursor that auto-respects header/footer ──────────────────────
              let y = logoAreaH + 6; // starting Y below header
              const usableBottom = pageH - footerH - 2;

              const ensureSpace = (needed) => {
                if (y + needed > usableBottom) {
                  pdf.addPage();
                  y = logoAreaH + 6;
                }
              };

              // ── Section heading helper ───────────────────────────────────────
              const sectionTitle = (title, isFirst = false) => {
                if (!isFirst) y += 5;   // inter-section gap (before heading)
                ensureSpace(12);
                pdf.setFontSize(8.5);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(...C.text);
                pdf.text(title, margin, y);
                y += 2.5;               // text → rule gap (was 1.5)
                pdf.setDrawColor(...C.border);
                pdf.setLineWidth(0.3);
                pdf.line(margin, y, margin + contentW, y);
                y += 4;                 // rule → content gap (was 3)
                pdf.setFont('helvetica', 'normal');
              };

              // ── autoTable wrapper that moves cursor after table ──────────────
              const autoT = (opts) => {
                const startY = y;
                pdf.autoTable({
                  startY,
                  margin: { left: margin, right: margin },
                  tableWidth: contentW,
                  styles: { fontSize: 7, cellPadding: 1.8, textColor: C.text, lineColor: C.border, lineWidth: 0.25 },
                  headStyles: { fillColor: C.tableBg, textColor: C.text, fontStyle: 'bold', fontSize: 7 },
                  alternateRowStyles: {},
                  ...opts,
                });
                y = pdf.lastAutoTable.finalY + 4;
              };

              // ════════════════════════════════════════════════════════════════
              //  PAGE 1  — cover + engine id + operating conditions
              // ════════════════════════════════════════════════════════════════

              // Report title block
              pdf.setFontSize(14);
              pdf.setFont('helvetica', 'bold');
              pdf.setTextColor(...C.text);
              pdf.text(getReportTitle(shopRef), margin, y);
              y += 5;
              pdf.setFontSize(8);
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor(...C.muted);
              pdf.text(`${getEngineLabel(shopRef)} \u00b7 Test load: ${testLoad?.toFixed(1)} % MCR`, margin, y);

              // Top-right: vessel / date
              const dateStr2 = m.date ? String(m.date) : new Date().toLocaleDateString();
              pdf.setFontSize(7.5);
              pdf.text(m.vessel || m.engineId || '\u2014', pageW - margin, logoAreaH + 8, { align: 'right' });
              if (m.imo) pdf.text(`IMO ${m.imo}`, pageW - margin, logoAreaH + 12, { align: 'right' });
              pdf.text(dateStr2, pageW - margin, logoAreaH + (m.imo ? 16 : 12), { align: 'right' });

              y += 5;
              pdf.setDrawColor(...C.border);
              pdf.setLineWidth(0.5);
              pdf.line(margin, y, margin + contentW, y);
              y += 5;

              // ── Section 1: Engine identification ────────────────────────────
              sectionTitle('1. Engine identification', true);
              const idRows = [
                ['Vessel', m.vessel || '\u2014', 'IMO', m.imo || '\u2014'],
                ['Engine model', `${shopRef.model} (${shopRef.manufacturer})`, 'Engine type', detected.type],
                ['Cylinders', String(shopRef.cylinders), 'MCR', `${shopRef.mcrPower_kW} kW @ ${shopRef.maxRPM} rpm`],
                ['Bore \u00d7 Stroke', `${shopRef.bore_m * 1000} \u00d7 ${(shopRef.stroke_m * 1000).toFixed(0)} mm`, 'Shop trial ref', shopRef.id],
              ];
              autoT({
                body: idRows,
                columns: [
                  { dataKey: 0, header: '' }, { dataKey: 1, header: '' },
                  { dataKey: 2, header: '' }, { dataKey: 3, header: '' },
                ],
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 32 }, 2: { fontStyle: 'bold', cellWidth: 32 } },
                theme: 'plain',
                styles: { fontSize: 7, cellPadding: 1.5, textColor: C.text, lineColor: C.border, lineWidth: 0.2 },
              });

              // ── Section 2: Operating conditions ─────────────────────────────
              sectionTitle('2. Operating conditions');

              // Build condition rows from live data
              const refBlowerT = refBlowerTemp(shopRef.tcSuction);
              const shopPscav  = computed.shopAtLoad.pscav;
              const shopAirFDP = computed.shopAtLoad.airFilterDP;
              const shopAcDP   = computed.shopAtLoad.airCoolerDP;
              const shopTurbBP = refTurbBackPress(testLoad);
              const rpmCubic   = shopRef.maxRPM ? (shopRef.maxRPM * Math.pow(testLoad / 100, 1/3)).toFixed(2) : '\u2014';

              const COND_PDF = [
                { label: 'Blower inlet temp',    cond: conditions.blowerTemp_C,        refStr: `${refBlowerT} \u00b0C`,                          unit: '\u00b0C'    },
                { label: 'Barometric press',     cond: conditions.baroPress_mbar,      refStr: '1000 mbar',                                      unit: 'mbar'      },
                { label: 'Air filter ΔP',   cond: conditions.airFilterDP_mmH2O,   refStr: shopAirFDP != null ? `${shopAirFDP.toFixed(0)} mmH\u2082O (model)` : '\u2014', unit: 'mmH\u2082O' },
                { label: 'Air cooler ΔP',   cond: conditions.airCoolerDP_mmH2O,   refStr: shopAcDP   != null ? `${shopAcDP.toFixed(0)} mmH\u2082O (model)` : '\u2014',   unit: 'mmH\u2082O' },
                { label: 'Scavenge air temp',    cond: conditions.scavTemp_C,          refStr: `${refScavTemp(testLoad, 'full').toFixed(1)} \u00b0C`,             unit: '\u00b0C'    },
                { label: 'Scavenge air press',   cond: conditions.scavPress_bar,       refStr: shopPscav  != null ? `${shopPscav.toFixed(2)} bar (shop)` : '\u2014',           unit: 'bar'       },
                { label: 'Exh receiver press',   cond: conditions.exhRecPress_bar,     refStr: '< scav press',                                   unit: 'bar'       },
                { label: 'Turb back press',      cond: conditions.turbBackPress_mmH2O, refStr: `${shopTurbBP.toFixed(0)} mmH\u2082O`,             unit: 'mmH\u2082O' },
                { label: 'Turb inlet temp',      cond: conditions.turbInletTemp_C,     refStr: 'max 600 \u00b0C',                                unit: '\u00b0C'    },
                { label: 'Shaft power',          cond: conditions.shaftPower_kW,       refStr: `${(shopRef.mcrPower_kW * testLoad / 100).toFixed(0)} kW (target)`, unit: 'kW'        },
                { label: 'Engine RPM',           cond: conditions.rpm,                 refStr: `${rpmCubic} rpm (cubic law)`,                    unit: 'rpm'       },
              ];

              // Rebuild condValidations inline for PDF
              const pdfCondValCtx = {
                modelAirFilterDP:   computed.shopAtLoad.airFilterDP,
                modelAirCoolerDP:   computed.shopAtLoad.airCoolerDP,
                modelScavPress_bar: computed.shopAtLoad.pscav,
                modelTurbBackPress: refTurbBackPress(testLoad),
                scavPress_bar:      conditions.scavPress_bar?.value,
              };
              // Map COND_PDF labels exactly → condition key for validation lookup
              const COND_PDF_KEY_MAP = {
                'Blower inlet temp':    'blowerTemp_C',
                'Barometric press':     'baroPress_mbar',
                'Air filter ΔP':   'airFilterDP_mmH2O',
                'Air cooler ΔP':   'airCoolerDP_mmH2O',
                'Scavenge air temp':    'scavTemp_C',
                'Scavenge air press':   'scavPress_bar',
                'Exh receiver press':   'exhRecPress_bar',
                'Turb back press':      'turbBackPress_mmH2O',
                'Turb inlet temp':      'turbInletTemp_C',
              };
              // All validations (every row) for lookup in didParseCell
              const pdfAllCondVals = {};
              Object.entries(COND_PDF_KEY_MAP).forEach(([label, key]) => {
                pdfAllCondVals[label] = validateCondition(key, conditions[key]?.value, pdfCondValCtx);
              });
              // Flagged-only list used for section 2b alert table
              const pdfCondValidations = Object.entries(COND_PDF_KEY_MAP)
                .map(([label, key]) => {
                  const val = validateCondition(key, conditions[key]?.value, pdfCondValCtx);
                  return { key, label, val, value: conditions[key]?.value, unit: {
                    blowerTemp_C: '\u00b0C', baroPress_mbar: 'mbar', airFilterDP_mmH2O: 'mmH\u2082O',
                    airCoolerDP_mmH2O: 'mmH\u2082O', scavTemp_C: '\u00b0C', scavPress_bar: 'bar',
                    exhRecPress_bar: 'bar', turbBackPress_mmH2O: 'mmH\u2082O', turbInletTemp_C: '\u00b0C',
                  }[key] };
                })
                .filter(r => r.val && r.val.status !== 'ok' && r.value != null);

              autoT({
                head: [['Parameter', 'Measured', 'Reference', 'Source', 'Status']],
                body: COND_PDF.map(({ label, cond, refStr, unit }) => {
                  const val = pdfAllCondVals[label];
                  const isFlagged = val && val.status !== 'ok' && cond?.value != null;
                  const srcText = cond?.source === 'FROM FILE' ? 'FROM FILE'
                                : cond?.source === 'MANUAL'    ? 'MANUAL'
                                : cond?.source === 'CONSTANT'  ? 'CONSTANT'
                                : '\u2014';
                  const statusText = isFlagged ? val.label
                                   : cond?.value != null ? '\u2713' : '\u2014';
                  return [
                    label,
                    cond?.value != null ? `${cond.value} ${unit}` : '\u2014',
                    refStr || '\u2014',
                    srcText,
                    statusText,
                  ];
                }),
                columnStyles: {
                  0: { cellWidth: 44 },
                  1: { cellWidth: 30 },
                  2: { cellWidth: 62 },
                  3: { cellWidth: 24 },
                  4: { cellWidth: 24 },
                },
                didParseCell: (data) => {
                  if (data.section === 'body') {
                    const rowLabel = data.row.raw[0];
                    const val = pdfAllCondVals[rowLabel];
                    const condRow = COND_PDF.find(r => r.label === rowLabel);
                    const isFlagged = val && val.status !== 'ok' && condRow?.cond?.value != null;
                    if (isFlagged) {
                      const isErr = val.status === 'invalid';
                      data.cell.styles.fillColor = isErr ? [255, 235, 235] : [255, 251, 230];
                      data.cell.styles.textColor = isErr ? [153, 27, 27]   : [113, 63, 18];
                    }
                    if (data.column.index === 3) {
                      const src = data.cell.raw;
                      if (src === 'FROM FILE') {
                        if (!isFlagged) data.cell.styles.fillColor = [234, 244, 236];
                        data.cell.styles.textColor = [39, 90, 70];
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fontSize = 6;
                      } else if (src === 'MANUAL') {
                        if (!isFlagged) data.cell.styles.fillColor = [255, 251, 230];
                        data.cell.styles.textColor = [113, 63, 18];
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fontSize = 6;
                      }
                    }
                    if (data.column.index === 4) {
                      const st = data.cell.raw;
                      if (st === '\u2713') {
                        data.cell.styles.textColor = [21, 128, 61];
                        data.cell.styles.fontStyle = 'bold';
                      } else if (st && st !== '\u2014') {
                        if (!isFlagged) data.cell.styles.fillColor = [255, 251, 230];
                        data.cell.styles.textColor = [113, 63, 18];
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fontSize = 6;
                      }
                    }
                  }
                },
              });

              // ── Section 2b: Condition alerts (if any) ───────────────────────
              if (pdfCondValidations.length > 0) {
                sectionTitle(`2b. Operating condition alerts (${pdfCondValidations.length} flag${pdfCondValidations.length !== 1 ? 's' : ''})`);
                pdf.setFontSize(6.5);
                pdf.setTextColor(...C.muted);
                pdf.text('The following measured conditions are outside the expected range and may affect the reliability of the ISO-corrected performance parameters.', margin, y, { maxWidth: contentW });
                y += 5;
                autoT({
                  head: [['Parameter', 'Measured', 'Status', 'Finding', 'Recommended action']],
                  body: pdfCondValidations.map(({ label, val, value, unit }) => [
                    label,
                    value != null ? `${value} ${unit}` : '\u2014',
                    val.label,
                    val.message || '',
                    val.guidance || '',
                  ]),
                  columnStyles: {
                    0: { cellWidth: 36 },
                    1: { halign: 'right', cellWidth: 26 },
                    2: { halign: 'center', cellWidth: 22 },
                    3: { cellWidth: 50 },
                    4: { cellWidth: 50 },
                  },
                  didParseCell: (data) => {
                    if (data.section === 'body') {
                      const status = data.row.raw[2];
                      const isHigh = status && (status.toLowerCase().includes('high') || status.toLowerCase().includes('too'));
                      data.cell.styles.fillColor = isHigh ? [255, 251, 230] : [255, 235, 235];
                      data.cell.styles.textColor = isHigh ? [113, 63, 18] : [153, 27, 27];
                    }
                  },
                  styles: { fontSize: 6.5, cellPadding: 1.8, textColor: C.text, lineColor: C.border, lineWidth: 0.25 },
                });
              }

              // ── Section 3: Cylinder balance ──────────────────────────────────
              sectionTitle('3. Cylinder balance');
              const cylBodyRows = computed.cyls.map(c => [
                String(c.idx),
                c.Pcomp_iso?.toFixed(1) ?? '\u2014',
                c.dPcomp != null ? `${c.dPcomp >= 0 ? '+' : ''}${c.dPcomp.toFixed(1)}` : '\u2014',
                c.Pmax_iso?.toFixed(1) ?? '\u2014',
                c.dPmax != null ? `${c.dPmax >= 0 ? '+' : ''}${c.dPmax.toFixed(1)}` : '\u2014',
                c.MIP?.toFixed(2) ?? '\u2014',
                c.Texh ?? '\u2014',
                c.health ?? '\u2014',
              ]);
              // Mean row
              cylBodyRows.push([
                'Mean',
                computed.means.Pcomp_iso?.toFixed(1) ?? '',
                '',
                computed.means.Pmax_iso?.toFixed(1) ?? '',
                '',
                computed.means.MIP?.toFixed(2) ?? '',
                computed.means.Texh?.toFixed(0) ?? '\u2014',
                String(computed.engineHealth ?? ''),
              ]);
              autoT({
                head: [['Cyl', 'Pcomp ISO (bar)', '\u0394Pcomp', 'Pmax ISO (bar)', '\u0394Pmax', 'MIP (bar)', 'Texh (\u00b0C)', 'Health']],
                body: cylBodyRows,
                columnStyles: {
                  0: { halign: 'left',   fontStyle: 'bold', cellWidth: 16 },
                  1: { halign: 'right',  cellWidth: 28 },
                  2: { halign: 'right',  cellWidth: 24 },
                  3: { halign: 'right',  cellWidth: 28 },
                  4: { halign: 'right',  cellWidth: 24 },
                  5: { halign: 'right',  cellWidth: 23 },
                  6: { halign: 'right',  cellWidth: 23 },
                  7: { halign: 'center', cellWidth: 18 },
                },
                didParseCell: (data) => {
                  if (data.section === 'body') {
                    const isLastRow = data.row.index === cylBodyRows.length - 1;
                    if (isLastRow) {
                      data.cell.styles.fillColor = C.tableBg;
                      data.cell.styles.fontStyle = 'bold';
                    } else {
                      const cylObj = computed.cyls[data.row.index];
                      const hasFindings = cylObj && (cylObj.insights||[]).filter(i => i.severity !== 'incomplete').length > 0;
                      if (hasFindings) data.cell.styles.fillColor = [255, 251, 230];
                    }
                  }
                },
              });

              // ── Section 3b: Cylinder deviation charts ──────────────────────────
              // Strategy: render the real DeviationChart React component into an
              // off-screen div (same component & props as the on-screen report),
              // capture it with html2canvas at 2× DPI, then embed as PNG via
              // pdf.addImage(). This avoids the jsPDF SVG plugin dependency and
              // handles Recharts CSS variables correctly.
              sectionTitle('3b. Cylinder deviation charts');
              {
                // ── Load html2canvas if not already available ────────────────
                if (!window.html2canvas) {
                  await loadScript(
                    'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js',
                    () => typeof window.html2canvas === 'function'
                  );
                }

                const TOL3b = getTolerance(computed.strokeType || 2);
                const strokeLabel3b = `${computed.strokeType || 2}-stroke`;
                const completeCyls3b = computed.cyls.filter(c => !c.dataIncomplete);

                // Identical props to the report page DeviationChart calls
                const charts3b = [
                  { field: 'dPmax',    title: `\u0394Pmax ISO vs Mean \u2014 limit \u00b1${TOL3b.Pmax_bar.warn} bar (${strokeLabel3b})`,  tol: TOL3b.Pmax_bar,  unit: 'bar' },
                  { field: 'dPcomp',   title: `\u0394Pcomp ISO vs Mean \u2014 limit \u00b1${TOL3b.Pcomp_bar.warn} bar (${strokeLabel3b})`, tol: TOL3b.Pcomp_bar, unit: 'bar' },
                  { field: 'dMIP_bar', title: `\u0394MIP vs Mean \u2014 limit \u00b1${TOL3b.MIP_bar.warn} bar (${strokeLabel3b})`,          tol: TOL3b.MIP_bar,   unit: 'bar' },
                  { field: 'dTexh',    title: '\u0394Texh vs Mean \u2014 warn >\u00b120 K, alarm >\u00b150 K',                              tol: TOL3b.Texh_K,    unit: 'K'   },
                ];

                // PDF layout constants
                const colCount3b = 2;
                const chartW3b   = (contentW - 6) / colCount3b;  // mm
                const chartH3b   = 58;                            // mm

                // Pixel canvas size — 2× scale for crisp rendering
                const SCALE    = 2;
                const hostPxW  = Math.round(chartW3b * (96 / 25.4));
                const hostPxH  = Math.round(chartH3b * (96 / 25.4));

                // Render one DeviationChart → PNG data-URL via html2canvas
                const renderChartPng = (chartDef) => new Promise((resolve) => {
                  // Host div: position in viewport bottom-left (html2canvas works
                  // best when the element is in the live DOM and partially visible)
                  const host = document.createElement('div');
                  host.setAttribute('data-theme', 'light');
                  Object.assign(host.style, {
                    position:   'fixed',
                    bottom:     '0',
                    left:       '0',
                    width:      `${hostPxW}px`,
                    height:     `${hostPxH}px`,
                    overflow:   'hidden',
                    background: '#ffffff',
                    zIndex:     '-1',
                    pointerEvents: 'none',
                  });
                  document.body.appendChild(host);

                  // Mount the real DeviationChart (same component as the report page)
                  const reactRoot = createRoot(host);
                  reactRoot.render(
                    React.createElement(DeviationChart, {
                      cyls:  completeCyls3b,
                      field: chartDef.field,
                      title: chartDef.title,
                      tol:   chartDef.tol,
                      unit:  chartDef.unit,
                    })
                  );

                  // Wait for React to commit + Recharts ResizeObserver to fire (3 frames)
                  const waitFrames = (n, cb) => {
                    if (n <= 0) return cb();
                    requestAnimationFrame(() => waitFrames(n - 1, cb));
                  };

                  waitFrames(4, async () => {
                    let pngDataUrl = null;
                    try {
                      // Inline all CSS custom properties so html2canvas sees real values
                      // (it doesn't evaluate var(--...) on its own)
                      const computedStyle = getComputedStyle(host);
                      const cssVarMap = [
                        '--bg-panel', '--br', '--br-mid', '--t1', '--t3', '--t4',
                      ];
                      cssVarMap.forEach(v => {
                        const val = computedStyle.getPropertyValue(v).trim();
                        if (val) host.style.setProperty(v, val);
                      });

                      const canvas = await window.html2canvas(host, {
                        scale:           SCALE,
                        useCORS:         true,
                        allowTaint:      true,
                        backgroundColor: '#ffffff',
                        logging:         false,
                        width:           hostPxW,
                        height:          hostPxH,
                      });
                      pngDataUrl = canvas.toDataURL('image/png');
                    } catch (e) {
                      console.warn('html2canvas capture failed:', e);
                    } finally {
                      reactRoot.unmount();
                      document.body.removeChild(host);
                    }
                    resolve(pngDataUrl);
                  });
                });

                // Render all 4 charts sequentially to avoid ResizeObserver contention
                const pngDataUrls = [];
                for (const ch of charts3b) {
                  pngDataUrls.push(await renderChartPng(ch));
                }

                // Place each PNG into the PDF grid using pdf.addImage()
                for (let ci = 0; ci < charts3b.length; ci++) {
                  const col3b    = ci % colCount3b;
                  if (col3b === 0) ensureSpace(chartH3b + 4);
                  const ox3b     = margin + col3b * (chartW3b + 6);
                  const oy3b     = y;
                  const pngUrl   = pngDataUrls[ci];

                  if (pngUrl) {
                    pdf.addImage(pngUrl, 'PNG', ox3b, oy3b, chartW3b, chartH3b);
                  } else {
                    // Graceful fallback if capture failed
                    pdf.setFillColor(245, 248, 245);
                    pdf.setDrawColor(...C.border);
                    pdf.setLineWidth(0.25);
                    pdf.roundedRect(ox3b, oy3b, chartW3b, chartH3b, 1.5, 1.5, 'FD');
                    pdf.setFontSize(5.5);
                    pdf.setFont('helvetica', 'normal');
                    pdf.setTextColor(120, 120, 120);
                    pdf.text('[Chart unavailable]', ox3b + chartW3b / 2, oy3b + chartH3b / 2, { align: 'center' });
                  }

                  if (col3b === colCount3b - 1 || ci === charts3b.length - 1) {
                    y += chartH3b + 4;
                  }
                }
              }

              // -- Section 3c: Indicator diagrams (drawn natively with jsPDF) --
              if (parsed?.data?.curves && parsed.data.curves.length > 0) {
                const curves3c   = parsed.data.curves;
                const nCyls3c    = parsed.data.meta.cylinders || 0;
                const strokeType = parsed.data.meta.strokeType || 2;
                const cycleLabel = strokeType === 4 ? "720° four-stroke" : "360° two-stroke cycle";
                const CYL_COLORS_PDF = [
                  [39,90,70],[164,198,129],[77,114,152],[203,94,100],[123,115,177],
                  [235,232,159],[178,213,241],[38,88,88],[212,132,90],[106,171,156],
                ];
                const step3c    = Math.max(1, Math.floor(curves3c.length / 360));
                const sampled3c = curves3c.filter((_, i) => i % step3c === 0);
                const angleMin3c = Math.min(...sampled3c.map(d => d.angle));
                const angleMax3c = Math.max(...sampled3c.map(d => d.angle));
                const colCount  = 2;
                const chartW    = (contentW - 4) / colCount;
                const chartH    = 42;
                const padL = 10, padR = 4, padT = 8, padB = 9;
                const plotW = chartW - padL - padR;
                const plotH = chartH - padT - padB;
                const xMap = (ang, ox) => ox + padL + ((ang - angleMin3c) / (angleMax3c - angleMin3c || 1)) * plotW;
                const yMap3c = (p, pMin, pMax, oy) => oy + padT + plotH - ((p - pMin) / (pMax - pMin || 1)) * plotH;
                // heading -- ensure space for heading + first row
                if (y + 5 + 14 + chartH > usableBottom) { pdf.addPage(); y = logoAreaH + 6; }
                sectionTitle('3c. Indicator diagram \u2014 per cylinder');
                for (let ci = 0; ci < nCyls3c; ci++) {
                  const col = ci % colCount;
                  if (col === 0) ensureSpace(chartH + 3);
                  const ox = margin + col * (chartW + 4);
                  const oy = y;
                  const pressVals = sampled3c.map(d => d.press?.[ci]).filter(v => v != null);
                  if (pressVals.length === 0) { if (col === colCount - 1 || ci === nCyls3c - 1) y += chartH + 3; continue; }
                  const pMax3c = Math.ceil(Math.max(...pressVals) * 1.1 / 10) * 10;
                  const cylColor = CYL_COLORS_PDF[ci % CYL_COLORS_PDF.length];
                  // box
                  pdf.setFillColor(250, 252, 250);
                  pdf.setDrawColor(...C.border); pdf.setLineWidth(0.25);
                  pdf.roundedRect(ox, oy, chartW, chartH, 1.5, 1.5, "FD");
                  // dot + title
                  pdf.setFillColor(...cylColor);
                  pdf.circle(ox + 3.5, oy + 3.8, 1.5, "F");
                  pdf.setFontSize(6.5); pdf.setFont('helvetica', "bold"); pdf.setTextColor(...C.text);
                  pdf.text(`Cylinder ${ci + 1} \u2014 ${cycleLabel}`, ox + 6.5, oy + 4.5);
                  // plot border
                  pdf.setDrawColor(200,210,200); pdf.setLineWidth(0.15);
                  pdf.rect(ox + padL, oy + padT, plotW, plotH);
                  // horizontal grid
                  pdf.setDrawColor(210,225,210); pdf.setLineWidth(0.1);
                  for (let gi = 1; gi <= 3; gi++) {
                    const gy = oy + padT + (gi / 4) * plotH;
                    pdf.line(ox + padL, gy, ox + padL + plotW, gy);
                  }
                  // TDC line
                  const tdcX = xMap(0, ox);
                  if (tdcX >= ox + padL && tdcX <= ox + padL + plotW) {
                    pdf.setDrawColor(180,180,180); pdf.setLineWidth(0.2);
                    pdf.line(tdcX, oy + padT, tdcX, oy + padT + plotH);
                    pdf.setFontSize(5); pdf.setTextColor(120,120,120);
                    pdf.text("TDC", tdcX, oy + padT - 0.8, { align: "center" });
                  }
                  // Y labels
                  pdf.setFontSize(5); pdf.setFont('helvetica', "normal"); pdf.setTextColor(100,100,100);
                  [0, pMax3c / 2, pMax3c].forEach(pVal => {
                    const ty = yMap3c(pVal, 0, pMax3c, oy);
                    pdf.text(`${Math.round(pVal)}`, ox + padL - 1, ty + 1, { align: "right" });
                  });
                  pdf.setFontSize(4.5); pdf.setTextColor(130,130,130);
                  pdf.text("bar", ox + padL - 1, oy + padT + plotH / 2, { align: "right" });
                  // X labels
                  pdf.setFontSize(5); pdf.setTextColor(100,100,100);
                  [-180, -90, 0, 90, 180].forEach(ang => {
                    if (ang < angleMin3c || ang > angleMax3c) return;
                    pdf.text(`${ang}`, xMap(ang, ox), oy + padT + plotH + 4.5, { align: "center" });
                  });
                  pdf.setFontSize(4.5); pdf.setTextColor(130,130,130);
                  pdf.text("Crank angle (°)", ox + padL + plotW / 2, oy + chartH - 1, { align: "center" });
                  // pressure curve
                  pdf.setDrawColor(...cylColor); pdf.setLineWidth(0.55);
                  let firstPt3c = true;
                  for (const d of sampled3c) {
                    const pv = d.press?.[ci];
                    if (pv == null) { firstPt3c = true; continue; }
                    const px = xMap(d.angle, ox);
                    const py = yMap3c(pv, 0, pMax3c, oy);
                    if (px < ox + padL - 0.5 || px > ox + padL + plotW + 0.5) { firstPt3c = true; continue; }
                    if (firstPt3c) { pdf.moveTo(px, py); firstPt3c = false; }
                    else pdf.lineTo(px, py);
                  }
                  pdf.stroke();
                  if (col === colCount - 1 || ci === nCyls3c - 1) y += chartH + 3;
                }
              }

              // ── Section 4: Findings & priority actions ───────────────────────
              sectionTitle('4. Findings & priority actions');
              const allFindingsPdf = computed.cyls.flatMap(c => c.insights.map(i => ({ cyl: c.idx, ...i })));
              const criticalPdf = allFindingsPdf.filter(f => f.severity === 'critical');
              const warningsPdf = allFindingsPdf.filter(f => f.severity === 'warning');

              if (pdfCondValidations.length > 0) {
                pdf.setFontSize(7);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(...C.text);
                pdf.text(`Operating condition alerts: ${pdfCondValidations.length} flag${pdfCondValidations.length !== 1 ? 's' : ''}`, margin, y);
                y += 4;
                pdf.setFont('helvetica', 'normal');
                pdfCondValidations.forEach(({ label, val }, idx) => {
                  ensureSpace(6);
                  pdf.setFontSize(6.5);
                  pdf.setFont('helvetica', 'normal');
                  pdf.setTextColor(...C.warnText);
                  pdf.text(`${idx + 1}. ${label} \u2014 ${val.label}. ${val.guidance || ''}`, margin + 2, y, { maxWidth: contentW - 2 });
                  y += 5;
                });
                y += 3;
              }

              if (allFindingsPdf.length === 0 && pdfCondValidations.length === 0) {
                pdf.setFontSize(7);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(...C.okText);
                pdf.text('No findings \u2014 all conditions within range and all cylinders within tolerance.', margin, y);
                y += 8;
              }else if (allFindingsPdf.length > 0) {
                  pdf.setFontSize(7);
                  pdf.setFont('helvetica', 'bold');
                  pdf.setTextColor(...C.text);
                  pdf.text(`Cylinder diagnostics: ${criticalPdf.length} critical · ${warningsPdf.length} warnings`, margin, y);
                  y += 6;

                  pdf.setFont('helvetica', 'normal');

                  [...criticalPdf, ...warningsPdf].forEach((f, idx) => {
                      ensureSpace(4);

                      const isCrit = f.severity === 'critical';
                      pdf.setFontSize(6.5);
                      pdf.setTextColor(...(isCrit ? C.errText : C.warnText));

                      const number = `${idx + 1}.`;
                      const cylText = `Cyl ${f.cyl} — ${f.title}`;

                      // Line 1: Number + Cylinder
                      pdf.setFont('helvetica', 'bold');   // ← add this
                      pdf.text(`${number} ${cylText}`, margin + 2, y);
                      y += 4;

                      // Line 2: Finding
                      if (f.finding) {
                          pdf.setFont('helvetica', 'normal');   // ← explicitly reset each time
                          pdf.setFontSize(6.5);                 // ← re-assert
                          //  size too
                          const findingText = `Finding: ${f.finding}`;
                          const findingLines = pdf.splitTextToSize(findingText, contentW - 8);
                          
                          findingLines.forEach(line => {
                              pdf.setFont('helvetica', 'normal');  // ← inside loop too
                              pdf.text(line, margin + 2, y);
                              y += 3.2;
                          });
                      }

                      // Line 3: Action
                      if (f.actions?.[0]) {
                          pdf.setFont('helvetica', 'normal');   // ← explicitly reset
                          pdf.setFontSize(6.5);                 // ← re-assert size
                          const actionText = `Action: ${f.actions[0]}`;
                          const actionLines = pdf.splitTextToSize(actionText, contentW - 8);
                          
                          actionLines.forEach(line => {
                              pdf.setFont('helvetica', 'normal');  // ← inside loop too
                              pdf.text(line, margin + 2, y);
                              y += 3.2;
                          });
                          y += 3.5;
                      }
                  });
              }

              // ── Draw page chrome on all pages ────────────────────────────────
              const totalPages = pdf.internal.getNumberOfPages();
              for (let pg = 1; pg <= totalPages; pg++) {
                pdf.setPage(pg);
                drawPageChrome(pg, totalPages);
              }

    pdf.save(`Engine_Report_${(m.vessel || shopRef.model || 'Report').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } finally {
      setPdfGenerating(false);
    }
  };

  return (
    <div>
      {pdfGenerating && <LoadingSpinner overlay />}
      {isHistorical && <ReadOnlyBanner />}
      <div className="flex items-center justify-between mb-4 print:hidden">
        <SectionHeader icon={FileText} title="Performance report" subtitle="Executive summary — print or save as PDF" inline />
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          {/* Save to history button — hidden when viewing a historical report */}
          {initialRole !== 'fleet' && !isHistorical && (
          <button onClick={handleSave} disabled={saved}
            style={saved
              ? { background:'var(--fill-ok)', border:'1px solid var(--br-ok)', color:'var(--c-ok)', borderRadius:'6px', padding:'6px 12px', fontSize:'12px', cursor:'default', display:'flex', alignItems:'center', gap:'6px' }
              : { background:'var(--bg-surface)', border:'1px solid var(--br-mid)', color:'var(--t2)', borderRadius:'6px', padding:'6px 12px', fontSize:'12px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }
            }>
            {saved
              ? <><CheckCircle2 style={{ width:'12px', height:'12px' }} />Saved to history</>
              : <><Save style={{ width:'12px', height:'12px' }} />Save to history</>
            }
          </button>
          )}
          {/* Download report as PDF */}
          <button onClick={() => generatePdf()}
            style={{ background:'var(--btn-p-bg)', border:'1px solid var(--btn-p-b)', color:'var(--btn-p-c)', borderRadius:'6px', padding:'6px 12px', fontSize:'12px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}>
            <Download style={{ width:'14px', height:'14px' }} />
          </button>
          <PageHelp stepId="report" />
        </div>
      </div>

      <div id="zq-report-content" className="bg-white text-slate-900 rounded p-8 print:p-0 print:bg-white" style={{ fontFamily: 'StevieSans, sans-serif' }}>
        {/* Report header */}
        <div className="border-b-2 border-slate-900 pb-3 mb-5">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold">{getReportTitle(shopRef)}</h1>
              <div className="text-xs text-slate-600 mt-1">
                {getEngineLabel(shopRef)} · Test load: <strong>{testLoad?.toFixed(1)} % MCR</strong>
              </div>
            </div>
            <div className="text-right text-xs text-slate-600">
              <div>{m.vessel || m.engineId || '—'}</div>
              <div>{m.imo ? `IMO ${m.imo}` : ''}</div>
              <div>{dateStr}</div>
            </div>
          </div>
        </div>

        {/* Section 1 — Engine identification */}
        <ReportSection title="1. Engine identification" noCut>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs">
            <div><strong>Vessel:</strong> {m.vessel || '—'}</div>
            <div><strong>IMO:</strong> {m.imo || '—'}</div>
            <div><strong>Engine model:</strong> {shopRef.model} ({shopRef.manufacturer})</div>
            <div><strong>Engine type:</strong> {detected.type}</div>
            <div><strong>Cylinders:</strong> {shopRef.cylinders}</div>
            <div><strong>MCR:</strong> {shopRef.mcrPower_kW} kW @ {shopRef.maxRPM} rpm</div>
            <div><strong>Bore × Stroke:</strong> {shopRef.bore_m * 1000} × {(shopRef.stroke_m * 1000).toFixed(0)} mm</div>
            <div><strong>Shop trial ref:</strong> {shopRef.id}</div>
          </div>
        </ReportSection>

        {/* Section 2 — Operating conditions */}
        <ReportSection title="2. Operating conditions">
          <table className="w-full text-xs border-collapse">
            <thead style={{ background: 'var(--bg-surface)' }}>
              <tr>
                <th className="text-left p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Parameter</th>
                <th className="text-right p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Measured</th>
                <th className="text-right p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Reference</th>
                <th className="text-center p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Source</th>
                <th className="text-center p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              <ReportRow label="Blower inlet temp"   cond={conditions.blowerTemp_C}        refStr={`${refBlowerTemp(shopRef.tcSuction)} °C`}               unit="°C"     validation={condValidations.find(r => r.key === 'blowerTemp_C')?.val} />
              <ReportRow label="Barometric press"    cond={conditions.baroPress_mbar}      refStr="1000 mbar"                                               unit="mbar"   validation={condValidations.find(r => r.key === 'baroPress_mbar')?.val} />
              <ReportRow label="Air filter ΔP"       cond={conditions.airFilterDP_mmH2O}   refStr={computed.shopAtLoad.airFilterDP != null ? `${computed.shopAtLoad.airFilterDP.toFixed(0)} mmH₂O (model)` : '—'} unit="mmH₂O"  validation={condValidations.find(r => r.key === 'airFilterDP_mmH2O')?.val} />
              <ReportRow label="Air cooler ΔP"       cond={conditions.airCoolerDP_mmH2O}   refStr={computed.shopAtLoad.airCoolerDP != null ? `${computed.shopAtLoad.airCoolerDP.toFixed(0)} mmH₂O (model)` : '—'}  unit="mmH₂O"  validation={condValidations.find(r => r.key === 'airCoolerDP_mmH2O')?.val} />
              <ReportRow label="Scavenge air temp"   cond={conditions.scavTemp_C}          refStr={`${refScavTemp(testLoad, 'full').toFixed(1)} °C`}         unit="°C"     validation={condValidations.find(r => r.key === 'scavTemp_C')?.val} />
              <ReportRow label="Scavenge air press"  cond={conditions.scavPress_bar}       refStr={`${computed.shopAtLoad.pscav?.toFixed(2)} bar (shop)`}    unit="bar"    validation={condValidations.find(r => r.key === 'scavPress_bar')?.val} />
              <ReportRow label="Exh receiver press"  cond={conditions.exhRecPress_bar}     refStr="< scav press"                                             unit="bar"    validation={condValidations.find(r => r.key === 'exhRecPress_bar')?.val} />
              <ReportRow label="Turb back press"     cond={conditions.turbBackPress_mmH2O} refStr={`${refTurbBackPress(testLoad).toFixed(0)} mmH₂O`}          unit="mmH₂O"  validation={condValidations.find(r => r.key === 'turbBackPress_mmH2O')?.val} />
              <ReportRow label="Turb inlet temp"     cond={conditions.turbInletTemp_C}     refStr="max 600 °C"                                               unit="°C"     validation={condValidations.find(r => r.key === 'turbInletTemp_C')?.val} />
              <ReportRow label="Shaft power"         cond={conditions.shaftPower_kW}       refStr={`${(shopRef.mcrPower_kW * testLoad / 100).toFixed(0)} kW (target)`} unit="kW" />
              <ReportRow label="Engine RPM"          cond={conditions.rpm}                 refStr={`${shopRef.maxRPM ? (shopRef.maxRPM * Math.pow(testLoad / 100, 1/3)).toFixed(2) : '—'} rpm (cubic law)`} unit="rpm" />
            </tbody>
          </table>
        </ReportSection>

        {/* Section 2b — Condition alerts (only rendered when there are alerts) */}
        {condValidations.length > 0 && (
          <ReportSection title={`2b. Operating condition alerts (${condValidations.length} flag${condValidations.length !== 1 ? 's' : ''})`}>
            <div style={{ marginBottom: '6px', fontSize: '11px', color: 'var(--t2)' }}>
              The following measured conditions are outside the expected range and may affect the reliability of the ISO-corrected performance parameters.
            </div>
            <table className="w-full text-xs border-collapse" style={{ marginBottom: '6px' }}>
              <thead>
                <tr style={{ background: 'var(--bg-surface)' }}>
                  <th className="text-left p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Parameter</th>
                  <th className="text-right p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Measured</th>
                  <th className="text-center p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Status</th>
                  <th className="text-left p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Finding</th>
                  <th className="text-left p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Recommended action</th>
                </tr>
              </thead>
              <tbody>
                {condValidations.map(({ key, label, val, value, unit }) => {
                  const ac = ALERT_COLOR[val.status] || ALERT_COLOR.high;
                  return (
                    <tr key={key} style={{ background: ac.bg }}>
                      <td className="p-1.5 font-medium" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{label}</td>
                      <td className="p-1.5 text-right font-mono" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>
                        {value != null ? `${value} ${unit}` : '—'}
                      </td>
                      <td className="p-1.5 text-center" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>
                        <span style={{ background: ac.badge, color: ac.text, border: `1px solid ${ac.border}`, borderRadius: '3px', padding: '1px 6px', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {val.label}
                        </span>
                      </td>
                      <td className="p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t2)', verticalAlign: 'middle' }}>{val.message}</td>
                      <td className="p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t2)', verticalAlign: 'middle' }}>{val.guidance}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ReportSection>
        )}

        {/* Section 3 — Cylinder balance */}
        <ReportSection title="3. Cylinder balance">
          <div data-pdf-block="cyl-table" style={{ breakInside: 'avoid', pageBreakInside: 'avoid', WebkitColumnBreakInside: 'avoid' }}>
          <table className="w-full text-xs border-collapse">
            <thead style={{ background: 'var(--bg-surface)' }}>
              <tr>
                <th className="text-left p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Cyl</th>
                <th className="text-right p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Pcomp ISO (bar)</th>
                <th className="text-right p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>ΔPcomp</th>
                <th className="text-right p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Pmax ISO (bar)</th>
                <th className="text-right p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>ΔPmax</th>
                <th className="text-right p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>MIP (bar)</th>
                <th className="text-right p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Texh (°C)</th>
                <th className="text-center p-1.5" style={{ border: '1px solid var(--br)', color: 'var(--t1)', verticalAlign: 'middle' }}>Health</th>
              </tr>
            </thead>
            <tbody>
              {computed.cyls.map(c => {
                const realFindings = (c.insights||[]).filter(i => i.severity !== 'incomplete');
                return (
                  <tr key={c.idx} style={realFindings.length ? { background: 'var(--fill-warn)' } : {}}>
                    <td className="p-1.5 font-bold" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{c.idx}</td>
                    <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{c.Pcomp_iso?.toFixed(1) || '—'}</td>
                    <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{c.dPcomp != null ? `${c.dPcomp >= 0 ? '+' : ''}${c.dPcomp.toFixed(1)}` : '—'}</td>
                    <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{c.Pmax_iso?.toFixed(1) || '—'}</td>
                    <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{c.dPmax != null ? `${c.dPmax >= 0 ? '+' : ''}${c.dPmax.toFixed(1)}` : '—'}</td>
                    <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{c.MIP?.toFixed(2) || '—'}</td>
                    <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{c.Texh ?? '—'}</td>
                    <td className="p-1.5 text-center" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{c.health ?? '—'}</td>
                  </tr>
                );
              })}
              <tr style={{ background: 'var(--bg-surface)', fontWeight: 700 }}>
                <td className="p-1.5" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>Mean</td>
                <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{computed.means.Pcomp_iso?.toFixed(1)}</td>
                <td className="p-1.5" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}></td>
                <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{computed.means.Pmax_iso?.toFixed(1)}</td>
                <td className="p-1.5" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}></td>
                <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{computed.means.MIP?.toFixed(2)}</td>
                <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{computed.means.Texh?.toFixed(0) || '—'}</td>
                <td className="p-1.5 text-center" style={{ border: '1px solid var(--br)', verticalAlign: 'middle' }}>{computed.engineHealth}</td>
              </tr>
            </tbody>
          </table>
          </div>
        </ReportSection>

        {/* Section 3b — Cylinder deviation charts */}
        {(() => {
          const TOL = getTolerance(computed.strokeType);
          const strokeLabel = `${computed.strokeType}-stroke`;
          const completeCyls = computed.cyls.filter(c => !c.dataIncomplete);
          return (
            <ReportSection title="3b. Cylinder deviation charts">
              <div data-pdf-block="dev-charts" style={{ breakInside: 'avoid', pageBreakInside: 'avoid', WebkitColumnBreakInside: 'avoid', display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'10px', marginBottom:'10px', minWidth:0 }}>
                <DeviationChart cyls={completeCyls} field="dPmax"    title={`ΔPmax ISO vs Mean — limit ±${TOL.Pmax_bar.warn} bar (${strokeLabel})`}  tol={TOL.Pmax_bar}  unit="bar" />
                <DeviationChart cyls={completeCyls} field="dPcomp"   title={`ΔPcomp ISO vs Mean — limit ±${TOL.Pcomp_bar.warn} bar (${strokeLabel})`} tol={TOL.Pcomp_bar} unit="bar" />
                <DeviationChart cyls={completeCyls} field="dMIP_bar" title={`ΔMIP vs Mean — limit ±${TOL.MIP_bar.warn} bar (${strokeLabel})`}          tol={TOL.MIP_bar}   unit="bar" />
                <DeviationChart cyls={completeCyls} field="dTexh"    title="ΔTexh vs Mean — warn >±20 K, alarm >±50 K"                                  tol={TOL.Texh_K}    unit="K"   />
              </div>
            </ReportSection>
          );
        })()}

        {/* Section 3c — Indicator diagram — one card per cylinder */}
        {parsed?.data?.curves && parsed.data.curves.length > 0 && (
          <ReportSection title="3c. Indicator diagram — per cylinder">
            <div data-pdf-block="indicator-diagrams" style={{ background: '#ffffff', overflow: 'hidden', display: 'block' }}>
              <IndicatorDiagramPerCylinder curves={parsed.data.curves} meta={parsed.data.meta} />
            </div>
          </ReportSection>
        )}

        {/* Section 4 — Findings & priority actions */}
        <ReportSection title="4. Findings & priority actions" noCut>
          {/* Condition alerts summary */}
          {condValidations.length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <p className="text-xs font-semibold" style={{ marginBottom: '4px', fontFamily: 'StevieSans, sans-serif' }}>
                Operating condition alerts: {condInvalids.length > 0 && <span style={{ color:'#991b1b' }}>{condInvalids.length} invalid</span>}
                {condInvalids.length > 0 && (condHighs.length > 0 || condLows.length > 0) && ' · '}
                {condHighs.length > 0 && <span style={{ color:'#713f12' }}>{condHighs.length} high</span>}
                {condHighs.length > 0 && condLows.length > 0 && ' · '}
                {condLows.length > 0 && <span style={{ color:'#1e3a8a' }}>{condLows.length} low</span>}
              </p>
              <ol className="text-xs space-y-1 list-decimal list-inside" style={{ marginBottom: '8px', fontFamily: 'StevieSans, sans-serif' }}>
                {condValidations.map(({ key, label, val }) => (
                  <li key={key} style={{ color: ALERT_COLOR[val.status]?.text, fontFamily: 'StevieSans, sans-serif' }}>
                    <strong>{label} — {val.label}.</strong> {val.guidance}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Cylinder diagnostic findings */}
          {allFindings.length === 0 && condValidations.length === 0 ? (
            <p className="text-xs text-emerald-700">No findings — all conditions within range and all cylinders within tolerance.</p>
          ) : (
            <>
              {allFindings.length > 0 && (
                <>
                  <p className="text-xs mb-2" style={{ fontFamily: 'StevieSans, sans-serif' }}>
                    Cylinder diagnostics: <strong>{critical.length} critical</strong> · <strong>{warnings.length} warnings</strong>
                  </p>
                  <ol className="text-xs space-y-2 list-decimal list-inside" style={{ fontFamily: 'StevieSans, sans-serif' }}>
                    {[...critical, ...warnings].map((f, i) => (
                      <li key={i} style={{ color: f.severity === 'critical' ? 'var(--c-err)' : 'var(--c-warn)', fontFamily: 'StevieSans, sans-serif' }}>
                        <strong>Cyl {f.cyl} — {f.title}.</strong> {f.finding} <span style={{ color: 'var(--t3)' }}>Action: {f.actions[0]}.</span>
                      </li>
                    ))}
                  </ol>
                </>
              )}
              {allFindings.length === 0 && (
                <p className="text-xs text-emerald-700">No cylinder diagnostic findings — all within tolerance.</p>
              )}
            </>
          )}
        </ReportSection>

      </div>

      <div className="print:hidden">
        <NavRow onPrev={onPrev} onNext={null} />
      </div>
    </div>
  );
}

function ReportSection({ title, children, noCut = false }) {
  return (
    <section className="mb-5" style={{ breakInside: noCut ? 'avoid' : undefined, pageBreakInside: noCut ? 'avoid' : undefined, fontFamily: 'StevieSans, sans-serif' }} {...(noCut ? { 'data-pdf-block': 'section' } : {})}>
      <h3 className="text-sm font-bold pb-1 mb-2" style={{ color: 'var(--t1)', borderBottom: '1px solid var(--br)', breakAfter: 'avoid', pageBreakAfter: 'avoid', fontFamily: 'StevieSans, sans-serif' }}>{title}</h3>
      {children}
    </section>
  );
}
function ReportRow({ label, cond, refStr, unit, validation }) {
  const val = validation && validation.status !== 'ok' ? validation : null;
  const ALERT_COLOR = {
    invalid: { bg: 'var(--fill-err)', text: 'var(--c-err)', badge: 'var(--fill-err)', badgeText: 'var(--c-err)', badgeBorder: 'var(--br-err)' },
    high:    { bg: 'var(--fill-warn)', text: 'var(--c-warn)', badge: 'var(--fill-warn)', badgeText: 'var(--c-warn)', badgeBorder: 'var(--br-warn)' },
    low:     { bg: 'var(--fill-info)', text: 'var(--c-info)', badge: 'var(--fill-info)', badgeText: 'var(--c-info)', badgeBorder: 'var(--br-info)' },
  };
  const ac = val ? ALERT_COLOR[val.status] : null;
  return (
    <tr style={ac ? { background: ac.bg } : {}}>
      <td className="p-1.5" style={{ border: '1px solid var(--br)', verticalAlign: 'middle', height: '28px', ...(ac ? { color: ac.text, fontWeight: 600 } : {}) }}>{label}</td>
      <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle', height: '28px', fontFamily: 'StevieSans, sans-serif' }}>{cond?.value != null ? `${cond.value} ${unit}` : '—'}</td>
      <td className="p-1.5 text-right" style={{ border: '1px solid var(--br)', verticalAlign: 'middle', height: '28px' }}>{refStr || '—'}</td>
      <td className="text-center" style={{ border: '1px solid var(--br)', verticalAlign: 'middle', padding: '0 4px', height: '28px' }}>
        {cond?.source === 'FROM FILE' ? (
          <span style={{ display:'inline-flex', alignItems:'center', background:'var(--tag-file-bg)', color:'var(--tag-file-c)', border:'1px solid var(--tag-file-b)', borderRadius:'3px', padding:'0 5px', fontSize:'9px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', lineHeight:'16px', whiteSpace:'nowrap' }}>FROM FILE</span>
        ) : cond?.source === 'MANUAL' ? (
          <span style={{ display:'inline-flex', alignItems:'center', background:'var(--tag-man-bg)', color:'var(--tag-man-c)', border:'1px solid var(--tag-man-b)', borderRadius:'3px', padding:'0 5px', fontSize:'9px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', lineHeight:'16px', whiteSpace:'nowrap' }}>MANUAL</span>
        ) : (
          <span style={{ color:'var(--t4)', fontSize:'10px' }}>—</span>
        )}
      </td>
      <td className="text-center" style={{ border: '1px solid var(--br)', verticalAlign: 'middle', padding: '0 4px', height: '28px' }}>
        {ac ? (
          <span style={{ display:'inline-flex', alignItems:'center', background: ac.badge, color: ac.badgeText, border: `1px solid ${ac.badgeBorder}`, borderRadius: '3px', padding: '0 5px', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight:'16px', whiteSpace:'nowrap' }}>
            {val.label}
          </span>
        ) : cond?.value != null ? (
          <span style={{ color: '#15803d', fontSize: '10px' }}>✓</span>
        ) : (
          <span style={{ color: '#94a3b8', fontSize: '10px' }}>—</span>
        )}
      </td>
    </tr>
  );
}

// =================================================================
//  SHARED UI
// =================================================================
// ─── Per-step help content ────────────────────────────────────────────────────
const STEP_HELP = {
  upload: {
    title: 'Upload — what to do here',
    steps: [
      'Upload a PMI Mark II / Leutert CSV file exported from the indicator card system, or a Test Report XLSX file from a speed trial.',
      'The tool will automatically read engine ID, bore, stroke, cylinder count, and per-cylinder Pmax / MIP / Pcomp values from the file.',
      'If you have previously saved analyses, switch to the History tab to reopen them.',
    ],
    note: 'Only one file per analysis session. To analyse a different engine, press Reset at the top.',
  },
  engine: {
    title: 'Engine Profile — what to do here',
    steps: [
      'Review the engine parameters automatically detected from your uploaded file.',
      'Check that the stroke type (2-stroke / 4-stroke), cylinder count, bore, and stroke all match the physical engine.',
      'If anything looks wrong, return to Upload and check your file, or confirm the correct engine type in the Shop Trial step.',
    ],
    note: 'These values are read-only — they come from the file. If they are incorrect, the file may be from a different engine.',
  },
  testload: {
    title: 'Test Load — what to do here',
    steps: [
      'Enter the shaft power or select the % MCR at which this performance test was conducted.',
      'The tool uses this load point to look up the corresponding shop-trial reference values for ISO correction.',
      'The chart below shows where your test load falls on the shop trial Pmax and Pcomp curves.',
    ],
    note: 'Use the shaft power field for the most accurate load calculation. Ensure the value matches the engine room power meter at the time of measurement.',
  },
  shopmatch: {
    title: 'Shop Trial — what to do here',
    steps: [
      'Select the shop trial reference record that matches the engine you are testing.',
      'The tool uses these shop trial curves to compute ISO-corrected Pmax, Pcomp, scavenge pressure, turbine temperatures, and SFOC.',
      'If no matching entry exists, add one manually using "Add manually" or import from an XLSX file using "Import from XLSX".',
    ],
    note: 'Selecting the wrong engine type or cylinder count will produce incorrect ISO corrections and diagnostic findings. The validation panel will warn you if there is a mismatch.',
  },
  conditions: {
    title: 'Conditions — what to do here',
    steps: [
      'Review the ambient and charge-air conditions. Values tagged FROM FILE were captured automatically from the uploaded file.',
      'Enter any empty fields (marked with *) from the engine room log or instrument readings recorded at the time of the test.',
      'Enter per-cylinder measured values (Pcomp, Pmax, MIP, Texh, Fuel index, VIT) in the table below if they are not pre-filled.',
    ],
    note: 'The four fields required for ISO correction (Blower inlet temp, Barometric pressure, Scavenge air temp, Turbine back pressure) are marked with *. ISO corrections will be skipped for any cylinder where these are missing.',
  },
  iso: {
    title: 'ISO Correction — what to do here',
    steps: [
      'Review the ISO 3046-corrected values computed from your measured conditions and shop-trial reference.',
      'The reference conditions table shows the standard values your measurements are being normalised to.',
      'The engine-level corrections table shows how Pscav, TC speed, turbine inlet temperature, and barometric pressure have been adjusted.',
    ],
    note: 'ISO correction removes the effect of varying ambient conditions so that measurements taken on different days and in different climates can be compared fairly against the shop trial baseline.',
  },
  analysis: {
    title: 'Analysis — what to do here',
    steps: [
      'Review the engine health score and cylinder-by-cylinder balance. Each cylinder is scored out of 100 based on diagnostic findings.',
      'Examine the deviation charts — bars crossing the dashed limit lines indicate cylinders outside tolerance.',
      'Use the indicator diagram to compare P-θ curves. Click cylinder buttons to show individual units or any combination simultaneously.',
      'Read the diagnostic findings for root-cause guidance and recommended actions for any out-of-tolerance cylinder.',
    ],
    note: 'Findings are only generated when deviations exceed the tolerance limits defined for your engine type (2-stroke: ΔPmax/Pcomp ±3 bar, ΔMIP ±0.7 bar | 4-stroke: ΔPmax/Pcomp ±5 bar, ΔMIP ±1.4 bar).',
  },
  report: {
    title: 'Report — what to do here',
    steps: [
      'Review the full performance report summarising engine identification, operating conditions, cylinder balance, and diagnostic findings.',
      'Press "Save to history" to store this analysis — it will appear in the History tab on the Upload screen.',
      'Press "Print / PDF" to open a print-ready version in a new window, then use your browser\'s print function to save as PDF or print.',
    ],
    note: 'The report is only saved when you explicitly click "Save to history". Opening historical reports does not create duplicate entries.',
  },
};

// ─── Help popup component ──────────────────────────────────────────────────────
function PageHelp({ stepId }) {
  const [open, setOpen] = useState(false);
  const help = STEP_HELP[stepId];
  if (!help) return null;

  return (
    <>
      {/* Help button */}
      <button
        onClick={() => setOpen(true)}
        title="What to do on this page"
        style={{
          display:'inline-flex', alignItems:'center', gap:'5px',
          padding:'4px 10px', borderRadius:'5px', fontSize:'11px', fontWeight:500,
          background:'var(--fill-info)', border:'1px solid var(--br-info)',
          color:'var(--c-info)', cursor:'pointer', flexShrink:0,
        }}>
        <Info style={{ width:'12px', height:'12px' }} />
        Help
      </button>

      {/* Backdrop + modal */}
      {open && (
        <div
          style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background:'var(--bg-panel)', border:'1px solid var(--br-mid)',
              borderRadius:'10px', padding:'24px', maxWidth:'520px', width:'100%',
              boxShadow:'0 20px 60px rgba(0,0,0,0.3)',
            }}>
            {/* Header */}
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'16px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <div style={{ width:'28px', height:'28px', borderRadius:'6px', background:'var(--fill-ok)', border:'1px solid var(--br-ok)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Info style={{ width:'14px', height:'14px', color:'var(--accent)' }} />
                </div>
                <h3 style={{ fontSize:'15px', fontWeight:700, color:'var(--t1)', fontFamily:'StevieSans,sans-serif', margin:0 }}>
                  {help.title}
                </h3>
              </div>
              <button onClick={() => setOpen(false)}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--t3)', padding:'4px', borderRadius:'4px', lineHeight:1 }}>
                <X style={{ width:'16px', height:'16px' }} />
              </button>
            </div>

            {/* Steps */}
            <ol style={{ margin:'0 0 14px', padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:'10px' }}>
              {help.steps.map((s, i) => (
                <li key={i} style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                  <span style={{ width:'20px', height:'20px', borderRadius:'50%', background:'var(--accent)', color:'#fff', fontSize:'11px', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:'1px' }}>
                    {i + 1}
                  </span>
                  <span style={{ fontSize:'13px', color:'var(--t2)', lineHeight:'1.55' }}>{s}</span>
                </li>
              ))}
            </ol>

            {/* Note */}
            {help.note && (
              <div style={{ padding:'10px 12px', borderRadius:'6px', background:'var(--fill-warn)', border:'1px solid var(--br-warn)', display:'flex', gap:'8px', alignItems:'flex-start' }}>
                <AlertCircle style={{ width:'14px', height:'14px', color:'var(--c-warn)', flexShrink:0, marginTop:'1px' }} />
                <span style={{ fontSize:'12px', color:'var(--c-warn)', lineHeight:'1.5' }}>{help.note}</span>
              </div>
            )}

            <button onClick={() => setOpen(false)}
              style={{ marginTop:'16px', width:'100%', padding:'8px', borderRadius:'6px', background:'var(--btn-p-bg)', border:'1px solid var(--btn-p-b)', color:'var(--btn-p-c)', fontSize:'13px', fontWeight:600, cursor:'pointer' }}>
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function SectionHeader({ icon: Ic, title, subtitle, inline, stepId }) {
  return (
    <div className={inline ? 'flex items-center gap-3' : 'mb-5'}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'12px', width: inline ? 'auto' : '100%' }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded flex items-center justify-center"
            style={{ background: 'var(--fill-ok)', border: '1px solid var(--br-ok)' }}>
            <Ic className="w-4 h-4" style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h2 className="text-base font-bold" style={{ fontFamily: 'StevieSans,sans-serif', color: 'var(--t1)' }}>{title}</h2>
            {subtitle && <p className="text-xs" style={{ color: 'var(--t3)' }}>{subtitle}</p>}
          </div>
        </div>
        {stepId && !inline && <PageHelp stepId={stepId} />}
      </div>
    </div>
  );
}
function Panel({ title, icon: Ic, children, className = '' }) {
  return (
    <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--br)', borderRadius: '8px' }}
      className={`p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3 pb-2" style={{ borderBottom: '1px solid var(--br)' }}>
        <Ic className="w-4 h-4" style={{ color: 'var(--accent)' }} />
        <h3 className="text-sm font-semibold" style={{ fontFamily: 'StevieSans,sans-serif', color: 'var(--t1)' }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}
function DefList({ items }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs">
      {items.map(([k, v], i) => (
        <React.Fragment key={i}>
          <dt className="text-slate-500" style={{ fontFamily: 'StevieSans, sans-serif' }}>{k}</dt>
          <dd className="text-slate-200" style={{ fontFamily: 'StevieSans, sans-serif' }}>{v ?? '—'}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
function DefRow({ label, value }) {
  return (
    <>
      <span className="text-slate-500" style={{ fontFamily: 'StevieSans, sans-serif' }}>{label}</span>
      <span className="text-slate-200" style={{ fontFamily: 'StevieSans, sans-serif' }}>{value}</span>
    </>
  );
}
function Tag({ children, tone = 'slate', small = false, large = false }) {
  const styleMap = {
    amber:   { bg:'var(--tag-man-bg)', color:'var(--tag-man-c)', border:'1px solid var(--tag-man-b)' },
    cyan:    { bg:'var(--tag-file-bg)', color:'var(--tag-file-c)', border:'1px solid var(--tag-file-b)' },
    emerald: { bg:'var(--tag-ok-bg)', color:'var(--tag-ok-c)', border:'1px solid var(--tag-ok-b)' },
    red:     { bg:'var(--tag-err-bg)', color:'var(--tag-err-c)', border:'1px solid var(--tag-err-b)' },
    slate:   { bg:'var(--tag-sl-bg)', color:'var(--tag-sl-c)', border:'1px solid var(--tag-sl-b)' },
  };
  const s = styleMap[tone] || styleMap.slate;
  const fontSize = small ? '9px' : large ? '13px' : '10px';
  const padding  = small ? '1px 6px' : large ? '4px 10px' : '2px 8px';
  return (
    <span style={{
      display: 'inline-block', borderRadius: '4px',
      background: s.bg, color: s.color, border: s.border,
      fontSize, padding,
      textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500,
      fontFamily: 'StevieSans, sans-serif',
    }}>{children}</span>
  );
}
function NavRow({ onPrev, onNext, canNext }) {
  return (
    <div className="flex justify-between items-center mt-6 pt-4" style={{ borderTop: '1px solid var(--br)' }}>
      {onPrev ? (
        <button onClick={onPrev}
          style={{ border:'1px solid var(--br-mid)', color:'var(--t3)', borderRadius:'6px', padding:'6px 14px', background:'transparent', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:'5px' }}>
          <ChevronLeft className="w-3 h-3" />Back
        </button>
      ) : <span />}
      {onNext && (
        <button onClick={onNext} disabled={!canNext}
          style={canNext
            ? { background:'var(--btn-p-bg)', border:'1px solid var(--btn-p-b)', color:'var(--btn-p-c)', borderRadius:'6px', padding:'6px 14px', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:'5px' }
            : { background:'var(--fill-ok)', border:'1px solid var(--br)', color:'var(--t4)', borderRadius:'6px', padding:'6px 14px', cursor:'not-allowed', fontSize:'12px', display:'flex', alignItems:'center', gap:'5px', opacity:0.5 }}>
          Next<ChevronRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}